import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Pencil, Trash2, X, Check, AlertTriangle, Wallet, Home, Receipt,
  ChevronRight, ChevronLeft, Phone, UserPlus, Zap, Droplets, Flame, Sparkles, Send, Undo2, Loader2, Gauge, Camera, FileText, Upload, MessageCircle,
  Eye, Settings, User, CheckCircle2, CalendarPlus, PenTool, ArrowRight, Fingerprint, ShieldCheck, Share2, Smartphone,
  ArrowUp, ArrowDown, ArrowUpCircle, ArrowDownCircle, Lock, LogOut, Copy, Download, TrendingUp, Landmark, ChevronDown, History, PiggyBank,
} from "lucide-react";
import { ShareAppPanel } from "./ShareApp.jsx";
import FeedbackPanel from "./FeedbackPanel.jsx";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

import {
  BILLS, BILL_ALIASES, CATS, CUR_KEY, CUR_M, CUR_Y, METERS, METER_LABEL,
  MONTHS, MONTHS_SHORT, TODAY, UNIT,
  activeIn, addYears, applyActionFocused,
  billsCycleOf, billsDayOf, billsDue, billsText, consumptionBillsText, rentText, contractText, contractsOf, currentTenancy, currentDebt,
  SHABBAT_CLAUSE_TITLE, SHABBAT_CLAUSE_TEXT,
  rentDebtRows, vaadDebtRows, consDebtRows, rentExpectedRows, rentCollectedRows, vaadCollectedRows, consPaidRows, expensesRows, totalCollectedRows, totalDebtRows,
  daysBetween, debtStatement, DEBT_RESOLUTIONS, defTariffs, deletedTenancies, emptyBill, findApt, heDate,
  EXTENSION_STATUS, extensionRequestsOf, pendingExtensionRequest, pendingExtensionRequests,
  methodBreakdown, methodLabel, payMethodOf, PAY_METHODS, PAY_METHOD_BY_KEY, defaultMethod,
  billsAnchorOf, billsMeter, billsOutstanding, statementText, unpaidBillMonths, effectiveRent, FIXED_BILLS,
  billsUtility, fixedBillOn, consumptionForMonth, billingMonthFor, fmtQty, roundBillAmount, utilityMode,
  latestValue, loanFor, loanState, purchaseEquity, currentEquity, projectValue, refinanceQuote,
  now, pad, UTILITY_MODE_KEYS, isBillingMonth, itemDue,
  itemPaid, lastReading, mKey, messageMode, migrate,
  missingFor, monthLabel, nextBillingMonth, nextMonth, nis, norm,
  pastDebts, pastTenancies, prevMonth, PROPERTY_TYPES, propertyType, resolvedDebts,
  rateFor, rateGas, readingSanity, readingsOf, rentDayOf, securityTotal,
  rentOverdue, rentPaidOf, rentTax, TAX_TRACKS, rowKey, setGuarantors, tenanciesOf, targetTenancy, tenancyEnd,
  tenancyForMonth, uid, upcomingTenancies, waLink, syncApartments, TEN_FIELDS, withPaidThrough,
  recordMeterReading, updateMeterReading, deleteMeterReading,
} from "@rental/shared";

import { C, SUN, BILL_ICONS, METER_HUES, inputStyle, readingInputStyle, fontStack, aptTint } from "./theme.js";
import {
  ApiError, ai, data as dataApi, fileUrl, portal as portalApi, uploadImage,
} from "./lib/api.js";
import { compressForUpload, readFile } from "./lib/images.js";
import { TEXT_SCALES, setTextScale, textScale } from "./lib/textScale.js";
import * as passkey from "./lib/passkey.js";
import { useInstallPrompt } from "./lib/installPrompt.js";
import { BackLayer, DateField, FilePick, Modal, StoredImage, useBackLayer } from "./ui.jsx";
import { SignaturePad, canvasHasInk } from "./SignaturePad.jsx";
import { SecureImage } from "./SecureImage.jsx";
import VaultSetup from "./VaultSetup.jsx";
import TrialBanner from "./TrialBanner.jsx";
import PasswordCard from "./PasswordCard.jsx";
import SecuritiesPicker, { SecuritiesPick, rentPayText, securitiesLabel } from "./SecuritiesPicker.jsx";
import ZoomLightbox, { LightboxHost, openLightbox } from "./ZoomLightbox.jsx";
import InventoryPick, { inventoryLines, inventorySummary } from "./InventoryPick.jsx";
import CollectionBoards from "./CollectionBoards.jsx";
import { MeterBlock } from "./MeterBlock.jsx";
import MethodPick, { MethodDefaults, MethodTag } from "./MethodPick.jsx";
import { LegalLinks } from "./Legal.jsx";
import * as vault from "./lib/keyvault.js";

/* יומן השיחה של העוזר — מקומי למכשיר, עבר מ-window.storage ל-localStorage.

   המפתח כולל את מזהה החשבון: שני בעלי נכסים שנכנסים מאותו דפדפן לא
   אמורים לראות זה את השיחה של זה (שמות דיירים, סכומים, "הנכס בפוקוס").
   המפתח הישן, הלא-ממופתח, נמחק בטעינה הראשונה — אי אפשר לדעת של מי הוא. */
const CHAT_KEY_LEGACY = "rentals:chat";
const chatKeyFor = (accountId) => (accountId ? `rentals:chat:${accountId}` : CHAT_KEY_LEGACY);
export function clearAssistantChat(accountId) {
  try { localStorage.removeItem(chatKeyFor(accountId)); localStorage.removeItem(CHAT_KEY_LEGACY); } catch { /* מצב פרטי */ }
}

/* BILLS ב-@rental/shared לא נושא אייקונים, כדי שהמודול ירוץ גם ב-Node.
   כאן הוא מורכב מחדש לתצוגה. */
const BILLS_UI = BILLS.map((b) => ({ ...b, icon: BILL_ICONS[b.k] }));

/* אירועים מצד הדייר שמסך הניהול מציג כהודעה צפה עם צליל. סוג שלא ברשימה
   (קוד שרת חדש מול לקוח ישן) פשוט מחכה עד שהלקוח יתעדכן — הוא לא נמחק. */
const NOTICE_TYPES = new Set(["contract_signed", "guarantor_signed", "extension_requested", "tenant_message", "platform_notice", "payment_reported"]);

/* תיבת ההודעות — איך כל סוג אירוע מוצג: אייקון, כותרת, ולאן הכפתור מוביל.
   סוג לא מוכר (שרת חדש מול לקוח ישן) מוצג כשורה כללית ולא נבלע. */
const INBOX_KINDS = {
  contract_signed: { Icon: PenTool, color: "#2E7D5B", title: (m) => `${m.tenant || "הדייר"} חתם/ה על החוזה`, go: "docs", goLabel: "הצגת החוזה" },
  guarantor_signed: { Icon: PenTool, color: "#2E7D5B", title: (m) => `${m.guarantor || "ערב"} חתם/ה על כתב הערבות${m.tenant ? ` של ${m.tenant}` : ""}`, go: "docs", goLabel: "לחוזים ותיעוד" },
  extension_requested: { Icon: CalendarPlus, color: "#B8860B", title: (m) => `${m.tenant || "הדייר"} ביקש/ה להאריך את החוזה${m.to ? ` עד ${heDate(m.to)}` : ""}`, go: "docs", goLabel: "הצגת הבקשה" },
  extension_withdrawn: { Icon: CalendarPlus, color: "#8A8F8A", title: (m) => `${m.tenant || "הדייר"} ביטל/ה את בקשת ההארכה`, go: "docs", goLabel: "לחוזים" },
  reading_submitted: { Icon: Gauge, color: "#2E8FCC", title: (m) => `${m.tenant || "הדייר"} דיווח/ה קריאת ${METER_LABEL[m.meter] || "מונה"}: ${m.value ?? ""}${m.billed && m.amount ? ` · חשבון ${nis(m.amount)}` : ""}`, go: "meters", goLabel: "למונים וחשבונות" },
  // דיווח העברה בנקאית — לא מסמן כלום כשולם לבד, ראו report_payment.
  payment_reported: { Icon: Receipt, color: "#B8860B", title: (m) => `${m.tenant || "הדייר"} דיווח/ה תשלום ${m.category === "bills" ? "חשבונות" : "שכר דירה"} ב${methodLabel(m.method) || m.method || ""}: ${nis(m.amount || 0)}${m.note ? ` · ${m.note}` : ""}`, go: "meters", goLabel: "פתיחת כרטיס הנכס" },
  tenant_message: { Icon: MessageCircle, color: "#128C7E", title: (m) => `${m.tenant || "הדייר"}: ${m.text || ""}`, go: null, goLabel: "" },
  // התשובה של בעל הנכס — חלק מההיסטוריה, בלי צליל ובלי "לא נקרא".
  owner_reply: { Icon: Send, color: "#2E8FCC", title: (m) => `השבת ל${m.tenant || "דייר"}: ${m.text || ""}`, go: null, goLabel: "" },
  // הודעה מהנהלת המערכת (בעל הפלטפורמה) — לבעל הנכס.
  platform_notice: { Icon: ShieldCheck, color: "#7A2E1E", title: (m) => `הנהלת המערכת: ${m.text || ""}`, go: null, goLabel: "" },
};

/**
 * בוחר קובץ → דוחס → מעלה ל-Spaces → מחזיר מזהה.
 * מחליף את savePhoto שכתב ל-window.storage. המזהה שנשמר במסמך זהה בצורתו
 * לקודם, ולכן כל אתרי הקריאה במסמך ממשיכים לעבוד בלי שינוי.
 *
 * encrypt: true מצפין בדפדפן לפני ההעלאה. נכון לצילומי תעודות זהות בלבד —
 * צילומי מונה חייבים להישאר קריאים לשרת כדי שזיהוי הקריאה יעבוד, וחתימות
 * מופיעות על החוזה שגם הדייר צריך לראות.
 *
 * חשבון שלא הפעיל הצפנה מעלה גלוי — לא נזרקת שגיאה. זה מה שכרטיס ההצפנה
 * מבטיח ("נשמרים כרגע בלי הצפנה") ומה שהקישור האישי של הערב עושה ממילא;
 * הזריקה כאן היא שגרמה לצילומי ת"ז להיעלם בלי שום הודעה.
 */
async function attachFile(file, { encrypt = false, scope = "owner", ...opts } = {}) {
  // וידיאו לא מוצפן ולא עובר דחיסת תמונה (שדוחפת את כל הקובץ ל-base64
  // בזיכרון — על סרטון מהטלפון זה יכול לתקוע את הדפדפן): עולה כקובץ גולמי.
  if (!encrypt && typeof file !== "string" && file.type?.startsWith("video/")) {
    return uploadImage(file, { scope, ...opts });
  }
  // מקבל גם data URL שכבר נדחס, כדי שמי שצריך את התמונה גם לזיהוי לא
  // ידחוס אותה פעמיים ולא ישלח לשרת גרסה אחרת מזו שנשמרה.
  const dataUrl = typeof file === "string" ? file : await compressForUpload(file);
  if (!encrypt) return uploadImage(dataUrl, { scope, ...opts });

  /* ownerPublicKey ולא publicKey: הסינכרוני מלא רק אחרי שמסך ההצפנה נטען,
     ובאשף או בקליטה מגיעים לצילום בלי לעבור שם — המפתח חייב להיטען כאן. */
  const pub = scope === "tenant" ? await vault.tenantPublicKey() : await vault.ownerPublicKey();
  if (!pub) return uploadImage(dataUrl, { scope, ...opts });
  const sealed = await vault.sealForUpload(dataUrl, pub);
  return uploadImage(dataUrl, { scope, ...opts, sealed });
}

/* ============================================================================
   ההזמנה לאזור האישי.

   ההודעה הראשונה שהדייר מקבל היא זו ששולחת לו את החוזה, וזה הרגע היחיד
   שבו הוא בטוח קורא. בלי קישור וקוד באותה הודעה הוא נשאר עם "נא להיכנס
   לאזור הדייר" ובלי מושג לאן, ובעל הנכס מגלה את זה רק כשאף אחד לא חתם.
   ============================================================================ */

const portalUrl = () => window.location.origin;

/** קישור ישיר לאזור של הדייר — נכנס בלחיצה, בלי להקליד קוד. */
const tenantLink = (code) => `${portalUrl()}/t/${code}`;

/**
 * השורות שנספחות לכל הודעה שמפנה את הדייר לאזור האישי.
 *
 * הקישור לבד בשורה שלו: וואטסאפ מזהה קישור רק כשהוא עומד בפני עצמו,
 * וטקסט שנדבק אליו הופך אותו לטקסט רגיל שאי אפשר ללחוץ עליו.
 */
function inviteLines(code) {
  if (!code) {
    return `\n\nהאזור האישי שלך:\n${portalUrl()}\nאשלח לך את הסיסמה בהודעה נפרדת.`;
  }
  return `\n\nהאזור האישי שלך — לחיצה על הקישור נכנסת ישירות:\n${tenantLink(code)}`;
}

/**
 * הקוד הפעיל של הדירה, לצורך ההזמנה.
 *
 * כשאין קוד פעיל — מנפיק אחד. זה בטוח דווקא כאן: הנפקה מבטלת קוד קודם,
 * ואם אין קודם אין מה לבטל. קוד ישן שנוצר לפני ההצפנה יחזור כ-null ואז
 * ההודעה תישלח בלי קוד, כי הנפקה חדשה הייתה נועלת דייר שכבר מחובר.
 */
async function ensurePortalCode(aptId) {
  try {
    const r = await dataApi.accessCode(aptId);
    if (r.active) return r.code || null;
    return (await dataApi.issueCode(aptId)).code;
  } catch {
    // בלי קוד ההודעה עדיין נשלחת, רק עם הקישור.
    return null;
  }
}

/* --------------------------------------------------------------- ערבים --- */

/** הכתובת שהערב מקבל. הנתיב הזה נתפס ב-Root לפני כל בדיקת סשן. */
const guarantorUrl = (token) => `${portalUrl()}/g/${token}`;

/**
 * מנפיק קישור חתימה לערב ומחזיר { url, expiresAt }.
 *
 * שני צדדים מנפיקים את אותו קישור: הדייר מהאזור שלו, ובעל הנכס ממסך
 * הניהול. הנתיב שונה, התוצאה זהה, ולכן ההבדל היחיד כאן הוא באיזו קריאה
 * להשתמש.
 */
async function issueGuarantorLink({ tenantMode, aptId, guarantorId, force = false }) {
  const r = tenantMode
    ? await portalApi.guarantorLink(guarantorId, force)
    : await dataApi.guarantorLink(aptId, guarantorId, force);
  return { url: guarantorUrl(r.token), expiresAt: r.expiresAt, reused: r.reused };
}

/** ההודעה שהערב מקבל. */
const guarantorMessage = ({ name, tenant, apartment, url }) =>
  `שלום ${name || ""}, אני ${tenant || ""}. אשמח שתחתום כערב על הסכם השכירות שלי ב${apartment || ""}.\n\n`
  + `בקישור הזה אפשר לקרוא את החוזה, לצלם תעודת זהות ולחתום — הכול מהטלפון, בלי להירשם:\n${url}\n\n`
  + `הקישור אישי ומיועד לך בלבד.`;

function usePortalCode(aptId, enabled = true) {
  const [code, setCode] = useState(null);

  useEffect(() => {
    if (!aptId || !enabled) return undefined;
    let alive = true;
    ensurePortalCode(aptId).then((c) => { if (alive) setCode(c); });
    return () => { alive = false; };
  }, [aptId, enabled]);

  return code;
}

/* ------------------------------------------------------ צליל ההתראה --- */

/**
 * צליל "התקבלה הודעה" קצר, מסונתז ב-WebAudio — בלי קובץ שמע ובלי רשת.
 *
 * דפדפנים חוסמים שמע עד מחווה ראשונה של המשתמש, ולכן ה-context נוצר
 * ומופעל ב-primeChime על הלחיצה/הקשה הראשונה במסך הניהול. אם הצליל
 * בכל זאת נחסם (לשונית שנפתחה ולא נגעו בה) — ההתראה החזותית עדיין שם.
 */
let chimeCtx = null;

function primeChime() {
  try {
    chimeCtx = chimeCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (chimeCtx.state === "suspended") chimeCtx.resume().catch(() => {});
  } catch { /* אין WebAudio — נשארים עם התראה חזותית */ }
}

function playChime() {
  try {
    primeChime();
    if (!chimeCtx || chimeCtx.state !== "running") return;
    const t0 = chimeCtx.currentTime + 0.02;
    // שני תווים עולים — סול-דו — כמו צליל הודעה נכנסת.
    [[784, 0], [1046.5, 0.16]].forEach(([freq, dt]) => {
      const osc = chimeCtx.createOscillator();
      const gain = chimeCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + dt);
      gain.gain.exponentialRampToValueAtTime(0.14, t0 + dt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.55);
      osc.connect(gain).connect(chimeCtx.destination);
      osc.start(t0 + dt);
      osc.stop(t0 + dt + 0.6);
    });
  } catch { /* שקט עדיף על קריסה */ }
}

/* ================= האפליקציה ================= */
/* ---------------- השלמת פרטי בעל הנכס — מיד אחרי ההרשמה ----------------
   שם, ת"ז, טלפון וכתובת נכנסים לכל חוזה כצד המשכיר, והחתימה מופיעה לצד
   חתימת הדייר. בלעדיהם החוזה יוצא עם קווים ריקים ("בין: ________"). לכן:
   חלון שקופץ בכל כניסה כל עוד משהו חסר, עם "אחר כך" שמשתיק אותו לסשן
   הזה בלבד — ותזכורת קבועה במסך הבית. */
const ownerGaps = (s) => [
  !String(s?.ownerName || "").trim() && "שם מלא",
  String(s?.ownerId || "").replace(/\D/g, "").length < 5 && "תעודת זהות",
  !String(s?.ownerPhone || "").trim() && "טלפון",
  !String(s?.ownerAddress || "").trim() && "כתובת",
  !(s?.ownerSignature || s?.ownerSignatureId) && "חתימה",
].filter(Boolean);

/* תזכורת: קריאת מונה ראשונית שחסרה לדייר שנכנס לאחרונה — למשל כי דילגו
   על השאלה באשף כי הדייר עוד לא נכנס לנכס באותו רגע. נגזר לגמרי מהקריאות
   והתקופות הקיימות, בלי לשמור שום דבר חדש. מוגבל לתקופות שהתחילו לאחרונה
   כדי לא להטריד לנצח על תקופות ותיקות שקדמו לתכונה הזו ואין להן דרך אמיתית
   להשלים את הקריאה בדיעבד. */
const ENTRY_READING_WINDOW_DAYS = 90;
const missingEntryReadings = (data) => {
  const floorDate = new Date(Date.now() - ENTRY_READING_WINDOW_DAYS * 86400000).toISOString().slice(0, 10);
  return data.apartments
    .filter((a) => a.tenant && a.leaseStart && a.leaseStart <= TODAY && a.leaseStart >= floorDate)
    .map((a) => ({ apt: a, meters: METERS.filter((m) => utilityMode(a, m) !== "none" && !lastReading(data, a.id, m)) }))
    .filter((x) => x.meters.length > 0);
};

const GATE_SNOOZE_KEY = "ownerGateSnoozed";

function OwnerDetailsGate({ data, setData, me, onClose }) {
  const s = data.settings || {};
  const [f, setF] = useState({
    ownerName: s.ownerName || me?.name || "",
    ownerId: s.ownerId || "",
    ownerPhone: s.ownerPhone || "",
    ownerAddress: s.ownerAddress || "",
    ownerEmail: s.ownerEmail || me?.email || "",
  });
  const [sig, setSig] = useState(null);          // dataURL של חתימה שצוירה עכשיו
  const [resign, setResign] = useState(false);   // "חתימה מחדש" על חתימה קיימת
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); };

  const existingSig = !resign && (s.ownerSignature || s.ownerSignatureId);
  const hasSig = !!(existingSig || sig);
  const missing = [
    !f.ownerName.trim() && "שם מלא",
    f.ownerId.replace(/\D/g, "").length < 5 && "תעודת זהות",
    !f.ownerPhone.trim() && "טלפון",
    !f.ownerAddress.trim() && "כתובת",
    !hasSig && "חתימה",
  ].filter(Boolean);

  const saveAll = async () => {
    if (missing.length) { setErr(`חסר עדיין: ${missing.join(", ")}`); return; }
    setBusy(true); setErr("");
    try {
      let sigPatch = {};
      if (sig) {
        const id = await uploadImage(sig, { scope: "owner", kind: "signature" });
        sigPatch = { ownerSignatureId: id, ownerSignature: null };
      }
      setData((d) => ({ ...d, settings: { ...d.settings, ...f, ...sigPatch } }));
      onClose(true);
    } catch (e) {
      setErr(`השמירה נכשלה: ${e.message}`);
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="השלמת פרטי בעל הנכס"
      className="fixed inset-0 flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(20,38,43,0.55)", zIndex: 90 }}>
      <div className="w-full max-w-lg rounded-2xl p-4 sm:p-5 overflow-y-auto" style={{ background: C.card, maxHeight: "94vh" }}>
        <div className="flex items-center gap-2 mb-1">
          <PenTool size={18} style={{ color: C.ochre }} />
          <h2 className="disp text-xl font-bold">השלמת פרטי בעל הנכס</h2>
        </div>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          הפרטים האלה נכנסים לכל חוזה כצד המשכיר, והחתימה מופיעה לצד חתימת הדייר.
          בלעדיהם החוזה יוצא עם קווים ריקים. אפשר לשנות אותם בכל רגע ב"עוד" ← "אזור אישי".
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="שם מלא"><input style={inputStyle} value={f.ownerName} onChange={set("ownerName")} placeholder="השם שיופיע בחוזה" autoFocus /></Field>
          <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={f.ownerId} onChange={set("ownerId")} /></Field>
          <Field label="טלפון"><PhoneField value={f.ownerPhone} onChange={(v) => { setF({ ...f, ownerPhone: v }); setErr(""); }} /></Field>
          <Field label="דוא״ל"><input style={inputStyle} value={f.ownerEmail} onChange={set("ownerEmail")} /></Field>
          <div className="sm:col-span-2">
            <Field label="כתובת / יישוב"><input style={inputStyle} value={f.ownerAddress} onChange={set("ownerAddress")} placeholder="גם מקום עריכת החוזה" /></Field>
          </div>
        </div>

        <div className="mt-3">
          <div className="text-xs mb-1" style={{ fontWeight: 600 }}>החתימה שלי</div>
          {existingSig ? (
            <div className="flex items-center gap-3">
              {s.ownerSignature
                ? <img src={s.ownerSignature} alt="חתימה" className="rounded border" style={{ height: 56, borderColor: C.line }} />
                : <StoredImage id={s.ownerSignatureId} className="rounded border" style={{ height: 56, borderColor: C.line }} />}
              <span className="text-xs" style={{ color: C.paid }}>✓ יש חתימה שמורה</span>
              <button type="button" onClick={() => setResign(true)} className="text-xs" style={{ color: C.muted }}>חתימה מחדש</button>
            </div>
          ) : sig ? (
            <div className="flex items-center gap-3">
              <img src={sig} alt="חתימה" className="rounded border" style={{ height: 56, borderColor: C.line, background: "#fff" }} />
              <span className="text-xs" style={{ color: C.paid }}>✓ החתימה נקלטה — תישמר עם הפרטים</span>
              <button type="button" onClick={() => setSig(null)} className="text-xs" style={{ color: C.muted }}>לחתום שוב</button>
            </div>
          ) : (
            <>
              <SignaturePad saveLabel="אישור החתימה" onSave={async (dataUrl) => { setSig(dataUrl); setErr(""); }} />
              <p className="text-[11px] mt-1" style={{ color: C.muted }}>ציירו באצבע או בעכבר ולחצו "אישור החתימה".</p>
            </>
          )}
        </div>

        {err && <div className="mt-3 rounded-lg p-2 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>}
        {!err && missing.length > 0 && (
          <div className="mt-3 text-[11px]" style={{ color: C.muted }}>חסר עדיין: {missing.join(", ")}</div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <button type="button" onClick={() => onClose(false)} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
            אחר כך
          </button>
          <button type="button" onClick={saveAll} disabled={busy} className="text-sm rounded-lg px-4 py-2"
            style={{ background: missing.length ? "#8FA9A7" : C.teal, color: "#fff", opacity: busy ? 0.6 : 1 }}>
            {busy ? "שומר…" : "שמירה — הפרטים ייכנסו לחוזים"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RentalApp({ me, onSignOut }) {
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(false);
  const [tab, setTabRaw] = useState("overview");
  /* מעבר לשונית נרשם בהיסטוריה: "אחורה" בטלפון חוזר ללשונית הקודמת
     במקום לצאת מהאתר. חלונות ומסכים פנימיים מטופלים ב-useBackLayer. */
  const setTab = (k) => {
    setTabRaw(k);
    try { window.history.pushState({ ...(window.history.state || {}), tab: k, layers: [] }, ""); } catch { /* אין היסטוריה */ }
  };
  useEffect(() => {
    try { window.history.replaceState({ ...(window.history.state || {}), tab: "overview", layers: [] }, ""); } catch { /* אין היסטוריה */ }
    const onPop = () => {
      const st = window.history.state;
      if (st?.tab && !(st.layers || []).length) setTabRaw(st.tab);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [portal, setPortal] = useState(null);
  const [intake, setIntake] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [conflict, setConflict] = useState(null);
  const [year, setYear] = useState(CUR_Y);
  const [modal, setModal] = useState(null);
  const [saveState, setSaveState] = useState("");
  /* המנוי במצב קריאה-בלבד: השרת מחזיר 402 על כל כתיבה. בלי הדגל הזה
     העריכה "הצליחה" על המסך, נכשלה בשקט, ונעלמה ברענון. */
  const [readOnly, setReadOnly] = useState(false);
  const [undo, setUndo] = useState(null);
  const [dismissedWelcome, setDismissedWelcome] = useState(false);
  // מונה ולא בוליאני: לחיצה חוזרת על "בעזרת העוזר" מתחילה אשף מחדש.
  const [guided, setGuided] = useState(0);
  /* שער פרטי בעל הנכס: "אחר כך" משתיק אותו לסשן הזה בלבד (sessionStorage),
     כך שבכניסה הבאה הוא קופץ שוב כל עוד משהו חסר. */
  const [gateSnoozed, setGateSnoozed] = useState(() => {
    try { return sessionStorage.getItem(GATE_SNOOZE_KEY) === "1"; } catch { return false; }
  });
  const closeGate = (saved) => {
    if (!saved) { try { sessionStorage.setItem(GATE_SNOOZE_KEY, "1"); } catch { /* מצב פרטי */ } }
    setGateSnoozed(true);
  };


  /* גרסת המסמך שהשרת מחזיק. נשלחת בכל שמירה כדי לזהות כתיבה ממכשיר אחר. */
  const versionRef = useRef(0);
  const saveTimer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { doc, version } = await dataApi.load();
        if (!alive) return;
        versionRef.current = version;
        setData(migrate(doc));
      } catch (err) {
        if (!alive) return;
        setLoadError(
          err instanceof ApiError && err.status === 0
            ? "אין חיבור לשרת. בדקו את החיבור לאינטרנט."
            : "טעינת הנתונים נכשלה.",
        );
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* שמירה מושהית: אין טעם לשלוח מסמך שלם בכל תו שמוקלד. */
  useEffect(() => {
    if (!ready || !data || loadError) return;
    pending.current = data;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const doc = pending.current;
      setSaveState("שומר…");
      try {
        const r = await dataApi.save(doc, versionRef.current);
        versionRef.current = r.version;
        setSaveState("נשמר");
        setTimeout(() => setSaveState((v) => (v === "נשמר" ? "" : v)), 1400);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          /* מכשיר אחר כתב בינתיים — לא דורסים. */
          setConflict(err.body?.current || null);
          setSaveState("");
        } else if (err instanceof ApiError && err.status === 402) {
          /* המנוי הסתיים — העריכה לא תישמר. מציגים חסימה ברורה במקום
             טקסט סטטוס אפור, ומחזירים את המסמך מהשרת כדי שהמסך לא
             יציג שינוי שלא קיים. */
          setReadOnly(true);
          setSaveState("");
          try {
            const cur = await dataApi.load();
            versionRef.current = cur.version;
            setData(migrate(cur.doc));
          } catch { /* המסמך המקומי יתוקן ברענון */ }
        } else {
          setSaveState("השמירה נכשלה");
        }
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [data, ready, loadError]);

  /* ------------------------------------------------ התראות חתימת דייר --- */

  /* דייר שחתם על החוזה מהאזור האישי שלו: השרת רשם התראה, וכאן היא
     נשאבת מדי דקה (וברגע שחוזרים ללשונית) — עם צליל של הודעה נכנסת. */
  const [signNotices, setSignNotices] = useState([]);
  const noticedIds = useRef(new Set());
  // הנכס שלשונית "חוזים ותיעוד" נפתחת עליו מתוך התראה — ולא הראשון ברשימה.
  const [docsFocus, setDocsFocus] = useState(null);
  /* כרטיס הנכס המאוחד מהדשבורד: אותם מודלים (פרטים/דיירים/גישה/כרטיס
     חוב) שהלשונית "נכסים" משתמשת בהם, אבל נפתחים ישירות בלי לעבור
     לשונית — עם שורת סעיפים משותפת (tabs) שמחליפה ביניהם באותו מודל. */
  const [propertyCard, setPropertyCard] = useState(null); // { aptId, section } | null
  const openProperty = (id) => setPropertyCard({ aptId: id, section: "details" });
  /* שכבת back אחת לכל משך הצגת הכרטיס, לא לכל סעיף בנפרד — כל סעיף
     (Modal עם tabs) ויתר על השכבה שלו בשביל זה. ר' useBackLayer. */
  useBackLayer(() => setPropertyCard(null), !!propertyCard);
  // דייר-לשעבר שנבחר בסעיף "היסטוריה" של כרטיס הנכס — התצוגה עצמה מגיעה ב-Task 7.
  const [tenantHistory, setTenantHistory] = useState(null); // { aptId, tenancyId } | null
  /* כרטיס המונים המאוחד מהדשבורד: אותו MeterApartmentCard שהלשונית
     "מונים וחשבונות" משתמשת בו, אבל נפתח ישירות במודל עם ניווט חודש
     עצמאי — בלי לעבור ללשונית ובלי להשפיע עליה. */
  const [meterCard, setMeterCard] = useState(null); // aptId | null
  const [meterCardMonth, setMeterCardMonth] = useState(CUR_KEY);
  const openMeters = (id) => { setMeterCard(id); setMeterCardMonth(CUR_KEY); };
  // כמה הודעות לא נקראו — התג על לשונית "הודעות". מתעדכן מאותה סקירה.
  const [unseenCount, setUnseenCount] = useState(0);

  // הכנת השמע על המגע הראשון — אחרת הדפדפן חוסם את הצליל כשההתראה תגיע.
  useEffect(() => {
    const prime = () => {
      primeChime();
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
    window.addEventListener("pointerdown", prime);
    window.addEventListener("keydown", prime);
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, []);

  useEffect(() => {
    if (!ready || loadError) return undefined;
    let alive = true;

    const check = async () => {
      try {
        const r = await dataApi.notifications();
        if (!alive) return;
        setUnseenCount((r.items || []).length);
        const fresh = (r.items || []).filter(
          (n) => NOTICE_TYPES.has(n.type) && !noticedIds.current.has(n.id),
        );
        if (!fresh.length) return;
        fresh.forEach((n) => noticedIds.current.add(n.id));
        setSignNotices((cur) => [...cur, ...fresh]);
        playChime();
      } catch { /* אין רשת או שרת ישן — ננסה בסבב הבא */ }
    };

    check();
    const timer = setInterval(check, 60 * 1000);
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [ready, loadError]);

  /* סגירה = ההתראה טופלה; רק אז היא מסומנת כנקראה בשרת, כדי שהתראה
     שהמסך פספס (רענון באמצע) תחזור ותוצג. */
  const closeSignNotices = () => {
    /* רק מה שהוצג — לא "הכול עד כאן": דיווח מונה שהגיע בינתיים ולא
       הוצג בהודעה הצפה נשאר "לא נקרא" בתיבת ההודעות. */
    const ids = signNotices.map((n) => n.id);
    setSignNotices([]);
    if (ids.length) {
      setUnseenCount((c) => Math.max(0, c - ids.length));
      dataApi.notificationsSeenIds(ids).catch(() => {});
    }
  };

  /* פתיחת "הודעות" = ההתראות "נמסרו": ההודעה הצפה יורדת (בלי לסמן כנקראו —
     זה קורה בלחיצה על השורה בתיבה), ולא חוזרת כשעוזבים את הלשונית. */
  useEffect(() => { if (tab === "inbox") setSignNotices([]); }, [tab]);

  /** "הצגה" — מושך את המסמך העדכני כדי שהחוזה החתום או הבקשה יופיעו מיד. */
  const showNoticeTarget = async () => {
    const latest = signNotices[signNotices.length - 1];
    try {
      const r = await dataApi.load();
      versionRef.current = r.version;
      setData(migrate(r.doc));
      // יציאה ממסכי משנה (קליטה/תצוגה מקדימה) אל היעד של ההתראה: חוזים —
      // על הנכס שההתראה מדברת עליו; הודעה חופשית — לתיבת ההודעות.
      setIntake(null);
      setPortal(null);
      if (latest?.type === "tenant_message" || latest?.type === "platform_notice") {
        setTab("inbox");
      } else {
        setDocsFocus(latest?.meta?.apartmentId || null);
        setTab("docs");
      }
    } catch { /* נשארים עם מה שעל המסך */ }
    closeSignNotices();
  };

  /* צילומי הת"ז נפתחים לבעל הנכס מעצמם: המפתח עטוף בתעודת הזהות שלו
     (byOwnerId) והיא כבר יושבת בהגדרות — אין מה להקליד. כספת ישנה
     שנפתחה בסיסמה או בקוד שחזור מקבלת כאן את עטיפת הת"ז, ומהפתיחה
     הבאה נפתחת לבד. */
  useEffect(() => {
    const id = data?.settings?.ownerId;
    if (!id || !vault.isAvailable()) return undefined;
    vault.autoUnlock(id);
    return vault.onVaultChange(() => {
      if (vault.isUnlocked()) vault.ensureOwnerIdWrapping(id).catch(() => {});
    });
  }, [data?.settings?.ownerId]);

  const run = (actions, focusId = null) => {
    let cur = data, focus = focusId, lastTenancyId = null;
    const logs = [];
    const entries = [];
    const at = new Date().toISOString();
    actions.forEach((a) => {
      // פעולות שסומנו כשייכות לתקופה החדשה מקבלות את המזהה שנוצר זה עתה
      const act2 = a.__useNewTenancy && lastTenancyId ? { ...a, tenancyId: lastTenancyId } : a;
      const r = applyActionFocused(cur, act2, focus);
      if (r.tenancyId) lastTenancyId = r.tenancyId;
      cur = r.data; logs.push({ text: r.log, ok: !r.fail });
      if (r.focus !== undefined) focus = r.focus;
      /* יומן הפעולות. המסך הבטיח "כל פעולה שתבצעו תישמר כאן" ואיש לא
         כתב אליו — הלשונית הייתה ריקה תמיד. הטקסט הוא אותו לוג שהפעולה
         כבר מייצרת, ולכן אין כאן ניסוח שני שעלול לסתור את מה שקרה.
         פעולות שנכשלו לא נרשמות: הן לא שינו כלום. */
      if (!r.fail && r.log) {
        entries.push({ id: uid(), at, text: r.log, aptId: r.focus || focus || "", type: act2.type });
      }
    });
    // 400 רשומות אחרונות — היומן חי בתוך מסמך שנשלח בכל שמירה.
    if (entries.length) cur = { ...cur, journal: [...entries, ...(cur.journal || [])].slice(0, 400) };
    setData(cur);
    return { logs, focus, data: cur };
  };

  /* run() רק מעדכן את המסמך המקומי — השמירה בפועל היא debounce נפרד
     (למעלה), שקטה ואסינכרונית: קריאה שקוראת run() לא יודעת אם השמירה
     בסוף הצליחה. לפעולה גדולה וקשה-לחזור-עליה כמו "עריכת חשבונות עבר"
     (עשרות חודשים בבת אחת) זה מסוכן — אם השמירה מתנגשת עם מכשיר/טאב
     אחר (409), הטופס כבר "נראה" כאילו הסתיים בעוד שכלום לא נשמר.
     saveNow שומר מיד וממתינה לתוצאה; בהתנגשות היא טוענת את הגרסה
     העדכנית מהשרת ומיישמת עליה את אותן פעולות שוב (לא דורסת ולא
     מוותרת), ומחזירה הצלחה/כישלון אמיתיים כדי שהממשק יתנהג בהתאם —
     לא נוגעת בנתיב השמירה הרגיל, שממשיך לעבוד בדיוק כמו קודם. */
  const saveNow = async (actions, focusId = null) => {
    const applyAll = (baseDoc) => {
      let cur = baseDoc, focus = focusId, lastTenancyId = null;
      let allOk = true;
      actions.forEach((a) => {
        const act2 = a.__useNewTenancy && lastTenancyId ? { ...a, tenancyId: lastTenancyId } : a;
        const r = applyActionFocused(cur, act2, focus);
        if (r.tenancyId) lastTenancyId = r.tenancyId;
        cur = r.data;
        if (r.fail) allOk = false;
        if (r.focus !== undefined) focus = r.focus;
      });
      return { cur, allOk };
    };

    const { cur, allOk } = applyAll(data);
    if (!allOk) return { ok: false, reason: "action" };
    try {
      const r = await dataApi.save(cur, versionRef.current);
      versionRef.current = r.version;
      setData(cur);
      return { ok: true };
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          const fresh = await dataApi.load();
          const retryBase = migrate(fresh.doc);
          const retry = applyAll(retryBase);
          if (!retry.allOk) return { ok: false, reason: "action" };
          const r2 = await dataApi.save(retry.cur, fresh.version);
          versionRef.current = r2.version;
          setData(retry.cur);
          return { ok: true, retried: true };
        } catch {
          return { ok: false, reason: "conflict" };
        }
      }
      return { ok: false, reason: "error" };
    }
  };

  const stats = useMemo(() => {
    if (!data) return null;
    const occ = data.apartments.filter((a) => a.tenant);
    const expected = occ.reduce((s, a) => s + Number(a.rent || 0), 0);
    const collected = occ.reduce((s, a) => s + rentPaidOf(data, a.id, CUR_KEY), 0);
    /* חוב שכר דירה — rentDebtRows (משותפת עם רשימת הפירוט שנפתחת בלחיצה
       על הכרטיס): שכר הדירה שהיה בתוקף בכל חודש (rentSteps, שכר יחסי
       לחודש ראשון), נטו מול מה שכבר שולם (לא רק "יש תשלום/אין תשלום"),
       וממועד תחילת החוזה בפועל ולא רק מ-1 בינואר של השנה הנוכחית. הלולאה
       הישנה ספרה כל חודש לא-משולם לפי שכר הדירה של היום בלבד, החל מינואר
       השנה — התעלמה מהעלאות שכר דירה ומתשלום חלקי, ולא תאמה את הכיתוב
       "מאז תחילת החוזה". */
    let billsDue = 0;
    const debt = rentDebtRows(data).reduce((s, x) => s + x.amount, 0);
    // נטו לכל נכס — עודף על סעיף אחד מקזז סעיף אחר באותו חודש (כמו billsDue).
    data.apartments.forEach((a) => { const b = data.bills[rowKey(a.id, CUR_KEY)]; if (b) billsDue += Math.max(0, BILLS.reduce((t, { k }) => t + itemDue(b[k]), 0)); });

    /* חשבונות החודש, בפילוח של לוחות הגבייה: ועד בית לחוד, צריכה (חשמל,
       מים, גז, ביוב) לחוד. "צפוי" לוועד בית הוא מה שנרשם לחודש, ואם לא
       נרשם — ועד הבית של הדייר. */
    const CONS = ["elec", "water", "gas", "sewage"];
    let vaadExpected = 0, vaadPaid = 0, consCharged = 0, consPaid = 0;
    data.apartments.forEach((a) => {
      const b = data.bills[rowKey(a.id, CUR_KEY)];
      const vaadAmt = Number(b?.vaad?.amount || 0) || (a.tenant && a.vaadActive !== false ? Number(a.vaad || 0) : 0);
      vaadExpected += vaadAmt;
      vaadPaid += itemPaid(b?.vaad);
      CONS.forEach((k) => { consCharged += Number(b?.[k]?.amount || 0); consPaid += itemPaid(b?.[k]); });
    });
    // כל החשבונות הפתוחים, גם מחודשים קודמים — זה החוב שבאמת נגרר.
    const billsOpenAll = data.apartments.reduce((t, a) => t + billsOutstanding(data, a.id), 0);
    // אותו חוב, מפולח לוועד בית ולצריכה בנפרד — לכרטיסי הדשבורד הייעודיים.
    const vaadDebt = vaadDebtRows(data).reduce((s, x) => s + x.amount, 0);
    const consDebt = consDebtRows(data).reduce((s, x) => s + x.amount, 0);
    // סה״כ לגביה — שכר דירה, ועד בית וצריכה יחד, בדיוק כמו collectedAll למטה.
    const totalDebtAll = debt + vaadDebt + consDebt;

    const monthExp = data.expenses.filter((e) => e.month === CUR_KEY).reduce((s, e) => s + Number(e.amount || 0), 0);
    const collectedAll = collected + vaadPaid + consPaid;
    // פנויות שכבר נסגר בהן דייר — לא מושכרות החודש, אבל גם לא להשכרה.
    const signed = data.apartments.filter((a) => !a.tenant && upcomingTenancies(data, a.id).length).length;

    /* חובות שסומנו במפורש "חוב אבוד" (לא ויתור) בלשונית "דיירים וחובות" —
       כולל תקופות שנמחקו רכות (deletedTenancies), כי הסימון נשאר תקף גם
       אחרי שהדייר ירד מהתצוגה. תקופה שנמחקה לצמיתות (purge) נעלמת יחד
       עם הסימון שלה — אין ממה לספור. */
    let lostDebt = 0;
    data.apartments.forEach((a) => {
      [...tenanciesOf(data, a.id), ...deletedTenancies(data, a.id)].forEach((t) => {
        if (t.debtResolution?.kind === "lost") lostDebt += Number(t.debtResolution.amount) || 0;
      });
    });

    return {
      expected, collected, debt, billsDue, monthExp, net: collectedAll - monthExp,
      vaadExpected, vaadPaid, consCharged, consPaid, billsOpenAll, vaadDebt, consDebt, totalDebtAll, collectedAll, lostDebt,
      occ: occ.length, signed, total: data.apartments.length,
    };
  }, [data]);


  /* מוצג גם מעל אשף הקליטה והתצוגה המקדימה — לא רק מעל הלוח הראשי. */
  const signToast = signNotices.length > 0 && (
    <SignedToast notices={signNotices} onShow={showNoticeTarget} onClose={closeSignNotices} />
  );

  if (loadError) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
        <div className="w-full max-w-sm rounded-2xl border p-6 text-center" style={{ background: C.card, borderColor: C.line }}>
          <AlertTriangle size={24} style={{ color: C.ochre, margin: "0 auto" }} />
          <h1 className="text-lg font-bold mt-2">{loadError}</h1>
          <button onClick={() => window.location.reload()} className="mt-4 w-full rounded-xl py-2.5 text-sm"
            style={{ background: C.teal, color: "#fff" }}>נסו שוב</button>
        </div>
      </div>
    );
  }

  if (!data) return <div dir="rtl" className="min-h-screen flex items-center justify-center" style={{ background: C.surface, color: C.muted }}>טוען…</div>;

  /* שני מכשירים ערכו במקביל. בחירה מפורשת במקום דריסה בשקט. */
  if (conflict) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
        <div className="w-full max-w-md rounded-2xl border p-6" style={{ background: C.card, borderColor: C.line }}>
          <AlertTriangle size={24} style={{ color: C.ochre }} />
          <h1 className="text-lg font-bold mt-2">המידע עודכן במכשיר אחר</h1>
          <p className="text-sm mt-1" style={{ color: C.muted }}>
            השינויים שלכם כאן לא נשמרו, כדי לא למחוק את מה שנשמר במכשיר השני.
          </p>
          <button
            onClick={() => {
              versionRef.current = conflict.version;
              setData(migrate(conflict.doc));
              setConflict(null);
            }}
            className="mt-4 w-full rounded-xl py-3 text-sm" style={{ background: C.teal, color: "#fff" }}>
            טעינת הגרסה העדכנית (השינויים כאן יבוטלו)
          </button>
          <button
            onClick={async () => {
              versionRef.current = conflict.version;
              setConflict(null);
              try {
                const r = await dataApi.save(pending.current, versionRef.current);
                versionRef.current = r.version;
                setSaveState("נשמר");
              } catch { setSaveState("השמירה נכשלה"); }
            }}
            className="mt-2 w-full rounded-xl py-2.5 text-sm" style={{ border: `1px solid ${C.line}` }}>
            לשמור דווקא את השינויים שלי
          </button>
        </div>
      </div>
    );
  }


  /* מצב הדייר נקבע ב-Root לפי עוגיית סשן חתומה, ולא בדגל מקומי
     שאפשר למחוק מכלי הפיתוח. כאן נשארת רק התצוגה המקדימה. */

  if (intake) {
    const apt = data.apartments.find((a) => a.id === intake);
    if (!apt) { setIntake(null); return null; }
    return (
      <>
        <BackLayer onBack={() => setIntake(null)} />
        <TenantIntake apt={apt} data={data} setData={setData} run={run} onExit={() => setIntake(null)} />
        {signToast}
      </>
    );
  }

  if (portal) {
    const apt = data.apartments.find((a) => a.id === portal);
    if (!apt) { setPortal(null); return null; }
    return (
      <>
        <BackLayer onBack={() => setPortal(null)} />
        <TenantPortal apt={apt} data={data} setData={setData} run={run} onExit={() => setPortal(null)} />
        {signToast}
      </>
    );
  }

  /* חשבון חדש נוחת על לוח מחוונים ריק שלא אומר לו מה לעשות. עד שיש נכס
     אחד, המסך הוא צעד ההמשך של ההרשמה ולא הדשבורד. */
  if (!data.apartments.length && !dismissedWelcome) {
    /* מסך אחד בכל רגע.
       קודם מסך "ברוך הבא" נטען יחד עם העוזר, והעוזר נפתח מעצמו כחלונית
       תחתונה וכיסה את חציו — שני מסכי פתיחה שונים במקביל, אף אחד מהם
       לא שלם ולא ברור מה עושים. עכשיו "ברוך הבא" הוא המסך, ולחיצה על
       הכפתור מעבירה לעוזר במסך מלא. */
    if (guided) {
      return (
        <Assistant
          data={data} run={run} setData={setData} undo={undo} setUndo={setUndo}
          accountId={me?.accountId}
          fullScreen
          onExit={() => setGuided(0)}
          startFlow={{ flow: "property", n: guided }}
        />
      );
    }

    return (
      <>
        <Welcome
          name={me?.email}
          onGuided={() => setGuided((n) => n + 1)}
          onManual={() => setModal({ kind: "apt", apt: null })}
          onSkip={() => setDismissedWelcome(true)}
          modal={modal?.kind === "apt" ? (
            <AptModal
              apt={null}
              onSave={(f) => { run([{ type: "add_apartment", ...f }]); setModal(null); }}
              onClose={() => setModal(null)}
            />
          ) : null}
        />
        {/* נרשם חדש: קודם הפרטים והחתימה, ואז הנכס הראשון. */}
        {!gateSnoozed && !readOnly && ownerGaps(data.settings).length > 0 && (
          <OwnerDetailsGate data={data} setData={setData} me={me} onClose={closeGate} />
        )}
      </>
    );
  }

  /* שלוש לשוניות בלבד: החודש (כולל גריד הנכסים), הודעות, ועוד. "נכסים"
     ו"מונים וחשבונות" היו לשוניות נפרדות עד Task 8 — הגריד עבר לתוך
     Overview (PropertyGrid) וכרטיס המונים נפתח כמודל מהדשבורד (Task 6),
     ולכן הלשוניות הישנות הוסרו כאן. */
  const TABS = [
    { k: "overview", label: "החודש" },
    /* המקום הקבוע להודעות מהדיירים — חתימות, בקשות, דיווחי מונים והודעות
       חופשיות. התג = כמה לא נקראו + בקשות שמחכות להחלטה. */
    { k: "inbox", label: "הודעות", badge: unseenCount + pendingExtensionRequests(data).length },
    { k: "more", label: "עוד" },
  ];

  /* overflow-x-hidden — רשת ביטחון: אלמנט אחד רחב מדי לא מותח את כל
     הדף לרוחב בטלפון (תוכן רחב באמת גולל בתוך המכל שלו, לא בדף). */
  return (
    <div dir="rtl" className="min-h-screen w-full pb-24 overflow-x-hidden" style={{ background: C.surface, color: C.ink, fontFamily: "'Assistant', system-ui, sans-serif" }}>
      <style>{`@media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { position: absolute !important; inset: 0 !important; width: 100% !important; max-height: none !important; overflow: visible !important; }
          .no-print { display: none !important; }
          /* חלון מודאלי הוא position:fixed, והדפדפן מדפיס אלמנט fixed מחדש
             בכל עמוד — כך החוזה שנפתח מתוך חלון יצא כפול ומתלבש על עצמו
             מהעמוד השני. בהדפסה הכול סטטי וזורם. */
          .fixed { position: static !important; }
          html, body { height: auto !important; overflow: visible !important; }
        }
        @media (prefers-reduced-motion: reduce){*{transition:none!important;animation:none!important}}`}</style>

      {/* הכותרת הכהה: הזהות, מצב השמירה והלשוניות — רצועה אחת ירוקה
          שמתעגלת אל הנייר שמתחתיה. */}
      <div className="em-appbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <header className="pt-6 pb-1 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="em-eyebrow tracking-widest mb-1">ניהול נכסים</div>
              <h1 className="disp text-3xl sm:text-4xl font-bold">הדירות שלי</h1>
              <p className="em-sub mt-1">{monthLabel(CUR_KEY)} · {stats.occ} מתוך {stats.total} מושכרים{stats.signed ? ` · עוד ${stats.signed} עם חוזה לכניסה` : ""}</p>
            </div>
            {/* flex-wrap הכרחי: חמישה כפתורים בשורה אחת רחבים ממסך טלפון,
                ושורה שלא נשברת מותחת את כל הדף לרוחב. */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end max-w-full">
              <div className="text-xs" style={{ color: "rgba(255,255,255,.72)" }}>{saveState}</div>
              {/* ניהול פלטפורמה, שיתוף, כניסת דייר וסדר נכסים עברו ללשונית "עוד" —
                  פעולות של פעם בתקופה, שלא צריכות מקום קבוע בכותרת. */}
              {/* יציאה — לא הייתה כאן בכלל: onSignOut הגיע מ-Root ואיש לא קרא לו,
                  ובמחשב משותף לא הייתה דרך להתנתק. */}
              <div className="flex flex-col items-end gap-1">
                <button onClick={() => { clearAssistantChat(me?.accountId); onSignOut(); }} title="יציאה מהחשבון"
                  className="em-iconbtn">
                  <LogOut size={13} /> יציאה
                </button>
                <FeedbackPanel role="owner" />
              </div>
            </div>
          </header>

          {/* בטלפון תשע הלשוניות לא נכנסות ברוחב המסך, ושלוש האחרונות
              (הוצאות, דוחות, הגדרות) נשארו מחוץ לו בלי שום סימן שהן שם.
              הצללית בקצה מופיעה רק כשבאמת יש עוד, והלשונית הנבחרת נגללת
              לתצוגה כשעוברים אליה מהעוזר או מכפתור אחר. */}
          <TabsBar tabs={TABS} tab={SECONDARY_TABS[tab] ? "more" : tab} setTab={setTab} onDark />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* ההודעה על אירוע מהדייר — בזרימת הדף מתחת לסרגל, לא צפה מעל תוכן.
            בתוך "הודעות" היא מיותרת (הכול שם) ולכן לא מוצגת. */}
        {signNotices.length > 0 && tab !== "inbox" && (
          <SignedToast notices={signNotices} onShow={showNoticeTarget} onClose={closeSignNotices} inline />
        )}

        {readOnly && (
          <div className="rounded-xl px-4 py-3 mb-3 mt-2 flex items-start gap-2" style={{ background: "#FBEDE9", color: "#7A2E1E" }}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="text-sm">
              <strong>החשבון במצב קריאה בלבד — השינוי האחרון לא נשמר.</strong>{" "}
              הנתונים זמינים לצפייה; עריכה תתאפשר אחרי חידוש המנוי.
            </div>
          </div>
        )}
        <TrialBanner />
        {/* תמונות מוגדלות (צילומי מונים ועוד) — מארח אחד לכל האפליקציה. */}
        <LightboxHost />
        {/* פרטי בעל הנכס והחתימה — קופץ כל עוד משהו חסר (לא בתצוגת דייר). */}
        {!gateSnoozed && !readOnly && !portal && ownerGaps(data.settings).length > 0 && (
          <OwnerDetailsGate data={data} setData={setData} me={me} onClose={closeGate} />
        )}
        {/* לשונית משנית (מתוך "עוד") — פירור לחם אחד: "עוד" מופיע פעם
            אחת והוא עצמו הדרך חזרה. הכפתור "← עוד" לצד "עוד › ..." הציג
            את המילה פעמיים. */}
        {SECONDARY_TABS[tab] && (
          <div className="mt-3 text-xs flex items-center gap-1.5" style={{ color: C.muted }}>
            <button onClick={() => setTab("more")} className="rounded-lg px-2 py-1 flex items-center gap-1"
              style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              <ArrowRight size={12} /> עוד
            </button>
            <span>›</span>
            <b style={{ color: C.ink }}>{SECONDARY_TABS[tab]}</b>
          </div>
        )}
        {tab === "overview" && <Overview data={data} setData={setData} stats={stats} year={year} setYear={setYear} run={run} saveNow={saveNow} setTab={setTab} unseenCount={unseenCount} onOpenProperty={openProperty} onOpenMeters={openMeters}
          onAddProperty={() => setModal({ kind: "apt", apt: blankApt() })} setModal={setModal} setIntake={setIntake} openDocs={(id) => { setDocsFocus(id); setTab("docs"); }} />}
        {tab === "more" && <MorePage setTab={setTab} setModal={setModal} me={me} />}
        {tab === "docs" && <Docs data={data} setData={setData} run={run} setModal={setModal} initialAptId={docsFocus} />}
        {tab === "inbox" && <Inbox data={data} setTab={setTab} setDocsFocus={setDocsFocus} setModal={setModal} unseenCount={unseenCount} setUnseenCount={setUnseenCount} onOpenMeters={openMeters} />}
        {tab === "tariffs" && <Tariffs data={data} setData={setData} run={run} accountId={me?.accountId} setModal={setModal} />}
        {tab === "expenses" && <Expenses data={data} setData={setData} run={run} />}
        {tab === "reports" && <Reports data={data} />}
        {tab === "value" && <PropertyValues data={data} run={run} />}
        {tab === "loans" && <Loans data={data} run={run} />}
        {tab === "equity" && <Equity data={data} />}
        {tab === "tenants" && <TenantsCenter data={data} setData={setData} run={run} saveNow={saveNow} setTenantHistory={setTenantHistory} />}
        {tab === "owner" && <OwnerProfile data={data} setData={setData} stats={stats} />}
        {tab === "backup" && <BackupShare data={data} setData={setData} />}
      </div>

      <Assistant data={data} run={run} setData={setData} undo={undo} setUndo={setUndo} accountId={me?.accountId} />

      {modal?.kind === "settings" && <SettingsModal data={data} setData={setData} onClose={() => setModal(null)} />}
      {modal?.kind === "apt" && <AptModal apt={modal.apt} settings={data.settings} onSave={(f) => { run([f.id ? { type: "update_apartment", apartment: f.id, ...f } : { type: "add_apartment", ...f }]); setModal(null); }} onClose={() => setModal(null)} />}
      {/* כרטיס נכס מאוחד — נפתח ישירות מהדשבורד (openProperty), בלי לעבור
          ללשונית "נכסים". אותם מודלים בדיוק, עם שורת סעיפים משותפת.
          לחיצה על דייר-לשעבר בסעיף "היסטוריה" קובעת tenantHistory, אבל
          עדיין אין מי שמציג אותו — התצוגה עצמה מגיעה ב-Task 7. */}
      {propertyCard && (() => {
        const apt = data.apartments.find((x) => x.id === propertyCard.aptId);
        if (!apt) return null;
        const onClose = () => setPropertyCard(null);
        const tabs = <PropertyCardTabs active={propertyCard.section} onChange={(section) => {
          // "מונים" לא נבנה כסעיף פנימי — הוא סוגר את כרטיס הנכס ופותח
          // את אותו מודל מונים עצמאי שנפתח מהדשבורד (openMeters), כדי
          // לא לשכפל את ניווט־החודש שכבר קיים שם.
          if (section === "meters") { onClose(); openMeters(apt.id); return; }
          setPropertyCard({ ...propertyCard, section });
        }} />;
        if (propertyCard.section === "tenants") {
          return <TenanciesModal apt={apt} data={data} run={run} tabs={tabs} onClose={onClose}
            onIntake={() => { onClose(); setIntake(apt.id); }} />;
        }
        if (propertyCard.section === "access") {
          return <AccessModal apt={apt} setData={setData} tabs={tabs} onClose={onClose} />;
        }
        if (propertyCard.section === "ledger") {
          return <LedgerModal apt={apt} data={data} run={run} saveNow={saveNow} tabs={tabs} onClose={onClose} />;
        }
        if (propertyCard.section === "history") {
          return <PropertyHistorySection apt={apt} data={data} tabs={tabs} onClose={onClose}
            onOpenDocs={() => { onClose(); setDocsFocus(apt.id); setTab("docs"); }}
            onOpenTenantHistory={(tenancyId) => setTenantHistory({ aptId: apt.id, tenancyId })} />;
        }
        return <AptModal apt={apt} settings={data.settings} tabs={tabs} onClose={onClose}
          onSave={(f) => { run([f.id ? { type: "update_apartment", apartment: f.id, ...f } : { type: "add_apartment", ...f }]); onClose(); }} />;
      })()}
      {/* כרטיס המונים המאוחד — נפתח ישירות מהדשבורד (openMeters), בלי לעבור
          ללשונית "מונים וחשבונות". אותו MeterApartmentCard בדיוק, עם ניווט
          חודש עצמאי (meterCardMonth) שלא משפיע על החודש בלשונית הישנה. */}
      {meterCard && (() => {
        const apt = data.apartments.find((x) => x.id === meterCard);
        if (!apt) return null;
        /* חייב להיות הדייר של החודש *המוצג* (meterCardMonth), לא הדייר של
           היום — אחרת בדיקת ה-handover בתוך MeterApartmentCard (curT.id
           !== t.id) לעולם לא תתקיים, וגם דפדוף לחודש עם דייר אחר יציג את
           פרטי דייר-היום במקום דייר אותו חודש. אותה גזירה שהלשונית הישנה
           "מונים וחשבונות" עושה לכל שורה (tenancyForMonth לפי month). */
        const t = tenancyForMonth(data, apt.id, meterCardMonth);
        return (
          <Modal title={`מונים וחשבונות — ${apt.name}`} onClose={() => setMeterCard(null)} wide>
            <div className="flex items-center justify-between mb-3">
              <IconBtn onClick={() => setMeterCardMonth((m) => prevMonth(m))} label="חודש קודם"><ChevronRight size={16} /></IconBtn>
              <span className="disp text-sm font-bold">{monthLabel(meterCardMonth)}</span>
              <IconBtn onClick={() => setMeterCardMonth((m) => nextMonth(m))} label="חודש הבא"><ChevronLeft size={16} /></IconBtn>
            </div>
            <MeterApartmentCard a={apt} t={t} idx={0} data={data} setData={setData} run={run} month={meterCardMonth} s={data.settings || defTariffs()} justFocused={null} />
          </Modal>
        );
      })()}
      {/* "היסטוריית דיירים מהעבר" — נכנסים אליה מ"היסטוריה" בכרטיס הנכס
          (onOpenTenantHistory למעלה) או מ"היסטוריית חובות דיירים"
          ב-TenantsCenter. onOpenDocs סוגר את המסך הזה ופותח את "חוזים
          ותיעוד" על הנכס הנכון, באותו דפוס שהיסטוריית הנכס כבר משתמשת בו —
          וגם סוגר את כרטיס הנכס אם הגענו דרכו (propertyCard נשאר פתוח
          ברקע כשלוחצים על דייר בסעיף "היסטוריה"), אחרת המודל הישן היה
          נשאר צף מעל לשונית "חוזים ותיעוד". */}
      {tenantHistory && (
        <TenantHistory aptId={tenantHistory.aptId} tenancyId={tenantHistory.tenancyId} data={data} run={run} saveNow={saveNow}
          onClose={() => setTenantHistory(null)}
          onOpenDocs={() => { setTenantHistory(null); setPropertyCard(null); setDocsFocus(tenantHistory.aptId); setTab("docs"); }} />
      )}
      {modal?.kind === "swap" && <SwapTenantModal apt={modal.apt} onSave={(p) => { run([{ type: "change_tenant", apartment: modal.apt.id, ...p }]); setModal(null); }} onClose={() => setModal(null)} />}
      {modal?.kind === "confirm" && <ConfirmModal {...modal} onClose={() => setModal(null)} />}
      {modal?.kind === "extend" && (
        <ExtendModal apt={modal.apt} settings={data.settings} request={modal.request || null} declineFirst={!!modal.declineFirst}
          onSave={(p) => {
            run([{ type: "extend_contract", apartment: modal.apt.id, ...p }]);
            /* ההחלטה מגיעה לדייר גם כהודעה בלוח ההודעות שלו — לא רק כמצב
               על הבקשה. נשמרת בהיסטוריה של בעל הנכס כתשובה. */
            if (modal.request) {
              dataApi.replyToTenant({
                apartmentId: modal.apt.id, requestId: modal.request.id,
                text: `בקשת ההארכה עד ${heDate(p.to || modal.request.to)} אושרה. נספח ההארכה מוכן לחתימה באזור האישי, תחת "החוזה שלי".`,
              }).catch(() => {});
            }
            setModal(null);
          }}
          /* דחיית בקשת הארכה של הדייר — הסיבה נשמרת על הבקשה, והדייר מקבל
             את הדחייה גם כהודעה בלוח ההודעות שלו. */
          onDecline={(reason) => {
            run([{ type: "decline_extension_request", apartment: modal.apt.id, requestId: modal.request?.id, reason }]);
            if (modal.request) {
              dataApi.replyToTenant({
                apartmentId: modal.apt.id, requestId: modal.request.id,
                text: `בקשת ההארכה עד ${heDate(modal.request.to)} נדחתה.${reason ? ` הסיבה: ${reason}` : ""}`,
              }).catch(() => {});
            }
            setModal(null);
          }}
          onClose={() => setModal(null)} />
      )}
      {modal?.kind === "contract" && <ContractModal apt={modal.apt} contract={modal.contract} settings={data.settings} setData={setData} onClose={() => setModal(null)} />}
    </div>
  );
}

/**
 * ההודעה הצפה על חתימת דייר. נשארת על המסך עד שבעל הנכס סוגר אותה —
 * חתימת חוזה היא אירוע שמצדיק מבט, לא הבזק שנעלם לבד אחרי כמה שניות.
 */
function SignedToast({ notices, onShow, onClose, inline = false }) {
  const latest = notices[notices.length - 1];
  const meta = latest?.meta || {};
  /* בקשת הארכה מהאזור האישי מגיעה באותו צינור של החתימה — אותו צליל,
     אותה הודעה צפה, רק הטקסט והכפתור שונים. */
  const isExt = latest?.type === "extension_requested";
  const isMsg = latest?.type === "tenant_message";
  const isSys = latest?.type === "platform_notice";
  const isGuar = latest?.type === "guarantor_signed";
  const Icon = isSys ? ShieldCheck : isMsg ? MessageCircle : isExt ? CalendarPlus : PenTool;
  const tone = isSys ? "#7A2E1E" : isMsg ? "#128C7E" : isExt ? C.ochre : C.paid;
  /* במסך הניהול ההודעה יושבת בזרימת הדף מתחת לסרגל (inline) ולא צפה —
     הצפה כיסתה תפריטים, כותרות טבלאות ואפילו את תיבת ההודעות עצמה. */
  return (
    <div dir="rtl"
      className={inline
        ? "mt-3 rounded-xl border shadow-sm p-4"
        : "fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 rounded-xl border shadow-lg p-4"}
      style={{ background: C.card, borderColor: isMsg ? "#BFE3DC" : isExt ? "#E8C98A" : "#CBE2D6", ...(inline ? {} : { zIndex: 90 }) }}
      role="status" aria-live="polite">
      <div className="flex items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0" style={{ color: tone }} />
        <div className="flex-1 text-sm">
          <div style={{ fontWeight: 600 }}>
            {isSys
              ? "הודעה מהנהלת המערכת"
              : isMsg
              ? `הודעה מ${meta.tenant || "הדייר"}`
              : isExt
                ? `${meta.tenant || "הדייר"} ביקש/ה להאריך את החוזה`
                : isGuar
                  ? `${meta.guarantor || "ערב"} חתם/ה על כתב הערבות${meta.tenant ? ` של ${meta.tenant}` : ""}`
                  : `${meta.tenant || "הדייר"} חתם/ה על החוזה`}
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: C.muted }}>
            {(isMsg || isSys) && meta.text ? <span style={{ color: C.ink }}>"{String(meta.text).slice(0, 140)}{String(meta.text).length > 140 ? "…" : ""}"<br /></span> : null}
            {meta.apartment || ""}
            {isExt && meta.to ? ` · עד ${heDate(meta.to)}` : ""}
            {isExt && meta.note ? ` · "${meta.note}"` : ""}
            {notices.length > 1 && ` · ועוד ${notices.length - 1} התראות`}
          </div>
        </div>
        <button onClick={onClose} aria-label="סגירת ההתראה" className="shrink-0 text-xs px-1"
          style={{ color: C.muted }}>
          ✕
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={onShow} className="flex-1 rounded-lg py-2 text-sm"
          style={{ background: C.teal, color: "#fff" }}>
          {isMsg || isSys ? "פתיחת ההודעות" : isExt ? "הצגת הבקשה — אישור או דחייה" : isGuar ? "הצגת כתב הערבות" : "הצגת החוזה החתום"}
        </button>
        <button onClick={onClose} className="rounded-lg px-3 text-sm"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          אחר כך
        </button>
      </div>
    </div>
  );
}

/**
 * תשובה כתובה לדייר מתוך תיבת ההודעות. נשמרת כהיסטוריה (owner_reply)
 * והדייר רואה אותה באזור האישי מתחת להודעות שלו. requestId — כשהתשובה
 * היא על בקשת הארכה (בלי לאשר או לדחות אותה). onSent — רענון הרשימה.
 */
function ReplyBox({ apt, inReplyTo = null, requestId = null, initialText = "", onSent, onClose }) {
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true); setErr("");
    try {
      await dataApi.replyToTenant({ apartmentId: apt.id, text: t, inReplyTo, requestId });
      setText("");
      if (onSent) onSent();
      if (onClose) onClose();
    } catch (e) {
      setErr(e.message || "השליחה נכשלה");
    }
    setBusy(false);
  };
  return (
    <div className="w-full mt-2 rounded-lg border p-2.5" style={{ borderColor: C.line, background: C.card }}
      onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <div className="text-[11px] mb-1" style={{ color: C.muted }}>תשובה ל{apt.tenant || "דייר"} — תופיע אצלו באזור האישי ותישמר כאן בהיסטוריה</div>
      <textarea value={text} rows={2} maxLength={1000} autoFocus onChange={(e) => setText(e.target.value)}
        placeholder="למשל: מאשר עקרונית, נדבר על המחיר בטלפון"
        aria-label="תשובה לדייר" style={{ ...inputStyle, resize: "vertical" }} />
      {err && <div className="text-[11px] mt-1" style={{ color: C.late }}>{err}</div>}
      <div className="flex flex-wrap gap-2 mt-2">
        <button onClick={send} disabled={busy || !text.trim()} className="rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"
          style={{ background: C.teal, color: "#fff", opacity: busy || !text.trim() ? 0.6 : 1 }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} שליחה
        </button>
        {apt.phone && (
          <a href={waLink(apt.phone, text.trim() || `שלום ${apt.tenant || ""}, `)} target="_blank" rel="noreferrer"
            className="rounded-lg px-3 py-1.5 text-xs flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: "#128C7E" }}>
            <MessageCircle size={13} /> בוואטסאפ
          </a>
        )}
        <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-xs" style={{ border: `1px solid ${C.line}`, color: C.muted }}>ביטול</button>
      </div>
    </div>
  );
}

/**
 * תיבת ההודעות של בעל הנכס — כל מה שהגיע מהדיירים, נקרא ולא נקרא,
 * מהחדש לישן. לחיצה על שורה מסמנת אותה כנקראה; על בקשת הארכה — אישור,
 * דחייה או תגובה ישר מהשורה; על הודעה — תגובה כתובה או וואטסאפ.
 */
function Inbox({ data, setTab, setDocsFocus, setModal, unseenCount, setUnseenCount, onOpenMeters = null }) {
  // איזו שורה פתוחה לתגובה: מזהה התראה, או "req:<id>" לבקשה ממתינה.
  const [replyFor, setReplyFor] = useState(null);
  // טקסט התחלתי לתגובה — "שליחת החשבון לדייר" ממלא את פירוט החשבון.
  const [replyPreset, setReplyPreset] = useState("");
  // בחירה מרובה למחיקה: מזהי השורות המסומנות.
  const [selected, setSelected] = useState(() => new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [aptFilter, setAptFilter] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);

  /* מה שמחכה להחלטה שלך — מתוך המסמך, לא מתוך ההתראות: בקשת הארכה
     שנוצרה בתצוגה המקדימה (או בשם הדייר) לא עברה דרך השרת ואין לה
     התראה, אבל היא עדיין ממתינה. התיבה משקפת את המצב האמיתי.
     מסונן לפי אותו בורר נכס כמו הרשימה — אחרת בקשה של נכס אחד הופיעה
     גם כשבחרו נכס אחר. */
  const pending = pendingExtensionRequests(data).filter(({ apt }) => !aptFilter || apt.id === aptFilter);

  const load = async (reset = false) => {
    setBusy(true); setErr("");
    try {
      const before = reset || !items.length ? null : items[items.length - 1].id;
      const r = await dataApi.notificationsAll(before, 50);
      setItems((cur) => (reset ? r.items || [] : [...cur, ...(r.items || [])]));
      setHasMore(!!r.hasMore);
    } catch (e) {
      setErr(e.message || "טעינת ההודעות נכשלה");
    }
    setBusy(false);
  };
  // נטען מחדש גם כשהסקירה מדווחת על הודעות חדשות בזמן שהלשונית פתוחה.
  useEffect(() => { load(true); }, [unseenCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const markSeen = (n) => {
    if (n.seenAt) return;
    const now = new Date().toISOString();
    setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, seenAt: now } : x)));
    setUnseenCount((c) => Math.max(0, c - 1));
    dataApi.notificationsSeenIds([n.id]).catch(() => {});
  };
  const markAll = () => {
    const newest = items[0]?.id;
    if (!newest) return;
    const now = new Date().toISOString();
    setItems((cur) => cur.map((x) => (x.seenAt ? x : { ...x, seenAt: now })));
    setUnseenCount(0);
    dataApi.notificationsSeen(newest).catch(() => {});
  };
  /* מחיקה. אישור לפני, כי תשובה של בעל הנכס נעלמת גם אצל הדייר. הודעות
     ואירועים מהדייר רק מוסתרים כאן — הדייר ממשיך לראות מה ששלח. */
  const removeIds = async (ids) => {
    if (!ids.length) return;
    const rows = items.filter((x) => ids.includes(x.id));
    const replies = rows.filter((x) => x.type === "owner_reply").length;
    const text = rows.length === 1
      ? (replies ? "למחוק את התשובה הזו? היא תימחק גם אצל הדייר." : "להסתיר את ההודעה הזו מהתיבה שלך? אצל הדייר היא נשארת.")
      : `למחוק ${rows.length} הודעות?${replies ? ` ${replies} תשובות שלך יימחקו גם אצל הדייר.` : ""} הודעות מהדייר מוסתרות אצלך בלבד.`;
    if (!window.confirm(text)) return;
    const unreadGone = rows.filter((x) => !x.seenAt).length;
    setItems((cur) => cur.filter((x) => !ids.includes(x.id)));
    setSelected(new Set());
    if (unreadGone) setUnseenCount((c) => Math.max(0, c - unreadGone));
    try { await dataApi.notificationsDelete(ids); } catch (e) { setErr(e.message || "המחיקה נכשלה"); load(true); }
  };
  const toggleSelect = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const go = (n) => {
    markSeen(n);
    const kind = INBOX_KINDS[n.type];
    if (!kind?.go) return;
    if (kind.go === "docs") { setDocsFocus(n.meta?.apartmentId || null); setTab("docs"); return; }
    /* "מונים וחשבונות" הייתה לשונית נפרדת; מ-Task 8 דיווח קריאה נפתח
       ישירות כמודל הכרטיס המאוחד של המונים (Task 6), על הנכס שדיווח. */
    if (kind.go === "meters") { if (n.meta?.apartmentId && onOpenMeters) onOpenMeters(n.meta.apartmentId); return; }
    setTab(kind.go);
  };

  const dayOf = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const timeOf = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
  const todayKey = dayOf(new Date().toISOString());
  const dayLabel = (k) => (k === todayKey ? "היום" : heDate(k));

  const shown = items.filter((n) =>
    (!aptFilter || n.meta?.apartmentId === aptFilter) && (!onlyUnread || !n.seenAt));

  return (
    <section className="mt-6">
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h2 className="disp text-xl font-bold">הודעות מהדיירים</h2>
            <p className="text-xs" style={{ color: C.muted }}>
              חתימות, בקשות הארכה, דיווחי מונים והודעות חופשיות · {unseenCount ? `${unseenCount} לא נקראו` : "הכול נקרא"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select style={{ ...inputStyle, maxWidth: 220 }} value={aptFilter} onChange={(e) => setAptFilter(e.target.value)} aria-label="סינון לפי נכס">
              <option value="">כל הנכסים</option>
              {data.apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button onClick={() => setOnlyUnread((v) => !v)} className="text-xs rounded-lg px-2.5 py-1.5"
              style={{ border: `1px solid ${C.line}`, background: onlyUnread ? "#EAF3EE" : "transparent", color: onlyUnread ? C.paid : C.muted }}>
              רק לא נקראו
            </button>
            <button onClick={markAll} disabled={!unseenCount} className="text-xs rounded-lg px-2.5 py-1.5"
              style={{ border: `1px solid ${C.line}`, color: C.muted, opacity: unseenCount ? 1 : 0.5 }}>
              סימון הכול כנקרא
            </button>
            {/* בחירה מרובה ומחיקה. תשובות שלך נמחקות גם אצל הדייר; הודעות
                ואירועים מהדייר מוסתרים אצלך בלבד. */}
            <button onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} className="text-xs rounded-lg px-2.5 py-1.5"
              style={{ border: `1px solid ${C.line}`, background: selectMode ? "#F1F4F1" : "transparent", color: C.muted }}>
              {selectMode ? "ביטול הבחירה" : "בחירה"}
            </button>
            {selectMode && (
              <button onClick={() => removeIds([...selected])} disabled={!selected.size}
                className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                style={{ background: C.late, color: "#fff", opacity: selected.size ? 1 : 0.5 }}>
                <Trash2 size={12} /> מחיקה{selected.size ? ` (${selected.size})` : ""}
              </button>
            )}
          </div>
        </div>

        {pending.length > 0 && (
          <div className="mb-4">
            <div className="text-xs mb-1.5 flex items-center gap-1.5" style={{ color: "#6B4E12", fontWeight: 600 }}>
              <CalendarPlus size={13} /> ממתין לטיפול שלך
            </div>
            <div className="space-y-1.5">
              {pending.map(({ apt, request: r }) => (
                <div key={r.id} className="rounded-lg border p-3 flex flex-wrap items-start gap-2" style={{ borderColor: "#E8C98A", background: "#FBF2E3" }}>
                  <CalendarPlus size={16} className="mt-0.5 shrink-0" style={{ color: "#B8860B" }} />
                  <div className="flex-1 min-w-0 text-sm">
                    <div style={{ fontWeight: 600 }}>
                      {apt.tenant || "הדייר"} ביקש/ה להאריך את החוזה עד {heDate(r.to)}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: "#6B4E12" }}>
                      {apt.name} · נשלחה ב-{heDate(r.createdAt)}{apt.leaseEnd ? ` · החוזה הנוכחי עד ${heDate(apt.leaseEnd)}` : ""}
                      {r.note ? ` · "${r.note}"` : ""}{r.by === "owner" ? " · נרשמה על ידיך (בשם הדייר / תצוגה מקדימה)" : ""}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap min-w-0">
                    <button onClick={() => setModal({ kind: "extend", apt, request: r })}
                      className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: C.teal, color: "#fff" }}>
                      <Check size={13} /> אישור — נספח הארכה
                    </button>
                    <button onClick={() => setModal({ kind: "extend", apt, request: r, declineFirst: true })}
                      className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: "1px solid #E8C98A", color: "#6B4E12" }}>
                      <X size={13} /> דחייה
                    </button>
                    <button onClick={() => setReplyFor(replyFor === `req:${r.id}` ? null : `req:${r.id}`)}
                      className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                      style={{ border: "1px solid #E8C98A", color: "#6B4E12", background: replyFor === `req:${r.id}` ? "#fff" : "transparent" }}>
                      <Send size={13} /> תגובה
                    </button>
                  </div>
                  {replyFor === `req:${r.id}` && (
                    <ReplyBox apt={apt} requestId={r.id} onSent={() => load(true)} onClose={() => setReplyFor(null)} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {err && <div className="rounded-lg p-2.5 text-sm mb-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>}
        {!busy && !shown.length && (
          <div className="text-sm py-8 text-center" style={{ color: C.muted }}>
            {items.length
              ? "אין הודעות שמתאימות לסינון."
              : "עדיין אין הודעות. כשדייר יחתום על חוזה, יבקש הארכה, ידווח קריאת מונה או יכתוב לך — זה יופיע כאן."}
          </div>
        )}

        <div className="space-y-1.5">
          {shown.map((n, i) => {
            const kind = INBOX_KINDS[n.type] || { Icon: MessageCircle, color: C.muted, title: () => n.type, go: null, goLabel: "" };
            const m = n.meta || {};
            const apt = data.apartments.find((a) => a.id === m.apartmentId);
            const phone = m.phone || apt?.phone || "";
            const unread = !n.seenAt;
            // בקשת ההארכה שההתראה מדברת עליה — מצבה העדכני מתוך המסמך.
            const req = n.type === "extension_requested" && apt && m.requestId
              ? extensionRequestsOf(data, apt.id).find((r) => r.id === m.requestId) || null
              : null;
            const canReply = !!apt && ["tenant_message", "extension_requested", "reading_submitted", "payment_reported"].includes(n.type);
            const day = dayOf(n.createdAt);
            const showDay = i === 0 || day !== dayOf(shown[i - 1].createdAt);
            return (
              <div key={n.id}>
                {showDay && <div className="text-[11px] mt-2 mb-1" style={{ color: C.muted }}>{dayLabel(day)}</div>}
                <div onClick={() => markSeen(n)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") markSeen(n); }}
                  className="rounded-lg border p-3 flex flex-wrap items-start gap-2 cursor-pointer"
                  style={{ borderColor: unread ? "#CBE2D6" : C.line, background: unread ? "#F6FBF8" : C.card }}>
                  {selectMode && (
                    <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggleSelect(n.id)}
                      onClick={(e) => e.stopPropagation()} className="mt-1" aria-label="בחירת הודעה" />
                  )}
                  <kind.Icon size={16} className="mt-0.5 shrink-0" style={{ color: kind.color }} />
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="break-words" style={{ fontWeight: unread ? 600 : 400 }}>{kind.title(m)}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                      {m.apartment || apt?.name || ""} · {timeOf(n.createdAt)}
                      {m.by && n.type !== "owner_reply" ? " · בוצע על ידיך מתוך האזור של הדייר" : ""}
                      {/* "נקרא" על התשובות שלך — הצד השני של אותו ✓✓. */}
                      {n.type === "owner_reply" && (
                        n.tenantSeenAt
                          ? <span style={{ color: C.paid }}> · נקרא על ידי הדייר ✓ {timeOf(n.tenantSeenAt)}</span>
                          : <span> · טרם נקרא על ידי הדייר</span>
                      )}
                    </div>
                    {/* דיווח מונה: הקריאה, הקודמת, הצריכה, מה חויב — והצילום
                        אם נשלח. כל מה שצריך כדי לקרוא את המונה ולשלוח את
                        החשבון בלי לעזוב את התיבה. */}
                    {n.type === "reading_submitted" && (
                      <div className="text-[11px] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: C.muted }}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>קריאה: {m.value ?? ""}</span>
                        {/* החשבון מבוסס על צריכת תקופת החיוב; ההפרש מהקריאה הקודמת
                            הוא מידע משני. קודם הוצגו "צריכה 4 מ״ק · חויב ₪249" יחד. */}
                        {m.periodDiff != null && m.periodDiff > 0
                          ? <span>החודש: {fmtQty(m.periodDiff)} {UNIT[m.meter] || ""}{m.periodFrom != null ? ` (מ-${m.periodFrom} ל-${m.periodTo ?? m.value})` : ""}</span>
                          : (m.previous != null && <span>קודמת: {m.previous}</span>)}
                        {m.diff != null && m.diff > 0 && (m.periodDiff == null || Math.abs(m.periodDiff - m.diff) > 0.001) && (
                          <span>מאז הקריאה הקודמת: {fmtQty(m.diff)} {UNIT[m.meter] || ""}</span>
                        )}
                        {m.billed && m.amount
                          ? <span style={{ color: C.ink }}>חויב {nis(m.amount)}</span>
                          : <span>לא נוצר חיוב (לא חודש חיוב, או משולם ישירות לספק)</span>}
                        {m.photoId && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); openLightbox(m.photoId, "owner"); }}
                            title="לחיצה להגדלה" aria-label="הגדלת צילום המונה" className="flex items-center gap-1.5">
                            <StoredImage id={m.photoId} className="rounded" style={{ height: 48 }} scope="owner" />
                            <span className="text-[10px]" style={{ color: C.teal }}>הגדלה</span>
                          </button>
                        )}
                      </div>
                    )}
                    {/* דיווח העברה: הסכום, התאריך, הערה — ותמונת האסמכתא.
                        אין כאן "חויב"/"שולם", כי הדיווח לא נוגע בזה בכלל —
                        הסימון בפועל הוא פעולה נפרדת של בעל הנכס. */}
                    {n.type === "payment_reported" && (
                      <div className="text-[11px] mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: C.muted }}>
                        <span style={{ color: C.ink, fontWeight: 600 }}>{nis(m.amount || 0)}</span>
                        <span>{m.category === "bills" ? "חשבונות" : "שכר דירה"} · {methodLabel(m.method) || m.method || ""}</span>
                        {m.date && <span>תאריך: {heDate(m.date)}</span>}
                        {m.note && <span>{m.note}</span>}
                        {m.photoId && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); openLightbox(m.photoId, "owner"); }}
                            title="לחיצה להגדלה" aria-label="הגדלת תמונת האסמכתא" className="flex items-center gap-1.5">
                            <StoredImage id={m.photoId} className="rounded" style={{ height: 48 }} scope="owner" />
                            <span className="text-[10px]" style={{ color: C.teal }}>הגדלה</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    {unread && <span className="w-2 h-2 rounded-full" style={{ background: C.late }} aria-label="לא נקרא" />}
                    {/* בקשת הארכה: כל עוד היא ממתינה — אישור ודחייה ישר מכאן;
                        אחרי ההחלטה — מה הוחלט. */}
                    {req && req.status === "pending" ? (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); markSeen(n); setModal({ kind: "extend", apt, request: req }); }}
                          className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: C.teal, color: "#fff" }}>
                          <Check size={12} /> אישור
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); markSeen(n); setModal({ kind: "extend", apt, request: req, declineFirst: true }); }}
                          className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: "1px solid #E8C98A", color: "#6B4E12" }}>
                          <X size={12} /> דחייה
                        </button>
                      </>
                    ) : req ? (
                      <span className="text-[11px] rounded-full px-2 py-0.5"
                        style={{ background: req.status === "approved" ? "#EAF3EE" : "#F1F4F1", color: req.status === "approved" ? C.paid : C.muted }}>
                        {EXTENSION_STATUS[req.status] || req.status}{req.reason ? ` — ${req.reason}` : ""}
                      </span>
                    ) : kind.go && (
                      <button onClick={(e) => { e.stopPropagation(); go(n); }} className="text-xs rounded-lg px-2.5 py-1.5"
                        style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                        {kind.goLabel}
                      </button>
                    )}
                    {/* דיווח מונה: שליחת פירוט החשבון לדייר — נפתח לעריכה לפני
                        השליחה, עם אותו נוסח של הודעת הוואטסאפ. */}
                    {n.type === "reading_submitted" && apt && (
                      <button onClick={(e) => {
                        e.stopPropagation(); markSeen(n);
                        setReplyPreset(statementText(apt, data, CUR_KEY, data.settings));
                        setReplyFor(n.id);
                      }}
                        className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                        style={{ background: C.ochre, color: "#fff" }}>
                        <Receipt size={12} /> שליחת החשבון לדייר
                      </button>
                    )}
                    {canReply && (
                      <button onClick={(e) => { e.stopPropagation(); markSeen(n); setReplyPreset(""); setReplyFor(replyFor === n.id ? null : n.id); }}
                        className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                        style={{ border: `1px solid ${C.line}`, color: "#2E8FCC", background: replyFor === n.id ? "#F3F8FA" : "transparent" }}>
                        <Send size={12} /> תגובה
                      </button>
                    )}
                    {n.type === "tenant_message" && phone && (
                      <a href={waLink(phone, `שלום ${m.tenant || ""}, בנוגע להודעתך${m.text ? ` "${String(m.text).slice(0, 80)}"` : ""} — `)}
                        target="_blank" rel="noreferrer" onClick={(e) => { e.stopPropagation(); markSeen(n); }}
                        className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                        style={{ border: `1px solid ${C.line}`, color: "#128C7E" }}>
                        <MessageCircle size={12} /> וואטסאפ
                      </a>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); removeIds([n.id]); }} aria-label="מחיקת ההודעה" title="מחיקה"
                      className="text-xs rounded-lg px-2 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                  {replyFor === n.id && apt && (
                    <ReplyBox key={`${n.id}:${replyPreset ? "bill" : "plain"}`} apt={apt} inReplyTo={n.id} requestId={req?.id || null}
                      initialText={replyPreset} onSent={() => load(true)} onClose={() => { setReplyFor(null); setReplyPreset(""); }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {hasMore && (
          <button onClick={() => load(false)} disabled={busy} className="mt-3 text-xs" style={{ color: C.teal }}>
            {busy ? "טוען…" : "הודעות ישנות יותר"}
          </button>
        )}
      </div>
    </section>
  );
}

/* הלשוניות שיושבות מאחורי "עוד" — עם השם שמופיע בשורת ההקשר. */
const SECONDARY_TABS = {
  docs: "חוזים ותיעוד",
  tenants: "היסטוריית חובות דיירים",
  reports: "דוחות",
  value: "שווי הנכסים",
  loans: "הלוואות ומשכנתאות",
  equity: "הון עצמי",
  expenses: "הוצאות",
  tariffs: "הגדרות",
  owner: "אזור אישי של בעל הנכסים",
  backup: "גיבוי ושיתוף",
};

/**
 * מסך "עוד" — כל מה שעושים פעם בתקופה, במקום אחד: חוזים, דוחות, הוצאות,
 * הגדרות, אזור אישי, גיבוי, שיתוף האפליקציה, כניסת דייר, ניהול פלטפורמה.
 */
function MorePage({ setTab, setModal, me }) {
  const install = useInstallPrompt();
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const items = [
    { k: "docs", label: "חוזים ותיעוד", sub: "חוזים, נספחים, ערבים, סריקות ותמונות", icon: FileText, onClick: () => setTab("docs") },
    { k: "tenants", label: "היסטוריית חובות דיירים", sub: "דיירים שכבר לא גרים בנכסים — מה נגבה, מי חייב, חוב אבוד ומחיקה", icon: UserPlus, onClick: () => setTab("tenants") },
    { k: "reports", label: "דוחות", sub: "הכנסות, חובות, שנים קודמות", icon: Wallet, onClick: () => setTab("reports") },
    { k: "value", label: "שווי הנכסים", sub: "שווי נכון לתאריך וסימולטור עליית ערך", icon: TrendingUp, onClick: () => setTab("value") },
    { k: "loans", label: "הלוואות ומשכנתאות", sub: "ריביות, החזרים, סימולטור מחזור וייעוץ AI", icon: Landmark, onClick: () => setTab("loans") },
    { k: "equity", label: "הון עצמי", sub: "מחיר רכישה מול משכנתא — בזמן הרכישה והיום", icon: PiggyBank, onClick: () => setTab("equity") },
    { k: "expenses", label: "הוצאות", sub: "תיקונים, ועד, הוצאות על הנכסים", icon: Receipt, onClick: () => setTab("expenses") },
    { k: "tariffs", label: "הגדרות", sub: "תעריפים, אמצעי תשלום, כניסה מהירה, סיסמה", icon: Settings, onClick: () => setTab("tariffs") },
    { k: "owner", label: "אזור אישי של בעל הנכסים", sub: "פרטי הרישום, פרטי התשלום, החתימה שלך", icon: User, onClick: () => setTab("owner") },
    { k: "backup", label: "גיבוי ושיתוף", sub: "ייצוא, ייבוא וגיבוי הנתונים", icon: Download, onClick: () => setTab("backup") },
    ...(!install.isInstalled && (install.canInstall || install.isIOS)
      ? [{
          k: "install", label: "התקנת האפליקציה", sub: "אייקון על מסך הבית, נפתחת כאפליקציה עצמאית בלי סרגל כתובת",
          icon: Smartphone,
          onClick: () => { if (install.canInstall) install.promptInstall(); else setShowIOSHelp(true); },
        }]
      : []),
    ...(me?.role === "platform_admin"
      ? [{ k: "admin", label: "ניהול פלטפורמה", sub: "חשבונות, מנויים, הגדרות המערכת", icon: ShieldCheck, onClick: () => { window.location.href = "/admin"; } }]
      : []),
  ];
  return (
    <section className="mt-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <button key={it.k} onClick={it.onClick}
            className="rounded-xl border p-4 text-right flex items-start gap-3"
            style={{ background: C.card, borderColor: C.line }}>
            <it.icon size={20} className="mt-0.5 shrink-0" style={{ color: C.teal }} />
            <span>
              <span className="block text-sm" style={{ fontWeight: 600 }}>{it.label}</span>
              <span className="block text-[12px] mt-0.5" style={{ color: C.muted }}>{it.sub}</span>
            </span>
          </button>
        ))}
      </div>
      <LegalLinks className="mt-6" />
      {showIOSHelp && <IOSInstallHelp onClose={() => setShowIOSHelp(false)} />}
    </section>
  );
}

/** הסבר "הוספה למסך הבית" ל-iOS/Safari — הפלטפורמה היחידה שבה אין
    API להציע התקנה, ולכן חייבים הנחיה ידנית. משותף לבעל הנכס
    (MorePage) ולדייר (TenantPortal, ראו למטה). */
function IOSInstallHelp({ onClose }) {
  return (
    <Modal title="התקנת האפליקציה" onClose={onClose}>
      <ol className="text-sm space-y-2" style={{ color: C.ink }}>
        <li>1. פתחו את הכתובת הזו ב-Safari (לא בדפדפן אחר).</li>
        <li>2. הקישו על כפתור השיתוף <Share2 size={14} className="inline align-text-bottom" /> בתחתית המסך.</li>
        <li>3. גללו ובחרו "הוסף למסך הבית".</li>
      </ol>
      <p className="text-xs mt-3" style={{ color: C.muted }}>
        אחרי זה תופיע אצלכם אייקון שפותח את האפליקציה במסך מלא, בלי סרגל הכתובת.
      </p>
      <p className="text-xs mt-2" style={{ color: C.muted }}>
        בפעם הראשונה באפליקציה המותקנת ייתכן שתתבקשו להיכנס מחדש — האייקון
        פותח אחסון נפרד מ-Safari, בלי הכניסה שכבר הייתה לכם. דיירים: אם
        מוצג כאן מסך של בעל נכס, בחרו למטה "כניסה לאזור הדייר" והקלידו
        את הסיסמה שלכם.
      </p>
    </Modal>
  );
}

/* ============ שווי הנכסים ============
   שווי נרשם כרשומות "נכון לתאריך" ולא כמספר בודד — כך רואים את
   ההתפתחות, והסימולטור מקרין קדימה מהרשומה האחרונה בריבית דריבית. */

/* מסך "בקרוב" — עוטף לשוניות שהושבתו זמנית (שווי הנכסים, הלוואות
   ומשכנתאות) בלי למחוק את התוכן/הלוגיקה שמאחוריהן. */
function ComingSoonLock({ title, text }) {
  return (
    <section className="mt-6">
      <div className="rounded-xl border p-8 text-center" style={{ background: C.card, borderColor: C.line }}>
        <div className="mx-auto mb-3 flex items-center justify-center rounded-full"
          style={{ width: 48, height: 48, background: C.surface }}>
          <Lock size={22} style={{ color: C.muted }} />
        </div>
        <h2 className="disp text-lg font-bold mb-1">{title}</h2>
        <p className="text-sm" style={{ color: C.muted }}>{text || "בקרוב — לא זמין כרגע."}</p>
      </div>
    </section>
  );
}

function PropertyValues({ data, run }) {
  const LOCKED = false;
  const apts = data.apartments || [];
  const [draft, setDraft] = useState({});           // { [aptId]: { amount, date } }
  const [pDraft, setPDraft] = useState({});         // { [aptId]: { price, date } } — מחיר רכישה, עריכה בלבד כשעוד לא נרשם
  const [history, setHistory] = useState(null);     // aptId עם היסטוריה פתוחה
  const [pct, setPct] = useState("3.5");
  const [years, setYears] = useState(5);

  const target = addYears(TODAY, years);
  /* ההקרנה מרשומת השווי עצמה ולא מהיום: הערכה בת שנה כבר "צברה" שנה
     מהעלייה השנתית, והתעלמות מזה מקטינה את התחזית בלי סיבה. */
  const projected = (a) => {
    const v = latestValue(a);
    if (!v) return null;
    return projectValue(v.amount, Number(pct) || 0, daysBetween(v.date || TODAY, target) / 365.25);
  };
  const withValue = apts.filter((a) => latestValue(a));
  const totalNow = withValue.reduce((s, a) => s + (latestValue(a)?.amount || 0), 0);
  const totalProj = withValue.reduce((s, a) => s + (projected(a) || 0), 0);

  const save = (a) => {
    const d = draft[a.id] || {};
    const amount = Number(String(d.amount || "").replace(/[^\d.]/g, ""));
    if (!amount) return;
    run([{ type: "set_property_value", apartment: a.id, amount, date: d.date || TODAY }]);
    setDraft((s) => ({ ...s, [a.id]: { amount: "", date: "" } }));
  };

  /* מחיר רכישה — עובדה חד-פעמית, לא רשומת שווי: נשמר ישירות על הנכס
     (update_apartment), לא בהיסטוריית values שמיועדת לשיערוכים חוזרים. */
  const savePurchase = (a) => {
    const d = pDraft[a.id] || {};
    const price = Number(String(d.price || "").replace(/[^\d.]/g, ""));
    if (!price) return;
    run([{ type: "update_apartment", apartment: a.id, purchasePrice: price, purchaseDate: d.date || TODAY }]);
    setPDraft((s) => ({ ...s, [a.id]: { price: "", date: "" } }));
  };

  if (LOCKED) {
    return <ComingSoonLock title="שווי הנכסים" text="הלשונית הזו לא זמינה כרגע." />;
  }

  return (
    <section className="mt-4 space-y-4">
      {/* התיק כולו */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-1.5 mb-1">
          <TrendingUp size={16} style={{ color: C.teal }} />
          <h2 className="disp text-xl font-bold">שווי התיק</h2>
        </div>
        {withValue.length === 0 ? (
          <p className="text-sm" style={{ color: C.muted }}>
            עדיין לא נרשם שווי לאף נכס. רשמו למטה שווי נוכחי או מחיר רכישה — והסימולטור יעבוד מיד.
          </p>
        ) : (
          <div className="flex flex-wrap gap-6 items-end">
            <div>
              <div className="text-[12px]" style={{ color: C.muted }}>שווי נוכחי ({withValue.length} מתוך {apts.length} נכסים)</div>
              <div className="disp text-2xl font-bold">{nis(Math.round(totalNow))}</div>
            </div>
            <div>
              <div className="text-[12px]" style={{ color: C.muted }}>תחזית ל-{heDate(target)} ({pct || 0}% לשנה)</div>
              <div className="disp text-2xl font-bold" style={{ color: C.teal }}>{nis(Math.round(totalProj))}</div>
            </div>
          </div>
        )}
      </div>

      {/* הסימולטור */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-lg font-bold mb-1">סימולטור עליית ערך</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          ריבית דריבית שנתית מרשומת השווי האחרונה של כל נכס. הערכה בלבד — השוק לא מבטיח דבר.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="text-sm flex items-center gap-2">
            עלייה שנתית
            <input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)}
              style={{ ...inputStyle, width: 70, textAlign: "center" }} />
            %
          </label>
          <div className="flex items-center gap-1.5">
            {[5, 10, 15, 20].map((y) => (
              <button key={y} onClick={() => setYears(y)} className="text-[12px] rounded-full px-3 py-1.5"
                style={years === y ? { background: C.teal, color: "#fff" } : { border: `1px solid ${C.line}`, color: C.muted }}>
                {y} שנים
              </button>
            ))}
          </div>
        </div>
        {withValue.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 420 }}>
              <thead>
                <tr className="text-[12px]" style={{ color: C.muted }}>
                  <th className="text-right py-1.5 font-normal">נכס</th>
                  <th className="text-right py-1.5 font-normal">שווי אחרון</th>
                  <th className="text-right py-1.5 font-normal">נכון ל-</th>
                  <th className="text-right py-1.5 font-normal">תחזית {heDate(target)}</th>
                </tr>
              </thead>
              <tbody>
                {withValue.map((a) => {
                  const v = latestValue(a);
                  return (
                    <tr key={a.id} className="border-t" style={{ borderColor: C.line }}>
                      <td className="py-2" style={{ fontWeight: 600 }}>{a.name}</td>
                      <td className="py-2">{nis(v.amount)}</td>
                      <td className="py-2 text-[12px]" style={{ color: C.muted }}>{heDate(v.date)}</td>
                      <td className="py-2" style={{ color: C.teal, fontWeight: 600 }}>{nis(Math.round(projected(a)))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* רישום שווי לכל נכס */}
      {apts.map((a) => {
        const v = latestValue(a);
        const d = draft[a.id] || {};
        const list = [...(a.values || [])].sort((x, y) => String(y.date).localeCompare(String(x.date)));
        return (
          <div key={a.id} className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm" style={{ fontWeight: 600 }}>{a.name}</div>
                <div className="text-[12px]" style={{ color: C.muted }}>
                  {v ? <>שווי אחרון: <b style={{ color: C.ink }}>{nis(v.amount)}</b> נכון ל-{heDate(v.date)}</> : "טרם נרשם שווי"}
                </div>
              </div>
              {list.length > 0 && (
                <button onClick={() => setHistory(history === a.id ? null : a.id)}
                  className="text-[12px] rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                  {history === a.id ? "סגירת ההיסטוריה" : `היסטוריה (${list.length})`}
                </button>
              )}
            </div>

            <div className="mt-2 pt-2 border-t text-[12px]" style={{ borderColor: C.line, color: C.muted }}>
              {a.purchasePrice ? (
                <>נרכשה ב-{nis(a.purchasePrice)}{a.purchaseDate ? ` · ${heDate(a.purchaseDate)}` : ""}</>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span>מחיר רכישה:</span>
                  <input inputMode="numeric" placeholder="מחיר רכישה בש״ח" value={(pDraft[a.id] || {}).price || ""}
                    onChange={(e) => setPDraft((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), price: e.target.value } }))}
                    style={{ ...inputStyle, width: 140 }} />
                  <DateField value={(pDraft[a.id] || {}).date || TODAY}
                    onChange={(iso) => setPDraft((s) => ({ ...s, [a.id]: { ...(s[a.id] || {}), date: iso } }))}
                    ariaLabel="תאריך רכישה" />
                  <button onClick={() => savePurchase(a)}
                    disabled={!Number(String((pDraft[a.id] || {}).price || "").replace(/[^\d.]/g, ""))}
                    className="text-[12px] rounded-lg px-2.5 py-1.5"
                    style={{ border: `1px solid ${C.line}`, opacity: Number(String((pDraft[a.id] || {}).price || "").replace(/[^\d.]/g, "")) ? 1 : 0.5 }}>
                    רישום
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <input inputMode="numeric" placeholder="שווי בש״ח" value={d.amount || ""}
                onChange={(e) => setDraft((s) => ({ ...s, [a.id]: { ...d, amount: e.target.value } }))}
                style={{ ...inputStyle, width: 140 }} />
              <DateField value={d.date || TODAY} onChange={(iso) => setDraft((s) => ({ ...s, [a.id]: { ...d, date: iso } }))} ariaLabel="נכון לתאריך" />
              <button onClick={() => save(a)} disabled={!Number(String(d.amount || "").replace(/[^\d.]/g, ""))}
                className="text-sm rounded-lg px-3 py-2" style={{ background: C.teal, color: "#fff", opacity: Number(String(d.amount || "").replace(/[^\d.]/g, "")) ? 1 : 0.5 }}>
                רישום שווי
              </button>
            </div>

            {history === a.id && (
              <div className="mt-3 space-y-1">
                {list.map((rec) => (
                  <div key={rec.id || rec.date} className="flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5" style={{ background: "#F1F4F1" }}>
                    <span>{nis(rec.amount)} · {heDate(rec.date)}{rec.notes ? ` · ${rec.notes}` : ""}</span>
                    <button onClick={() => run([{ type: "remove_property_value", apartment: a.id, valueId: rec.id, date: rec.date }])}
                      aria-label="מחיקה" style={{ color: C.late }}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

/* ============ הלוואות ומשכנתאות ============
   כל החישובים בלוח שפיצר וללא הצמדה — יתרה ידנית שנמסרה גוברת.
   היועץ מקבל מהשרת את המספרים המחושבים ומנתח, לא מחשב בעצמו. */

const RATE_TYPES = ["קבועה לא צמודה", "קבועה צמודה", "פריים", "משתנה צמודה", "משתנה לא צמודה", "מעורב"];

/** חודשים כטקסט — "18 שנים ו-4 חודשים". */
const monthsText = (m) => {
  const y = Math.floor(m / 12), r = m % 12;
  if (!y) return `${r} חודשים`;
  return `${y} שנים${r ? ` ו-${r} חודשים` : ""}`;
};

function LoanCard({ loan, apts, onSave, onDelete }) {
  const [f, setF] = useState(() => ({
    name: loan.name || "", lender: loan.lender || "", aptId: loan.aptId || "",
    principal: loan.principal || "", rate: loan.rate ?? "", years: loan.months ? String(Math.round((loan.months / 12) * 10) / 10) : "",
    start: loan.start || "", rateType: loan.rateType || "", balance: loan.balance ?? "", balanceDate: loan.balanceDate || "",
    notes: loan.notes || "",
  }));
  const [refi, setRefi] = useState({ rate: "", years: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const st = loan.id ? loanState(loan) : null;
  const quote = st && Number(refi.rate) > 0
    ? refinanceQuote(st, Number(refi.rate), refi.years ? Math.round(Number(refi.years) * 12) : st.remainingMonths)
    : null;

  const save = () => onSave({
    ...(loan.id ? { loanId: loan.id } : {}),
    name: f.name.trim() || "הלוואה",
    lender: f.lender, apartment: f.aptId || "",
    principal: Number(f.principal) || 0, rate: Number(f.rate) || 0,
    months: Math.max(1, Math.round((Number(f.years) || 0) * 12)) || (loan.months || 12),
    start: f.start, rateType: f.rateType,
    balance: f.balance === "" ? null : Number(f.balance) || 0,
    balanceDate: f.balance === "" ? null : f.balanceDate || TODAY,
    notes: f.notes,
  });

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm" style={{ fontWeight: 600 }}>{loan.id ? (loan.name || "הלוואה") : "הלוואה חדשה"}</div>
        {loan.id && (
          <button onClick={onDelete} aria-label="מחיקת ההלוואה" style={{ color: C.late }}><Trash2 size={14} /></button>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        <Field label="שם ההלוואה"><input style={inputStyle} value={f.name} onChange={set("name")} placeholder='למשל "משכנתא הרצל 12"' /></Field>
        <Field label="גוף מלווה"><input style={inputStyle} value={f.lender} onChange={set("lender")} placeholder="בנק / חברת ביטוח" /></Field>
        <Field label="נכס מקושר">
          <select style={inputStyle} value={f.aptId} onChange={set("aptId")}>
            <option value="">ללא קישור לנכס</option>
            {apts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="קרן מקורית (₪)"><input style={inputStyle} inputMode="numeric" value={f.principal} onChange={set("principal")} /></Field>
        <Field label="ריבית שנתית (%)"><input style={inputStyle} inputMode="decimal" value={f.rate} onChange={set("rate")} /></Field>
        <Field label="תקופה (שנים)"><input style={inputStyle} inputMode="decimal" value={f.years} onChange={set("years")} /></Field>
        <Field label="תחילת ההלוואה">
          <input type="month" style={inputStyle} value={f.start} onChange={set("start")} />
        </Field>
        <Field label="מסלול הריבית">
          <select style={inputStyle} value={f.rateType} onChange={set("rateType")}>
            <option value="">לא צוין</option>
            {RATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="יתרה עדכנית (₪, רשות)"><input style={inputStyle} inputMode="numeric" value={f.balance} onChange={set("balance")} placeholder="מדוח היתרות של הבנק" /></Field>
      </div>
      <div className="mt-2">
        <Field label="הערות"><input style={inputStyle} value={f.notes} onChange={set("notes")} placeholder="למשל: תחנת יציאה ב-2028, עמלת פירעון" /></Field>
      </div>

      <button onClick={save} disabled={!(Number(f.principal) > 0 && Number(f.years) > 0)}
        className="mt-3 text-sm rounded-lg px-4 py-2"
        style={{ background: C.teal, color: "#fff", opacity: Number(f.principal) > 0 && Number(f.years) > 0 ? 1 : 0.5 }}>
        {loan.id ? "שמירת השינויים" : "הוספת ההלוואה"}
      </button>

      {st && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[11px]" style={{ color: C.muted }}>החזר חודשי (שפיצר)</div><b>{nis(Math.round(st.monthly))}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>יתרה {st.manualBalance ? `(ידנית, ${heDate(loan.balanceDate || TODAY)})` : "(מחושבת)"}</div><b>{nis(Math.round(st.balance))}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>נותרו</div><b>{monthsText(st.remainingMonths)}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>ריבית שנותרה</div><b>{nis(Math.round(st.interestRemaining))}</b></div>
          </div>

          {/* סימולטור מחזור */}
          <div className="mt-3 rounded-lg p-3" style={{ background: "#F1F4F1" }}>
            <div className="text-[12px] mb-2" style={{ fontWeight: 600 }}>סימולטור מחזור — אותה יתרה, תנאים אחרים</div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[12px] flex items-center gap-1.5">ריבית מוצעת
                <input inputMode="decimal" value={refi.rate} onChange={(e) => setRefi((s) => ({ ...s, rate: e.target.value }))}
                  style={{ ...inputStyle, width: 64, textAlign: "center" }} />%
              </label>
              <label className="text-[12px] flex items-center gap-1.5">תקופה
                <input inputMode="decimal" value={refi.years} onChange={(e) => setRefi((s) => ({ ...s, years: e.target.value }))}
                  placeholder={String(Math.round((st.remainingMonths / 12) * 10) / 10)}
                  style={{ ...inputStyle, width: 64, textAlign: "center" }} />שנים
              </label>
            </div>
            {quote && (
              <div className="mt-2 text-sm">
                החזר חדש: <b>{nis(Math.round(quote.monthly))}</b> ל-{monthsText(quote.months)}
                {" · "}
                <span style={{ color: quote.monthlySaving >= 0 ? C.paid : C.late, fontWeight: 600 }}>
                  {quote.monthlySaving >= 0 ? "חיסכון" : "תוספת"} {nis(Math.abs(Math.round(quote.monthlySaving)))} בחודש
                </span>
                {" · "}
                <span style={{ color: quote.totalSaving >= 0 ? C.paid : C.late }}>
                  סה"כ {quote.totalSaving >= 0 ? "חיסכון" : "תוספת"} {nis(Math.abs(Math.round(quote.totalSaving)))}
                </span>
                <div className="text-[11px] mt-1" style={{ color: C.muted }}>
                  לפני עמלות פירעון מוקדם והיוון — בדקו מול הבנק. החישוב ללא הצמדה.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Loans({ data, run }) {
  const LOCKED = false;
  const loans = data.loans || [];
  const apts = data.apartments || [];
  const [adding, setAdding] = useState(false);
  const [question, setQuestion] = useState("");
  const [advice, setAdvice] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");

  const states = loans.map((l) => loanState(l));
  const totalBalance = states.reduce((s, x) => s + x.balance, 0);
  const totalMonthly = states.reduce((s, x) => s + x.monthly, 0);
  const totalRent = apts.reduce((s, a) => s + (Number(a.rent) || 0), 0);
  const totalValue = apts.reduce((s, a) => s + (latestValue(a)?.amount || 0), 0);

  const saveLoan = (fields) => { run([{ type: "set_loan", ...fields }]); setAdding(false); };

  const ask = async () => {
    setAiBusy(true); setAiErr("");
    try {
      const r = await ai.loansAdvice(question);
      // המודל מתבקש טקסט רגיל; כוכביות Markdown שבכל זאת חוזרות מנוקות.
      setAdvice(String(r.advice || "").replace(/\*\*|__|^#+\s*/gm, ""));
    } catch (e) {
      setAiErr(e?.message || "הייעוץ נכשל. נסו שוב.");
    }
    setAiBusy(false);
  };

  if (LOCKED) {
    return <ComingSoonLock title="הלוואות ומשכנתאות" text="הלשונית הזו לא זמינה כרגע." />;
  }

  return (
    <section className="mt-4 space-y-4">
      {/* סיכום */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-1.5 mb-1">
          <Landmark size={16} style={{ color: C.teal }} />
          <h2 className="disp text-xl font-bold">הלוואות ומשכנתאות</h2>
        </div>
        {loans.length === 0 ? (
          <p className="text-sm" style={{ color: C.muted }}>
            אין עדיין הלוואות רשומות. הוסיפו את המשכנתאות וההלוואות על הנכסים — ותקבלו החזר חודשי,
            יתרה, סימולטור מחזור וייעוץ AI על התמהיל.
          </p>
        ) : (
          <div className="flex flex-wrap gap-6">
            <div><div className="text-[12px]" style={{ color: C.muted }}>יתרת חוב כוללת</div><div className="disp text-2xl font-bold">{nis(Math.round(totalBalance))}</div></div>
            <div><div className="text-[12px]" style={{ color: C.muted }}>החזר חודשי כולל</div><div className="disp text-2xl font-bold">{nis(Math.round(totalMonthly))}</div></div>
            {totalRent > 0 && (
              <div><div className="text-[12px]" style={{ color: C.muted }}>מול שכירות חודשית</div><div className="disp text-2xl font-bold">{nis(totalRent)}</div></div>
            )}
            {totalValue > 0 && (
              <div><div className="text-[12px]" style={{ color: C.muted }}>חוב מול שווי (LTV)</div><div className="disp text-2xl font-bold">{Math.round((totalBalance / totalValue) * 100)}%</div></div>
            )}
          </div>
        )}
        <p className="text-[11px] mt-2" style={{ color: C.muted }}>
          החישובים בלוח שפיצר וללא הצמדה למדד. יתרה ידנית מדוח הבנק גוברת על המחושבת — כדאי לעדכן אותה מדי תקופה.
        </p>
      </div>

      {loans.map((l) => (
        <LoanCard key={l.id} loan={l} apts={apts} onSave={saveLoan}
          onDelete={() => run([{ type: "remove_loan", loanId: l.id }])} />
      ))}

      {adding
        ? <LoanCard loan={{}} apts={apts} onSave={saveLoan} onDelete={null} />
        : (
          <button onClick={() => setAdding(true)} className="text-sm rounded-lg px-4 py-2.5 flex items-center gap-1.5"
            style={{ border: `1px solid ${C.line}`, color: C.teal }}>
            <Plus size={15} /> הוספת הלוואה או משכנתא
          </button>
        )}

      {/* יועץ ה-AI */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles size={16} style={{ color: C.ochre }} />
          <h2 className="disp text-lg font-bold">ייעוץ AI — ריביות ומחזור</h2>
        </div>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          היועץ מנתח את ההלוואות, השווי והשכירות שרשומים כאן: עלות כל מסלול, יחס חוב לשווי,
          וכדאיות מחזור מול ההחזר הקיים. אפשר לשאול שאלה ממוקדת או לבקש ניתוח כללי.
        </p>
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)}
          placeholder='רשות — למשל: "הציעו לי מחזור ב-4.1% קבועה צמודה, האם כדאי?"'
          style={{ ...inputStyle, height: 64 }} />
        <button onClick={ask} disabled={aiBusy || loans.length === 0}
          className="mt-2 text-sm rounded-lg px-4 py-2 flex items-center gap-1.5"
          style={{ background: C.teal, color: "#fff", opacity: aiBusy || loans.length === 0 ? 0.6 : 1 }}>
          {aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {aiBusy ? "מנתח…" : loans.length === 0 ? "קודם הוסיפו הלוואה" : "ניתוח וייעוץ"}
        </button>
        {aiErr && <div className="mt-2 rounded-lg p-2.5 text-sm" style={{ background: "#FBEDEA", color: C.late }}>{aiErr}</div>}
        {advice && (
          <div className="mt-3 rounded-lg p-3 text-sm" style={{ background: "#F1F4F1", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {advice}
          </div>
        )}
      </div>
    </section>
  );
}

/* ============ הון עצמי ============
   משלב שלושה מקורות שכל אחד כבר קיים בנפרד: מחיר הרכישה (על הנכס),
   ההלוואה המקושרת (loanFor) ורשומת השווי האחרונה (latestValue). לא
   מחשב כלום בעצמו — purchaseEquity/currentEquity ב-shared/domain.js. */
function Equity({ data }) {
  const apts = data.apartments || [];
  const rows = apts.map((a) => {
    const loan = loanFor(data, a.id);
    return { apt: a, loan, value: latestValue(a), initial: purchaseEquity(a, loan), current: currentEquity(a, loan) };
  });
  const sumInitial = rows.reduce((s, r) => s + (r.initial || 0), 0);
  const sumCurrent = rows.reduce((s, r) => s + (r.current || 0), 0);
  const anyInitial = rows.some((r) => r.initial != null);
  const anyCurrent = rows.some((r) => r.current != null);

  return (
    <section className="mt-4 space-y-4">
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-2">הון עצמי בתיק</h2>
        {!anyInitial && !anyCurrent ? (
          <p className="text-sm" style={{ color: C.muted }}>
            עדיין אין מספיק נתונים (מחיר רכישה או שווי נוכחי) לחישוב הון עצמי. הוסיפו אותם ב"שווי הנכסים".
          </p>
        ) : (
          <div className="flex flex-wrap gap-6 items-end">
            {anyInitial && (
              <div>
                <div className="text-[12px]" style={{ color: C.muted }}>הון עצמי בזמן הרכישה</div>
                <div className="disp text-2xl font-bold">{nis(Math.round(sumInitial))}</div>
              </div>
            )}
            {anyCurrent && (
              <div>
                <div className="text-[12px]" style={{ color: C.muted }}>הון עצמי נוכחי</div>
                <div className="disp text-2xl font-bold" style={{ color: C.teal }}>{nis(Math.round(sumCurrent))}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {rows.map(({ apt: a, loan, value, initial, current }) => (
        <div key={a.id} className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
          <div className="text-sm mb-2" style={{ fontWeight: 600 }}>{a.name}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><div className="text-[11px]" style={{ color: C.muted }}>מחיר רכישה</div><b>{a.purchasePrice ? nis(a.purchasePrice) : "לא נרשם"}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>משכנתא מקורית</div><b>{loan ? nis(loan.principal) : "אין הלוואה מקושרת"}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>הון עצמי בזמן הרכישה</div><b>{initial != null ? nis(initial) : "—"}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>שווי נוכחי</div><b>{value ? nis(value.amount) : "לא נרשם"}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>יתרת משכנתא נוכחית</div><b>{loan ? nis(loanState(loan).balance) : "—"}</b></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>הון עצמי נוכחי</div><b style={{ color: C.teal }}>{current != null ? nis(current) : "—"}</b></div>
          </div>
        </div>
      ))}
    </section>
  );
}

/** סרגל הלשוניות, עם חיווי גלילה בטלפון. onDark — על גבי הכותרת הירוקה. */
function TabsBar({ tabs, tab, setTab, onDark = false }) {
  const ref = useRef(null);
  const [more, setMore] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    // scrollLeft שלילי ב-RTL; מה שחשוב הוא כמה נשאר לגלול לכיוון הקצה.
    setMore(el.scrollWidth - el.clientWidth - Math.abs(el.scrollLeft) > 8);
  };

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // מעבר לשונית שלא נראית (מהעוזר, מ"מילוי הפרטים") — מביא אותה למסך.
  useEffect(() => {
    const el = ref.current?.querySelector(`[data-tab="${tab}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    measure();
  }, [tab]);

  return (
    <div className="relative">
      <nav ref={ref} onScroll={measure} className="flex gap-1 overflow-x-auto" style={{ borderBottom: onDark ? "0" : `1px solid ${C.line}`, scrollbarWidth: "none" }}>
        {tabs.map((t) => (
          <button key={t.k} data-tab={t.k} onClick={() => setTab(t.k)} className="px-2.5 sm:px-4 py-2.5 text-sm whitespace-nowrap inline-flex items-center gap-1.5"
            style={{
              color: tab === t.k ? (onDark ? "#fff" : C.ink) : (onDark ? "rgba(255,255,255,.62)" : C.muted),
              borderBottom: `3px solid ${tab === t.k ? SUN : "transparent"}`,
              fontWeight: tab === t.k ? 700 : 600,
            }}>
            <span className="sm:hidden">{t.label}</span>
            <span className="hidden sm:inline">{t.label}</span>
            {t.badge > 0 && (
              <span className="text-[10px] rounded-full px-1.5 leading-4" aria-label={`${t.badge} לא נקראו`}
                style={{ background: C.late, color: "#fff", minWidth: 16, textAlign: "center", fontWeight: 600 }}>
                {t.badge > 99 ? "99+" : t.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
      {/* הצללית יושבת בקצה שאליו הלשוניות גולשות — ב-RTL זה הצד השמאלי.
          insetInlineStart הוא הצד הימני ב-RTL, ולכן הרצועה נחתה על
          "סקירה" וכיסתה את האותיות. left מפורש: האפליקציה כולה RTL. */}
      {more && (
        <div className="absolute pointer-events-none" aria-hidden="true"
          style={{ left: 0, top: 0, bottom: 2, width: 44, background: `linear-gradient(to left, transparent, ${onDark ? "rgba(11,60,52,.9)" : C.surface})` }} />
      )}
    </div>
  );
}

/**
 * המסך שאחרי ההרשמה.
 *
 * החשבון נפתח ריק לגמרי — בלי דירות הדגמה, כי נתוני דמה בתוך מערכת
 * שמנהלת כסף אמיתי הם בדיוק מה שגורם למישהו לשלוח חשבון לדייר מדומיין.
 * במקום זה, צעד אחד ברור: הנכס הראשון.
 */
function Welcome({ name, onGuided, onManual, onSkip, modal }) {
  const steps = [
    { n: 1, t: "מוסיפים נכס", d: "כתובת, שכר דירה ותאריכי חוזה" },
    { n: 2, t: "מכניסים דייר", d: "פרטים, קריאות מונה וחוזה לחתימה מרחוק" },
    { n: 3, t: "פותחים לו אזור אישי", d: "הוא מדווח מונים ורואה מה לתשלום" },
  ];

  return (
    <div dir="rtl" className="min-h-screen w-full flex flex-col" style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
      <div className="max-w-lg w-full mx-auto px-5 py-10 flex-1 flex flex-col">
        <div className="text-xs tracking-widest mb-1" style={{ color: C.ochre }}>ניהול נכסים</div>
        <h1 className="disp text-4xl font-bold leading-tight">נתחיל מהנכס הראשון</h1>
        <p className="text-sm mt-2" style={{ color: C.muted }}>
          העוזר ישאל שאלה אחת בכל פעם, ויציג סיכום לאישור לפני שנשמר משהו.
          לוקח כדקה.
        </p>

        {/* הפעולה לפני ההסבר: בטלפון שלושה כרטיסי הסבר דחפו את הכפתור
            אל מתחת לקפל, והמסך הראשון נראה כמו טקסט בלי מה לעשות בו. */}
        <button onClick={onGuided} className="mt-6 w-full rounded-xl py-4 text-base flex items-center justify-center gap-2 shadow-sm"
          style={{ background: C.teal, color: "#fff", fontWeight: 600 }}>
          <Sparkles size={18} /> בואו נתחיל
        </button>

        <button onClick={onManual} className="mt-2.5 w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2"
          style={{ border: `1px solid ${C.line}`, background: C.card, color: C.ink }}>
          <Plus size={15} /> אמלא את הפרטים בעצמי
        </button>

        <div className="mt-8">
          <div className="text-xs mb-2" style={{ color: C.muted }}>מה מחכה</div>
          <div className="rounded-xl border overflow-hidden" style={{ background: C.card, borderColor: C.line }}>
            {steps.map((s, i) => (
              <div key={s.n} className="flex gap-3 items-start p-3.5"
                style={i ? { borderTop: `1px solid ${C.line}` } : undefined}>
                <div className="shrink-0 rounded-full flex items-center justify-center disp text-[13px] font-bold"
                  style={{ width: 26, height: 26, background: C.surface, color: C.ochre }}>
                  {s.n}
                </div>
                <div className="min-w-0">
                  <div className="text-sm" style={{ fontWeight: 600 }}>{s.t}</div>
                  <div className="text-[12px] mt-0.5" style={{ color: C.muted }}>{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1" />

        <button onClick={onSkip} className="mt-8 w-full text-center text-[13px]" style={{ color: C.muted }}>
          אעשה את זה אחר כך — קחו אותי למערכת
        </button>
        {name && (
          <div className="mt-3 text-center text-[11px]" style={{ color: C.muted }}>
            מחובר כ-{name}
          </div>
        )}
      </div>

      {modal}
    </div>
  );
}


/* ---------------- סקירה ---------------- */
function Overview({ data, setData, stats, year, setYear, run, saveNow, setTab, unseenCount = 0, onOpenProperty = null, onOpenMeters = null, onAddProperty = null, setModal, setIntake, openDocs = () => {} }) {
  // מה שמחכה להחלטה — בקשות הארכה והודעות שלא נקראו — בראש מסך הבית.
  const pendingReqs = pendingExtensionRequests(data);
  const st = data.settings || defTariffs();
  const ownerMissing = ownerGaps(st);
  const missingReadings = useMemo(() => missingEntryReadings(data), [data]);
  /* לחיצה על כרטיס מדד פותחת רשימת נכסים + סכום — רק כשהמתג בהגדרות
     דלוק ("ניווט לחיץ בדשבורד"), וכשיש onOpenProperty לנווט איתו.
     onOpenDetail (ברירת המחדל החדשה) פותח את כרטיס החוב של הדייר במקום
     לנווט; "הוצאות החודש" ממשיך על הניווט הישן דרך openDrilldown הרגילה. */
  const [drilldown, setDrilldown] = useState(null);
  const [ledgerFor, setLedgerFor] = useState(null);   // { aptId, month } — כרטיס חוב מקונן מתוך רשימת הפירוט
  const statsClickable = !!data.settings?.clickableDashboardStats && !!onOpenProperty;
  const openDrilldown = (title, rows) => () => setDrilldown({ title, rows, mode: "navigate" });
  const openDetailDrilldown = (title, rows, month = null) => () => setDrilldown({ title, rows, mode: "detail", month });

  /* בורר חודש לכרטיסי "החודש" בלבד (צפוי/נגבה/ועד/צריכה/סה״כ נגבה) —
     נפרד מהניווט חודש/שנה של "לוחות גבייה" למטה. כרטיסי החוב (מצטברים,
     "נכון להיום") לא מושפעים מהבורר הזה בכלל. */
  const [statsMonth, setStatsMonth] = useState(CUR_KEY);
  const monthStats = useMemo(() => {
    const occ = data.apartments.filter((a) => a.tenant);
    const expected = occ.reduce((s, a) => s + Number(a.rent || 0), 0);
    const collected = occ.reduce((s, a) => s + rentPaidOf(data, a.id, statsMonth), 0);
    const CONS = ["elec", "water", "gas", "sewage"];
    let vaadExpected = 0, vaadPaid = 0, consCharged = 0, consPaid = 0;
    data.apartments.forEach((a) => {
      const b = data.bills[rowKey(a.id, statsMonth)];
      const vaadAmt = Number(b?.vaad?.amount || 0) || (a.tenant && a.vaadActive !== false ? Number(a.vaad || 0) : 0);
      vaadExpected += vaadAmt;
      vaadPaid += itemPaid(b?.vaad);
      CONS.forEach((k) => { consCharged += Number(b?.[k]?.amount || 0); consPaid += itemPaid(b?.[k]); });
    });
    const monthExp = data.expenses.filter((e) => e.month === statsMonth).reduce((s, e) => s + Number(e.amount || 0), 0);
    const collectedAll = collected + vaadPaid + consPaid;
    return { expected, collected, vaadExpected, vaadPaid, consCharged, consPaid, monthExp, collectedAll };
  }, [data, statsMonth]);
  const chart = useMemo(() => {
    const out = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(CUR_Y, CUR_M - 1 - i, 1), y = d.getFullYear(), m = d.getMonth() + 1, mk = mKey(y, m);
      /* "נגבה" סכם שכר דירה בלבד, בזמן שה-KPI שמעליו ("סה״כ נגבה החודש")
         כולל גם ועד בית וצריכה — שני מספרים שונים באותו מסך תחת אותה
         מילה. כאן נספר אותו דבר: תשלומי שכר דירה + מה ששולם על חשבונות. */
      const rent = data.apartments.reduce((t, a) => t + rentPaidOf(data, a.id, mk), 0);
      const bills = data.apartments.reduce((t, a) => {
        const b = data.bills[rowKey(a.id, mk)];
        return t + (b ? BILLS.reduce((x, i) => x + itemPaid(b[i.k]), 0) : 0);
      }, 0);
      out.push({
        name: MONTHS_SHORT[m - 1],
        "נגבה": rent + bills,
        "הוצאות": data.expenses.filter((e) => e.month === mk).reduce((s, e) => s + Number(e.amount || 0), 0),
      });
    }
    return out;
  }, [data]);

  const alerts = data.apartments.flatMap((a) => {
    // נכס פנוי שומר leaseEnd ישן של מי שעזב — אין חוזה להאריך.
    if (!a.tenant || !a.leaseEnd) return [];
    const days = Math.round((new Date(a.leaseEnd) - now) / 86400000);
    return days >= 0 && days <= 75 ? [`החוזה של ${a.tenant || a.name} מסתיים בעוד ${days} ימים — כדאי להתחיל הארכה`] : [];
  });

  /* המספר של החודש — כמה נכנס מול כמה צפוי. תצוגה בלבד: סכימה של
     monthStats (החודש הנבחר בבורר), בשביל כרטיס הגיבור שבראש המסך. */
  const expectedAll = monthStats.expected + monthStats.vaadExpected + monthStats.consCharged;
  const collectedPct = expectedAll > 0 ? Math.min(100, Math.round((monthStats.collectedAll / expectedAll) * 100)) : 0;
  const onCurrentMonth = statsMonth === CUR_KEY;

  return (
    <>
      <div className="em-hero mt-5">
        <div className="flex items-center justify-between gap-2" style={{ opacity: .9 }}>
          <div className="text-xs" style={{ opacity: .8 }}>נגבה ב{monthLabel(statsMonth)}</div>
          {/* בורר חודש לכרטיסי "החודש" בלבד — נפרד מהניווט של "לוחות
              גבייה" למטה. כרטיסי החוב לא מושפעים ולכן אין להם תג חודש. */}
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setStatsMonth((m) => prevMonth(m))} aria-label="חודש קודם"
              className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,.15)" }}>
              <ChevronRight size={14} />
            </button>
            {!onCurrentMonth && (
              <button type="button" onClick={() => setStatsMonth(CUR_KEY)}
                className="text-[11px] rounded-md px-1.5 py-0.5" style={{ background: "rgba(255,255,255,.15)" }}>
                היום
              </button>
            )}
            <button type="button" onClick={() => setStatsMonth((m) => nextMonth(m))} aria-label="חודש הבא"
              disabled={onCurrentMonth} className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ background: "rgba(255,255,255,.15)", opacity: onCurrentMonth ? .4 : 1 }}>
              <ChevronLeft size={14} />
            </button>
          </div>
        </div>
        <div className="em-amount">{nis(monthStats.collectedAll)}</div>
        <div className="text-xs mt-1" style={{ opacity: .82 }}>
          {expectedAll > 0 ? `מתוך ${nis(expectedAll)} צפוי · ` : ""}שכר דירה, ועד בית וצריכה
        </div>
        {expectedAll > 0 && (<>
          <div className="em-bar" style={{ marginTop: 12 }}><i style={{ width: `${collectedPct}%` }} /></div>
          <div className="flex justify-between text-[11px] mt-1.5" style={{ opacity: .8 }}>
            <span>{collectedPct}% נגבו</span>
            {stats.debt > 0 && <span>חוב שכר דירה {nis(stats.debt)}</span>}
          </div>
        </>)}
      </div>

      {ownerMissing.length > 0 && (
        <div className="mt-5 rounded-lg p-3 text-sm flex flex-wrap items-center justify-between gap-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <span className="flex items-center gap-2">
            <AlertTriangle size={15} style={{ color: C.ochre }} />
            תזכורת — חסרים פרטים שנכנסים לחוזה: {ownerMissing.join(", ")}
          </span>
          <button onClick={() => setTab("owner")} className="text-xs rounded-lg px-2.5 py-1.5" style={{ background: C.ochre, color: "#fff" }}>מילוי הפרטים עכשיו</button>
        </div>
      )}

      {missingReadings.length > 0 && (
        <div className="mt-5 rounded-lg p-3 text-sm space-y-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          {missingReadings.map(({ apt, meters }) => (
            <div key={apt.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <AlertTriangle size={15} style={{ color: C.ochre }} />
                תזכורת — קריאת מונה ראשונית חסרה ב{apt.name}: {meters.map((m) => METER_LABEL[m]).join(", ")}
              </span>
              {onOpenMeters && (
                <button onClick={() => onOpenMeters(apt.id)} className="text-xs rounded-lg px-2.5 py-1.5" style={{ background: C.ochre, color: "#fff" }}>מילוי הקריאה</button>
              )}
            </div>
          ))}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mt-5 rounded-lg p-3 text-sm space-y-1" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          {alerts.map((a, i) => <div key={i} className="flex items-center gap-2"><AlertTriangle size={15} style={{ color: C.ochre }} /> {a}</div>)}
        </div>
      )}

      {/* מה מחכה לך — ממסך הבית ישר לטיפול בלשונית "הודעות". */}
      {(pendingReqs.length > 0 || unseenCount > 0) && (
        <button onClick={() => setTab("inbox")}
          className="mt-5 w-full text-right rounded-xl border p-3 text-sm flex flex-wrap items-center justify-between gap-2"
          style={{ background: "#E9F5F0", borderColor: "#C9E5DA", color: C.teal }}>
          <span className="flex items-center gap-2">
            <MessageCircle size={16} />
            {pendingReqs.length > 0 && <span>{pendingReqs.length === 1 ? "בקשת הארכה אחת" : `${pendingReqs.length} בקשות הארכה`} ממתינות להחלטתך</span>}
            {pendingReqs.length > 0 && unseenCount > 0 && <span>·</span>}
            {unseenCount > 0 && <span>{unseenCount === 1 ? "הודעה אחת שלא נקראה" : `${unseenCount} הודעות שלא נקראו`}</span>}
          </span>
          <span className="text-xs rounded-lg px-2.5 py-1" style={{ background: C.teal, color: "#fff" }}>לטיפול</span>
        </button>
      )}
      {drilldown && (
        drilldown.mode === "detail" ? (
          <DashboardDrilldown title={drilldown.title} rows={drilldown.rows}
            onOpenDetail={(aptId) => { setDrilldown(null); setLedgerFor({ aptId, month: drilldown.month }); }}
            onClose={() => setDrilldown(null)} />
        ) : (
          <DashboardDrilldown title={drilldown.title} rows={drilldown.rows} onOpenProperty={onOpenProperty} onClose={() => setDrilldown(null)} />
        )
      )}
      {ledgerFor && (() => {
        const ledgerApt = data.apartments.find((a) => a.id === ledgerFor.aptId);
        return ledgerApt ? (
          <LedgerModal apt={ledgerApt} data={data} run={run} saveNow={saveNow} initialOpenMonth={ledgerFor.month} onClose={() => setLedgerFor(null)} />
        ) : null;
      })()}

      {/* פריסת מדדים: כרטיס-פס מלא ("צפוי החודש"), ואז זוגות נגבה/חוב —
          נגבה בכחול/ירוק בצד ימין, חוב באדום/כתום בצד שמאל (כיוון rtl:
          האיבר הראשון ב-DOM יושב מימין). שורה תחתונה של סה״כ, ואז שני
          כרטיסי-פס מלאים לסיום: הוצאות וחובות אבודים.
          כרטיסי "החודש" (צפוי/נגבה/ועד/צריכה/סה״כ נגבה) מוצגים לפי
          statsMonth הנבחר בבורר שבכרטיס הגיבור; כרטיסי החוב (מצטברים,
          "נכון להיום") לא תלויים בבחירה ונשארים על stats הרגיל. לחיצה
          על כרטיס פותחת את כרטיס החוב של הדייר (onOpenDetail) — חוץ
          מ"הוצאות החודש", שממשיך לנווט לכרטיס הנכס. */}
      <div className="mt-6 space-y-3">
        <Stat label="צפוי החודש" value={nis(monthStats.expected)}
          sub={`${stats.occ} ${stats.occ === 1 ? "יחידה מושכרת" : "יחידות מושכרות"}${stats.signed ? ` · עוד ${stats.signed} מחוזה לכניסה` : ""}`}
          color={STAT_TINTS.blue.text} bg={STAT_TINTS.blue.bg} border={STAT_TINTS.blue.line} icon={<Home size={16} />}
          onClick={statsClickable ? openDetailDrilldown("צפוי החודש", rentExpectedRows(data, statsMonth), statsMonth) : null} />

        <div className="grid grid-cols-2 gap-3">
          <Stat label="נגבה החודש" value={nis(monthStats.collected)}
            sub={monthStats.expected ? `מתוך ${nis(monthStats.expected)} · ${Math.round((monthStats.collected / monthStats.expected) * 100)}%` : "אין שכירות צפויה"}
            color={STAT_TINTS.green.text} bg={STAT_TINTS.green.bg} border={STAT_TINTS.green.line} icon={<Wallet size={16} />}
            onClick={statsClickable ? openDetailDrilldown("נגבה החודש", rentCollectedRows(data, statsMonth), statsMonth) : null} />
          <Stat label="חוב שכר דירה" value={nis(stats.debt)}
            sub={stats.debt ? "חודשים שלא נגבו מאז תחילת החוזה" : "אין חוב פתוח"}
            color={STAT_TINTS.red.text} bg={STAT_TINTS.red.bg} border={STAT_TINTS.red.line} icon={<AlertTriangle size={16} />}
            onClick={statsClickable ? openDetailDrilldown("חוב שכר דירה", rentDebtRows(data)) : null} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat label="ועד בית שנגבה החודש" value={nis(monthStats.vaadPaid)}
            sub={monthStats.vaadExpected ? `מתוך ${nis(monthStats.vaadExpected)}` : "אין ועד בית שנגבה דרכך"}
            color={STAT_TINTS.blue.text} bg={STAT_TINTS.blue.bg} border={STAT_TINTS.blue.line} icon={<Receipt size={16} />}
            onClick={statsClickable ? openDetailDrilldown("ועד בית שנגבה החודש", vaadCollectedRows(data, statsMonth), statsMonth) : null} />
          <Stat label="חוב ועד בית" value={nis(stats.vaadDebt)}
            sub={stats.vaadDebt ? "כולל חודשים קודמים שלא נגבו" : "אין חוב ועד בית פתוח"}
            color={STAT_TINTS.orange.text} bg={STAT_TINTS.orange.bg} border={STAT_TINTS.orange.line} icon={<AlertTriangle size={16} />}
            onClick={statsClickable ? openDetailDrilldown("חוב ועד בית", vaadDebtRows(data)) : null} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat label="חשבונות צריכה ששולמו החודש" value={nis(monthStats.consPaid)}
            sub={monthStats.consCharged ? `מתוך ${nis(monthStats.consCharged)} · חשמל, מים, גז` : "טרם חושבו חשבונות לחודש"}
            color={STAT_TINTS.blue.text} bg={STAT_TINTS.blue.bg} border={STAT_TINTS.blue.line} icon={<Gauge size={16} />}
            onClick={statsClickable ? openDetailDrilldown("חשבונות צריכה ששולמו החודש", consPaidRows(data, statsMonth), statsMonth) : null} />
          <Stat label="חוב חשבונות צריכה" value={nis(stats.consDebt)}
            sub={stats.consDebt ? "חשמל, מים וגז — כולל חודשים קודמים" : "אין חוב צריכה פתוח"}
            color={STAT_TINTS.orange.text} bg={STAT_TINTS.orange.bg} border={STAT_TINTS.orange.line} icon={<AlertTriangle size={16} />}
            onClick={statsClickable ? openDetailDrilldown("חוב חשבונות צריכה", consDebtRows(data)) : null} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stat label="סה״כ נגבה החודש" value={nis(monthStats.collectedAll)} sub="שכר דירה, ועד בית וצריכה"
            color={STAT_TINTS.blue.text} bg={STAT_TINTS.blue.bg} border={STAT_TINTS.blue.line} icon={<Wallet size={16} />}
            onClick={statsClickable ? openDetailDrilldown("סה״כ נגבה החודש", totalCollectedRows(data, statsMonth), statsMonth) : null} />
          <Stat label="סה״כ לגביה החודש" value={nis(stats.totalDebtAll)} sub="שכר דירה, ועד בית וצריכה"
            color={STAT_TINTS.red.text} bg={STAT_TINTS.red.bg} border={STAT_TINTS.red.line} icon={<AlertTriangle size={16} />}
            onClick={statsClickable ? openDetailDrilldown("סה״כ לגביה החודש", totalDebtRows(data)) : null} />
        </div>

        <Stat label="הוצאות החודש" value={nis(monthStats.monthExp)} sub="מה שרשמת בלשונית הוצאות"
          color={STAT_TINTS.orange.text} bg={STAT_TINTS.orange.bg} border={STAT_TINTS.orange.line} icon={<Receipt size={16} />}
          onClick={statsClickable ? openDrilldown("הוצאות החודש", expensesRows(data, statsMonth)) : null} />

        {/* חובות שסומנו במפורש כ"חוב אבוד" בלשונית "דיירים וחובות" —
            תמיד גלוי, לא רק כשיש חוב, כדי שהמעקב יהיה שוטף. לחיץ תמיד
            (לא תלוי במתג) — זו התנהגות קיימת, לא חלק מהתכונה הזו. */}
        <Stat label="חובות אבודים" value={nis(stats.lostDebt)}
          sub={stats.lostDebt ? "דיירים לשעבר שסומנו — לצפייה ולניהול" : "אין חובות שסומנו כאבודים"}
          color={STAT_TINTS.red.text} bg={STAT_TINTS.red.bg} border={STAT_TINTS.red.line} icon={<Trash2 size={16} />}
          onClick={() => setTab("tenants")} />
      </div>

      <CollectionBoards data={data} setData={setData} year={year} setYear={setYear} run={run} onOpenProperty={onOpenProperty} onOpenMeters={onOpenMeters} />

      {/* גריד הנכסים — היה לשונית "נכסים" נפרדת, ומ-Task 8 חי כאן ישירות.
          עריכה/תפריט על כרטיס פותחים את הכרטיס המאוחד (onOpenProperty);
          "הוספת נכס" נשארת על AptModal ריק דרך מנגנון ה-modal הכללי, כי
          לנכס חדש עוד אין מזהה לפתוח לו כרטיס מאוחד. */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="disp text-xl font-bold">הנכסים</h2>
          {onAddProperty && (
            <button onClick={onAddProperty} className="flex items-center gap-1.5 text-sm rounded-lg px-3 py-1.5" style={{ background: C.teal, color: "#fff" }}>
              <Plus size={15} /> הוספת נכס
            </button>
          )}
        </div>
        <PropertyGrid data={data} run={run} onOpenProperty={onOpenProperty} setModal={setModal} setIntake={setIntake} openDocs={openDocs} />
      </div>

      <section className="mt-6 rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-3">חצי שנה אחרונה</h2>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.line} vertical={false} />
              <XAxis dataKey="name" reversed tick={{ fontSize: 12, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis orientation="right" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => v / 1000 + "K"} />
              <Tooltip formatter={(v) => nis(v)} contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12, direction: "rtl" }} />
              <Bar dataKey="נגבה" fill={C.teal} radius={[3, 3, 0, 0]} />
              <Bar dataKey="הוצאות" fill={C.ochre} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </>
  );
}

/* ---------------- נכסים ---------------- */
/* גריד הנכסים של הדשבורד — חולץ מתוך הלשונית "נכסים" הישנה שנמחקה
   ב-Task 8, כדי שיוצג ישירות בתוך Overview בלי לעבור ללשונית נפרדת.
   עריכה ותפריט "עוד ⋯" שפעם פתחו מודל מקומי (AptModal/TenanciesModal/
   LedgerModal/AccessModal) קוראים עכשיו ל-onOpenProperty(a.id), שפותח
   את הכרטיס המאוחד (Task 4) על סעיף "פרטים" — משם אפשר לנווט לכל
   הסעיפים דרך שורת הסעיפים המשותפת שלו. "חוזים ותיעוד", "קליטת דייר/
   החלפה" ו"תצוגת הדייר" נשארו פעולות ישירות — הן לא סעיפים בכרטיס. */
function PropertyGrid({ data, run, onOpenProperty, setModal, setIntake, openDocs = () => {}, focusId = null }) {
  const [justFocused, setJustFocused] = useState(null);

  /* ניווט לחיץ מהדשבורד (לוחות הגבייה): גלילה לכרטיס הנכס עם הבהוב קצר
     כדי שיהיה ברור לאיזה כרטיס הגיעו מתוך רשימה ארוכה. */
  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`apt-card-${focusId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setJustFocused(focusId);
    const t = setTimeout(() => setJustFocused(null), 1800);
    return () => clearTimeout(t);
  }, [focusId]);
  /* "תצוגת הדייר" הייתה הדמיה מקומית בלי סשן דייר: דיווחי מונים, בקשות
     והודעות משם לא עברו דרך השרת — לא יצרו התראות ולא אפשרו שליחה, וזה
     נראה כמו באג בכל בדיקה. עכשיו היא כניסה אמיתית לאזור של הדייר
     (סשן מסומן כהתחזות, כל פעולה נרשמת על שמך) — בדיוק מה שהדייר רואה. */
  const enterTenant = async (id) => {
    try {
      await dataApi.enterPortal(id);
      window.location.reload();
    } catch (e) {
      alert(e.message || "הכניסה לאזור של הדייר נכשלה");
    }
  };
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {data.apartments.map((a) => {
          /* שכר הדירה של החודש: שולם במלואו / חלקית / לא. הכפתור היה ירוק
             "שכ״ד התקבל" גם על 2,000 מתוך 5,200 — כי נבדקה רק קיומה של
             רשומת תשלום. */
          /* חודש שההחלפה קרתה בו שייך לדייר היוצא — כפתורי "סימון תשלום"
             ו"חשבונות" של החודש לא שייכים לדייר החדש עד החודש הבא. */
          const myMonthP = tenancyForMonth(data, a.id, CUR_KEY)?.id === currentTenancy(data, a.id)?.id;
          const rentPaidNow = myMonthP ? rentPaidOf(data, a.id, CUR_KEY) : 0;
          const rentOwedNow = myMonthP ? Math.max(0, Number(a.rent || 0) - rentPaidNow) : 0;
          const paidNow = !myMonthP || (Number(a.rent || 0) > 0 ? (rentPaidNow > 0 && rentOwedNow === 0) : !!data.payments[rowKey(a.id, CUR_KEY)]);
          const partialNow = rentPaidNow > 0 && rentOwedNow > 0;
          const bill = data.bills[rowKey(a.id, CUR_KEY)] || emptyBill();

          // כרטיס החוב המלא — שכר דירה וחשבונות, על פני כל החודשים של
          // התקופה הנוכחית בלבד.
          const debt = debtStatement(data, a, CUR_KEY);
          /* החשבונות הפתוחים של התקופה הנוכחית בלבד — billsOutstanding
             ברמת הנכס גרר לכאן חובות של דיירים קודמים אחרי החלפה, והם
             הופיעו על הכפתור של הדייר החדש. חוב העבר מוצג בנפרד למטה. */
          const billsOwed = debt.billsDue;
          const owedMonths = debt.months.filter((x) => x.bills.due > 0).map((x) => x.month);
          const upcoming = upcomingTenancies(data, a.id);
          const billsCharged = Object.entries(data.bills || {})
            .filter(([k]) => String(k).split("|")[0] === a.id)
            .reduce((t, [, b]) => t + BILLS.reduce((x, i) => x + Number(b[i.k]?.amount || 0), 0), 0);
          return (
            <div key={a.id} id={`apt-card-${a.id}`} className="rounded-xl border p-4 flex flex-col"
              style={{
                background: C.card, borderColor: C.line,
                boxShadow: justFocused === a.id ? `0 0 0 3px ${C.teal}` : undefined,
                transition: "box-shadow .3s ease",
              }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="disp text-base font-bold truncate">{a.name || "ללא שם"}</div>
                  {/* "3" לבד לא אומר כלום — "3 חדרים"; ולנכס מסחרי בלי חדרים — סוג הנכס. */}
                  <div className="text-xs" style={{ color: C.muted }}>
                    {a.rooms ? (propertyType(a).residential ? `${a.rooms} חדרים` : `${a.rooms} מ״ר`) : propertyType(a).label}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => onOpenProperty(a.id)} aria-label="עריכה" style={{ color: C.muted }}><Pencil size={15} /></button>
                  <button onClick={() => setModal({ kind: "confirm", title: `להסיר את ${a.name}?`, body: "כל התשלומים, החשבונות, החוזים והתמונות של הנכס יימחקו.", action: () => run([{ type: "remove_apartment", apartment: a.id }]) })}
                    aria-label="הסרה" style={{ color: C.late }}><Trash2 size={15} /></button>
                </div>
              </div>
              {/* שכר הדירה שרלוונטי לכרטיס: של הדייר הנוכחי; בנכס עם דייר
                  עתידי — שלו (קודם הוצג ₪0); בנכס פנוי — האחרון, מסומן ככזה. */}
              {(() => {
                const nextTen = !a.tenant ? upcoming[0] : null;
                const lastTen = !a.tenant && !nextTen ? pastTenancies(data, a.id).slice(-1)[0] : null;
                const shownRent = a.tenant ? a.rent : nextTen ? nextTen.rent : lastTen ? lastTen.rent : 0;
                const rentNote = a.tenant ? " לחודש" : nextTen ? ` לחודש מ-${heDate(nextTen.leaseStart)}` : lastTen ? " לחודש (הדייר הקודם)" : " לחודש";
                return shownRent > 0 || a.tenant
                  ? <div className="mt-3 disp text-2xl font-bold">{nis(shownRent)}<span className="text-xs font-normal" style={{ color: C.muted }}>{rentNote}</span></div>
                  : <div className="mt-3 disp text-2xl font-bold" style={{ color: C.muted }}>—<span className="text-xs font-normal"> שכר דירה ייקבע מול הדייר</span></div>;
              })()}

              {/* כרטיס החוב. חודש שלא שולם נשאר כאן גם אחרי שהגיע החודש
                  הבא — זה בדיוק המקום שבו בעל הנכס רואה שהחוב נגרר, ולא
                  רק את החשבון האחרון. */}
              {a.tenant && (debt.total > 0 ? (
                <div className="mt-2 rounded-lg p-2" style={{ background: "#FBF2E3" }}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs flex items-center gap-1.5" style={{ color: "#6B4E12" }}>
                      <AlertTriangle size={12} />
                      {debt.arrears.length ? "חוב נגרר" : "לתשלום"}
                    </span>
                    <b className="text-sm" style={{ color: "#6B4E12" }}>{nis(debt.total)}</b>
                  </div>

                  <div className="mt-1 space-y-0.5">
                    {debt.months.filter((x) => x.due > 0).slice(-4).map((x) => (
                      <button key={x.month} onClick={() => onOpenProperty(a.id)}
                        className="w-full flex items-center justify-between text-[11px]"
                        style={{ color: x.month === CUR_KEY ? "#6B4E12" : C.late }}>
                        <span>
                          {monthLabel(x.month)}
                          {x.month === CUR_KEY ? " (החודש)" : ""}
                          {x.rent.due > 0 && x.bills.due > 0 ? " · שכ״ד וחשבונות"
                            : x.rent.due > 0 ? " · שכר דירה" : " · חשבונות"}
                        </span>
                        <span style={{ fontWeight: 600 }}>{nis(x.due)}</span>
                      </button>
                    ))}
                    {debt.months.filter((x) => x.due > 0).length > 4 && (
                      <button onClick={() => onOpenProperty(a.id)} className="text-[11px]" style={{ color: C.muted }}>
                        ועוד {debt.months.filter((x) => x.due > 0).length - 4} חודשים…
                      </button>
                    )}
                  </div>
                </div>
              ) : billsCharged > 0 && (
                <div className="mt-1 text-sm flex items-center gap-1.5" style={{ color: C.paid }}>
                  <CheckCircle2 size={13} /> הכול שולם
                </div>
              ))}

              {/* נכס פנוי שמי שעזב ממנו נשאר חייב: החוב לא נעלם כשהדירה
                  התפנתה — ה-KPI והדוח כבר ספרו אותו, רק הכרטיס שתק. */}
              {!a.tenant && pastDebts(data, a).length > 0 && (
                <button onClick={() => onOpenProperty(a.id)} className="mt-2 w-full rounded-lg p-2 text-right" style={{ background: "#FBF2E3" }}>
                  {pastDebts(data, a).map(({ tenancy, statement }) => (
                    <div key={tenancy.id} className="flex items-center justify-between gap-2 text-[11px]" style={{ color: "#6B4E12" }}>
                      <span className="flex items-center gap-1.5 min-w-0"><AlertTriangle size={12} className="shrink-0" /> <span className="truncate">חוב מדייר קודם · {tenancy.tenant}</span></span>
                      <b className="text-sm shrink-0">{nis(statement.total)}</b>
                    </div>
                  ))}
                </button>
              )}
              <div className="mt-3 text-sm space-y-1 flex-1">
                {a.tenant ? (<>
                  <div>{a.tenant}</div>
                  {a.phone && <div className="flex items-center gap-1.5 text-xs" style={{ color: C.muted }}><Phone size={12} /> {a.phone}</div>}
                  {a.leaseEnd && (
                    /* חוזה שכבר עבר את תאריך הסיום שלו ואין דייר הבא רשום —
                       המערכת ממשיכה לצפות ממנו לשכ״ד (ולהחשיב אותו "פעיל") בכוונה,
                       כי אין עדיין דרך לדעת אם הוא עזב או נשאר; אבל בלי סימון כלשהו
                       בעל הנכס עלול לא לשים לב חודשים שהחוזה "רץ" בלי חידוש. */
                    a.leaseEnd < TODAY && !upcoming.length ? (
                      <div className="text-xs flex items-center gap-1" style={{ color: C.late }}>
                        <AlertTriangle size={12} /> החוזה הסתיים ב-{heDate(a.leaseEnd)} — אין דייר הבא רשום
                      </div>
                    ) : (
                      <div className="text-xs" style={{ color: C.muted }}>חוזה עד {heDate(a.leaseEnd)}</div>
                    )
                  )}
                  <div className="text-xs" style={{ color: C.muted }}>
                    שכ״ד עד ה-{rentDayOf(a, data.settings)} · חשבונות עד ה-{billsDayOf(a, data.settings)}
                    {billsCycleOf(a, data.settings) === 2 ? " (דו-חודשי)" : ""}
                  </div>
                  {rentOverdue(a, data.settings, data.payments) && (
                    <div className="text-xs flex items-center gap-1" style={{ color: C.late }}>
                      <AlertTriangle size={12} /> שכר הדירה באיחור
                    </div>
                  )}
                </>) : upcoming.length ? (
                  /* נסגר דייר לעתיד: הנכס פנוי עד הכניסה אבל לא להשכרה.
                     "פנויה — להשכרה" כאן נראה כאילו החוזה לא נסגר. */
                  <>
                    <div className="text-sm" style={{ color: C.paid }}>הושכרה — {upcoming[0].tenant}</div>
                    <div className="text-xs" style={{ color: C.muted }}>
                      כניסה ב-{heDate(upcoming[0].leaseStart)}{upcoming[0].leaseEnd ? ` · חוזה עד ${heDate(upcoming[0].leaseEnd)}` : ""}
                    </div>
                    {upcoming[0].phone && <div className="flex items-center gap-1.5 text-xs" style={{ color: C.muted }}><Phone size={12} /> {upcoming[0].phone}</div>}
                  </>
                ) : <div className="text-sm" style={{ color: C.ochre }}>פנויה — {a.notes || "להשכרה"}</div>}
                {a.history?.length > 0 && <div className="text-[11px] pt-1" style={{ color: C.muted }}>דיירים קודמים: {a.history.map((h) => h.tenant).join(", ")}</div>}
                {missingFor(a, data).length > 0 && (
                  <div className="text-[11px] pt-1" style={{ color: C.ochre }}>עדיין חסר: {missingFor(a, data).map((x) => x.label).join(", ")}</div>
                )}
                {/* כשיש דייר נוכחי, הבא אחריו מוצג כתג. כשאין — הוא כבר שורת המצב למעלה. */}
                {a.tenant && upcoming.map((t) => (
                  <div key={t.id} className="text-[11px] mt-1 rounded px-2 py-1 flex items-center gap-1" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
                    <CalendarPlus size={11} /> {t.tenant} נכנס/ת ב-{heDate(t.leaseStart)}
                  </div>
                ))}
                {/* בקשת הארכה שהדייר שלח מהאזור האישי — מחכה להחלטה. לחיצה
                    פותחת את חלון ההארכה עם התאריך שביקש, ושם גם הדחייה. */}
                {a.tenant && pendingExtensionRequest(data, a.id) && (
                  <button onClick={() => setModal({ kind: "extend", apt: a, request: pendingExtensionRequest(data, a.id) })}
                    className="text-[11px] mt-1 rounded px-2 py-1 flex items-center gap-1 w-full text-right"
                    style={{ background: "#FBF2E3", color: "#6B4E12", border: "1px solid #E8C98A" }}>
                    <CalendarPlus size={11} /> בקשת הארכה עד {heDate(pendingExtensionRequest(data, a.id).to)} — לאישור או דחייה
                  </button>
                )}
              </div>
              {/* הכפתורים לפי מה שעושים עם הנכס הזה לרוב: נכס מושכר — הפעולות
                  החודשיות (תשלום, חשבונות, וואטסאפ) בחזית, והשאר ב"עוד";
                  נכס פנוי — פעולה אחת: קליטת דייר. */}
              <div className="mt-4 flex flex-wrap gap-2">
                {a.tenant ? (<>
                  {/* סימון — מיד. ביטול — באישור: לחיצה שנייה בטעות (קל
                      במיוחד בטלפון) מחקה את התאריך ואת אמצעי התשלום, ואין
                      מאיפה לשחזר אותם. */}
                  <button
                    onClick={() => {
                      if (!paidNow && !partialNow) { run([{ type: "set_rent_paid", apartment: a.id, paid: true }]); return; }
                      if (partialNow) {
                        // תשלום חלקי: לחיצה משלימה את היתרה (אחרי אישור), לא מבטלת.
                        if (window.confirm(`לסמן את יתרת שכר הדירה (${nis(rentOwedNow)}) כהתקבלה?\n${a.tenant} · עד עכשיו התקבלו ${nis(rentPaidNow)} מתוך ${nis(a.rent)}.`)) {
                          run([{ type: "set_rent_paid", apartment: a.id, paid: true, amount: Number(a.rent || 0) }]);
                        }
                        return;
                      }
                      setModal({
                        kind: "confirm",
                        title: `לבטל את סימון התשלום של ${monthLabel(CUR_KEY)}?`,
                        body: `${a.tenant} · ${nis(rentPaidOf(data, a.id, CUR_KEY))}. התאריך ואמצעי התשלום שנרשמו יימחקו.`,
                        action: () => run([{ type: "set_rent_paid", apartment: a.id, paid: false }]),
                      });
                    }}
                    className="flex-1 rounded-lg py-2 text-xs"
                    style={{
                      background: paidNow ? "#EAF3EE" : partialNow ? C.ochre : C.teal,
                      color: paidNow ? C.paid : "#fff",
                      border: `1px solid ${paidNow ? "#CBE2D6" : partialNow ? C.ochre : C.teal}`,
                    }}>
                    {paidNow ? "שכ״ד התקבל" : partialNow ? `שכ״ד חלקי · נותרו ${nis(rentOwedNow)}` : "סימון תשלום"}
                  </button>

                  {/* מקבילה של כפתור שכר הדירה. לחיצה סוגרת את כל החשבונות
                      הפתוחים — גם חודשים קודמים — ולא רק את הנוכחי. */}
                  {billsCharged > 0 && (
                    <button
                      onClick={() => run([{ type: "set_bills_paid", apartment: a.id, paid: billsOwed > 0 }])}
                      className="flex-1 rounded-lg py-2 text-xs"
                      title={billsOwed > 0
                        ? `לסמן ${nis(billsOwed)} כהתקבלו${owedMonths.length > 1 ? ` (${owedMonths.map(monthLabel).join(", ")})` : ""}`
                        : "לבטל את סימון קבלת החשבונות"}
                      style={billsOwed > 0
                        ? { background: C.ochre, color: "#fff", border: `1px solid ${C.ochre}` }
                        : { background: "#EAF3EE", color: C.paid, border: "1px solid #CBE2D6" }}>
                      {billsOwed > 0 ? `חשבונות ${nis(billsOwed)}` : "חשבונות התקבלו"}
                    </button>
                  )}
                  {/* statementText זהה ל-billsText כשאין חוב קודם, ומוסיף את
                      החודשים הפתוחים ואת הסכום המצטבר כשיש. */}
                  {a.phone && (
                    <a href={waLink(a.phone, statementText(a, data, CUR_KEY, data.settings))} target="_blank" rel="noreferrer"
                      className="rounded-lg px-2.5 py-2 text-xs flex items-center gap-1"
                      style={{ border: `1px solid ${C.line}`, color: "#128C7E" }}>
                      <MessageCircle size={14} /> {debt.arrears.length ? "שליחת חוב מצטבר" : "וואטסאפ"}
                    </a>
                  )}
                  <MoreMenu items={[
                    { label: "חוזים ותיעוד", icon: FileText, onClick: () => openDocs(a.id) },
                    { label: `דיירים${tenanciesOf(data, a.id).filter((t) => t.tenant).length > 1 ? ` (${tenanciesOf(data, a.id).filter((t) => t.tenant).length})` : ""}`, icon: Pencil, onClick: () => onOpenProperty(a.id) },
                    { label: "דייר נוסף / החלפת דייר", icon: UserPlus, onClick: () => setIntake(a.id) },
                    { label: "כרטיס חוב", icon: Receipt, onClick: () => onOpenProperty(a.id) },
                    { label: "תצוגת הדייר", icon: Eye, onClick: () => enterTenant(a.id) },
                    { label: "גישה לדייר", icon: User, onClick: () => onOpenProperty(a.id), tone: C.teal },
                  ]} />
                </>) : upcoming.length ? (<>
                  {/* דייר עתידי כבר רשום: הפעולה הראשית היא לראות/לערוך אותו.
                      "קליטת דייר" כאן הייתה מזהירה "כבר רשום דייר" — מבלבל. */}
                  <button onClick={() => onOpenProperty(a.id)} className="flex-1 rounded-lg py-2.5 text-sm flex items-center justify-center gap-1.5"
                    style={{ border: `1px solid ${C.teal}`, color: C.teal }}>
                    <CalendarPlus size={15} /> {upcoming[0].tenant} נכנס/ת ב-{heDate(upcoming[0].leaseStart)} · פרטים
                  </button>
                  <MoreMenu items={[
                    { label: "חוזים ותיעוד", icon: FileText, onClick: () => openDocs(a.id) },
                    { label: "קליטת דייר אחר (החלפה)", icon: UserPlus, onClick: () => setIntake(a.id) },
                    { label: "כרטיס חוב", icon: Receipt, onClick: () => onOpenProperty(a.id) },
                  ]} />
                </>) : (<>
                  <button onClick={() => setIntake(a.id)} className="flex-1 rounded-lg py-2.5 text-sm flex items-center justify-center gap-1.5"
                    style={{ background: C.teal, color: "#fff" }}>
                    <UserPlus size={15} /> קליטת דייר
                  </button>
                  <MoreMenu items={[
                    { label: "חוזים ותיעוד", icon: FileText, onClick: () => openDocs(a.id) },
                    { label: "דיירים ותקופות", icon: Pencil, onClick: () => onOpenProperty(a.id) },
                    { label: "כרטיס חוב", icon: Receipt, onClick: () => onOpenProperty(a.id) },
                  ]} />
                </>)}
              </div>
            </div>
          );
        })}
      </div>
  );
}

/** תפריט "עוד" לפעולות משניות בכרטיס הנכס. */
function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  // נפתח כלפי מעלה כשאין מקום למטה — פס העוזר יושב בתחתית המסך (z-40)
  // והתפריט נבלע מתחתיו; גם ה-z-index של התפריט מעל הפס.
  const [up, setUp] = useState(false);
  const ref = useRef(null);
  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setUp(window.innerHeight - r.bottom < 60 + items.length * 36);
    }
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} aria-expanded={open} aria-haspopup="menu"
        className="rounded-lg px-2.5 py-2 text-xs flex items-center gap-1"
        style={{ border: `1px solid ${open ? C.teal : C.line}`, color: C.muted }}>
        עוד ⋯
      </button>
      {open && (
        <div role="menu" className="absolute z-50 rounded-lg border py-1 shadow-lg"
          style={{ background: C.card, borderColor: C.line, insetInlineEnd: 0, ...(up ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }), minWidth: 170 }}>
          {items.map((it) => (
            <button key={it.label} role="menuitem" onClick={() => { setOpen(false); it.onClick(); }}
              className="w-full text-right px-3 py-2 text-xs flex items-center gap-2"
              style={{ color: it.tone || C.ink }}>
              <it.icon size={13} style={{ color: it.tone || C.muted }} /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- מונים וחשבונות ---------------- */
/* קריאות מונה, יחידות והפרשי תאריכים, וכן recordMeterReading/
   updateMeterReading/deleteMeterReading — כולן ב-@rental/shared (עברו
   משם ל-1.9.2026 כדי ש-CollectionBoards.jsx ישתמש באותה לוגיקה בלי
   import מעגלי מ-App.jsx). */

/* חיוב לפי הצריכה של חודש נתון — מהקריאה האחרונה שלפני תקופת החיוב ועד
   האחרונה בתוכה. משותפת בין "מונים וחשבונות" (החודש המוצג) ל"היסטוריית
   תשלומים וחובות" (החודש האחרון של דייר שעזב/הוחלף). */
function computeConsumptionBills(data, run, a, s, month) {
  const b = data.bills[rowKey(a.id, month)] || emptyBill();
  const acts = [];
  ["elec", "water", "gas"].forEach((meter) => {
    if (!billsMeter(a, s, meter)) return;   // משולם ישירות לספק
    const cons = consumptionForMonth(data, a, s, meter, month);
    if (!cons || cons.diff <= 0) return;
    const billKey = meter === "elec" ? "elec" : meter === "gas" ? "gas" : "water";
    acts.push({
      type: "set_bill", apartment: a.id, month, bill: billKey,
      amount: Math.round(cons.diff * rateFor(a, s, meter, cons.to.date)),
    });
  });
  /* סעיפים קבועים — ועד בית, ארנונה, היטל — חודשיים תמיד, גם כשהצריכה
     דו-חודשית: הוועד גובה כל חודש וכך גם כתוב בחוזה. */
  FIXED_BILLS.forEach(({ k, amountField, activeField }) => {
    if (a[activeField] === false) return;
    const amount = Number(a[amountField] || 0);
    if (amount > 0) acts.push({ type: "set_bill", apartment: a.id, month, bill: k, amount });
  });
  if (s.gasFixed && !acts.some((x) => x.bill === "gas")) acts.push({ type: "set_bill", apartment: a.id, month, bill: "gas", amount: s.gasFixed });
  if (!acts.length) return false;
  // חשבון ששולם משתנה רק באישור — הסכום החדש לא משנה את מה ששולם.
  const touchesPaid = acts.some((x) => itemPaid(b[x.bill]) > 0 && Number(b[x.bill].amount || 0) !== x.amount);
  if (touchesPaid && !confirm(`חלק מהחשבונות של ${monthLabel(month)} כבר שולמו. לעדכן את הסכומים? מה ששולם יישאר, וההפרש יופיע כיתרה.`)) return false;
  run(acts);
  return true;
}

/* כרטיס המונים/חשבונות של נכס אחד לחודש נתון — במקור חולץ מתוך ה-.map()
   של לשונית "מונים וחשבונות" הישנה (נמחקה כליל ב-Task 8), כדי שאפשר יהיה
   גם לפתוח אותו לבד במודל ליחיד (מהדשבורד / מכפתור "מונים" בכרטיס הנכס).
   justFocused היה ה-state של הבהוב הגלילה שאותה לשונית ניהלה (תלוי
   ב-focusId שהגיע מבחוץ); כאן הוא רק נקרא, לא משתנה, ומאז שהלשונית
   נמחקה קריאת היחיד היחידה (למטה) תמיד מעבירה null — הפרופ נשאר כדי
   לא לשנות את החתימה בלי צורך. */
function MeterApartmentCard({ a, t, idx, data, setData, run, month, s, justFocused }) {
  const bill = (id) => data.bills[rowKey(id, month)] || emptyBill();

  const setBillField = (aptId, bk, patch) => setData((d) => {
    const k = rowKey(aptId, month), b = { ...(d.bills[k] || emptyBill()) };
    let it = { ...b[bk], ...patch };
    /* שדות התשלום במסך הזה לא עוברים דרך set_bill, ולכן נקודת "שולם עד"
       (הקריאה האחרונה ברגע התשלום — קובעת אילו קריאות נעולות) נקבעת
       כאן באותו כלל בדיוק. */
    if ("paid" in patch || "paidAmount" in patch) {
      it = withPaidThrough(d, d.apartments.find((x) => x.id === aptId), month, bk, it);
    }
    b[bk] = it;
    return { ...d, bills: { ...d.bills, [k]: b } };
  });

  // עטיפות דקות סביב הפונקציות המשותפות (מעל הקומפוננטה) — אותה התנהגות בדיוק.
  const addReading = (aptId, meter, value, date, photoId = null) => recordMeterReading(setData, aptId, meter, value, date, photoId);
  const editReading = (id, patch) => updateMeterReading(setData, id, patch);
  const delReading = (id) => deleteMeterReading(setData, id);
  const autoPrice = (a) => computeConsumptionBills(data, run, a, s, month);

  /* אילו סעיפים להציג לנכס. סעיף שלא נגבה דרך בעל הנכס — ארנונה שהדייר
     משלם לרשות, חשמל ישירות מול חברת החשמל, ועד שכובה — רק מבלבל כשהוא
     ריק. אבל סעיף שכבר נרשם עליו סכום בחודש הזה תמיד מוצג: נתון קיים לא
     נעלם רק כי ההגדרה השתנתה אחר כך. */
  const hasData = (item) => Number(item?.amount || 0) > 0 || itemPaid(item) > 0;
  const showBill = (a, b, k) => {
    if (hasData(b[k])) return true;
    if (k === "vaad") return a.vaadActive !== false;
    if (k === "arnona" || k === "municipal") return fixedBillOn(a, k);
    if (k === "sewage") return false;   // הביוב הוא חלק מ"מים וביוב"; מוצג רק עם נתון ישן
    return billsUtility(a, s, k);
  };
  /* מוצגים רק מונים שמשולמים דרך בעל הנכס ("משולם לי, לפי מונה") — מונה
     שמוגדר "ישירות לספק" או "ללא חיוב" לא מופיע כאן ולא אצל הדייר: אין בו
     מה לגבות. נכס בלי הגדרה מפורשת נשאר כמו שהיה — ברירת המחדל בנכס
     ממונה היא "משולם לי". */
  /* גז: גם כשהוא דרך בעל הנכס, רק כשהוא רלוונטי — הוגדר במפורש, יש תעריף
     או שכבר נרשמה קריאה. ברוב הדירות הגז מול חברת הגז, ועמודה ריקה של
     "מונה גז" לכל דייר היא רעש (אותו כלל כמו באזור הדייר). */
  const showMeter = (a, meter) => {
    if (utilityMode(a, meter) !== "landlord") return false;
    return meter !== "gas"
      || a?.utilities?.gas === "landlord"
      || rateGas(a, s) > 0
      || readingsOf(data, a.id, "gas").length > 0;
  };
  const [mY, mM] = month.split("-").map(Number);

  /* חודש שהוחלף בו דייר שייך ליוצא — הפרדה מוחלטת: מסך זה לא
     מציג עליו שום דבר (שם, סכום, תג), לא רק בכותרת. הדייר
     הנוכחי נראה כאילו נכנס לנכס ריק; חשבונות החודש של היוצא
     (כולל מה שכבר חושב) מנוהלים אך ורק ב"דיירים וחובות" —
     כרטיס חוב. לכן b (החשבון החודשי, משותף לחודש ולא לדייר)
     מוחלף בריק בחודש חפיפה, לפני שנגזר ממנו כל השאר. */
  const curT = currentTenancy(data, a.id);
  const handover = month === CUR_KEY && !!curT && !!t && curT.id !== t.id;
  const b = handover ? emptyBill() : bill(a.id);
  // מה שנותר על החשבונות — אחרי תשלום חלקי, ולא "שולם/לא שולם" בלבד.
  const due = billsDue(b);
  const billing = isBillingMonth(a, s, month);
  const nextBill = billing ? null : nextBillingMonth(a, s, month);
  const tint = aptTint(idx);
  const meters = ["elec", "water", "gas"].filter((m) => showMeter(a, m));
  /* בחודש ללא חיוב (דו-חודשי) אין חשבונות צריכה — אבל ועד בית,
     ארנונה והיטל הם חודשיים ונגבים גם בו. */
  const fixedKeys = FIXED_BILLS.map((x) => x.k);
  /* סעיף צריכה שיש לו כרטיס מונה במסך לא מקבל קוביה נפרדת ברשת —
     הסכום והסטטוס שלו מוצגים בתוך כרטיס המונה עצמו (ביוב רוכב
     על המים). הקוביות נשארות רק לסעיפים בלי כרטיס: ועד, ארנונה,
     היטל, וצריכה שאין לה מונה מוצג אבל נרשם בה נתון. */
  const inMeterCard = { elec: "elec", water: "water", sewage: "water", gas: "gas" };
  const bills = handover ? [] : BILLS_UI.filter(({ k }) =>
    showBill(a, b, k)
    && (billing || fixedKeys.includes(k) || hasData(b[k]))
    && !(inMeterCard[k] && showMeter(a, inMeterCard[k])));
  /* שם הדייר של החודש המוצג, לא בהכרח הדייר של היום — חודש עבר
     מציג את מי שגר בו אז, לא את הדייר הנוכחי. תיקון רגרסיה:
     `a.tenant || tenantName` הציג את הדייר הנוכחי גם על חשבון
     ישן של דייר קודם כשגללו לחודש שהוא כבר עזב בו. ההפרדה
     המוחלטת רלוונטית רק לחודש ההחלפה עצמו (handover), לא
     לעיון בהיסטוריה. */
  const tenantName = t?.tenant || a.tenant;
  const headName = handover ? curT.tenant : tenantName;
  /* שכר הדירה של החודש המוצג — לפי התקופה והמדרגה שהיו בתוקף בו,
     ולא הסכום של היום: אחרי העלאה, חודש עבר הציג "נותרו" שלא היו.
     בחודש חפיפה אין טעם לחשב זאת בכלל — הסעיף כולו מוסתר. */
  const scope = t && !handover ? { ...a, ...t, id: a.id } : a;
  const rentCharged = !handover && activeIn(scope, mY, mM) ? Number(effectiveRent(scope, month)) || 0 : 0;
  const rentLeft = Math.max(0, rentCharged - rentPaidOf(data, a.id, month));
  // הסכום הבולט הוא כל מה שנותר לחודש — שכר דירה וחשבונות יחד.
  const total = due + rentLeft;
  // חשבונות שנשארו פתוחים בחודשים אחרים — שלא ייראה "הכול שולם" כשיש חוב נגרר.
  const openOther = Math.max(0, billsOutstanding(data, a.id) - due);
  const pill = total
    ? { bg: "#FBEAE6", line: "#F0C9C0", color: C.late, label: "לתשלום" }
    : openOther
      ? { bg: "#FBF2E3", line: "#E8D9B5", color: "#6B4E12", label: "החודש שולם" }
      : { bg: "#EAF3EE", line: "#CBE2D6", color: C.paid, label: "הכול שולם" };
  return (
    <div key={a.id} id={`apt-card-${a.id}`} className="rounded-xl border p-4"
      style={{
        background: tint.bg, borderColor: C.line, borderInlineStart: `5px solid ${tint.bar}`,
        boxShadow: justFocused === a.id ? `inset 0 0 0 3px ${tint.bar}` : undefined,
        transition: "box-shadow .3s ease",
      }}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="rounded-full flex items-center justify-center" style={{ width: 34, height: 34, background: tint.bar, color: "#fff" }}>
            <User size={17} />
          </div>
          <div>
            <div className="disp text-lg font-bold leading-tight" style={{ color: tint.bar }}>{headName}</div>
            <div className="text-[11px]" style={{ color: C.muted }}>
              {a.name} · חשבונות עד ה-{billsDayOf(a, s)}{billsCycleOf(a, s) === 2 ? " · דו-חודשי" : ""}
              {handover ? ` · נכנס/ה ${heDate(curT.leaseStart)}` : ""}
            </div>
          </div>
        </div>
        {!handover && (
        <div className="flex items-center gap-2">
          <div className="rounded-xl px-3 py-1.5 text-center" style={{ background: pill.bg, border: `1px solid ${pill.line}`, minWidth: 96 }}>
            <div className="text-[10px] leading-none" style={{ color: pill.color }}>
              {pill.label}
            </div>
            {total > 0 && <div className="disp text-xl font-bold leading-tight" style={{ color: pill.color }}>{nis(total)}</div>}
            {total > 0 && rentLeft > 0 && due > 0 && (
              <div className="text-[10px] leading-none mt-0.5" style={{ color: pill.color }}>שכ״ד {nis(rentLeft)} · חשבונות {nis(due)}</div>
            )}
            {openOther > 0 && (
              <div className="text-[10px] leading-none mt-0.5" style={{ color: "#6B4E12" }}>פתוח מחודשים אחרים {nis(openOther)}</div>
            )}
          </div>
          {/* גם בחודש ללא חיוב צריכה: ועד הבית והסעיפים הקבועים
              חודשיים, וקודם לא הייתה דרך לרשום אותם בחודש כזה. */}
          {(billing || bills.length > 0) && (
            <button onClick={() => autoPrice(a)} className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              {billing ? "חישוב לפי צריכה" : "רישום ועד בית"}
            </button>
          )}
          {a.tenant && a.phone && (billing || bills.length > 0 || due > 0) && (
            <a href={waLink(a.phone, consumptionBillsText(a, b, month, s, data, messageMode(a, data, month)))}
              target="_blank" rel="noreferrer"
              className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: "#128C7E", color: "#fff" }}>
              <MessageCircle size={13} /> שליחת חשבונות
            </a>
          )}
          {a.tenant && a.phone && Number(a.rent || 0) > 0 && (
            <a href={waLink(a.phone, rentText(a, month, s, data, messageMode(a, data, month)))}
              target="_blank" rel="noreferrer"
              className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: "#128C7E", color: "#fff" }}>
              <MessageCircle size={13} /> שליחת שכ״ד
            </a>
          )}
        </div>
        )}
      </div>

      {/* חודש חפיפה: הפרדה מוחלטת — בלי שם, סכום או תג של מי שעזב.
          חשבונות אוגוסט (כולל מה שכבר חושב לפני ההחלפה) מנוהלים
          אך ורק דרך "דיירים וחובות" ← כרטיס חוב; מכאן זה נראה
          כמו נכס שדייר חדש נכנס אליו נקי. */}
      {handover && (
        <div className="rounded-lg p-2.5 mb-3 text-[12px]" style={{ background: "#FBF7EE", color: "#6B4E12" }}>
          {curT.tenant} נכנס/ה ב-{heDate(curT.leaseStart)}. חיובי החודש הזה מנוהלים ב"היסטוריית דיירים וחובות"; החיוב של {curT.tenant} כאן מתחיל ב{monthLabel(nextMonth(month))}.
        </div>
      )}

      {a.tenant && !handover && (
        <div className="rounded-lg border p-2.5 mb-3 flex flex-wrap items-center gap-2" style={{ borderColor: C.line, background: "#FBFCFB" }}>
          <span className="text-xs" style={{ fontWeight: 600 }}>שכר דירה {nis(rentCharged)}</span>
          <span className="text-[11px]" style={{ color: rentLeft ? C.late : C.paid }}>
            {rentLeft ? `נותרו ${nis(rentLeft)}` : "שולם במלואו"}
          </span>
          <input type="number" placeholder="שולם" value={rentPaidOf(data, a.id, month) || ""}
            onChange={(e) => setData((d) => {
              const v = Number(e.target.value), k = rowKey(a.id, month), pay = { ...d.payments };
              if (!v) delete pay[k];
              else pay[k] = { ...pay[k], amount: v, date: TODAY, method: payMethodOf(pay[k]?.method, d.settings, "rent", null, a) };
              return { ...d, payments: pay };
            })}
            style={{ ...inputStyle, width: 110, padding: "5px 8px", fontSize: 13 }} />
          <button onClick={() => run([{ type: "set_rent_paid", apartment: a.id, month, paid: true, amount: rentCharged || Number(a.rent || 0) }])}
            className="text-[11px] rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.teal }}>התקבל במלואו</button>
          {rentPaidOf(data, a.id, month) > 0 && (
            <MethodPick
              value={data.payments[rowKey(a.id, month)]?.method}
              onChange={(v) => setData((d) => {
                const k = rowKey(a.id, month);
                if (!d.payments[k]) return d;
                return { ...d, payments: { ...d.payments, [k]: { ...d.payments[k], method: v } } };
              })} />
          )}
        </div>
      )}

      {meters.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          {meters.map((meter) => (
            <MeterBlock key={meter} apt={a} meter={meter} data={data} month={month}
              rate={rateFor(a, s, meter, `${month}-28`)}
              billed={billsMeter(a, s, meter)}
              onAdd={addReading} onEdit={editReading} onDelete={delReading} />
          ))}
        </div>
      )}

      {!billing && (
        <div className="rounded-lg p-2.5 text-xs mb-3" style={{ background: "#F1F4F1", color: C.muted }}>
          אין חיוב צריכה בחודש הזה — הגבייה בנכס דו-חודשית{nextBill ? `. החיוב הבא: ${monthLabel(nextBill)}` : ""}. הקריאות ממשיכות להישמר{bills.length ? "; ועד בית וסעיפים קבועים נגבים כל חודש" : ""}.
        </div>
      )}

      {/* החשבונות יחד: חויב · שולם · נותרו. בלי השורה הזו ראו "מים:
          נותרו 140", "חשמל: שולם 130 על 65" וכותרת "חשבונות 75" — שלושה
          מספרים שלא מסתדרים; היתרה מקוזזת בין הסעיפים (billsDue). */}
      {(() => {
        const chargedAll = BILLS.reduce((t, x) => t + Number(b[x.k]?.amount || 0), 0);
        const paidAll = BILLS.reduce((t, x) => t + itemPaid(b[x.k]), 0);
        const over = BILLS.some((x) => Number(b[x.k]?.amount || 0) > 0 && itemPaid(b[x.k]) > Number(b[x.k]?.amount || 0));
        if (!bills.length || !(chargedAll > 0 && paidAll > 0) || !(billsDue(b) > 0 || over)) return null;
        const left = billsDue(b);
        return (
          <div className="mb-2 rounded-lg px-3 py-2 text-[12px] flex flex-wrap gap-x-2 gap-y-1" style={{ background: "#F5F8F7" }}>
            <span style={{ fontWeight: 600 }}>חשבונות החודש יחד:</span>
            <span>חויב {nis(chargedAll)} · שולם {nis(paidAll)} ·</span>
            <span style={{ fontWeight: 600, color: left > 0 ? C.late : C.paid }}>{left > 0 ? `נותרו ${nis(left)}` : "שולם במלואו"}</span>
            {over && <span style={{ color: C.muted }}>· עודף על סעיף אחד מקוזז מול האחרים</span>}
          </div>
        );
      })()}
      {/* הסעיפים שנשארו בלי כרטיס מונה (ועד בית, ארנונה, היטל) —
          כרטיסים ברוחב מלא בשפה של כרטיסי המונים: קו צבע ייעודי
          בצד ימין, אייקון בריבוע מגוון, והשדות בשורה שמתקפלת
          בטלפון. הקוביות הקטנות ברשת נראו כמו שריד ממסך אחר. */}
      <div className="space-y-3" style={{ display: bills.length ? undefined : "none" }}>
        {bills.map(({ k, label, icon: Icon, color }) => {
          /* גבייה נועלת את הסכום: ברגע שנרשם תשלום (גם חלקי) שדה
             "לחיוב" קפוא — עריכה אחרי גבייה משכתבת בשקט את מה
             שהדייר שילם. איפוס "שולם" או ביטול הסימון פותחים. */
          const collected = itemPaid(b[k]) > 0;
          // ועד בית בצבע הלוח שלו בסקירה — אותו סעיף, אותו צבע.
          const accent = k === "vaad" ? "#3D6B99" : color;
          return (
          <div key={k} className="border p-3"
            style={{ borderColor: C.line, background: b[k].paid ? "#F4FAF6" : C.card, borderInlineStart: `4px solid ${accent}`, borderRadius: 16 }}>
            <div className="flex items-center gap-2 text-sm mb-2" style={{ fontWeight: 700 }}>
              <span className="rounded-lg flex items-center justify-center shrink-0"
                style={{ width: 30, height: 30, background: `${accent}1A`, color: accent }}>
                <Icon size={15} />
              </span>
              {label}
              {collected && <Lock size={11} style={{ color: C.muted }} aria-label="נעול לעריכה" />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input type="number" value={b[k].amount || ""} placeholder="לחיוב" disabled={collected}
                title={collected ? "החשבון נגבה — נעול לעריכה. ביטול הסימון פותח אותו." : undefined}
                aria-label={`${label} — סכום לחיוב`}
                onChange={(e) => setBillField(a.id, k, { amount: Number(e.target.value) })}
                style={{ ...inputStyle, padding: "6px 8px", fontSize: 13, maxWidth: 120, ...(collected ? { background: "#F1F4F1", color: C.muted } : {}) }} />
              {/* עיגול בלחיצה — רלוונטי רק לסעיף צריכה נדיר שנשאר
                  כאן בלי כרטיס מונה, ורק לפני שנרשמה גבייה. */}
              {["elec", "water", "gas", "sewage"].includes(k) && Number(b[k].amount || 0) > 0 && !collected && (() => {
                const amt = Number(b[k].amount || 0);
                const arrow = (dir, Icon2, hint) => {
                  const target = roundBillAmount(amt, dir);
                  return (
                    <button onClick={() => setBillField(a.id, k, { amount: target })} disabled={target === amt}
                      title={target === amt ? "עיגול אינו מאפס את החשבון" : `${hint} — ${nis(target)}`} aria-label={`עיגול ${label} ${hint}`}
                      className="rounded flex items-center justify-center px-2 py-1"
                      style={{ border: `1px solid ${C.line}`, color: target === amt ? C.line : C.teal }}>
                      <Icon2 size={15} />
                    </button>
                  );
                };
                return <>{arrow(1, ArrowUpCircle, "כלפי מעלה")}{arrow(-1, ArrowDownCircle, "כלפי מטה")}</>;
              })()}
              {["elec", "water", "gas", "sewage"].includes(k) ? (() => {
                const amt = Number(b[k].amount || 0), paidAmt = itemPaid(b[k]), due = itemDue(b[k]);
                if (!(amt > 0 || paidAmt > 0)) return null;
                const paidFull = due <= 0 && amt > 0;
                return (
                  <span className="rounded text-[11px] px-2.5 py-1"
                    title="את התשלום מסמנים בלוח חשבונות הצריכה שבסקירה"
                    style={paidFull ? { background: "#EAF3EE", color: C.paid } : { background: "#FBEDED", color: C.late }}>
                    {paidFull
                      ? (paidAmt > amt ? `שולם · עודף ${nis(paidAmt - amt)}` : "שולם")
                      : paidAmt > 0 ? `נותרו ${nis(due)}` : "לתשלום"}
                  </span>
                );
              })() : (
                <>
                  <input type="number" value={itemPaid(b[k]) || ""} placeholder="שולם"
                    aria-label={`${label} — כמה שולם`}
                    onChange={(e) => { const v = Number(e.target.value); setBillField(a.id, k, { paidAmount: v, paid: v >= Number(b[k].amount || 0) && v > 0 }); }}
                    style={{ ...inputStyle, padding: "6px 8px", fontSize: 13, maxWidth: 120, background: itemPaid(b[k]) ? "#F4FAF6" : "#fff" }} />
                  <button onClick={() => {
                    const full = itemDue(b[k]) > 0;
                    setBillField(a.id, k, {
                      paidAmount: full ? Number(b[k].amount || 0) : 0, paid: full,
                      // הנכס מועבר — ברירת המחדל היא מה שסוכם מול הדייר הזה.
                      method: full ? payMethodOf(b[k].method, s, k, null, a) : null,
                    });
                  }}
                    className="rounded text-[11px] px-3 py-1.5"
                    style={{ background: itemDue(b[k]) <= 0 && b[k].amount ? C.paid : "transparent", color: itemDue(b[k]) <= 0 && b[k].amount ? "#fff" : C.muted, border: `1px solid ${itemDue(b[k]) <= 0 && b[k].amount ? C.paid : C.line}` }}>
                    {itemDue(b[k]) <= 0 && b[k].amount
                      ? (itemPaid(b[k]) > Number(b[k].amount || 0) ? `שולם · עודף ${nis(itemPaid(b[k]) - Number(b[k].amount || 0))}` : "שולם במלואו")
                      : itemDue(b[k]) < Number(b[k].amount || 0) ? `נותרו ${nis(itemDue(b[k]))}` : "סימון תשלום מלא"}
                  </button>
                  {itemPaid(b[k]) > 0 && (
                    <MethodPick compact value={b[k].method}
                      onChange={(v) => setBillField(a.id, k, { method: v })} />
                  )}
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

/* MeterBlock עברה לקובץ עצמאי ./MeterBlock.jsx (1.9.2026) — ראו שם. */

/* ---------------- חוזים ותיעוד ---------------- */
/**
 * תיעוד שיקים שהתקבלו לתשלום שכר הדירה.
 *
 * צילום ולא הקלדה: השיק עצמו — סכום, תאריך, בנק וחתימה — הוא הראיה,
 * והתיאור החופשי משלים את מה שלא רואים בתמונה (לאילו חודשים, כמה שיקים).
 * הצילומים נשמרים כ-media של הנכס (kind "check") לצד תיעוד מצב הדירה,
 * ולכן מגובים ומיוצאים כמו כל תמונה אחרת. משמש גם בקליטת דייר וגם
 * בלשונית "חוזים ותיעוד".
 */
function ChecksSection({ apt, data, setData, tenant = null }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const checks = (data.media || []).filter((m) => m.aptId === apt.id && m.kind === "check");

  const add = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const id = await attachFile(file, { apartmentId: apt.id, kind: "check" });
      setData((d) => ({
        ...d,
        media: [...(d.media || []), {
          id, aptId: apt.id, kind: "check",
          label: note.trim() || "שיקים לשכר דירה",
          date: TODAY, tenant: tenant || apt.tenant || "",
        }],
      }));
      setNote("");
    } catch (e) { setErr(e?.message || "העלאת הצילום נכשלה. נסו קובץ קטן יותר."); }
    setBusy(false);
  };

  return (
    <>
      <p className="text-xs mb-2" style={{ color: C.muted }}>
        צלמו את השיקים שקיבלתם (אפשר כמה שיקים בתמונה אחת). התיאור נשמר עם
        הצילום — למשל "12 שיקים, ינואר–דצמבר, בנק הפועלים".
      </p>
      <input style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder='תיאור לצילום הבא — למשל "12 שיקים לחודשים 1–12"' />
      <div className="flex gap-2 mt-2 mb-3">
        <FilePick capture title="צילום שיקים" onPick={add}
          className="text-xs rounded-lg px-2.5 py-2 gap-1" style={{ background: C.teal, color: "#fff" }}>
          <Camera size={14} /> {busy ? "מעלה…" : "צילום שיקים"}
        </FilePick>
        <FilePick title="העלאה מהגלריה" onPick={add}
          className="text-xs rounded-lg px-2.5 py-2 gap-1" style={{ border: `1px solid ${C.line}` }}>
          <Upload size={14} /> מהגלריה
        </FilePick>
      </div>
      {err && <div className="text-[11px] mb-2" style={{ color: C.late }}>{err}</div>}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {checks.map((m) => (
          <div key={m.id} className="relative">
            <StoredImage id={m.id} className="w-full rounded-lg" style={{ height: 92, objectFit: "cover" }} />
            <button onClick={() => setData((d) => ({ ...d, media: d.media.filter((x) => x.id !== m.id) }))}
              aria-label="מחיקת הצילום" className="absolute top-1 left-1 rounded-full w-6 h-6 flex items-center justify-center"
              style={{ background: "rgba(20,38,43,0.6)", color: "#fff" }}><X size={12} /></button>
            <div className="text-[10px] mt-1" style={{ color: C.muted }}>
              <span className="block truncate" title={m.label}>{m.label}</span>
              {heDate(m.date)}{m.tenant ? ` · ${m.tenant}` : ""}
            </div>
          </div>
        ))}
        {checks.length === 0 && (
          <div className="col-span-full text-xs py-3 text-center" style={{ color: C.muted }}>
            עדיין לא תועדו שיקים לנכס הזה.
          </div>
        )}
      </div>
    </>
  );
}

function Docs({ data, setData, run, setModal, initialAptId = null }) {
  const hasApt = (id) => !!id && data.apartments.some((a) => a.id === id);
  const [aptId, setAptId] = useState(hasApt(initialAptId) ? initialAptId : (data.apartments[0]?.id || ""));
  const apt = data.apartments.find((a) => a.id === aptId);
  const [busy, setBusy] = useState("");
  const [upErr, setUpErr] = useState("");
  const [shareOpen, setShareOpen] = useState(null);   // מזהה החוזה שחלופות השליחה שלו פתוחות
  const [tenId, setTenId] = useState("");             // "" = התקופה הפעילה
  /* עורך הנוסח: { contract } = עריכת חוזה קיים, { create: true } = חוזה
     חופשי חדש. נוסח מותאם נשמר על הרשומה (customText) וגובר על התבנית. */
  const [editCtr, setEditCtr] = useState(null);
  // לפני ההחזרה המוקדמת — סדר ה-hooks חייב להישאר קבוע בין רינדורים.
  const inviteCode = usePortalCode(aptId);
  /* התראה ("הצגת הבקשה") מכוונת ללשונית הזו עם נכס מסוים — גם כשהלשונית
     כבר פתוחה על נכס אחר. */
  useEffect(() => {
    if (hasApt(initialAptId)) { setAptId(initialAptId); setTenId(""); }
  }, [initialAptId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!apt) return <div className="mt-6 text-sm" style={{ color: C.muted }}>אין נכסים.</div>;

  /* בחירת תקופה. החוזים של דיירים קודמים היו קיימים במסמך אבל לא נגישים
     משום מסך — "תקופות שכירות" רק ספר אותם ("2 מתוך 2 חתומים"). כשמדובר
     בחוזה שנחתם דיגיטלית, זה המקום היחיד שבו הוא קיים. */
  const tenancies = tenanciesOf(data, apt.id).filter((t) => t.tenant);
  const activeTen = targetTenancy(data, apt.id);
  const picked = tenId ? tenancies.find((t) => t.id === tenId) : null;
  /* מהתקופה ולא מהנכס: בנכס עם תקופה עתידית בלבד, apt.contracts הוא
     עותק ישן — וחוזה שנשלח לחתימה המשיך להופיע כטיוטה. */
  const contracts = picked ? (picked.contracts || []) : contractsOf(data, apt.id);
  // הכפתורים והנוסח פועלים על התקופה שמוצגת, כדי שלא ייווצר חוזה חדש
  // לדייר שכבר עזב.
  const viewingPast = !!picked && picked.id !== activeTen?.id;
  const contractApt = picked
    ? { ...apt, ...Object.fromEntries(TEN_FIELDS.filter((k) => picked[k] !== undefined).map((k) => [k, picked[k]])), id: apt.id }
    : apt;
  const media = (data.media || []).filter((m) => m.aptId === apt.id);

  const upload = async (file, kind, label) => {
    if (!file) return;
    setBusy(kind); setUpErr("");
    try {
      const id = await attachFile(file, { apartmentId: apt.id, kind });
      setData((d) => ({ ...d, media: [...(d.media || []), { id, aptId: apt.id, kind, label: label || file.name, date: TODAY, tenant: apt.tenant }] }));
    } catch { setUpErr("העלאת הקובץ נכשלה. נסו קובץ קטן יותר או תמונה במקום PDF."); }
    setBusy("");
  };

  const removeMedia = (id) => setData((d) => ({ ...d, media: d.media.filter((m) => m.id !== id) }));

  /* קובץ חוזה פרטי — נכנס כרשומת חוזה מן המניין (שליחה לחתימה, חתימה
     ומחיקה עובדות עליו), בשונה מ"סריקת חוזה" שנשמרת בתיעוד בלבד. */
  const uploadContractFile = async (file) => {
    if (!file) return;
    setBusy("contract-file"); setUpErr("");
    try {
      const id = await attachFile(file, { apartmentId: apt.id, kind: "contract" });
      run([{ type: "new_contract", apartment: apt.id, fileId: id, fileName: file.name || "", fileMime: file.type || "" }]);
    } catch { setUpErr("העלאת קובץ החוזה נכשלה. נסו קובץ קטן יותר או תמונה במקום PDF."); }
    setBusy("");
  };

  return (
    <section className="mt-6 space-y-4">
      {upErr && <div className="rounded-lg p-2.5 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{upErr}</div>}

      {/* ההצפנה יושבת כאן ולא בהגדרות: היא מגינה בדיוק על מה שמועלה
          במסך הזה — צילומי תעודות הזהות שנלווים לחוזים. */}
      <VaultCard ownerId={data.settings?.ownerId || ""} />

      <div className="flex flex-wrap gap-2">
        <select style={{ ...inputStyle, maxWidth: 320 }} value={aptId} onChange={(e) => { setAptId(e.target.value); setTenId(""); }}>
          {data.apartments.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.tenant || "פנויה"}</option>)}
        </select>
        {/* הבורר מופיע גם כשיש תקופה אחת שאינה פעילה — נכס פנוי שהחוזה
            של הדייר הקודם הוא הדבר היחיד שיש בו. */}
        {(tenancies.length > 1 || (tenancies.length === 1 && !activeTen)) && (
          <select style={{ ...inputStyle, maxWidth: 260 }} value={tenId} onChange={(e) => setTenId(e.target.value)} aria-label="תקופת שכירות">
            <option value="">{activeTen?.tenant ? `${activeTen.tenant} — נוכחי` : "הנכס פנוי — בחרו תקופה"}</option>
            {tenancies.filter((t) => t.id !== activeTen?.id).map((t) => (
              <option key={t.id} value={t.id}>
                {/* tenancyEnd ולא leaseEnd: תקופה שנסגרה לפני התיקון עדיין
                    נושאת את תאריך החוזה, והתצוגה הראתה סיום שלא היה. */}
                {t.tenant} · {heDate(t.leaseStart)}–{tenancyEnd(t) ? heDate(tenancyEnd(t)) : "…"}{t.closed ? " (הסתיים)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>
      {viewingPast && (
        <div className="rounded-lg p-2.5 text-[12px]" style={{ background: "#F1F4F1", color: C.muted }}>
          מוצגים החוזים של {picked.tenant} — תקופה שהסתיימה. הפקת חוזה חדש והארכה פועלות על התקופה הנוכחית.
        </div>
      )}

      {/* חוזים */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="disp text-xl font-bold">חוזים</h2>
          <div className="flex flex-wrap gap-2 justify-end">
            <FilePick accept="image/*,application/pdf" title="סריקת חוזה" onPick={(f) => upload(f, "contract", "חוזה סרוק")}
              className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}` }}>
              <Upload size={13} /> סריקת חוזה
            </FilePick>
            {/* חוזה פרטי: קובץ של בעל הנכס (PDF או תמונה) שנכנס כחוזה
                לכל דבר, או חוזה שנכתב חופשי בפלטפורמה. */}
            <FilePick accept="image/*,application/pdf" title="העלאת קובץ חוזה פרטי" onPick={uploadContractFile}
              className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              <Upload size={13} /> {busy === "contract-file" ? "מעלה…" : "חוזה פרטי (קובץ)"}
            </FilePick>
            <button onClick={() => setEditCtr({ create: true })} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              <PenTool size={13} /> חוזה בכתיבה חופשית
            </button>
            {apt.tenant && (
              <button onClick={() => run([{ type: "new_contract", apartment: apt.id }])} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: C.teal, color: "#fff" }}>
                <FileText size={13} /> חוזה חדש
              </button>
            )}
            <button onClick={() => setModal({ kind: "extend", apt })} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}` }}>
              <CalendarPlus size={13} /> הארכת חוזה
            </button>
          </div>
        </div>

        {/* בקשת הארכה שהדייר שלח מהאזור האישי. יושבת כאן, ליד "הארכת
            חוזה", כי ההחלטה עליה היא בדיוק הפעולה הזו — אישור מפיק נספח
            עם התאריך שביקש, דחייה חוזרת אליו עם הסיבה. */}
        {!viewingPast && (() => {
          const req = pendingExtensionRequest(data, apt.id);
          const history = extensionRequestsOf(data, apt.id).filter((r) => r.status !== "pending").slice(-3).reverse();
          if (!req && !history.length) return null;
          return (
            <div className="mb-3 space-y-2">
              {req && (
                <div className="rounded-lg border p-3" style={{ borderColor: "#E8C98A", background: "#FBF2E3" }}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="text-sm">
                      <div className="flex items-center gap-1.5" style={{ fontWeight: 600, color: "#6B4E12" }}>
                        <CalendarPlus size={14} /> {apt.tenant || "הדייר"} ביקש/ה להאריך את החוזה עד {heDate(req.to)}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "#6B4E12" }}>
                        נשלחה ב-{heDate(req.createdAt)}{apt.leaseEnd ? ` · החוזה הנוכחי מסתיים ב-${heDate(apt.leaseEnd)}` : ""}
                        {req.note ? ` · "${req.note}"` : ""}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setModal({ kind: "extend", apt, request: req })}
                        className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: C.teal, color: "#fff" }}>
                        <Check size={13} /> אישור — נספח הארכה
                      </button>
                      <button onClick={() => setModal({ kind: "extend", apt, request: req, declineFirst: true })}
                        className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid #E8C98A`, color: "#6B4E12" }}>
                        <X size={13} /> דחייה
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {history.map((r) => (
                <div key={r.id} className="text-[11px] flex items-center gap-1.5 px-1" style={{ color: C.muted }}>
                  <CalendarPlus size={11} /> בקשת הארכה עד {heDate(r.to)} · {EXTENSION_STATUS[r.status] || r.status}
                  {r.decidedAt ? ` (${heDate(r.decidedAt)})` : ""}{r.reason ? ` — ${r.reason}` : ""}
                </div>
              ))}
            </div>
          );
        })()}

        {contracts.length === 0 && (
          <div className="text-sm py-6 text-center" style={{ color: C.muted }}>
            אין חוזה לנכס הזה. "חוזה חדש" יוצר חוזה לתקופת השכירות הנוכחית, "הארכת חוזה" יוצר נספח לתקופה הבאה, ואפשר גם להעלות סריקה של חוזה קיים.
          </div>
        )}

        <div className="space-y-2">
          {contracts.map((c) => (
            <div key={c.id} className="rounded-lg border p-3 flex flex-wrap items-center justify-between gap-2" style={{ borderColor: C.line }}>
              <div>
                <div className="text-sm" style={{ fontWeight: 600 }}>
                  {c.fileId ? (c.fileName || "קובץ חוזה") : c.type === "extension" ? "נספח הארכה" : "חוזה שכירות"} · {heDate(c.from)} — {heDate(c.to)}
                </div>
                <div className="text-[11px]" style={{ color: C.muted }}>{nis(c.rent)} לחודש · ביטחונות {nis(c.deposit)}</div>
              </div>
              {/* flex-wrap: בטלפון שורת הפעולות ארוכה מהכרטיס, ובלי שבירה
                  כפתור "שיתוף / הורדה" נדחף אל מחוץ למסך ונחתך. */}
              <div className="flex flex-wrap items-center gap-2">
                {/* חוזה שאינו התבנית — שיֵדעו מה בדיוק נשלח לדייר. */}
                {(c.fileId || c.customText) && (
                  <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: "#EEF3FB", color: "#3D6B99" }}>
                    {c.fileId ? "קובץ שהועלה" : "נוסח אישי"}
                  </span>
                )}
                <span className="text-[11px] rounded-full px-2 py-0.5" style={{
                  background: c.status === "signed" ? "#EAF3EE" : c.status === "sent" ? "#FBF2E3" : "#F1F4F1",
                  color: c.status === "signed" ? C.paid : c.status === "sent" ? "#6B4E12" : C.muted,
                }}>
                  {c.status === "signed" ? "חתום" : c.status === "sent" ? "נשלח לחתימה" : "טיוטה"}
                </span>
                <button onClick={() => setModal({ kind: "contract", apt: contractApt, contract: c })} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}` }}>
                  <FileText size={13} /> פתיחה
                </button>
                {/* עריכת הנוסח — עד החתימה. חוזה-קובץ אין מה לערוך כטקסט. */}
                {c.status !== "signed" && !c.fileId && (
                  <button onClick={() => setEditCtr({ contract: c })} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                    <PenTool size={13} /> עריכת נוסח
                  </button>
                )}
                <button aria-label="מחיקת החוזה" title="מחיקת החוזה"
                  onClick={() => setModal({
                    kind: "confirm",
                    title: c.status === "signed" ? "למחוק חוזה חתום?" : "למחוק את החוזה?",
                    body: c.status === "signed"
                      ? "החוזה כבר נחתם. מחיקה מסירה אותו מהמערכת לצמיתות — החתימה והתיעוד לא ישוחזרו."
                      : `${c.type === "extension" ? "נספח ההארכה" : "החוזה"} לתקופה ${heDate(c.from)}–${heDate(c.to)} יימחק. אפשר ליצור חוזה חדש אחר כך.`,
                    action: () => run([{ type: "remove_contract", apartment: apt.id, contractId: c.id, force: c.status === "signed" }]),
                  })}
                  className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ border: `1px solid ${C.line}`, color: C.late }}>
                  <Trash2 size={13} />
                </button>
                {apt.phone && c.status !== "signed" && (
                  <a href={waLink(apt.phone, contractInvite(apt, c, inviteCode))}
                    target="_blank" rel="noreferrer" className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: "#128C7E", color: "#fff" }}
                    onClick={() => setData((d) => markContractSent(d, apt.id, c.id))}>
                    <Send size={13} /> שליחה לחתימה
                  </a>
                )}
                {c.status !== "signed" && (
                  <button onClick={() => setShareOpen(shareOpen === c.id ? null : c.id)} aria-label="שיתוף או הורדה" title="שיתוף בדרך אחרת / הורדה"
                    className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                    style={{ border: `1px solid ${shareOpen === c.id ? C.teal : C.line}`, color: C.teal }}>
                    <Share2 size={13} /> שיתוף / הורדה
                  </button>
                )}
              </div>
              {/* חלופות לוואטסאפ: שיתוף דרך המכשיר, העתקה, PDF. גם כשאין טלפון לדייר. */}
              {shareOpen === c.id && c.status !== "signed" && (
                <div className="basis-full">
                  <ContractSendRow text={contractInvite(apt, c, inviteCode)}
                    onDoc={() => setModal({ kind: "contract", apt: contractApt, contract: c })}
                    onSent={() => setData((d) => markContractSent(d, apt.id, c.id))} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ערבים — לדייר קיים. עד עכשיו לא היה בצד הניהול שום מקום להוסיף
          או להחתים ערב אחרי הקליטה; העורך פועל על התקופה הנוכחית, ולכן
          מוצג רק כשלא צופים בתקופה שהסתיימה. */}
      {!viewingPast && (
        <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
          <div className="flex items-center gap-1.5 mb-1">
            <PenTool size={16} style={{ color: C.teal }} />
            <h2 className="disp text-xl font-bold">ערבים</h2>
          </div>
          <GuarantorsEditor apt={apt} setData={setData} />
        </div>
      )}

      {/* שיקים שהתקבלו לשכר דירה */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-1.5 mb-1">
          <Receipt size={16} style={{ color: C.teal }} />
          <h2 className="disp text-xl font-bold">שיקים שהתקבלו</h2>
        </div>
        <ChecksSection apt={apt} data={data} setData={setData} />
      </div>

      {/* תיעוד */}
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="disp text-xl font-bold">תיעוד מצב הנכס</h2>
          <div className="flex gap-2">
            <FilePick capture accept="image/*,video/*" title="צילום כניסה" onPick={(f) => upload(f, "movein", "כניסה לדירה")}
              className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ background: C.ochre, color: "#fff" }}>
              <Camera size={13} /> {busy === "movein" ? "מעלה…" : "צילום כניסה"}
            </FilePick>
            <FilePick accept="image/*,video/*" title="בחירה מהגלריה" onPick={(f) => upload(f, "movein", "כניסה לדירה")}
              className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}` }}>
              <Upload size={13} /> מהגלריה
            </FilePick>
          </div>
        </div>
        <p className="text-xs mb-3" style={{ color: C.muted }}>תמונות או סרטון מיום הכניסה — הראיה למצב הנכס בסוף החוזה</p>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {media.filter((m) => m.kind !== "meter" && m.kind !== "check").map((m) => (
            <div key={m.id} className="relative">
              <StoredImage id={m.id} className="w-full rounded-lg" style={{ height: 92, objectFit: "cover" }} />
              <button onClick={() => removeMedia(m.id)} aria-label="מחיקה" className="absolute top-1 left-1 rounded-full w-6 h-6 flex items-center justify-center" style={{ background: "rgba(20,38,43,0.6)", color: "#fff" }}><X size={12} /></button>
              <div className="text-[10px] mt-1 truncate" style={{ color: C.muted }}>{m.kind === "contract" ? "חוזה סרוק" : m.label} · {heDate(m.date)}</div>
            </div>
          ))}
          {media.filter((m) => m.kind !== "meter" && m.kind !== "check").length === 0 && (
            <div className="col-span-full text-sm py-6 text-center" style={{ color: C.muted }}>אין תמונות. צלמו את הדירה לפני כניסת הדייר.</div>
          )}
        </div>
      </div>

      {editCtr && (
        <ContractEditModal apt={contractApt} contract={editCtr.contract || null} settings={data.settings || defTariffs()}
          onSave={(text) => {
            run(editCtr.contract
              ? [{ type: "set_contract_text", apartment: apt.id, contractId: editCtr.contract.id, text }]
              : [{ type: "new_contract", apartment: apt.id, customText: text }]);
            setEditCtr(null);
          }}
          onClose={() => setEditCtr(null)} />
      )}
    </section>
  );
}

/**
 * עריכת נוסח החוזה בפלטפורמה, או כתיבת חוזה חופשי חדש (contract = null).
 *
 * העריכה מתחילה מהנוסח שמוצג היום — המותאם אם נשמר, ואחרת התבנית — כך
 * שבעל הנכס מתקן סעיף במקום להקליד חוזה שלם. החתימות והנספחים לא חלק
 * מהטקסט: הם מתווספים בתחתית המסמך אוטומטית, כמו בכל חוזה.
 */
function ContractEditModal({ apt, contract, settings, onSave, onClose }) {
  const [text, setText] = useState(() =>
    contract ? (contract.customText || contractText(apt, contract, settings)) : (settings?.contractTemplate || ""));
  const isCustom = !!contract?.customText;

  return (
    <Modal title={contract ? "עריכת נוסח החוזה" : "חוזה בכתיבה חופשית"} onClose={onClose} wide>
      <textarea dir="rtl" value={text} rows={18} autoFocus
        onChange={(e) => setText(e.target.value)}
        placeholder={contract ? "" : "כתבו כאן את נוסח החוזה המלא…"}
        style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.7, fontSize: 13, whiteSpace: "pre-wrap" }} />
      <p className="text-[11px] mt-2" style={{ color: C.muted }}>
        הנוסח נשמר כלשונו ומוצג לדייר לחתימה בדיוק כפי שנכתב. חתימות השוכר, המשכיר והערבים מתווספות בתחתית המסמך.
        {contract ? " שינוי פרטים בנכס לא יעדכן נוסח מותאם — הוא קפוא כפי שנכתב." : ""}
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => { if (text.trim()) onSave(text); }}
          className="flex-1 rounded-lg py-2.5 text-sm" style={{ background: text.trim() ? C.teal : "#8FA9A7", color: "#fff" }}>
          {contract ? "שמירת הנוסח" : "יצירת החוזה"}
        </button>
        {isCustom && (
          <button onClick={() => onSave("")} className="rounded-lg px-4 py-2.5 text-sm" style={{ border: `1px solid ${C.line}`, color: C.muted }}
            title="מחיקת הנוסח המותאם וחזרה לנוסח שהמערכת בונה מפרטי הנכס">
            חזרה לנוסח המערכת
          </button>
        )}
        <button onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
      </div>
    </Modal>
  );
}

/* ---------------- תעריפים ---------------- */
/* לשונית ההגדרות. השם "תעריפים" תיאר רק את החלק הראשון שלה — בפועל
   יושבות כאן ברירות המחדל של כל המערכת, ומאז גם האבטחה של החשבון. */
/**
 * איפוס הפלטפורמה — מתוך ההגדרות, עם קוד שנשלח למייל של בעל הנכס.
 * שני היקפים: הדיירים בלבד (הנכסים נשארים כקליפות ריקות) או הכול (גם
 * הנכסים). ההגדרות, פרטי בעל הנכס והחתימה נשמרים בשניהם. האיפוס נעשה
 * בשרת (גם קודי הדיירים והסשנים שלהם מתבטלים), ואז הדף נטען מחדש.
 */
/* ---------------- שחזור ומחיקה — תחתית ההגדרות ----------------
   שתי הפעולות מאושרות בתעודת הזהות של בעל החשבון (כפי שמולאה ב"פרטי בעל
   הנכס"). לא קוד למייל: המייל לא תמיד מוגדר, ותעודת הזהות היא משהו שרק
   בעל החשבון יודע ומסך פתוח במכשיר משותף לא מגלה. */

const OWNER_ID_HINT = 'תעודת הזהות של בעל החשבון היא הקוד שמאשר מחיקה ושחזור. היא עדיין לא מולאה — "עוד" ← "אזור אישי" ← פרטי בעל הנכס.';
const idLooksFull = (v) => v.replace(/\D/g, "").length >= 5;

function OwnerIdInput({ value, onChange, disabled }) {
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)} inputMode="numeric" placeholder="תעודת זהות" maxLength={12}
      disabled={disabled} aria-label="תעודת הזהות של בעל החשבון"
      style={{ ...inputStyle, width: 150, background: "#fff", textAlign: "center", letterSpacing: 2 }} />
  );
}

const whenLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit" })} · ${d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`;
};
const countsLabel = (s) =>
  `${s?.apartments ?? 0} נכסים · ${s?.tenants ?? 0} דיירים · ${s?.payments ?? 0} תשלומים · ${s?.bills ?? 0} חשבונות`;

function RestoreCard({ hasOwnerId }) {
  const pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`; });
  const [time, setTime] = useState(() => { const n = new Date(); return `${pad(n.getHours())}:${pad(n.getMinutes())}`; });
  const [items, setItems] = useState(null);    // null = עוד לא חיפשו
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [idNum, setIdNum] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const search = async () => {
    setBusy(true); setMsg(null); setSel(null); setPreview(null);
    try {
      const at = new Date(`${date}T${time || "23:59"}`);
      const r = await dataApi.history({ at: Number.isNaN(at.getTime()) ? null : at.toISOString(), limit: 30 });
      setItems(r.items || []);
      if (!(r.items || []).length) {
        setMsg({ ok: false, text: "אין נקודת שחזור לפני הרגע הזה. נקודות נשמרות מכל שמירה מאז שהאפשרות נוספה (23.8.2026)." });
      }
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy(false);
  };
  const pick = async (it) => {
    setSel(it); setPreview(null); setMsg(null);
    try { setPreview(await dataApi.historyItem(it.id)); } catch (e) { setMsg({ ok: false, text: e.message }); }
  };
  const doRestore = async () => {
    if (!sel) return;
    if (!window.confirm(`לשחזר את המצב מ-${whenLabel(sel.savedAt)}? המצב של עכשיו נשמר כנקודת שחזור, כך שאפשר יהיה לחזור.`)) return;
    setBusy(true); setMsg(null);
    try {
      await dataApi.restore(sel.id, idNum.trim());
      // המסמך השתנה בשרת — טעינה מחדש היא הדרך הבטוחה לסנכרן גרסה ומסך.
      window.location.reload();
    } catch (e) { setMsg({ ok: false, text: e.message || "השחזור נכשל" }); setBusy(false); }
  };

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-xl font-bold mb-1">שחזור מצב קודם</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        כל שמירה נשמרת כנקודת שחזור: כל שינוי ב-24 השעות האחרונות, אחת לשעה עד חודש, אחת ליום עד חצי שנה.
        בוחרים תאריך ושעה, רואים מה היה אז, ומשחזרים. המצב של עכשיו נשמר גם הוא — אפשר לחזור ממנו.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="תאריך" style={{ ...inputStyle, width: 150 }} />
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="שעה" style={{ ...inputStyle, width: 110 }} />
        <button onClick={search} disabled={busy} className="text-sm rounded-lg px-3 py-2"
          style={{ background: C.teal, color: "#fff", opacity: busy ? 0.6 : 1 }}>
          {busy && !sel ? "מחפש…" : "הצגת נקודות שחזור"}
        </button>
      </div>

      {items && items.length > 0 && (
        <div className="mt-3 space-y-1.5" style={{ maxHeight: 280, overflowY: "auto" }}>
          {items.map((it) => (
            <label key={it.id} className="flex items-start gap-2 rounded-lg border p-2 cursor-pointer"
              style={{ borderColor: sel?.id === it.id ? C.teal : C.line, background: sel?.id === it.id ? "#E8F3F1" : "transparent" }}>
              <input type="radio" name="restore-point" checked={sel?.id === it.id} onChange={() => pick(it)} className="mt-1" />
              <span className="min-w-0">
                <span className="block text-sm" style={{ fontWeight: 600 }}>{whenLabel(it.savedAt)} — {it.label || "עדכון"}</span>
                <span className="block text-[11px]" style={{ color: C.muted }}>{countsLabel(it.summary)}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      {sel && (
        <div className="mt-3 rounded-lg p-3" style={{ background: "#E8F3F1" }}>
          <div className="text-xs mb-2" style={{ color: C.ink }}>
            <b>המצב ב-{whenLabel(sel.savedAt)}:</b> {countsLabel(sel.summary)}
            {preview?.apartments?.length ? (
              <ul className="mt-1 pr-4 list-disc">
                {preview.apartments.slice(0, 12).map((a) => (
                  <li key={a.id}>{a.name || "נכס"}{a.tenant ? ` — ${a.tenant}` : " — פנוי"}</li>
                ))}
                {preview.apartments.length > 12 && <li>ועוד {preview.apartments.length - 12}…</li>}
              </ul>
            ) : preview ? <div className="mt-1">אין נכסים במצב הזה.</div> : <div className="mt-1">טוען תצוגה מקדימה…</div>}
          </div>
          {hasOwnerId ? (
            <div className="flex flex-wrap gap-2 items-center">
              <OwnerIdInput value={idNum} onChange={setIdNum} disabled={busy} />
              <button onClick={doRestore} disabled={busy || !idLooksFull(idNum)} className="text-sm rounded-lg px-3 py-2"
                style={{ background: C.teal, color: "#fff", opacity: busy || !idLooksFull(idNum) ? 0.5 : 1 }}>
                {busy ? "משחזר…" : "שחזור למצב הזה"}
              </button>
            </div>
          ) : (
            <div className="text-[12px] rounded-lg p-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{OWNER_ID_HINT}</div>
          )}
        </div>
      )}
      {msg && (
        <div className="mt-2 rounded-lg p-2 text-[12px]" style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function DeleteDataCard({ hasOwnerId, accountId }) {
  const [scope, setScope] = useState("tenants");
  const [idNum, setIdNum] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const doReset = async () => {
    const q = scope === "all"
      ? "למחוק הכול — כל הדיירים וכל הנכסים? המצב של עכשיו נשמר כנקודת שחזור."
      : "למחוק את כל הדיירים? הנכסים יישארו ריקים. המצב של עכשיו נשמר כנקודת שחזור.";
    if (!window.confirm(q)) return;
    setBusy(true); setMsg(null);
    try {
      await dataApi.reset({ scope, idNumber: idNum.trim() });
      /* גם השיחה עם העוזר, השמורה במכשיר הזה: היא מלאה בשמות דיירים,
         סכומים ו"הנכס בפוקוס" שנמחקו הרגע — ואין טעם שתופיע אחרי המחיקה. */
      clearAssistantChat(accountId);
      // המסמך השתנה בשרת — טעינה מחדש היא הדרך הבטוחה לסנכרן גרסה ומסך.
      window.location.reload();
    } catch (e) {
      setMsg({ ok: false, text: e.message || "המחיקה נכשלה" });
      setBusy(false);
    }
  };

  const opts = [
    { k: "tenants", label: "מחיקת הדיירים בלבד", sub: "כל הדיירים, התשלומים, החשבונות, הקריאות, החוזים וההודעות נמחקים. הנכסים נשארים — ריקים ומוכנים לקליטת דייר." },
    { k: "all", label: "מחיקת הדיירים והנכסים", sub: "הכול נמחק, כולל הנכסים. החשבון עצמו נשאר: הכניסה, ההגדרות, פרטי בעל הנכס והחתימה." },
  ];

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: "#F0C9C0" }}>
      <h2 className="disp text-xl font-bold mb-1" style={{ color: C.late }}>מחיקת נתוני החשבון</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        מאושרת בתעודת הזהות של בעל החשבון. המצב שלפני המחיקה נשמר כנקודת שחזור ("שחזור מצב קודם" למעלה), ומומלץ גם להוריד גיבוי ("עוד" ← "גיבוי ושיתוף").
      </p>
      <div className="space-y-2">
        {opts.map((o) => (
          <label key={o.k} className="flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer"
            style={{ borderColor: scope === o.k ? C.late : C.line, background: scope === o.k ? "#FBEDE9" : "transparent" }}>
            <input type="radio" name="reset-scope" checked={scope === o.k} onChange={() => { setScope(o.k); setMsg(null); }} className="mt-1" />
            <span>
              <span className="block text-sm" style={{ fontWeight: 600 }}>{o.label}</span>
              <span className="block text-[11px]" style={{ color: C.muted }}>{o.sub}</span>
            </span>
          </label>
        ))}
      </div>

      {hasOwnerId ? (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <OwnerIdInput value={idNum} onChange={setIdNum} disabled={busy} />
          <button onClick={doReset} disabled={busy || !idLooksFull(idNum)} className="text-sm rounded-lg px-3 py-2"
            style={{ background: C.late, color: "#fff", opacity: busy || !idLooksFull(idNum) ? 0.5 : 1 }}>
            {busy ? "מוחק…" : scope === "all" ? "אישור — מחיקת הדיירים והנכסים" : "אישור — מחיקת הדיירים"}
          </button>
        </div>
      ) : (
        <div className="mt-3 text-[12px] rounded-lg p-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{OWNER_ID_HINT}</div>
      )}
      {msg && (
        <div className="mt-2 rounded-lg p-2 text-[12px]" style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/**
 * גודל הטקסט בפלטפורמה — בחירה מתוך שלושה גדלים, מוחלת מיד.
 *
 * ההגדלה ב-zoom על שורש המסמך: הטקסט והמיכל שסביבו גדלים יחד, ולכן שום
 * טקסט לא חורג מהמקום שבו הוא יושב. העדפה של המכשיר (localStorage) —
 * בטלפון אפשר גדול, במחשב רגיל — ולא נכתבת למסמך החשבון.
 */
function TextSizeCard() {
  const [scale, setScale] = useState(() => textScale());
  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-xl font-bold mb-1">גודל הטקסט</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        מגדיל את כל הפלטפורמה במכשיר הזה. הטקסט והמסגרות גדלים יחד,
        ולכן שום דבר לא נחתך ולא גולש — הבחירה נשמרת למכשיר הזה בלבד.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {TEXT_SCALES.map(({ v, label }) => (
          <button key={v} onClick={() => { setTextScale(v); setScale(v); }}
            className="rounded-lg px-4 py-2"
            style={{
              // התווית עצמה מדגימה את הגודל שתקבלו.
              fontSize: 14 * v,
              ...(scale === v
                ? { background: C.teal, color: "#fff" }
                : { border: `1px solid ${C.line}`, color: C.muted }),
            }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * ניווט לחיץ מהדשבורד. כשמופעל, שם הנכס בלוחות הגבייה הופך לקישור:
 * בשורת שכר הדירה — לעמוד הנכס; בשורות ועד הבית והצריכה — למונים
 * ולחשבונות של אותו נכס. כבוי כברירת מחדל, כדי לא לשנות התנהגות קיימת
 * בלי בחירה מפורשת — שם נכס היה תמיד טקסט בלבד.
 */
function DashboardNavCard({ data, setData }) {
  const on = !!data.settings?.clickablePropertyNames;
  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-xl font-bold mb-1">ניווט לחיץ בדשבורד</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        שם הנכס בלוחות הגבייה הופך לקישור: בשורת שכר הדירה — לעמוד הנכס;
        בשורות ועד הבית והצריכה — למונים ולחשבונות של אותו נכס.
      </p>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input type="checkbox" className="mt-1" checked={on}
          onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, clickablePropertyNames: e.target.checked } }))} />
        <span>שם הנכס בלוחות הגבייה לחיץ</span>
      </label>

      {/* כרטיסי המדדים למעלה בדשבורד (חוב שכר דירה, נגבה החודש וכו') —
          מתג נפרד, כי "חובות אבודים" כבר לחיץ תמיד (מוביל ל"דיירים
          וחובות") ואי אפשר לכבות התנהגות שכבר קיימת אצל מי שלא נגע
          במתג הזה. */}
      <label className="flex items-start gap-2 text-sm cursor-pointer mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
        <input type="checkbox" className="mt-1" checked={!!data.settings?.clickableDashboardStats}
          onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, clickableDashboardStats: e.target.checked } }))} />
        <span>כרטיסי המדדים בדשבורד (חוב שכר דירה, נגבה החודש וכו׳) לחיצים — פותחים רשימת נכסים מפורטת</span>
      </label>
    </div>
  );
}

/** רשימת נכס + סכום שנפתחת בלחיצה על כרטיס מדד בדשבורד. שורה לחיצה
    עוברת לכרטיס הנכס — אותו ניווט שכבר קיים מלוחות הגבייה. */
/* onOpenDetail — פותח את כרטיס החוב של הדייר (עם החודש הרלוונטי כבר
   פרוש) במקום לנווט לכרטיס הנכס; זו ברירת המחדל החדשה לכל כרטיסי
   הדשבורד חוץ מ"הוצאות החודש", שנשאר על הניווט הישן ל-onOpenProperty
   כי הוצאות בעל הנכס אינן חלק מכרטיס החוב של הדייר. */
function DashboardDrilldown({ title, rows, onOpenProperty, onOpenDetail, onClose }) {
  return (
    <Modal title={title} onClose={onClose}>
      {rows.length === 0 ? (
        <div className="text-sm text-center py-6" style={{ color: C.muted }}>אין נכסים עם סכום בסעיף הזה כרגע.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <button key={r.aptId}
              onClick={() => (onOpenDetail ? onOpenDetail(r.aptId) : (onClose(), onOpenProperty(r.aptId)))}
              className="w-full flex items-center justify-between gap-2 rounded-lg border p-3 text-sm text-right"
              style={{ borderColor: C.line }}>
              <span className="min-w-0">
                {/* שם הדייר בולט — זה מי שמזהים קודם; שם הנכס מתחתיו, קטן
                    יותר. בנכס פנוי (בלי דייר נוכחי) נשאר רק שם הנכס. */}
                <div className="font-bold truncate">{r.tenant || r.name}</div>
                {r.tenant && <div className="text-[11px] truncate" style={{ color: C.muted }}>{r.name}</div>}
              </span>
              <span className="disp font-bold shrink-0" style={{ color: C.teal }}>{nis(r.amount)}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* כותרת קטנה שמקבצת ויזואלית כמה כרטיסים תחת נושא אחד — "לעשות סדר"
   בלי לפרק את הכרטיסים עצמם או לגעת בהתנהגות שלהם. */
function SettingsGroupLabel({ children }) {
  return <div className="em-eyebrow tracking-widest mt-6 mb-1.5 first:mt-0" style={{ color: C.ochre }}>{children}</div>;
}

function Tariffs({ data, setData, run, accountId, setModal }) {
  const s = data.settings || defTariffs();
  /* הערך נשלח כפי שהוא: set_tariff כבר יודע להבדיל בין שדה מספרי לשדה
     טקסט (אמצעי תשלום). Number() כאן הפך "bit" ל-NaN והבחירה לא נתפסה. */
  const set = (k, v) => run([{ type: "set_tariff", tariff: k, value: v }]);

  const rows = [
    { k: "elecRate", label: "חשמל", unit: "₪ לקוט״ש", step: 0.01, icon: Zap },
    { k: "waterSewageRate", label: "מים וביוב", unit: "₪ למ״ק", step: 0.5, icon: Droplets },
    { k: "gasFixed", label: "גז — חיוב קבוע", unit: "₪ לחודש", step: 1, icon: Flame },
    { k: "gasRate", label: "גז — לפי מונה", unit: "₪ למ״ק (0 = ללא)", step: 0.1, icon: Flame },
    { k: "indexPct", label: "הצמדה למדד", unit: "% מקסימום לשנה", step: 0.5, icon: Settings },
    { k: "lateFeePct", label: "פיצוי על איחור", unit: "% לחודש", step: 0.5, icon: AlertTriangle },
    { k: "waterRateAbove", label: "מים מעבר למכסה", unit: "₪ למ״ק (0 = ללא)", step: 0.1, icon: Droplets },
    { k: "waterQuota", label: "מכסת מים", unit: "מ״ק לנפש לחודש", step: 0.5, icon: Droplets },
    { k: "depositMonths", label: "פיקדון (ברירת מחדל)", unit: "חודשי שכירות", step: 1, icon: Wallet },
    { k: "securityCheck", label: "שיק ביטחון (ברירת מחדל)", unit: "₪ (0 = ללא)", step: 100, icon: Receipt },
    { k: "paintDeposit", label: "פיקדון צבע (ברירת מחדל)", unit: "₪ (0 = ללא)", step: 100, icon: Receipt },
    { k: "cleaningDeposit", label: "פיקדון ניקיון (ברירת מחדל)", unit: "₪ (0 = ללא)", step: 100, icon: Receipt },
    { k: "noticeMonths", label: "הודעה מוקדמת", unit: "חודשים", step: 1, icon: CalendarPlus },
    { k: "lateVacateFee", label: "פיצוי איחור בפינוי", unit: "₪ ליום", step: 50, icon: AlertTriangle },
    { k: "taxCeiling", label: "תקרת הפטור ממס לשכ״ד", unit: "₪ לחודש", step: 1, icon: Wallet },
    { k: "taxMarginalRate", label: "שיעור המס השולי שלי", unit: "% (31 מתחת לגיל 60)", step: 1, icon: Receipt },
    { k: "paymentDay", label: "יום תשלום שכ״ד (ברירת מחדל)", unit: "1–28", step: 1, icon: CalendarPlus },
    { k: "billsDay", label: "יום תשלום חשבונות (ברירת מחדל)", unit: "1–28", step: 1, icon: Receipt },
    { k: "billsCycle", label: "מחזור חשבונות (ברירת מחדל)", unit: "1 = חודשי, 2 = דו-חודשי", step: 1, icon: Gauge },
  ];
  return (
    <section className="mt-6 space-y-4">
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="disp text-xl font-bold">תעריפים ותנאים</h2>
        {setModal && (
          <button onClick={() => setModal({ kind: "settings" })}
            className="rounded-lg px-3 py-1.5 text-xs flex items-center gap-1 shrink-0"
            style={{ border: `1px solid ${C.line}` }}>
            <ArrowUp size={13} /> סדר הנכסים
          </button>
        )}
      </div>
      <p className="text-xs mb-4" style={{ color: C.muted }}>מכאן נגזרים חישובי החשבונות, סעיפי החוזה והודעות הוואטסאפ. שינוי מועד תשלום או מחזור חיוב מתעדכן גם בנכסים שלא שינית להם ידנית. הביטחונות כאן הם ברירת מחדל בלבד — לכל דייר אפשר לבחור הרכב אחר בכניסת הדייר.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map(({ k, label, unit, step, icon: Icon }) => (
          <div key={k} className="rounded-lg border p-3 flex items-center justify-between gap-3" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-2 text-sm"><Icon size={15} style={{ color: C.muted }} /> {label}</div>
            <div className="flex items-center gap-2">
              <NumInput value={s[k]} step={step} onCommit={(v) => set(k, v === "" ? 0 : v)} style={{ ...inputStyle, width: 90, padding: "6px 8px", fontSize: 13 }} />
              <span className="text-[11px] w-24" style={{ color: C.muted }}>{unit}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ברירות המחדל לסימון תשלום. הן מה שהופך את הסימון החודשי ללחיצה
          אחת — האמצעי נרשם מעצמו, ומשנים אותו רק בחודש חריג. */}
      <div className="mt-5 pt-4 border-t" style={{ borderColor: C.line }}>
        <h3 className="text-sm mb-1" style={{ fontWeight: 600 }}>אמצעי תשלום — ברירת מחדל</h3>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          מה שנרשם כשמסמנים "התקבל", בלי לבחור בכל פעם. אפשר לשנות בכל תשלום בנפרד,
          והאמצעי נשמר על התשלום עצמו כדי שאפשר יהיה להצליב מול דף הבנק.
        </p>
        <MethodDefaults settings={s} onChange={(k, v) => set(k, v)} />
      </div>

      {/* אימות זהות לפני חתימת הדייר — הגנה על קישור החוזה שנשלח בוואטסאפ:
          מי שקיבל את הקישור (במקום הדייר) לא יכול לחתום בלי לדעת ת"ז/טלפון
          שרשומים אצל בעל הנכס. ברירת המחדל "בלי אימות" — כמו שהיה תמיד. */}
      <div className="mt-5 pt-4 border-t" style={{ borderColor: C.line }}>
        <h3 className="text-sm mb-1" style={{ fontWeight: 600 }}>אימות לפני חתימת הדייר על החוזה</h3>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          לפני שהדייר חותם, המערכת יכולה לבקש ממנו להקליד ת"ז או טלפון ולוודא שהם תואמים למה
          שרשום אצלכם — כדי שרק הדייר עצמו יוכל לחתום על החוזה שנשלח אליו.
        </p>
        <select style={{ ...inputStyle, maxWidth: 260 }} value={s.contractSignVerify || "none"}
          onChange={(e) => set("contractSignVerify", e.target.value)}>
          <option value="none">בלי אימות</option>
          <option value="tenantId">הקלדת מספר תעודת זהות</option>
          <option value="phone">הקלדת מספר טלפון</option>
        </select>
      </div>

      {/* סעיפי בית בחוזה. הנוסח כלל אותם קשיח לכל משתמש — שמירת שבת,
          איסור מנגל על הדשא, מערכת הגברה בחצר. אלה כללי בית של נכס
          מסוים, ולכן הם בחירה. */}
      <div className="mt-5 pt-4 border-t" style={{ borderColor: C.line }}>
        <h3 className="text-sm mb-1" style={{ fontWeight: 600 }}>סעיפי בית בחוזה</h3>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          נכנסים לנוסח החוזה של כל דייר חדש. חוזים שכבר נחתמו אינם משתנים.
        </p>
        <label className="flex items-start gap-2 text-sm mb-2 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={s.clauseShabbat !== false}
            onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, clauseShabbat: e.target.checked } }))} />
          <span>שמירת השבת ומועדי ישראל <span style={{ color: C.muted }}>— בלי עבודה רועשת, אש או מוזיקה הנשמעת מחוץ למושכר</span></span>
        </label>
        <label className="flex items-start gap-2 text-sm mb-3 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={s.clauseYard !== false}
            onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, clauseYard: e.target.checked } }))} />
          <span>שימוש במרפסות ובחצר <span style={{ color: C.muted }}>— מנגל ונרגילה, אש ליד בלוני הגז, פסולת, ומערכת הגברה</span></span>
        </label>
        <Field label="כללי בית נוספים — שורה לכל סעיף">
          <textarea value={s.houseRules || ""} rows={3}
            onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, houseRules: e.target.value } }))}
            placeholder={"חניה מספר 4 בלבד\nאין לתלות כביסה במרפסת הקדמית"}
            style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
      </div>

      {/* נוסח חוזה בכתיבה חופשית — ברירת מחדל. משפיע רק על "חוזה בכתיבה
          חופשית" חדש (מתחיל ממנה במקום מדף ריק); "חוזה חדש" הרגיל וחוזים
          קיימים ממשיכים בדיוק כמו היום. */}
      <div className="mt-5 pt-4 border-t" style={{ borderColor: C.line }}>
        <h3 className="text-sm mb-1" style={{ fontWeight: 600 }}>נוסח חוזה בכתיבה חופשית — ברירת מחדל</h3>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          כשממלאים כאן נוסח, "חוזה בכתיבה חופשית" (בלשונית חוזים ותיעוד) מתחיל ממנו במקום מדף ריק —
          אפשר לכלול בו גם את הנספחים (מטלטלין, ערבות) באותה תיבה. לא משנה חוזה קיים, ולא משפיע על "חוזה חדש" הרגיל שממשיך להיבנות אוטומטית מפרטי הנכס.
        </p>
        <textarea value={s.contractTemplate || ""} rows={8} dir="rtl"
          onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, contractTemplate: e.target.value } }))}
          placeholder="כתבו כאן את נוסח החוזה שברצונכם להתחיל ממנו בכל חוזה חדש בכתיבה חופשית…"
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.7, whiteSpace: "pre-wrap" }} />
      </div>

      </div>

      {/* תצוגה — העדפות מכשיר, לא מסמך החשבון. */}
      <SettingsGroupLabel>תצוגה</SettingsGroupLabel>
      <div className="space-y-4">
        <TextSizeCard />
        <DashboardNavCard data={data} setData={setData} />
      </div>

      {/* אבטחת החשבון. ישבה קודם באזור האישי וחצצה בין פרטי הרישום לבין
          פרטי התשלום והחתימה — שלושה שדות שממלאים ברצף אחד. סיסמה
          וטביעת אצבע הן הגדרות של החשבון ולא פרטים שנכנסים לחוזה. */}
      <SettingsGroupLabel>אבטחת החשבון</SettingsGroupLabel>
      <div className="space-y-4">
        <PasskeySettings />
        <PasswordCard />
      </div>

      {/* שחזור ומחיקה — בתחתית ההגדרות, מאחורי תעודת הזהות של בעל החשבון. */}
      <SettingsGroupLabel>נתונים</SettingsGroupLabel>
      <div className="space-y-4">
        <RestoreCard hasOwnerId={!!s.ownerId} />
        <DeleteDataCard hasOwnerId={!!s.ownerId} accountId={accountId} />
      </div>
    </section>
  );
}

/* ---------------- הוצאות ---------------- */
function Expenses({ data, setData, run }) {
  const [amount, setAmount] = useState(""), [aptId, setAptId] = useState("");
  const [cat, setCat] = useState(CATS[0]), [desc, setDesc] = useState("");
  /* חודש ההוצאה. עד כאן כל הוצאה נרשמה לחודש הנוכחי, ולכן חשבונית
     מיולי שנרשמה באוגוסט נכנסה לאוגוסט — ודוח ההכנסות של יולי יצא
     גבוה מדי. */
  const [month, setMonth] = useState(CUR_KEY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [filterApt, setFilterApt] = useState("");
  const [filterYear, setFilterYear] = useState(String(CUR_Y));
  const [showAll, setShowAll] = useState(false);

  const add = () => {
    if (!amount) return;
    run([{ type: "add_expense", apartment: aptId, cat, amount: Number(amount), desc, month }]);
    setAmount(""); setDesc("");
  };
  const aptName = (id) => data.apartments.find((a) => a.id === id)?.name || "כללי";

  const years = [...new Set((data.expenses || []).map((e) => String(e.month || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const filtered = (data.expenses || [])
    .filter((e) => (!filterApt || e.aptId === filterApt) && (filterYear === "all" || String(e.month || "").startsWith(filterYear)))
    .sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
  const total = filtered.reduce((t, e) => t + Number(e.amount || 0), 0);
  const shown = showAll ? filtered : filtered.slice(0, 12);

  const monthOptions = [];
  for (let i = 0; i < 18; i++) {
    const dt = new Date(CUR_Y, CUR_M - 1 - i, 1);
    monthOptions.push(mKey(dt.getFullYear(), dt.getMonth() + 1));
  }

  return (
    <section className="mt-6 grid lg:grid-cols-2 gap-4">
      <RecurringExpensesCard data={data} run={run} />

      <div className="rounded-xl border p-4 self-start" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-3">הוצאה חדשה</h2>
        <div className="grid grid-cols-2 gap-2">
          {/* "כללי" ראשון וכברירת מחדל: ביטוח מבנה ודמי ניהול אינם שייכים
              לנכס אחד, והקוד ידע לרשום אותם כך — רק לא הייתה דרך לבחור. */}
          <Field label="נכס">
            <select style={inputStyle} value={aptId} onChange={(e) => setAptId(e.target.value)}>
              <option value="">כללי — לא משויך לנכס</option>
              {data.apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="קטגוריה">
            <select style={inputStyle} value={cat} onChange={(e) => setCat(e.target.value)}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          </Field>
          <Field label="סכום">
            <input style={inputStyle} type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="חודש">
            <select style={inputStyle} value={month} onChange={(e) => setMonth(e.target.value)}>
              {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-2"><Field label="על מה?"><input style={inputStyle} placeholder="תיקון דוד, ביטוח מבנה…" value={desc} onChange={(e) => setDesc(e.target.value)} /></Field></div>
        <button onClick={add} disabled={!amount} className="mt-3 w-full rounded-lg py-2 text-sm" style={{ background: C.ochre, color: "#fff", opacity: amount ? 1 : 0.5 }}>הוספת הוצאה</button>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="disp text-xl font-bold">הוצאות</h2>
          <div className="text-left">
            <div className="disp text-lg font-bold" style={{ color: C.ochre }}>{nis(total)}</div>
            <div className="text-[11px]" style={{ color: C.muted }}>{filtered.length} רשומות</div>
          </div>
        </div>

        {/* סינון. קודם הוצגו עשר האחרונות בלבד, בלי דרך לראות את השאר
            ובלי סכום — והמספר היחיד היה בדוח השנתי. */}
        <div className="flex flex-wrap gap-2 mb-3">
          <select style={{ ...inputStyle, width: "auto", flex: 1, minWidth: 140, fontSize: 13, padding: "6px 8px" }}
            value={filterApt} onChange={(e) => { setFilterApt(e.target.value); setShowAll(false); }}>
            <option value="">כל הנכסים</option>
            <option value="">— כללי —</option>
            {data.apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select style={{ ...inputStyle, width: "auto", fontSize: 13, padding: "6px 8px" }}
            value={filterYear} onChange={(e) => { setFilterYear(e.target.value); setShowAll(false); }}>
            <option value="all">כל השנים</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div className="divide-y" style={{ borderColor: C.line }}>
          {filtered.length === 0 && <div className="text-sm py-6 text-center" style={{ color: C.muted }}>אין הוצאות בטווח שנבחר.</div>}
          {shown.map((e) => (
            <div key={e.id} className="py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate">{e.cat}{e.desc && ` · ${e.desc}`}</div>
                  <div className="text-[11px]" style={{ color: C.muted }}>{aptName(e.aptId)} · {monthLabel(e.month)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{nis(e.amount)}</span>
                  <button onClick={() => setConfirmDel(confirmDel === e.id ? null : e.id)} aria-label="מחיקה" style={{ color: confirmDel === e.id ? C.late : C.muted }}><Trash2 size={14} /></button>
                </div>
              </div>
              {/* מחיקה בלחיצה אחת מחקה רשומה שנרשמה לפני חודשים בלי דרך
                  חזרה; האישור כאן הוא שורה אחת ולא חלון. */}
              {confirmDel === e.id && (
                <div className="mt-1 flex items-center justify-end gap-2 text-[11px]">
                  <span style={{ color: C.muted }}>למחוק את ההוצאה?</span>
                  <button onClick={() => { setData((d) => ({ ...d, expenses: d.expenses.filter((x) => x.id !== e.id) })); setConfirmDel(null); }}
                    className="rounded-md px-2 py-1" style={{ background: C.late, color: "#fff" }}>מחיקה</button>
                  <button onClick={() => setConfirmDel(null)} className="rounded-md px-2 py-1" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {filtered.length > shown.length && (
          <button onClick={() => setShowAll(true)} className="mt-2 text-xs" style={{ color: C.teal }}>
            הצגת כל {filtered.length} הרשומות
          </button>
        )}
      </div>
    </section>
  );
}

/* הוצאה קבועה — ועד בית, ארנונה, הלוואה עם תאריך סיום וכו'. במקום להזין
   כל חודש ידנית, התבנית נרשמת פעם אחת והשרת (migrate) מייצר אוטומטית
   רשומת הוצאה רגילה לכל חודש שעובר — ראו materializeRecurringExpenses
   ב-shared/domain.js. */
function RecurringExpenseForm({ data, item, onSave, onCancel }) {
  const [aptIds, setAptIds] = useState(item?.aptIds || []);
  const [cat, setCat] = useState(item?.cat || CATS[0]);
  const [amount, setAmount] = useState(item ? String(item.amount) : "");
  const [desc, setDesc] = useState(item?.desc || "");
  const [startMonth, setStartMonth] = useState(item?.startMonth || CUR_KEY);
  const [limited, setLimited] = useState(!!item?.endMonth);
  const [endMonth, setEndMonth] = useState(item?.endMonth || "");

  const toggleApt = (id) => setAptIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const save = () => {
    if (!(Number(amount) > 0)) return;
    onSave({
      ...(item ? { id: item.id } : {}),
      aptIds, cat, amount: Number(amount), desc,
      startMonth, endMonth: limited ? (endMonth || null) : null,
    });
  };

  return (
    <div className="rounded-lg border p-3 mt-2" style={{ borderColor: C.line, background: "#FBFCFB" }}>
      <div className="grid grid-cols-2 gap-2">
        <Field label="קטגוריה">
          <select style={inputStyle} value={cat} onChange={(e) => setCat(e.target.value)}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label={aptIds.length > 1 ? "סכום כולל (₪)" : "סכום (₪)"}>
          <input style={inputStyle} type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="מתחיל בחודש">
          <input type="month" style={inputStyle} value={startMonth} onChange={(e) => setStartMonth(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm self-end pb-2">
          <input type="checkbox" checked={limited} onChange={(e) => setLimited(e.target.checked)} />
          מוגבל בזמן (כמו הלוואה)
        </label>
        {limited && (
          <Field label="מסתיים בחודש">
            <input type="month" style={inputStyle} value={endMonth} onChange={(e) => setEndMonth(e.target.value)} />
          </Field>
        )}
      </div>

      <div className="mt-2">
        <Field label="על מה? (רשות)"><input style={inputStyle} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="למשל שם הבנק / חברת הביטוח" /></Field>
      </div>

      <div className="mt-2">
        <div className="text-xs mb-1" style={{ color: C.muted }}>נכסים (בלי לסמן = כללי, לא משויך)</div>
        <div className="flex flex-wrap gap-2">
          {data.apartments.map((a) => (
            <label key={a.id} className="flex items-center gap-1 text-xs rounded-lg px-2 py-1 border" style={{ borderColor: C.line }}>
              <input type="checkbox" checked={aptIds.includes(a.id)} onChange={() => toggleApt(a.id)} />
              {a.name}
            </label>
          ))}
        </div>
        {aptIds.length > 1 && (
          <div className="text-[11px] mt-1" style={{ color: C.muted }}>
            הסכום יתחלק שווה בשווה בין {aptIds.length} הנכסים — {nis(Math.round((Number(amount) || 0) / aptIds.length))} לכל נכס.
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-3">
        <button onClick={save} disabled={!(Number(amount) > 0)} className="text-sm rounded-lg px-3 py-2"
          style={{ background: C.teal, color: "#fff", opacity: Number(amount) > 0 ? 1 : 0.5 }}>
          {item ? "שמירת השינויים" : "הוספה"}
        </button>
        <button onClick={onCancel} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
      </div>
    </div>
  );
}

function RecurringExpensesCard({ data, run }) {
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [confirmDel, setConfirmDel] = useState(null);
  const list = data.recurringExpenses || [];
  const aptName = (id) => data.apartments.find((a) => a.id === id)?.name || "כללי";

  const save = (payload) => { run([{ type: "set_recurring_expense", ...payload }]); setEditing(null); };
  const del = (id) => { run([{ type: "remove_recurring_expense", id }]); setConfirmDel(null); };

  return (
    <div className="rounded-xl border p-4 lg:col-span-2" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="disp text-xl font-bold">הוצאות קבועות</h2>
        {editing !== "new" && (
          <button onClick={() => setEditing("new")} className="text-xs rounded-lg px-2.5 py-1.5" style={{ background: C.ochre, color: "#fff" }}>
            + הוצאה קבועה
          </button>
        )}
      </div>
      <p className="text-xs mt-1 mb-2" style={{ color: C.muted }}>
        ועד בית, ארנונה, הלוואה ועוד — נרשמת פעם אחת ונכנסת אוטומטית לרשימת ההוצאות בכל חודש.
      </p>

      {list.length === 0 && editing !== "new" && (
        <div className="text-sm py-2" style={{ color: C.muted }}>אין הוצאות קבועות מוגדרות.</div>
      )}

      <div className="divide-y" style={{ borderColor: C.line }}>
        {list.map((r) => (
          editing === r.id ? (
            <div key={r.id} className="py-2">
              <RecurringExpenseForm data={data} item={r} onSave={save} onCancel={() => setEditing(null)} />
            </div>
          ) : (
            <div key={r.id} className="py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate">{r.cat}{r.desc && ` · ${r.desc}`} · {nis(r.amount)}{(r.aptIds || []).length > 1 ? ` (מתחלק ל-${r.aptIds.length} נכסים)` : ""}</div>
                  <div className="text-[11px]" style={{ color: C.muted }}>
                    {(r.aptIds || []).length ? r.aptIds.map(aptName).join(", ") : "כללי"} · מ-{monthLabel(r.startMonth)}{r.endMonth ? ` עד ${monthLabel(r.endMonth)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setEditing(r.id)} aria-label="עריכה" style={{ color: C.muted }}><Pencil size={14} /></button>
                  <button onClick={() => setConfirmDel(confirmDel === r.id ? null : r.id)} aria-label="מחיקה" style={{ color: confirmDel === r.id ? C.late : C.muted }}><Trash2 size={14} /></button>
                </div>
              </div>
              {confirmDel === r.id && (
                <div className="mt-1 flex items-center justify-end gap-2 text-[11px]">
                  <span style={{ color: C.muted }}>למחוק את ההוצאה הקבועה? הוצאות שכבר נוצרו ממנה יישארו.</span>
                  <button onClick={() => del(r.id)} className="rounded-md px-2 py-1" style={{ background: C.late, color: "#fff" }}>מחיקה</button>
                  <button onClick={() => setConfirmDel(null)} className="rounded-md px-2 py-1" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
                </div>
              )}
            </div>
          )
        ))}
      </div>

      {editing === "new" && <RecurringExpenseForm data={data} item={null} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

/* ---------------- אזור אישי של בעל הנכס ---------------- */
/**
 * מצב ההצפנה של צילומי תעודות הזהות.
 * מוצג בפרופיל בעל הנכס כי זו הגדרה של החשבון, לא של נכס בודד.
 */
function VaultCard({ ownerId = "" }) {
  const [state, setState] = useState("loading"); // loading | none | locked | open | unsupported
  const [setup, setSetup] = useState(false);

  const sync = () => {
    if (!vault.isAvailable()) return setState("unsupported");
    vault.loadKeys(true)
      .then(() => setState(!vault.hasKeys() ? "none" : vault.isUnlocked() ? "open" : "locked"))
      .catch(() => setState("none"));
  };

  useEffect(() => {
    sync();
    return vault.onVaultChange(() => setState(vault.isUnlocked() ? "open" : "locked"));
  }, []);

  /* "פתיחה" מנסה קודם את תעודת הזהות שכבר בהגדרות — רוב הפתיחות לא
     צריכות דיאלוג בכלל. הדיאלוג נשאר לכספות ישנות (סיסמה) ולאי-התאמה. */
  const openVault = async () => {
    if (await vault.autoUnlock(ownerId)) return;
    window.dispatchEvent(new CustomEvent("vault:unlock"));
  };

  const body = {
    loading: "בודק…",
    unsupported: "הצפנה אינה זמינה בדפדפן הזה. נדרש חיבור מאובטח (https).",
    none: "צילומי תעודות הזהות נשמרים כרגע בלי הצפנה. הפעלה תמנע מכל מי שיש לו גישה לשרת או לגיבוי לפתוח אותם.",
    locked: "ההצפנה פעילה. הצילומים נפתחים עם תעודת הזהות של בעל הנכס.",
    open: "ההצפנה פעילה והצילומים פתוחים לצפייה בלשונית הזו.",
  }[state];

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-xl font-bold mb-1">צילומי תעודות זהות</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>{body}</p>

      {state === "none" && (
        <button onClick={() => setSetup(true)} className="rounded-lg px-3 py-2 text-sm"
          style={{ background: C.teal, color: "#fff" }}>
          הפעלת הצפנה
        </button>
      )}
      {state === "locked" && (
        <button onClick={openVault}
          className="rounded-lg px-3 py-2 text-sm" style={{ border: `1px solid ${C.line}` }}>
          פתיחה
        </button>
      )}
      {state === "open" && (
        <button onClick={() => vault.lock()} className="rounded-lg px-3 py-2 text-sm"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          נעילה
        </button>
      )}

      {state === "open" && <EncryptExisting />}
      {setup && <VaultSetup ownerId={ownerId} onClose={() => { setSetup(false); sync(); }} />}
    </div>
  );
}

/**
 * הצפנה בדיעבד של צילומי ת"ז שהגיעו מהייבוא ונשמרו בגלוי.
 * מוצג רק כשהכספת פתוחה, כי ההצפנה עצמה דורשת את המפתח הציבורי בלבד
 * אבל העדכון של המסמך דורש שהמשתמש באמת נמצא כאן.
 */
function EncryptExisting() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const run = async () => {
    setBusy(true); setMsg("");
    try {
      const { doc, version } = await dataApi.load();
      const ids = idPhotoIdsOf(doc);
      if (!ids.length) { setMsg("אין צילומי תעודות זהות לא מוצפנים."); setBusy(false); return; }

      const map = await vault.encryptExisting(ids, {
        onProgress: (n, total) => setMsg(`מצפין ${n}/${total}…`),
      });
      const count = Object.keys(map).length;
      if (!count) { setMsg("הכול כבר מוצפן."); setBusy(false); return; }

      const next = remapPhotoIds(doc, map);
      await dataApi.save(next, version);
      setMsg(`${count} צילומים הוצפנו. רעננו את הדף.`);
    } catch (e) {
      setMsg(`ההצפנה נכשלה: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
      <button onClick={run} disabled={busy} className="text-xs rounded-lg px-2.5 py-1.5"
        style={{ border: `1px solid ${C.line}`, color: C.muted }}>
        {busy ? "עובד…" : "הצפנת צילומים ישנים מהייבוא"}
      </button>
      {msg && <div className="text-[11px] mt-2" style={{ color: C.muted }}>{msg}</div>}
    </div>
  );
}

/** רק מזהי צילומי תעודות זהות — לא מונים ולא חתימות. */
const idPhotoIdsOf = (d) => {
  const ids = new Set();
  const add = (v) => { if (v && typeof v === "string") ids.add(v); };
  const walk = (h) => {
    const k = h?.intake || {};
    add(k.tenantIdPhoto);
    add(k.partnerIdPhoto);
    (h?.guarantors || []).forEach((g) => add(g.idPhoto));
  };
  (d.apartments || []).forEach(walk);
  (d.tenancies || []).forEach(walk);
  return [...ids];
};

function OwnerProfile({ data, setData, stats }) {
  const s = data.settings || defTariffs();
  const set = (k) => (e) => setData((d) => ({ ...d, settings: { ...d.settings, [k]: e.target.value } }));
  const missing = [!s.ownerName && "שם", !s.ownerId && "תעודת זהות", !s.ownerPhone && "טלפון", !s.ownerAddress && "כתובת"].filter(Boolean);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);
  const [msg, setMsg] = useState("");

  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    const src = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"), { x, y } = pos(e);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = C.ink;
    ctx.lineTo(x, y); ctx.stroke();
    if (!hasSig) setHasSig(true);
  };
  const end = () => { drawing.current = false; };

  /* החתימה נשמרת כקובץ ולא כ-dataURL בתוך המסמך.
     קודם היא נשמרה בגוף הנתונים, וזה עבד כשהמסמך ישב בדפדפן. עכשיו הוא
     נשלח לשרת בכל שמירה, וכל תמונה שמוטמעת בו נשלחת שוב ושוב איתו. */
  const storeSig = async (dataUrl) => {
    try {
      const id = await uploadImage(dataUrl, { scope: "owner", kind: "signature" });
      setData((d) => ({ ...d, settings: { ...d.settings, ownerSignatureId: id, ownerSignature: null } }));
      setHasSig(false);
      setMsg("החתימה נשמרה ותופיע על החוזים");
    } catch (err) {
      setMsg(`שמירת החתימה נכשלה: ${err.message}`);
    }
    setTimeout(() => setMsg(""), 3000);
  };

  const saveSig = async () => {
    const c = canvasRef.current;
    if (!c) { setMsg("שדה החתימה לא נטען. נסו לרענן את העמוד."); return; }
    if (!canvasHasInk(c)) { setMsg("צריך לחתום בתוך המסגרת לפני השמירה."); return; }
    await storeSig(c.toDataURL("image/png"));
  };

  const uploadSig = async (file) => {
    if (!file) return;
    try {
      await storeSig(await compressForUpload(file, { max: 800, q: 0.8 }));
    } catch { setMsg("העלאת קובץ החתימה נכשלה"); }
  };

  const monthlyIncome = data.apartments.filter((a) => a.tenant).reduce((t, a) => t + Number(a.rent || 0), 0);

  return (
    <section className="mt-6 space-y-4">
      {msg && <div className="rounded-lg p-2.5 text-sm" style={{ background: "#EAF3EE", color: C.paid }}>{msg}</div>}

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <div className="flex items-center gap-2 mb-1">
          <User size={17} style={{ color: C.ochre }} />
          <h2 className="disp text-xl font-bold">פרטי הרישום שלי</h2>
        </div>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          נכנסים אוטומטית לכל חוזה כצד המשכיר, ואליהם פונה הדייר מהאזור האישי שלו.
          {missing.length ? ` חסר עדיין: ${missing.join(", ")}.` : ""}
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="שם מלא"><input style={inputStyle} value={s.ownerName || ""} onChange={set("ownerName")} placeholder="השם שיופיע בחוזה" /></Field>
          <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={s.ownerId || ""} onChange={set("ownerId")} /></Field>
          <Field label="טלפון"><PhoneField value={s.ownerPhone} onChange={(v) => setData((d) => ({ ...d, settings: { ...d.settings, ownerPhone: v } }))} /></Field>
          <Field label="דוא״ל"><input style={inputStyle} value={s.ownerEmail || ""} onChange={set("ownerEmail")} /></Field>
          <Field label="כתובת / יישוב"><input style={inputStyle} value={s.ownerAddress || ""} onChange={set("ownerAddress")} placeholder="גם מקום עריכת החוזה" /></Field>
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">פרטי תשלום</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>יצורפו להודעות התשלום שנשלחות לדיירים</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="בנק"><input style={inputStyle} value={s.payBank || ""} onChange={set("payBank")} /></Field>
          <Field label="סניף"><input style={inputStyle} value={s.payBranch || ""} onChange={set("payBranch")} /></Field>
          <Field label="מספר חשבון"><input style={inputStyle} value={s.payAccount || ""} onChange={set("payAccount")} /></Field>
          <Field label="ביט / פייבוקס"><input style={inputStyle} value={s.payBit || ""} onChange={set("payBit")} placeholder="מספר טלפון" /></Field>
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">החתימה שלי</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>תופיע על החוזים לצד חתימת הדייר</p>
        {(s.ownerSignature || s.ownerSignatureId) && (
          <div className="flex items-center gap-3 mb-3">
            {s.ownerSignature
              ? <img src={s.ownerSignature} alt="חתימה" className="rounded border" style={{ height: 56, borderColor: C.line }} />
              : <StoredImage id={s.ownerSignatureId} className="rounded border" style={{ height: 56, borderColor: C.line }} />}
            <button onClick={() => setData((d) => ({ ...d, settings: { ...d.settings, ownerSignature: null, ownerSignatureId: null } }))}
              className="text-xs" style={{ color: C.late }}>מחיקה וחתימה מחדש</button>
          </div>
        )}
        {!(s.ownerSignature || s.ownerSignatureId) && (
          <>
            <canvas ref={canvasRef} width={640} height={160} className="w-full rounded-lg border touch-none"
              style={{ borderColor: hasSig ? C.teal : C.line, background: "#fff", height: 130 }}
              onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={draw} onTouchEnd={end} onTouchCancel={end} />
            <div className="flex gap-2 mt-2">
              <button onClick={saveSig} className="flex-1 rounded-lg py-2 text-sm" style={{ background: hasSig ? C.teal : "#8FA9A7", color: "#fff" }}>שמירת החתימה</button>
              <button onClick={() => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false); setMsg(""); }}
                className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}` }}>ניקוי</button>
              <FilePick title="העלאת חתימה סרוקה" onPick={uploadSig} className="rounded-lg px-3 text-sm gap-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                <Upload size={15} />
              </FilePick>
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>
              ציירו באצבע או בעכבר ולחצו שמירה. אפשר גם להעלות תמונה של חתימה סרוקה.
            </p>
          </>
        )}
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-3">התיק שלי</h2>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><div className="disp text-2xl font-bold">{data.apartments.length}</div><div className="text-[11px]" style={{ color: C.muted }}>נכסים</div></div>
          <div><div className="disp text-2xl font-bold">{stats.occ}</div><div className="text-[11px]" style={{ color: C.muted }}>מושכרים</div></div>
          <div><div className="disp text-2xl font-bold">{nis(monthlyIncome)}</div><div className="text-[11px]" style={{ color: C.muted }}>הכנסה חודשית</div></div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- דוחות שנתיים ---------------- */
const REPORT_SOURCES = [
  { k: "rent", label: "שכר דירה" },
  { k: "elec", label: "חשמל" },
  { k: "water", label: "מים וביוב" },
  { k: "gas", label: "גז" },
  { k: "vaad", label: "ועד בית" },
  { k: "arnona", label: "ארנונה" },
  { k: "municipal", label: "היטל עירוני" },
];

/* סכומים שהתקבלו בפועל בחודש נתון, מפוצלים לפי מקור */
/**
 * כניסה מהירה — ניהול המכשירים שמורשים להיכנס בטביעת אצבע.
 *
 * מה שנשמר בשרת הוא מפתח ציבורי בלבד. טביעת האצבע נשארת במכשיר ולא
 * מגיעה לשרת בשום שלב, ולכן "הסרה" כאן היא הסרת ההרשאה של המכשיר —
 * לא מחיקה של נתון ביומטרי אצלנו, כי אין כזה.
 */
function PasskeySettings() {
  const [items, setItems] = useState(null);
  const [supported, setSupported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try { setItems((await passkey.list()).credentials); }
    catch { setItems([]); }
  };

  useEffect(() => {
    /* במכשיר בלי חיישן/תמיכה הרשימה לא נטענה והכרטיס נשאר על "טוען…"
       לנצח. אין תמיכה = רשימה ריקה, והמסך אומר שהמכשיר לא מתאים. */
    passkey.hasDeviceSensor()
      .then((ok) => { setSupported(ok); if (!ok) setItems((cur) => cur ?? []); })
      .catch(() => { setSupported(false); setItems((cur) => cur ?? []); });
    load().catch(() => setItems((cur) => cur ?? []));
  }, []);

  const add = async () => {
    setBusy(true); setMsg(null);
    try {
      await passkey.enroll(deviceLabel());
      setMsg({ ok: true, text: "מעכשיו אפשר להיכנס במכשיר הזה בטביעת אצבע." });
      await load();
    } catch (e) {
      if (!e.cancelled) setMsg({ ok: false, text: e.message });
    }
    setBusy(false);
  };

  const drop = async (id) => {
    setBusy(true); setMsg(null);
    try {
      await passkey.remove(id);
      const left = (await passkey.list()).credentials;
      setItems(left);
      // בלי אף מכשיר רשום — כפתור טביעת האצבע במסך הכניסה יורד גם הוא.
      if (!left.length) passkey.forgetKnown();
      setMsg({ ok: true, text: "המכשיר הוסר. כניסה משם תדרוש שוב סיסמה." });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center gap-2 mb-1">
        <Fingerprint size={17} style={{ color: C.ochre }} />
        <h2 className="disp text-xl font-bold">כניסה מהירה</h2>
      </div>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        במקום להקליד סיסמה בכל כניסה — טביעת אצבע, זיהוי פנים או קוד המכשיר.
        הסיסמה ממשיכה לעבוד תמיד, וכך נכנסים ממכשיר חדש או אחרי החלפת מכשיר.
      </p>

      {items === null ? (
        <div className="text-xs" style={{ color: C.muted }}>טוען…</div>
      ) : items.length === 0 ? (
        <div className="text-xs" style={{ color: C.muted }}>עדיין לא הוגדר אף מכשיר.</div>
      ) : (
        <div className="divide-y" style={{ borderColor: C.line }}>
          {items.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <div style={{ fontWeight: 600 }}>{c.label}</div>
                <div className="text-[11px]" style={{ color: C.muted }}>
                  הוגדר {heDate(c.createdAt)}
                  {c.lastUsedAt ? ` · שימוש אחרון ${heDate(c.lastUsedAt)}` : " · טרם היה בשימוש"}
                </div>
              </div>
              <button onClick={() => drop(c.id)} disabled={busy}
                className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.late }}>
                הסרה
              </button>
            </div>
          ))}
        </div>
      )}

      {supported ? (
        <button onClick={add} disabled={busy}
          className="mt-3 rounded-lg px-3 py-2 text-sm flex items-center gap-2"
          style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
          <Fingerprint size={15} /> {busy ? "ממתין למכשיר…" : "הוספת המכשיר הזה"}
        </button>
      ) : (
        <div className="mt-3 text-[11px]" style={{ color: C.muted }}>
          במכשיר הזה אין חיישן מתאים, או שהדף אינו בחיבור מאובטח (https).
        </div>
      )}

      {msg && (
        <div className="mt-2 rounded-lg p-2.5 text-sm"
          style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/** שם קריא למכשיר, כדי שברשימה יהיה אפשר להבדיל בין שניים. */
function deviceLabel() {
  const ua = navigator.userAgent || "";
  const os = /iPhone|iPad/.test(ua) ? "iPhone"
    : /Android/.test(ua) ? "Android"
      : /Mac/.test(ua) ? "Mac"
        : /Windows/.test(ua) ? "Windows"
          : "מכשיר";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /Chrome\//.test(ua) ? "Chrome"
      : /Safari\//.test(ua) ? "Safari"
        : /Firefox\//.test(ua) ? "Firefox"
          : "";
  return browser ? `${os} · ${browser}` : os;
}

function monthIncome(d, aptId, mk_) {
  const b = d.bills[rowKey(aptId, mk_)] || emptyBill();
  return {
    rent: rentPaidOf(d, aptId, mk_),
    elec: itemPaid(b.elec),
    water: itemPaid(b.water) + itemPaid(b.sewage),
    gas: itemPaid(b.gas),
    vaad: itemPaid(b.vaad),
    arnona: itemPaid(b.arnona),
    municipal: itemPaid(b.municipal),
  };
}

/* ============================================================================
   דוח חובות פתוחים.

   שאר הדוחות מסתכלים על מה שנכנס. זה מסתכל על מה שלא — לפי נכס ולפי חודש,
   כולל דיירים שכבר עזבו והשאירו חוב. זה גם התיעוד: כל חודש שלא שולם מופיע
   כאן עם הסכום והרכבו, ואפשר להדפיס אותו כמסמך אחד.
   ============================================================================ */
/* ============================================================================
   פילוח לפי אמצעי תשלום.

   זה דוח ההצלבה: הוראות קבע והעברות אמורות להופיע בדף הבנק, ביט ופייבוקס
   בדוחות של האפליקציות, ומזומן לא מופיע בשום מקום חוץ מכאן — ולכן הוא
   מסומן בנפרד. "לא נרשם" הם תשלומים ישנים, מלפני שהשדה הזה היה קיים.
   ============================================================================ */
function MethodReport({ data, apts, years }) {
  const { rows, total } = useMemo(() => {
    const ys = [...years].sort();
    return methodBreakdown(data, {
      aptIds: apts.map((a) => a.id),
      from: `${ys[0]}-01`,
      to: `${ys[ys.length - 1]}-12`,
    });
  }, [data, apts, years]);

  if (!total) return null;

  const cash = rows.filter((r) => !r.traceable).reduce((t, r) => t + r.amount, 0);

  return (
    <div className="rounded-xl border p-4" style={{ background: "#fff", borderColor: C.line }}>
      <div className="flex items-start justify-between border-b pb-3 mb-3" style={{ borderColor: C.line }}>
        <div>
          <h2 className="disp text-xl font-bold">לפי אמצעי תשלום</h2>
          <div className="text-xs mt-0.5" style={{ color: C.muted }}>
            מה שנרשם כהתקבל · {years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : years[0]}
          </div>
        </div>
        <div className="disp text-2xl font-bold">{nis(total)}</div>
      </div>

      <div className="space-y-1.5">
        {rows.map((r) => {
          const pct = Math.round((r.amount / total) * 100);
          return (
            <div key={r.k}>
              <div className="flex items-center justify-between text-[12px]">
                <span style={{ color: r.k === "unknown" ? C.ochre : C.ink }}>
                  {r.label}
                  {!r.traceable && r.k !== "unknown" && (
                    <span className="text-[10px]" style={{ color: C.muted }}> · לא מופיע בדף הבנק</span>
                  )}
                </span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {nis(r.amount)} <span style={{ color: C.muted }}>· {pct}%</span>
                </span>
              </div>
              <div className="mt-0.5 rounded-full" style={{ height: 4, background: "#EEF1EE" }}>
                <div className="rounded-full" style={{
                  height: 4, width: `${pct}%`,
                  background: r.k === "unknown" ? C.ochre : r.traceable ? C.teal : C.ochre,
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {cash > 0 && (
        <p className="text-[11px] mt-3" style={{ color: C.muted }}>
          {nis(cash)} התקבלו באמצעים שאינם מותירים תיעוד חיצוני. הרישום כאן הוא
          התיעוד היחיד שלהם.
        </p>
      )}
      {rows.some((r) => r.k === "unknown") && (
        <p className="text-[11px] mt-1" style={{ color: C.ochre }}>
          "לא נרשם" הם תשלומים שסומנו לפני שאמצעי התשלום נשמר. אפשר להשלים אותם
          בכרטיס החוב של כל נכס.
        </p>
      )}
    </div>
  );
}

function ArrearsReport({ data, apts, standalone = true }) {
  const [open, setOpen] = useState(false);

  const rows = useMemo(() => {
    const out = [];
    for (const a of apts) {
      /* הדייר הנוכחי (אם יש) ואחריו כל תקופה סגורה שנשאר בה חוב.
         קודם נוסף כאן גם debtStatement בלי tenancyId, שבנכס פנוי נופל
         לתקופה האחרונה — והחוב של מי שעזב נספר פעמיים ("ללא דייר" + שמו). */
      const cur = currentDebt(data, a, CUR_KEY);
      if (cur && cur.statement.total > 0) out.push({ apt: a, who: cur.tenancy.tenant, closed: false, st: cur.statement });
      for (const { tenancy, statement } of pastDebts(data, a, CUR_KEY)) {
        out.push({ apt: a, who: tenancy.tenant, closed: true, st: statement, tenancy });
      }
    }
    return out.sort((x, y) => y.st.total - x.st.total);
  }, [data, apts]);

  /* חובות שסומנו בסגירה כאבודים או כוויתור — לא חוב פתוח, אבל ראוי
     שיופיעו בדוח כסיכום: זה הכסף שלא ייגבה. */
  const resolved = useMemo(() => apts.flatMap((a) =>
    resolvedDebts(data, a, CUR_KEY).map(({ tenancy, statement }) => ({ apt: a, tenancy, st: statement }))), [data, apts]);

  const total = rows.reduce((t, r) => t + r.st.total, 0);
  const rentTotal = rows.reduce((t, r) => t + r.st.rentDue, 0);
  const billsTotal = rows.reduce((t, r) => t + r.st.billsDue, 0);

  return (
    <div className={`rounded-xl border p-4 ${standalone ? "print-area" : ""}`}
      style={{ background: "#fff", borderColor: C.line, breakBefore: standalone ? "auto" : "page" }}>
      <div className="flex items-start justify-between border-b pb-3 mb-3" style={{ borderColor: C.line }}>
        <div>
          <h2 className="disp text-xl font-bold">חובות פתוחים</h2>
          <div className="text-xs mt-0.5" style={{ color: C.muted }}>
            <span>נכון ל-</span><span dir="ltr">{heDate(TODAY)}</span>
            <span> · {rows.length ? `${rows.length} ${rows.length === 1 ? "דייר" : "דיירים"}` : "אין חוב פתוח"}</span>
          </div>
        </div>
        {total > 0 && (
          <div className="text-left">
            <div className="disp text-2xl font-bold" style={{ color: C.late }}>{nis(total)}</div>
            <div className="text-[11px]" style={{ color: C.muted }}>
              שכ״ד {nis(rentTotal)} · חשבונות {nis(billsTotal)}
            </div>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm" style={{ color: C.paid }}>כל החיובים שולמו. אין חוב פתוח באף נכס.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: C.muted }}>
                  <th className="text-right py-1.5 font-normal">נכס</th>
                  <th className="text-right py-1.5 font-normal">דייר</th>
                  <th className="text-right py-1.5 font-normal">מאז</th>
                  <th className="text-right py-1.5 font-normal">חודשים</th>
                  <th className="text-right py-1.5 font-normal">שכ״ד</th>
                  <th className="text-right py-1.5 font-normal">חשבונות</th>
                  <th className="text-right py-1.5 font-normal">סה״כ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
                    <td className="py-1.5">{r.apt.name}</td>
                    <td>
                      {r.who}
                      {r.closed && <span className="text-[10px]" style={{ color: C.ochre }}> · עזב/ה</span>}
                    </td>
                    <td>{monthLabel(r.st.since)}</td>
                    <td>{r.st.months.filter((x) => x.due > 0).length}</td>
                    <td>{r.st.rentDue ? nis(r.st.rentDue) : "—"}</td>
                    <td>{r.st.billsDue ? nis(r.st.billsDue) : "—"}</td>
                    <td style={{ fontWeight: 600, color: C.late }}>{nis(r.st.total)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${C.ink}` }}>
                  <td className="py-1.5" colSpan={4} style={{ fontWeight: 600 }}>סה״כ</td>
                  <td style={{ fontWeight: 600 }}>{nis(rentTotal)}</td>
                  <td style={{ fontWeight: 600 }}>{nis(billsTotal)}</td>
                  <td style={{ fontWeight: 700, color: C.late }}>{nis(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <button onClick={() => setOpen(!open)} className="mt-3 text-xs no-print" style={{ color: C.teal }}>
            {open ? "הסתרת הפירוט לפי חודש" : "פירוט לפי חודש"}
          </button>

          {/* בהדפסה הפירוט תמיד מופיע — הדוח המודפס הוא המסמך, ו"לחצו
              להרחבה" חסר משמעות על נייר. */}
          <div className={open ? "" : "hidden print:block"}>
            {rows.map((r, i) => (
              <div key={i} className="mt-3 pt-2" style={{ borderTop: `1px solid ${C.line}` }}>
                <div className="text-[12px] mb-1" style={{ fontWeight: 600 }}>
                  {r.apt.name} — {r.who}
                  {r.closed && <span style={{ color: C.ochre, fontWeight: 400 }}> (עזב/ה {heDate(r.tenancy?.closedAt || r.tenancy?.leaseEnd)} — חוב של הדייר)</span>}
                </div>
                {r.st.months.filter((x) => x.due > 0).map((x) => (
                  <div key={x.month} className="flex items-center justify-between text-[11px] py-0.5">
                    <span style={{ color: C.muted }}>
                      {monthLabel(x.month)}
                      {x.rent.due > 0 && ` · שכר דירה ${nis(x.rent.due)}`}
                      {x.bills.due > 0 && ` · חשבונות ${nis(x.bills.due)}`}
                      {x.paid > 0 && ` · שולם ${nis(x.paid)} מתוך ${nis(x.charged)}`}
                    </span>
                    <b>{nis(x.due)}</b>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {resolved.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
          <div className="text-sm mb-1" style={{ fontWeight: 600 }}>חובות שלא ייגבו</div>
          <p className="text-[11px] mb-2" style={{ color: C.muted }}>
            סומנו בסגירת התקופה כחוב אבוד או כוויתור. אינם נכללים בסך החוב הפתוח.
          </p>
          {resolved.map(({ apt: a, tenancy: t, st }) => (
            <div key={t.id} className="flex items-center justify-between text-[12px] py-1" style={{ borderTop: `1px solid ${C.line}` }}>
              <span>
                {a.name} — {t.tenant}
                <span style={{ color: C.muted }}> · {DEBT_RESOLUTIONS.find((x) => x.k === t.debtResolution?.kind)?.label || ""}{t.debtResolution?.date ? ` · ${heDate(t.debtResolution.date)}` : ""}{t.debtResolution?.notes ? ` · ${t.debtResolution.notes}` : ""}</span>
              </span>
              <b style={{ color: C.muted }}>{nis(st.total)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   מרכז הדיירים והחובות — "עוד" ← דיירים וחובות.

   הרעיון: חוב של דייר קודם לא רודף את הדייר החדש. כשמחליפים דייר, החוב
   נשאר על התקופה שלו — וכאן המקום היחיד שבו רואים ומנהלים את כולו:
   כל הדיירים של כל נכס (או נכס אחד), כמה שכר דירה וחשבונות נגבו מכל
   אחד, מי השאיר חוב פתוח, סימון חוב אבוד / ויתור, תזכורת בוואטסאפ,
   ומחיקת היסטוריה — עם או בלי שמירת הכספים בדוחות.
   ============================================================================ */
/* סינון בבחירה מרובה: כמה נכסים וכמה סטטוסים בו-זמנית, בלי צורך לבחור
   אחד ולוותר על האחר. "חייבים" הוא אחד מהסטטוסים ולא צ'קבוקס נפרד —
   כך אפשר "חייבים בהרצל 12 ובעכו עכו" באותה תצוגה.
   הלשונית היא היסטוריה של מי שכבר לא גר בנכסים — הדייר הפעיל של כל
   נכס מנוהל בדשבורד ובעמוד הנכס, ולכן אין כאן סטטוס "פעיל"/"עתידי". */
const STATUS_FILTERS = [
  { k: "debt", label: "חייבים", match: (r) => r.st.total > 0 && !r.resolved },
  { k: "lost", label: "אבוד / ויתור", match: (r) => !!r.resolved },
  { k: "past", label: "הסתיים", match: (r) => r.status === "past" },
  { k: "deleted", label: "נמחק", match: (r) => r.status === "deleted" },
];

function TenantsCenter({ data, setData, run, saveNow, setTenantHistory }) {
  // קבוצה ריקה = הכול; בחירה מוסיפה/מסירה מהקבוצה, לא מחליפה אותה.
  const [aptIds, setAptIds] = useState(() => new Set());
  const [statusIds, setStatusIds] = useState(() => new Set());
  // בחירה מרובה של שמות דיירים, לא חיפוש חופשי — "דנה וגם יוסי" בו-זמנית.
  const [nameIds, setNameIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(null);   // מזהה תקופה שפתוחה למחיקה
  const [delNotes, setDelNotes] = useState("");
  const [purge, setPurge] = useState(false);
  // אילו כרטיסי דייר פתוחים לקריאת מונה אחרונה וחישוב חשבון (מזהי תקופה).
  const [meterOpenIds, setMeterOpenIds] = useState(() => new Set());

  const toggleIn = (setFn, id) => setFn((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const apts = aptIds.size ? data.apartments.filter((a) => aptIds.has(a.id)) : data.apartments;

  /* מה נגבה בפועל מכל תקופה — שכר דירה לחוד וחשבונות לחוד, לפי החודשים
     ששייכים לה. אותה שיטת ייחוס כמו בדוחות (tenancyForMonth). */
  const collectedOf = (a, t) => {
    let rent = 0, bills = 0;
    for (let y = 2020; y <= CUR_Y + 1; y++) {
      for (let m = 1; m <= 12; m++) {
        const mk_ = mKey(y, m);
        if (tenancyForMonth(data, a.id, mk_)?.id !== t.id) continue;
        const inc = monthIncome(data, a.id, mk_);
        rent += inc.rent;
        bills += REPORT_SOURCES.filter((x) => x.k !== "rent").reduce((s, x) => s + inc[x.k], 0);
      }
    }
    return { rent, bills };
  };

  /* הלשונית היא היסטוריה: רק מי שכבר לא גר בנכס (הסתיים) או שנמחק.
     הדייר הפעיל ודייר עתידי שטרם נכנס מנוהלים בדשבורד ובעמוד הנכס —
     הפרדה מוחלטת אומרת שהם לא מופיעים כאן, לא רק שם. */
  const rows = [];
  apts.forEach((a) => {
    const cur = currentTenancy(data, a.id);
    [...tenanciesOf(data, a.id), ...deletedTenancies(data, a.id)].forEach((t) => {
      if (!t.tenant) return;
      const status = t.deleted ? "deleted"
        : cur?.id === t.id ? "active"
          : !t.closed && t.leaseStart > TODAY ? "future" : "past";
      if (status === "active" || status === "future") return;
      const st = debtStatement(data, a, CUR_KEY, { tenancyId: t.id });
      const col = collectedOf(a, t);
      const resolved = t.debtResolution && t.debtResolution.kind !== "tenant"
        ? DEBT_RESOLUTIONS.find((x) => x.k === t.debtResolution.kind) || null
        : null;
      rows.push({ a, t, st, col, resolved, status });
    });
  });
  rows.sort((x, y) => (x.t.leaseStart < y.t.leaseStart ? 1 : -1));
  const tenantNameOptions = [...new Set(rows.map((r) => r.t.tenant))].sort((x, y) => x.localeCompare(y, "he"));
  const shown = rows.filter((r) => {
    if (statusIds.size && ![...statusIds].some((k) => STATUS_FILTERS.find((x) => x.k === k)?.match(r))) return false;
    if (nameIds.size && !nameIds.has(r.t.tenant)) return false;
    return true;
  });

  /* המספרים שבשבילם נכנסים: כמה נגבה בסך הכול מדיירים לשעבר, כמה חוב
     פתוח נשאר, וכמה נרשם כאבוד/ויתור. */
  const totCollected = rows.reduce((s, r) => s + r.col.rent + r.col.bills, 0);
  const totOpenPast = rows.filter((r) => !r.resolved).reduce((s, r) => s + r.st.total, 0);
  const totLost = rows.filter((r) => r.resolved)
    .reduce((s, r) => s + (Number(r.t.debtResolution?.amount) || r.st.total || 0), 0);

  const STATUS = {
    past: { label: "הסתיים", bg: "#F1F4F1", color: C.muted },
    deleted: { label: "נמחק", bg: "#EEEFED", color: C.muted },
  };

  return (
    <section className="mt-6 space-y-4">
      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">היסטוריית דיירים וחובות</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          כאן רואים רק מי שכבר לא גר בנכסים — מה נגבה מכל אחד, מי השאיר חוב, וכאן מסמנים
          חוב אבוד או מוחקים היסטוריה. הדייר הפעיל של כל נכס מנוהל בדשבורד ובעמוד הנכס.
        </p>

        <div className="grid grid-cols-3 gap-3 text-center mb-3">
          <div><div className="disp text-lg font-bold" style={{ color: C.paid }}>{nis(totCollected)}</div><div className="text-[11px]" style={{ color: C.muted }}>נגבה מדיירים לשעבר</div></div>
          <div><div className="disp text-lg font-bold" style={{ color: C.late }}>{nis(totOpenPast)}</div><div className="text-[11px]" style={{ color: C.muted }}>חוב פתוח של דיירים שעזבו</div></div>
          <div><div className="disp text-lg font-bold" style={{ color: C.muted }}>{nis(totLost)}</div><div className="text-[11px]" style={{ color: C.muted }}>אבוד / ויתור</div></div>
        </div>

        {/* בחירה מרובה: כמה נכסים, כמה סטטוסים וכמה דיירים בו-זמנית.
            "כל הנכסים" מרוקן את הבחירה במקום להיות עוד אפשרות בתוכה. */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setAptIds(new Set())} className="text-xs rounded-full px-3 py-1.5"
            style={!aptIds.size ? { background: C.ink, color: "#fff", border: `1px solid ${C.ink}` } : { border: `1px solid ${C.line}`, color: C.muted }}>
            כל הנכסים
          </button>
          {data.apartments.map((a) => (
            <button key={a.id} onClick={() => toggleIn(setAptIds, a.id)} className="text-xs rounded-full px-3 py-1.5"
              style={aptIds.has(a.id) ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` } : { border: `1px solid ${C.line}`, color: C.muted }}>
              {a.name}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {STATUS_FILTERS.map(({ k, label }) => (
            <button key={k} onClick={() => toggleIn(setStatusIds, k)} className="text-xs rounded-full px-3 py-1.5 flex items-center gap-1"
              style={statusIds.has(k)
                ? { background: k === "debt" ? C.late : C.teal, color: "#fff", border: `1px solid ${k === "debt" ? C.late : C.teal}` }
                : { border: `1px solid ${k === "debt" ? C.late : C.line}`, color: k === "debt" ? C.late : C.muted }}>
              {k === "debt" && <AlertTriangle size={11} />} {label}
            </button>
          ))}
          <TenantNameCombo options={tenantNameOptions} selected={nameIds} onToggle={(n) => toggleIn(setNameIds, n)} onClear={() => setNameIds(new Set())} />
        </div>
      </div>

      {shown.length === 0 && (
        <div className="rounded-xl border p-8 text-center text-sm" style={{ background: C.card, borderColor: C.line, color: C.muted }}>
          {statusIds.size || nameIds.size ? "אין דיירים התואמים לסינון." : "אין היסטוריית דיירים להצגה."}
        </div>
      )}

      {shown.map(({ a, t, st, col, resolved, status }) => {
        const stx = STATUS[status];
        const openDebt = st.total > 0 && !resolved;
        const meterOpen = meterOpenIds.has(t.id);
        return (
          <div key={t.id} className="rounded-xl border p-4" style={{ background: C.card, borderColor: openDebt ? "#EBC7BF" : C.line }}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm" style={{ fontWeight: 700 }}>{t.tenant}</div>
                <div className="text-[11px]" style={{ color: C.muted }}>
                  {a.name} · {heDate(t.leaseStart)} — {t.closed ? heDate(t.closedAt || t.leaseEnd) : t.leaseEnd ? heDate(t.leaseEnd) : "ללא מועד סיום"}
                  {t.phone ? ` · ${t.phone}` : ""}
                </div>
                {t.deleteNotes && <div className="text-[11px] mt-0.5" style={{ color: C.late }}>הערת מחיקה: {t.deleteNotes}</div>}
              </div>
              <span className="text-[11px] rounded-full px-2 py-0.5 shrink-0" style={{ background: stx.bg, color: stx.color }}>{stx.label}</span>
            </div>

            {/* המספרים של הדייר: מה נגבה, מה נשאר, מה נרשם כאבוד. */}
            <div className="grid grid-cols-3 gap-2 text-center mt-3 rounded-lg p-2" style={{ background: "#FBFCFB" }}>
              <div><div className="text-[13px]" style={{ fontWeight: 700 }}>{nis(col.rent)}</div><div className="text-[10px]" style={{ color: C.muted }}>שכר דירה ששולם</div></div>
              <div><div className="text-[13px]" style={{ fontWeight: 700 }}>{nis(col.bills)}</div><div className="text-[10px]" style={{ color: C.muted }}>חשבונות ששולמו</div></div>
              <div>
                {resolved ? (
                  <>
                    <div className="text-[13px]" style={{ fontWeight: 700, color: C.muted, textDecoration: "line-through" }}>{nis(Number(t.debtResolution?.amount) || st.total)}</div>
                    <div className="text-[10px]" style={{ color: C.muted }}>{resolved.label}</div>
                  </>
                ) : (
                  <>
                    <div className="text-[13px]" style={{ fontWeight: 700, color: st.total > 0 ? C.late : C.paid }}>{st.total > 0 ? nis(st.total) : "—"}</div>
                    <div className="text-[10px]" style={{ color: C.muted }}>{st.total > 0 ? "חוב פתוח" : "אין חוב"}</div>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* ייעוד החוב — אבוד / ויתור / נשאר של הדייר. גם אחרי שנים. */}
              {t.closed && !t.deleted && st.total > 0 && (
                <select value={t.debtResolution?.kind || "tenant"} aria-label="ייעוד החוב"
                  onChange={(e) => run([{ type: "resolve_tenancy_debt", apartment: a.id, tenancyId: t.id, kind: e.target.value, amount: st.total }])}
                  className="text-[11px] rounded-lg px-2 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.ink, background: "#fff" }}>
                  {DEBT_RESOLUTIONS.map((x) => <option key={x.k} value={x.k}>{x.label}</option>)}
                </select>
              )}
              {openDebt && status === "past" && t.phone && (
                <a href={waLink(t.phone, `שלום ${t.tenant}, על תקופת השכירות שלך ב${a.name} נשאר חוב פתוח בסך ${nis(st.total)}. אשמח להסדרת התשלום. תודה.`)}
                  target="_blank" rel="noreferrer"
                  className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: "#128C7E", color: "#fff" }}>
                  <MessageCircle size={11} /> תזכורת חוב בוואטסאפ
                </a>
              )}
              {/* המסך המאוחד: כרטיס חוב פר-תקופה (פירוט חודש-חודש של מה
                  שחויב ומה שהתקבל), ועוד תכתובת מהתקופה וקיצור לתיעוד
                  הנכס — הכול במקום אחד. כפתור ה"היסטוריית תשלומים וחובות"
                  הישן נמחק: הוא פתח בדיוק את אותה טבלה, בלי התכתובת
                  והתיעוד — תת-קבוצה מיותרת של הכפתור הזה. */}
              <button onClick={() => setTenantHistory({ aptId: a.id, tenancyId: t.id })}
                className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                <History size={11} /> היסטוריה מלאה
              </button>
              {/* קריאת מונה אחרונה וחישוב חשבון — לדייר שעזב/הוחלף באמצע
                  החודש: המסך הרגיל של מונים וחשבונות כבר לא מציג עליו
                  כלום (הפרדה מוחלטת), אז זה המקום היחיד שנשאר לתעד קריאה
                  אחרונה ולחשב ממנה את חשבון הצריכה הסופי. */}
              {status === "past" && (
                <button onClick={() => setMeterOpenIds((s) => {
                  const next = new Set(s);
                  if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                  return next;
                })}
                  className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                  <Gauge size={11} /> {meterOpen ? "סגירת קריאת מונה" : "קריאת מונה וחישוב חשבון"}
                </button>
              )}
              {t.deleted ? (
                <button onClick={() => run([{ type: "restore_tenancy", apartment: a.id, tenancyId: t.id }])}
                  className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                  <Undo2 size={11} /> שחזור
                </button>
              ) : (
                <button onClick={() => { setDeleting(t.id); setDelNotes(""); setPurge(false); }}
                  className="text-[11px] rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.late }}>
                  <Trash2 size={11} /> מחיקת ההיסטוריה
                </button>
              )}
            </div>

            {meterOpen && status === "past" && (
              <PastTenantMeters apt={a} tenancy={t} data={data} setData={setData} run={run} />
            )}

            {deleting === t.id && (
              <DeleteTenancy
                tenant={t}
                income={col.rent + col.bills}
                notes={delNotes} setNotes={setDelNotes}
                purge={purge} setPurge={setPurge}
                onCancel={() => setDeleting(null)}
                onConfirm={() => {
                  /* מחיקה לצמיתות מוחקת גם את ההתכתבות — אותו חלון זמן
                     כמו במסך התקופות: מסוף התקופה הקודמת ועד תחילת הבאה. */
                  if (purge) {
                    const others = (data.tenancies || []).filter((x) => x.aptId === a.id && x.id !== t.id);
                    const prevEnd = others.filter((x) => x.leaseStart < t.leaseStart)
                      .map((x) => x.closedAt || x.leaseEnd || x.leaseStart).sort().slice(-1)[0] || null;
                    const nextStart = others.filter((x) => x.leaseStart > t.leaseStart).map((x) => x.leaseStart).sort()[0] || null;
                    const iso = (d) => { const x = d ? new Date(`${d}T00:00:00`) : null; return x && !Number.isNaN(x.getTime()) ? x.toISOString() : null; };
                    dataApi.notificationsForgetTenancy({ apartmentId: a.id, tenancyId: t.id, from: iso(prevEnd), until: iso(nextStart) })
                      .catch(() => { /* ההתכתבות תישאר בתיבה — אפשר למחוק משם ידנית */ });
                  }
                  run([{ type: "remove_tenancy", apartment: a.id, tenancyId: t.id, purge, notes: delNotes }]);
                  setDeleting(null); setDelNotes(""); setPurge(false);
                }}
              />
            )}
          </div>
        );
      })}
    </section>
  );
}

/* קומבו לבחירה מרובה של שמות דיירים — מ"חיפוש חופשי" ליכולת לסמן כמה
   דיירים בו-זמנית ("דנה וגם יוסי"). רשימה קטנה, בלי אפשרות שרת: פותח
   כלפי מטה עם חיפוש פנימי וסימון עם checkbox, וסוגר בלחיצה מחוץ לו. */
function TenantNameCombo({ options, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const term = q.trim();
  const filtered = term ? options.filter((n) => n.includes(term)) : options;
  const label = !selected.size ? "כל הדיירים" : selected.size === 1 ? [...selected][0] : `${selected.size} דיירים נבחרו`;
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs rounded-full px-3 py-1.5 flex items-center gap-1"
        style={selected.size ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` } : { border: `1px solid ${C.line}`, color: C.muted }}>
        {label} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 rounded-lg border p-2" style={{ background: "#fff", borderColor: C.line, minWidth: 220, maxHeight: 280, overflowY: "auto", boxShadow: "0 4px 16px rgba(0,0,0,.12)" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש דייר" autoFocus
            style={{ ...inputStyle, padding: "5px 8px", fontSize: 12, marginBottom: 6 }} />
          {selected.size > 0 && (
            <button type="button" onClick={onClear} className="text-[11px] mb-1 block" style={{ color: C.teal }}>ניקוי הבחירה</button>
          )}
          {options.length === 0 && <div className="text-[11px] p-1" style={{ color: C.muted }}>אין דיירים להצגה</div>}
          {options.length > 0 && filtered.length === 0 && <div className="text-[11px] p-1" style={{ color: C.muted }}>לא נמצא</div>}
          {filtered.map((n) => (
            <label key={n} className="flex items-center gap-2 text-xs py-1 px-1 rounded cursor-pointer">
              <input type="checkbox" checked={selected.has(n)} onChange={() => onToggle(n)} />
              <span className="truncate">{n}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* קריאת מונה אחרונה וחישוב חשבון לדייר שעזב/הוחלף באמצע החודש — מסך
   "מונים וחשבונות" כבר לא מציג עליו כלום (הפרדה מוחלטת מדייר נוכחי),
   אז זה המקום שממנו מזינים את הקריאה הסופית ומחשבים ממנה את חשבון
   הצריכה של החודש שבו התקופה הסתיימה. */
function PastTenantMeters({ apt, tenancy, data, setData, run }) {
  const s = data.settings || defTariffs();
  const end = tenancyEnd(tenancy);
  const month = end ? end.slice(0, 7) : null;
  const showMeterHere = (meter) => {
    if (utilityMode(apt, meter) !== "landlord") return false;
    return meter !== "gas"
      || apt?.utilities?.gas === "landlord"
      || rateGas(apt, s) > 0
      || readingsOf(data, apt.id, "gas").length > 0;
  };
  const meters = month ? METERS.filter((m) => showMeterHere(m) && billsMeter(apt, s, m)) : [];

  if (!month || meters.length === 0) {
    return (
      <div className="mt-3 rounded-lg border p-3 text-[12px]" style={{ borderColor: C.line, background: "#FBFCFB", color: C.muted }}>
        {!month ? "אין תאריך סיום רשום לתקופה הזו — אי אפשר לדעת לאיזה חודש שייכת הקריאה." : "אין מונה שמחויב דרך בעל הנכס לתקופה הזו."}
      </div>
    );
  }

  const handleAutoPrice = () => {
    const ok = computeConsumptionBills(data, run, apt, s, month);
    if (!ok) alert(`אין עדיין מספיק קריאות לחישוב הצריכה של ${monthLabel(month)}.`);
  };

  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: C.line, background: "#FBFCFB" }}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[12px]" style={{ color: C.muted }}>
          קריאה אחרונה וחישוב חשבון הצריכה של {tenancy.tenant} — {monthLabel(month)} (חודש הסיום)
        </div>
        <button onClick={handleAutoPrice} className="text-[11px] rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          חישוב לפי צריכה
        </button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {meters.map((meter) => (
          <MeterBlock key={meter} apt={apt} meter={meter} data={data} month={month}
            rate={rateFor(apt, s, meter, `${month}-28`)}
            billed={billsMeter(apt, s, meter)}
            onAdd={(aptId, m, value, date, photoId) => recordMeterReading(setData, aptId, m, value, date, photoId)}
            onEdit={(id, patch) => updateMeterReading(setData, id, patch)}
            onDelete={(id) => deleteMeterReading(setData, id)} />
        ))}
      </div>
    </div>
  );
}

/* ============================================================================
   אומדן המס השנתי על שכר הדירה — שלושת המסלולים זה מול זה.

   מחושב תמיד על כל הנכסים יחד, גם כשהדוח מסונן לנכס אחד: המס הוא של
   בעל הנכס ולא של נכס בודד, ותקרת הפטור נבחנת על סך ההכנסה ממגורים.
   ============================================================================ */
function TaxReport({ data, years }) {
  const th = { padding: "6px 8px", fontSize: 11, color: C.muted, fontWeight: 400, textAlign: "right", borderBottom: `1px solid ${C.line}` };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid #EEF1EE` };

  const list = years
    .filter((y) => y <= CUR_Y)
    .map((y) => rentTax(data, y))
    .filter((t) => t.residential > 0 || t.other > 0);
  if (!list.length) return null;

  return (
    <>
      {list.map((t) => (
        <div key={t.year} className="rounded-xl border p-4" style={{ background: "#fff", borderColor: C.line }}>
          <div className="flex items-start justify-between mb-1">
            <h2 className="disp text-xl font-bold">אומדן מס — שכר דירה {t.year}</h2>
            {t.tracks[t.best].tax === 0 ? (
              <span className="text-[11px] rounded-full px-2 py-0.5 shrink-0" style={{ background: "#EAF3EE", color: C.paid }}>פטור מלא ממס</span>
            ) : (
              <span className="text-[11px] rounded-full px-2 py-0.5 shrink-0" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
                המשתלם: {TAX_TRACKS.find((x) => x.k === t.best)?.label}
              </span>
            )}
          </div>
          <div className="text-xs mb-3" style={{ color: C.muted }}>
            הכנסה ממגורים שנגבתה {nis(t.residential)}
            {t.expenses > 0 && <> · הוצאות שנרשמו {nis(t.expenses)}</>}
            {t.year === CUR_Y && <> · השנה עדיין רצה — האומדן על מה שנגבה עד כה</>}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>מסלול</th>
                <th style={th}>הכנסה חייבת</th>
                <th style={{ ...th, fontWeight: 600, color: C.ink }}>מס משוער</th>
              </tr>
            </thead>
            <tbody>
              {TAX_TRACKS.map(({ k, label, hint }) => {
                const row = t.tracks[k];
                const isBest = k === t.best;
                return (
                  <tr key={k} style={isBest ? { background: "#F4FAF6" } : undefined}>
                    <td style={{ ...td, fontWeight: isBest ? 600 : 400 }}>
                      {label}
                      <div className="text-[10px]" style={{ color: C.muted, fontWeight: 400 }}>{hint}</div>
                    </td>
                    <td style={td}>{row.taxable ? nis(row.taxable) : "—"}</td>
                    <td style={{ ...td, fontWeight: 600, color: isBest ? C.paid : C.ink }}>{nis(row.tax)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="text-[11px] mt-3 space-y-1" style={{ color: C.muted }}>
            {t.overCeilingMonths.length > 0 ? (
              <div>
                ב-{t.overCeilingMonths.length === 1 ? "חודש אחד" : `${t.overCeilingMonths.length} חודשים`} ההכנסה ממגורים עברה את
                תקרת הפטור ({nis(t.ceiling)} לחודש) — במסלול הפטור כל שקל מעל התקרה מקטין את הפטור בשקל.
              </div>
            ) : t.residential > 0 && (
              <div>כל החודשים מתחת לתקרת הפטור ({nis(t.ceiling)} לחודש) — במסלול הפטור אין מס ואין חובת דיווח שוטפת.</div>
            )}
            {t.other > 0 && (
              <div>
                בנוסף נגבו {nis(t.other)} מנכסים שאינם למגורים (חנות / מחסן / משרד) — עליהם אין פטור ואין מסלול 10%,
                והם ממוסים במס השולי. אינם כלולים בטבלה.
              </div>
            )}
            <div>במסלול 10% המס משולם עד 30 בינואר {t.year + 1}. חישובי הפטור והמס השולי לפי {t.marginalPct}% — שיעור המס השולי שלך ותקרת הפטור ניתנים לעדכון ב"תעריפים ותנאים".</div>
            <div>אומדן בלבד על בסיס מה שנרשם במערכת — אינו ייעוץ מס ואינו תחליף לרואה חשבון; פחת, ניכויים נוספים ותנאי הזכאות למסלולים לא נבדקים כאן.</div>
          </div>
        </div>
      ))}
    </>
  );
}

function Reports({ data }) {
  const [years, setYears] = useState([CUR_Y]);
  const [aptIds, setAptIds] = useState([]);   // ריק = כל הנכסים
  const [picked, setPicked] = useState(["rent"]);

  const yearList = [...years].sort((a, b) => a - b);
  const allApts = aptIds.length === 0;
  const apts = allApts ? data.apartments : data.apartments.filter((a) => aptIds.includes(a.id));
  const year = yearList[yearList.length - 1] || CUR_Y;
  const aptId = allApts ? "all" : aptIds[0];

  const toggleYear = (y) => setYears((p) => (p.includes(y) ? (p.length > 1 ? p.filter((x) => x !== y) : p) : [...p, y]));
  const toggleApt = (id) => setAptIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const active = REPORT_SOURCES.filter((x) => picked.includes(x.k));

  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const allOn = picked.length === REPORT_SOURCES.length;

  // מטריצה: שורה לחודש, עמודה למקור
  const rows = useMemo(() => {
    const out = [];
    yearList.forEach((y) => {
      for (let m = 1; m <= 12; m++) {
        const mk_ = mKey(y, m);
        const cell = { key: mk_, year: y, month: m, total: 0 };
        REPORT_SOURCES.forEach(({ k }) => { cell[k] = 0; });
        apts.forEach((a) => {
          const inc = monthIncome(data, a.id, mk_);
          REPORT_SOURCES.forEach(({ k }) => { cell[k] += inc[k]; });
        });
        cell.total = active.reduce((t, x) => t + cell[x.k], 0);
        out.push(cell);
      }
    });
    return out;
  }, [data, years, aptIds, picked]);

  const totals = useMemo(() => {
    const t = { total: 0 };
    REPORT_SOURCES.forEach(({ k }) => { t[k] = rows.reduce((sum, r) => sum + r[k], 0); });
    t.total = active.reduce((sum, x) => sum + t[x.k], 0);
    return t;
  }, [rows, picked]);

  // סיכום שנתי כששנה אחת לא מספיקה
  const byYear = useMemo(() => yearList.map((y) => {
    const r = { year: y, total: 0 };
    REPORT_SOURCES.forEach(({ k }) => { r[k] = rows.filter((x) => x.year === y).reduce((sum, x) => sum + x[k], 0); });
    r.total = active.reduce((sum, x) => sum + r[x.k], 0);
    return r;
  }), [rows, picked]);

  // פירוט לפי נכס
  const perApt = useMemo(() => apts.map((a) => {
    const r = { name: a.name, tenant: a.tenant, total: 0 };
    REPORT_SOURCES.forEach(({ k }) => { r[k] = 0; });
    /* מי שגר כאן היום (a.tenant) הוא לא בהכרח מי שהרוויח מכספו של הנכס
       בשנה המדווחת: דייר שעזב באמצע השנה מייצר הכנסה בחודשים שלו, והנכס
       יכול כבר להיות מאוכלס בדייר הבא (או ריק) בזמן שמריצים את הדוח.
       כאן עוקבים אחרי מי שהחודש שבו נרשמה הכנסה שייך לו בפועל (כמו
       ב-perTenant למטה), כדי לייחס את הסכום לשם הנכון. */
    const earners = new Set();
    yearList.forEach((y) => {
      for (let m = 1; m <= 12; m++) {
        const mk_ = mKey(y, m);
        const inc = monthIncome(data, a.id, mk_);
        REPORT_SOURCES.forEach(({ k }) => { r[k] += inc[k]; });
        if (Object.values(inc).some((v) => v)) {
          const ten = tenancyForMonth(data, a.id, mk_);
          if (ten?.tenant) earners.add(ten.tenant);
        }
      }
    });
    if (earners.size) r.tenant = [...earners].join(" · ");
    r.total = active.reduce((t, x) => t + r[x.k], 0);
    return r;
  }), [data, years, aptIds, picked]);

  // הכנסה לפי דייר — כל תקופת שכירות בנפרד, כולל דיירים שכבר עזבו
  const perTenant = useMemo(() => {
    const out = [];
    (data.tenancies || []).filter((t) => t.tenant && (allApts || aptIds.includes(t.aptId))).forEach((t) => {
      const apt = data.apartments.find((a) => a.id === t.aptId);
      const r = { id: t.id, tenant: t.tenant, aptName: apt?.name || "", from: t.leaseStart, to: t.closed ? (t.closedAt || t.leaseEnd) : t.leaseEnd, total: 0 };
      REPORT_SOURCES.forEach(({ k }) => { r[k] = 0; });
      yearList.forEach((y) => {
        for (let m = 1; m <= 12; m++) {
          const mk_ = mKey(y, m);
          if (tenancyForMonth(data, t.aptId, mk_)?.id !== t.id) continue;
          const inc = monthIncome(data, t.aptId, mk_);
          REPORT_SOURCES.forEach(({ k }) => { r[k] += inc[k]; });
        }
      });
      r.total = active.reduce((sum, x) => sum + r[x.k], 0);
      if (r.total > 0) out.push(r);
    });
    return out.sort((a, b) => (a.from < b.from ? -1 : 1));
  }, [data, years, aptIds, picked]);

  const expenses = useMemo(() => data.expenses
    .filter((e) => yearList.includes(Number(String(e.month || "").slice(0, 4))) && (allApts || aptIds.includes(e.aptId))), [data, years, aptIds]);
  const expTotal = expenses.reduce((t, e) => t + Number(e.amount || 0), 0);

  const chart = rows.map((r) => ({ name: yearList.length > 1 ? `${MONTHS_SHORT[r.month - 1]} ${String(r.year).slice(2)}` : MONTHS_SHORT[r.month - 1], "הכנסה": r.total }));

  /* כמה חודשים באמת נכללים בדוח: שנה שעברה = 12, השנה הנוכחית = עד
     החודש הזה. שנה עתידית לא מוסיפה מכנה. */
  const avgMonths = Math.max(1, yearList.reduce((t, y) => t + (y < CUR_Y ? 12 : y === CUR_Y ? CUR_M : 0), 0));

  const th = { padding: "6px 8px", fontSize: 11, color: C.muted, fontWeight: 400, textAlign: "right", borderBottom: `1px solid ${C.line}` };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid #EEF1EE` };

  return (
    <section className="mt-6 space-y-4">
      <div className="rounded-xl border p-4 no-print" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-3">בניית הדוח</h2>

        <div className="text-xs mb-2" style={{ color: C.muted }}>שנים — אפשר לבחור כמה</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {[CUR_Y + 1, CUR_Y, CUR_Y - 1, CUR_Y - 2, CUR_Y - 3, CUR_Y - 4].map((y) => (
            <button key={y} onClick={() => toggleYear(y)} className="text-xs rounded-full px-3 py-1.5"
              style={years.includes(y)
                ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` }
                : { border: `1px solid ${C.line}`, color: C.muted }}>
              {y}
            </button>
          ))}
        </div>

        <div className="text-xs mb-2" style={{ color: C.muted }}>נכסים — ריק = כל הנכסים</div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setAptIds([])} className="text-xs rounded-full px-3 py-1.5"
            style={allApts
              ? { background: C.ink, color: "#fff", border: `1px solid ${C.ink}` }
              : { border: `1px solid ${C.line}`, color: C.muted }}>
            כל הנכסים
          </button>
          {data.apartments.map((a) => (
            <button key={a.id} onClick={() => toggleApt(a.id)} className="text-xs rounded-full px-3 py-1.5"
              style={aptIds.includes(a.id)
                ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` }
                : { border: `1px solid ${C.line}`, color: C.muted }}>
              {a.name}
            </button>
          ))}
        </div>

        <div className="text-xs mb-2" style={{ color: C.muted }}>מקורות ההכנסה שייכללו</div>
        <div className="flex flex-wrap gap-2">
          {REPORT_SOURCES.map(({ k, label }) => (
            <button key={k} onClick={() => toggle(k)} className="text-xs rounded-full px-3 py-1.5"
              style={picked.includes(k)
                ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` }
                : { border: `1px solid ${C.line}`, color: C.muted }}>
              {label}
            </button>
          ))}
          <button onClick={() => setPicked(allOn ? ["rent"] : REPORT_SOURCES.map((x) => x.k))}
            className="text-xs rounded-full px-3 py-1.5" style={{ border: `1px solid ${C.ochre}`, color: C.ochre }}>
            {allOn ? "רק שכר דירה" : "הכול"}
          </button>
        </div>

        <button onClick={printContract} disabled={!active.length}
          className="mt-4 w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
          style={{ background: active.length ? C.teal : C.line, color: "#fff" }}>
          <FileText size={15} /> הדפסה / שמירה כ-PDF
        </button>
        {/* "הפקת PDF" נשמע כאילו יירד קובץ, ובפועל נפתח חלון ההדפסה —
            ההסבר אומר מראש איפה ה-PDF מסתתר בתוכו. */}
        <p className="text-[11px] mt-1.5 text-center" style={{ color: C.muted }}>
          נפתח חלון ההדפסה של הדפדפן — שם בוחרים מדפסת, או "שמירה כ-PDF" כדי לקבל קובץ.
        </p>
      </div>

      {/* מסמך הדפסה אחד לשני הדוחות. print-area ממוקם absolute ב-inset:0,
          ולכן שני אזורים על אותו מסך היו נדפסים אחד על השני. */}
      <div className="print-area space-y-4">
      <MethodReport data={data} apts={apts} years={yearList} />
      <ArrearsReport data={data} apts={apts} standalone={false} />

      {active.length === 0 ? (
        <div className="rounded-xl border p-8 text-center text-sm no-print" style={{ background: C.card, borderColor: C.line, color: C.muted }}>
          בחרו לפחות מקור הכנסה אחד.
        </div>
      ) : (
        <div className="rounded-xl border p-4" style={{ background: "#fff", borderColor: C.line }}>
          <div className="flex items-start justify-between border-b pb-3 mb-3" style={{ borderColor: C.line }}>
            <div>
              <h2 className="disp text-xl font-bold">
                דוח הכנסות {yearList.length > 1 ? `${yearList[0]}–${yearList[yearList.length - 1]}` : yearList[0]}
              </h2>
              <div className="text-xs mt-0.5" style={{ color: C.muted }}>
                {allApts ? `כל הנכסים (${data.apartments.length})` : apts.map((a) => a.name).join(" · ")}
              </div>
              <div className="text-xs" style={{ color: C.muted }}>{active.map((x) => x.label).join(", ")}</div>
            </div>
            <div className="text-left text-xs" style={{ color: C.muted }}>
              {data.settings?.ownerName && <div style={{ fontWeight: 600, color: C.ink }}>{data.settings.ownerName}</div>}
              {data.settings?.ownerId && <div>ת.ז. {data.settings.ownerId}</div>}
              <div>הופק {heDate(TODAY)}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div><div className="text-[11px]" style={{ color: C.muted }}>סה״כ הכנסות</div><div className="disp text-xl font-bold" style={{ color: C.teal }}>{nis(totals.total)}</div></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>הוצאות</div><div className="disp text-xl font-bold" style={{ color: C.ochre }}>{nis(expTotal)}</div></div>
            <div><div className="text-[11px]" style={{ color: C.muted }}>נטו</div><div className="disp text-xl font-bold">{nis(totals.total - expTotal)}</div></div>
            {/* בשנה שעדיין רצה מחלקים בחודשים שכבר עברו. חלוקה קבועה
                ב-12 הציגה באוגוסט ₪11,433 במקום ₪17,150 — מספר שנראה
                נכון ואינו, וקשה לתפוס אותו בלי לחשב ידנית. */}
            <div>
              <div className="text-[11px]" style={{ color: C.muted }}>ממוצע חודשי</div>
              <div className="disp text-xl font-bold">{nis(totals.total / avgMonths)}</div>
              <div className="text-[10px]" style={{ color: C.muted }}>לפי {avgMonths} {avgMonths === 1 ? "חודש" : "חודשים"}</div>
            </div>
          </div>

          <div style={{ width: "100%", height: 180 }} className="mb-4">
            <ResponsiveContainer>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.line} vertical={false} />
                <XAxis dataKey="name" reversed tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                <YAxis orientation="right" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => v / 1000 + "K"} />
                <Tooltip formatter={(v) => nis(v)} contentStyle={{ direction: "rtl", fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="הכנסה" fill={C.teal} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="text-sm mb-1" style={{ fontWeight: 600 }}>פירוט חודשי</div>
          <div className="overflow-x-auto mb-4">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              <thead>
                <tr>
                  {yearList.length > 1 && <th style={th}>שנה</th>}
                  <th style={th}>חודש</th>
                  {active.map((x) => <th key={x.k} style={th}>{x.label}</th>)}
                  <th style={{ ...th, fontWeight: 600, color: C.ink }}>סה״כ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    {yearList.length > 1 && <td style={{ ...td, color: C.muted }}>{r.year}</td>}
                    <td style={{ ...td, color: C.muted }}>{MONTHS[r.month - 1]}</td>
                    {active.map((x) => <td key={x.k} style={td}>{r[x.k] ? nis(r[x.k]) : "—"}</td>)}
                    <td style={{ ...td, fontWeight: 600 }}>{r.total ? nis(r.total) : "—"}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, fontWeight: 600, borderTop: `2px solid ${C.line}` }} colSpan={yearList.length > 1 ? 2 : 1}>סה״כ</td>
                  {active.map((x) => <td key={x.k} style={{ ...td, fontWeight: 600, borderTop: `2px solid ${C.line}` }}>{nis(totals[x.k])}</td>)}
                  <td style={{ ...td, fontWeight: 700, borderTop: `2px solid ${C.line}`, color: C.teal }}>{nis(totals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {yearList.length > 1 && (
            <>
              <div className="text-sm mb-1" style={{ fontWeight: 600 }}>סיכום לפי שנה</div>
              <div className="overflow-x-auto mb-4">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 380 }}>
                  <thead>
                    <tr>
                      <th style={th}>שנה</th>
                      {active.map((x) => <th key={x.k} style={th}>{x.label}</th>)}
                      <th style={{ ...th, fontWeight: 600, color: C.ink }}>סה״כ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byYear.map((r) => (
                      <tr key={r.year}>
                        <td style={{ ...td, fontWeight: 600 }}>{r.year}</td>
                        {active.map((x) => <td key={x.k} style={td}>{r[x.k] ? nis(r[x.k]) : "—"}</td>)}
                        <td style={{ ...td, fontWeight: 600 }}>{nis(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {perTenant.length > 0 && (
            <>
              <div className="text-sm mb-1" style={{ fontWeight: 600 }}>פירוט לפי דייר</div>
              <div className="overflow-x-auto mb-4">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                  <thead>
                    <tr>
                      <th style={th}>דייר</th>
                      <th style={th}>תקופה</th>
                      {active.map((x) => <th key={x.k} style={th}>{x.label}</th>)}
                      <th style={{ ...th, fontWeight: 600, color: C.ink }}>סה״כ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perTenant.map((r) => (
                      <tr key={r.id}>
                        <td style={td}>{r.tenant}<span className="text-[10px]" style={{ color: C.muted }}> · {r.aptName}</span></td>
                        <td style={{ ...td, color: C.muted, fontSize: 11 }}>{heDate(r.from)} — {r.to ? heDate(r.to) : "בתוקף"}</td>
                        {active.map((x) => <td key={x.k} style={td}>{r[x.k] ? nis(r[x.k]) : "—"}</td>)}
                        <td style={{ ...td, fontWeight: 600 }}>{nis(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {perApt.length > 1 && (
            <>
              <div className="text-sm mb-1" style={{ fontWeight: 600 }}>פירוט לפי נכס</div>
              <div className="overflow-x-auto mb-4">
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
                  <thead>
                    <tr>
                      <th style={th}>נכס</th>
                      {active.map((x) => <th key={x.k} style={th}>{x.label}</th>)}
                      <th style={{ ...th, fontWeight: 600, color: C.ink }}>סה״כ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perApt.map((r) => (
                      <tr key={r.name}>
                        <td style={td}>{r.name}<span className="text-[10px]" style={{ color: C.muted }}>{r.tenant ? ` · ${r.tenant}` : ""}</span></td>
                        {active.map((x) => <td key={x.k} style={td}>{r[x.k] ? nis(r[x.k]) : "—"}</td>)}
                        <td style={{ ...td, fontWeight: 600 }}>{nis(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {expenses.length > 0 && (
            <>
              <div className="text-sm mb-1" style={{ fontWeight: 600 }}>הוצאות</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {expenses.slice(0, 30).map((e) => (
                    <tr key={e.id}>
                      <td style={{ ...td, color: C.muted, width: 90 }}>{monthLabel(e.month)}</td>
                      <td style={td}>{e.cat}{e.desc ? ` · ${e.desc}` : ""}</td>
                      <td style={{ ...td, textAlign: "left" }}>{nis(e.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...td, fontWeight: 600, borderTop: `2px solid ${C.line}` }} colSpan={2}>סה״כ הוצאות</td>
                    <td style={{ ...td, fontWeight: 600, textAlign: "left", borderTop: `2px solid ${C.line}`, color: C.ochre }}>{nis(expTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          <div className="text-[10px] mt-4 pt-3 border-t" style={{ borderColor: C.line, color: C.muted }}>
            הדוח מבוסס על תשלומים שנרשמו כמתקבלים בפועל במערכת. אינו מהווה מסמך חשבונאי רשמי.
          </div>
        </div>
      )}

      <TaxReport data={data} years={yearList} />
      </div>
    </section>
  );
}

/* ---------------- גיבוי ושיתוף ---------------- */
/* אוסף כל מזהי הקבצים שהמסמך מפנה אליהם.
   הגרסה הקודמת דילגה על צילומי תעודות הזהות, על חתימת בעל הנכס ועל כל מה
   שתלוי בתקופות שכירות שהסתיימו — ולכן גיבוי "עם תמונות" יצא חסר בשקט.
   הרשימה כאן חייבת להישאר מלאה: היא גם מקור האמת לניקוי קבצים יתומים. */
const photoIdsOf = (d) => {
  const ids = new Set();
  const add = (v) => { if (v && typeof v === "string") ids.add(v); };

  (d.media || []).forEach((m) => add(m.id));
  (d.readings || []).forEach((r) => add(r.photoId));
  add((d.settings || {}).ownerSignatureId);

  const walkHolder = (h) => {
    if (!h) return;
    (h.contracts || []).forEach((c) => { add(c.signatureId); add(c.scanId); add(c.fileId); });
    (h.guarantors || []).forEach((g) => { add(g.idPhoto); add(g.signatureId); });
    const k = h.intake || {};
    add(k.tenantIdPhoto); add(k.partnerIdPhoto);
    add(k.elecPhoto); add(k.waterPhoto); add(k.gasPhoto);
  };

  // גם הנכסים וגם התקופות: תקופה שנסגרה כבר לא משתקפת על הנכס.
  (d.apartments || []).forEach(walkHolder);
  (d.tenancies || []).forEach(walkHolder);

  // חשבונות ישנים מלפני המעבר לרשומות קריאה.
  Object.values(d.bills || {}).forEach((b) => { add(b.elecPhoto); add(b.waterPhoto); });

  return [...ids];
};

/**
 * מחליף מזהי קבצים ישנים בחדשים בכל המסמך.
 *
 * נדרש בשחזור גיבוי: הקבצים מועלים מחדש ומקבלים מזהים חדשים, ולכן כל
 * ההפניות במסמך חייבות לעבור יחד איתם. השדות כאן חייבים להישאר מסונכרנים
 * עם photoIdsOf שמעל — כל שדה שמופיע שם ולא כאן ישאיר תמונה יתומה.
 */
const remapPhotoIds = (d, map) => {
  if (!Object.keys(map).length) return d;
  const m = (v) => (v && map[v] ? map[v] : v);

  const holder = (h) => ({
    ...h,
    contracts: (h.contracts || []).map((c) => ({ ...c, signatureId: m(c.signatureId), scanId: m(c.scanId), fileId: m(c.fileId) })),
    guarantors: (h.guarantors || []).map((g) => ({ ...g, idPhoto: m(g.idPhoto), signatureId: m(g.signatureId) })),
    intake: {
      ...(h.intake || {}),
      tenantIdPhoto: m(h.intake?.tenantIdPhoto),
      partnerIdPhoto: m(h.intake?.partnerIdPhoto),
      elecPhoto: m(h.intake?.elecPhoto),
      waterPhoto: m(h.intake?.waterPhoto),
      gasPhoto: m(h.intake?.gasPhoto),
    },
  });

  return {
    ...d,
    media: (d.media || []).map((x) => ({ ...x, id: m(x.id) })),
    readings: (d.readings || []).map((r) => ({ ...r, photoId: m(r.photoId) })),
    apartments: (d.apartments || []).map(holder),
    tenancies: (d.tenancies || []).map(holder),
    settings: { ...(d.settings || {}), ownerSignatureId: m(d.settings?.ownerSignatureId) },
    bills: Object.fromEntries(
      Object.entries(d.bills || {}).map(([k, b]) => [
        k,
        { ...b, elecPhoto: m(b.elecPhoto), waterPhoto: m(b.waterPhoto) },
      ]),
    ),
  };
};

function reportText(d) {
  const s = d.settings || defTariffs();
  const occ = d.apartments.filter((a) => a.tenant);
  const collected = occ.reduce((t, a) => t + rentPaidOf(d, a.id, CUR_KEY), 0);
  const expected = occ.reduce((t, a) => t + Number(a.rent || 0), 0);
  const rows = d.apartments.map((a) => {
    if (!a.tenant) {
      const next = upcomingTenancies(d, a.id)[0];
      return next ? `• ${a.name} — הושכרה ל${next.tenant}, כניסה ב-${heDate(next.leaseStart)}` : `• ${a.name} — פנויה`;
    }
    const paid = !!d.payments[rowKey(a.id, CUR_KEY)];
    /* החוב המצטבר ולא רק החודש הנוכחי — דוח שמראה חשבון אחד פתוח בזמן
       שנגררים שלושה הוא בדיוק מה שגורם לפספס חוב. */
    const st = debtStatement(d, a, CUR_KEY);
    const arrears = st.arrears.length
      ? ` · חוב נגרר ${nis(st.arrearsTotal)} מ-${st.arrears.length} חודשים`
      : "";
    return `• ${a.name} — ${a.tenant} · שכ"ד ${paid ? "שולם" : "ממתין"}${st.total ? ` · חוב פתוח ${nis(st.total)}` : ""}${arrears}`;
  });
  const owed = d.apartments.reduce((t, a) => t + (a.tenant ? debtStatement(d, a, CUR_KEY).total : 0), 0);
  return `*דוח ניהול — ${monthLabel(CUR_KEY)}*

מושכרות: ${occ.length} מתוך ${d.apartments.length}
נגבה החודש: ${nis(collected)} מתוך ${nis(expected)}
סך חוב פתוח: ${nis(owed)}

${rows.join("\n")}`;
}

function BackupShare({ data, setData }) {
  const [withPhotos, setWithPhotos] = useState(false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [fallback, setFallback] = useState("");
  const [pending, setPending] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState("");
  /* איפוס — דורש הקלדה של המילה, לא שני קליקים. הפעולה נשמרת מיד לשרת
     ואין ממנה דרך חזרה חוץ מגיבוי, ולכן הכפתור יושב כאן, ליד הגיבוי. */

  const note = (t) => { setMsg(t); setTimeout(() => setMsg(""), 4000); };

  const prune = async () => {
    setBusy("prune");
    try {
      const r = await dataApi.pruneFiles();
      note(r.deleted ? `נמחקו ${r.deleted} קבצים שאינם בשימוש` : "אין קבצים למחיקה");
    } catch (e) {
      note(e instanceof ApiError ? e.message : "הניקוי נכשל");
    }
    setBusy("");
  };

  const buildBackup = async () => {
    const payload = { app: "rental-manager", version: 1, exportedAt: new Date().toISOString(), data, photos: {} };
    if (withPhotos) {
      // התמונות יושבות ב-Spaces. מושכים כל אחת דרך ה-API וממירים ל-dataURL,
      // כדי שקובץ הגיבוי יישאר עצמאי ויעבוד גם בלי חשבון.
      for (const id of photoIdsOf(data)) {
        try {
          const res = await fetch(fileUrl(id), { credentials: "include" });
          if (!res.ok) continue;
          const blob = await res.blob();
          payload.photos[id] = await new Promise((ok, no) => {
            const r = new FileReader();
            r.onload = () => ok(r.result);
            r.onerror = no;
            r.readAsDataURL(blob);
          });
        } catch { /* תמונה חסרה לא תעצור את הגיבוי */ }
      }
    }
    return JSON.stringify(payload);
  };

  const download = async () => {
    setBusy("dl");
    try {
      const text = await buildBackup();
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `גיבוי-נכסים-${TODAY}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      note("קובץ הגיבוי ירד למכשיר");
    } catch { note("ההורדה נחסמה — השתמשו בהעתקה ללוח"); }
    setBusy("");
  };

  const copyBackup = async () => {
    setBusy("copy");
    try {
      const text = await buildBackup();
      await navigator.clipboard.writeText(text);
      note("הגיבוי הועתק ללוח — הדביקו אותו בפתק או במייל");
    } catch { setFallback(await buildBackup()); }
    setBusy("");
  };

  const restore = async (file) => {
    if (!file) return;
    setBusy("restore");
    try {
      const raw = await readFile(file);
      const json = JSON.parse(decodeURIComponent(escape(atob(raw.split(",")[1]))));
      if (!json?.data?.apartments) throw new Error("bad");
      setPending(json);
    } catch { note("הקובץ אינו גיבוי תקין"); }
    setBusy("");
  };

  const applyRestore = async () => {
    setBusy("restore");
    try {
      // מזהי הקבצים בגיבוי לא בהכרח קיימים בחשבון הזה, ולכן כל תמונה
      // מועלית מחדש ומקבלת מזהה חדש. המסמך מתעדכן למזהים החדשים.
      const photos = pending.photos || {};
      const remap = {};
      for (const [oldId, dataUrl] of Object.entries(photos)) {
        try { remap[oldId] = await uploadImage(dataUrl, { scope: "owner" }); }
        catch { /* תמונה שלא עלתה תישאר חסרה */ }
      }
      const restored = remapPhotoIds(migrate(pending.data), remap);
      setData(restored);
      setPending(null);
      note(`הגיבוי שוחזר · ${Object.keys(remap).length} תמונות`);
    } catch { note("השחזור נכשל"); }
    setBusy("");
  };

  const share = async (title, text) => {
    try { if (navigator.share) { await navigator.share({ title, text }); return note("שותף"); } } catch { /* ממשיכים ללוח */ }
    try { await navigator.clipboard.writeText(text); return note("הועתק ללוח"); } catch { setFallback(text); }
  };

  return (
    <section className="mt-6 space-y-4">
      {msg && <div className="rounded-lg p-2.5 text-sm" style={{ background: "#EAF3EE", color: C.paid }}>{msg}</div>}

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">שמירה וגיבוי</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          הנתונים נשמרים אוטומטית בחשבון שלכם ונגישים מכל מכשיר. קובץ הגיבוי הוא עותק אישי
          שנשאר אצלכם — לשמירה מחוץ למערכת, להעברה לרואה חשבון, או לשחזור אחרי טעות.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={withPhotos} onChange={(e) => setWithPhotos(e.target.checked)} />
          לכלול תמונות וחתימות ({photoIdsOf(data).length}) — הקובץ יהיה גדול בהרבה
        </label>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} disabled={!!busy} className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5" style={{ background: C.teal, color: "#fff" }}>
            {busy === "dl" ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} style={{ transform: "rotate(180deg)" }} />} הורדת קובץ גיבוי
          </button>
          <button onClick={copyBackup} disabled={!!busy} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}` }}>
            {busy === "copy" ? "מכין…" : "העתקת הגיבוי ללוח"}
          </button>
          <FilePick accept="application/json,.json" title="שחזור מקובץ" onPick={(f) => restore(f)}
            className="text-sm rounded-lg px-3 py-2 gap-1.5" style={{ border: `1px solid ${C.line}` }}>
            <Upload size={15} /> שחזור מקובץ
          </FilePick>
          <button onClick={() => setPasteOpen(!pasteOpen)} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}` }}>
            שחזור מהדבקה
          </button>
        </div>

        {pasteOpen && (
          <div className="mt-3">
            <textarea value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="הדביקו כאן את טקסט הגיבוי שהעתקתם"
              style={{ ...inputStyle, height: 120, fontSize: 11, fontFamily: "monospace" }} />
            <button onClick={() => {
              try {
                const json = JSON.parse(paste);
                if (!json?.data?.apartments) throw new Error("bad");
                setPending(json); setPasteOpen(false); setPaste("");
              } catch { note("הטקסט אינו גיבוי תקין"); }
            }} className="mt-2 text-sm rounded-lg px-3 py-2" style={{ background: C.teal, color: "#fff" }}>קריאת הגיבוי</button>
          </div>
        )}

        {pending && (
          <div className="mt-3 rounded-lg p-3 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
            <div>הגיבוי מכיל {pending.data.apartments.length} נכסים ו-{Object.keys(pending.photos || {}).length} תמונות, מתאריך {heDate(pending.exportedAt)}.</div>
            <div className="text-[11px] mt-1">השחזור יחליף את כל הנתונים הקיימים.</div>
            <div className="flex gap-2 mt-2">
              <button onClick={applyRestore} className="text-xs rounded-lg px-3 py-1.5" style={{ background: C.late, color: "#fff" }}>שחזור והחלפה</button>
              <button onClick={() => setPending(null)} className="text-xs rounded-lg px-3 py-1.5" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">ניקוי אחסון</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>
          מוחק מהאחסון תמונות שאף רשומה כבר לא מפנה אליהן — צילומי מונה של
          קריאות שנמחקו, והעלאות שנקטעו באמצע. קבצים מהיממה האחרונה לא נוגעים
          בהם, כדי לא למחוק צילום שהעלאתו הסתיימה אבל טרם נשמר.
        </p>
        <button onClick={prune} disabled={busy === "prune"}
          className="rounded-lg px-3 py-2 text-sm" style={{ border: `1px solid ${C.line}`, opacity: busy === "prune" ? 0.6 : 1 }}>
          {busy === "prune" ? "מנקה…" : "ניקוי קבצים יתומים"}
        </button>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">איפוס החשבון</h2>
        <p className="text-xs" style={{ color: C.muted }}>
          האיפוס עבר ל"הגדרות" (מתחת לסיסמה) — איפוס הדיירים בלבד או איפוס מלא, עם קוד אימות שנשלח למייל שלך.
          כדאי להוריד גיבוי מכאן לפני.
        </p>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">שיתוף</h2>
        <p className="text-xs mb-3" style={{ color: C.muted }}>סיכום חודשי קצר של כל הנכסים — מה נגבה, מה פתוח ומה הוצא — לשליחה או להעתקה.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => share("דוח ניהול נכסים", reportText(data))} className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5" style={{ background: C.teal, color: "#fff" }}>
            <Send size={15} /> שיתוף דוח החודש
          </button>
          <a href={`https://wa.me/?text=${encodeURIComponent(reportText(data))}`} target="_blank" rel="noreferrer"
            className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5" style={{ background: "#128C7E", color: "#fff" }}>
            <MessageCircle size={15} /> שליחת הדוח בוואטסאפ
          </a>
          <button onClick={() => share("דוח ניהול נכסים", reportText(data))} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}` }}>
            העתקת הדוח
          </button>
        </div>
      </div>

      <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
        <h2 className="disp text-xl font-bold mb-1">הזמנת בעלי נכסים</h2>
        <ShareAppPanel />
      </div>

      {fallback && (
        <div className="rounded-xl border p-4" style={{ background: C.card, borderColor: C.line }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm" style={{ fontWeight: 600 }}>העתקה ידנית</h3>
            <button onClick={() => setFallback("")} aria-label="סגירה" style={{ color: C.muted }}><X size={16} /></button>
          </div>
          <textarea readOnly value={fallback} onFocus={(e) => e.target.select()} style={{ ...inputStyle, height: 160, fontSize: 11, fontFamily: "monospace" }} />
        </div>
      )}
    </section>
  );
}

/* ============ עמוד קליטת דייר ============
   כל תהליך הכניסה במקום אחד: פרטי הדייר ובן/בת הזוג, תנאי השכירות,
   קריאות מונה עם צילום, מסירת מפתחות ובלון גז, תיעוד מצב הדירה,
   ולבסוף יצירת החוזה ושליחתו לחתימה מרחוק. */
function TenantIntake({ apt, data, setData, run, onExit }) {
  const s = data.settings || defTariffs();
  const cur = currentTenancy(data, apt.id);
  const replacing = !!cur;
  /* דיירים שכבר רשומים להיכנס בעתיד. קליטה נוספת בלי לשים לב הייתה
     יוצרת שני רישומים מתחרים על אותו נכס — ולכן מוצגת אזהרה למעלה,
     והשמירה דורשת בחירה מפורשת: להחליף את הרישום הקיים או להוסיף
     את הדייר החדש לצידו. */
  const upcoming = upcomingTenancies(data, apt.id);
  const [upcomingChoice, setUpcomingChoice] = useState(null); // "replace" | "keep"
  const [f, setF] = useState({
    tenant: "", tenantId: "", phone: "", email: "",
    partnerName: "", partnerId: "", partnerPhone: "",
    rent: apt.rent || "", leaseStart: TODAY, leaseEnd: addYears(TODAY, 1),
    prorateFirstMonth: false, firstMonthAmount: null,
    vaadProrateFirstMonth: false, vaadFirstMonthAmount: null,
    arnonaProrateFirstMonth: false, arnonaFirstMonthAmount: null,
    municipalProrateFirstMonth: false, municipalFirstMonthAmount: null,
    vaad: apt.vaad || "",
    securities: Array.isArray(apt.securities) ? apt.securities : undefined,
    // בלי ברירת מחדל לאמצעי התשלום: "12 שיקים" אוטומטי נכנס לחוזים של מי
    // שכלל לא משלם בשיקים. ריק = ברירת המחדל מההגדרות.
    payMethodRent: apt.payMethodRent || "", rentChecks: apt.rentChecks || "",
    rentDay: rentDayOf(apt, s), billsDay: billsDayOf(apt, s),
    billsCycle: billsCycleOf(apt, s), billsAnchor: billsAnchorOf(apt),
    entryElec: "", entryWater: "", entryGas: "",
    keys: "", gasBalloon: false, gasAmount: "", gasSmallAmount: "", handoverNotes: "",
    extraTerms: "", moveOut: TODAY, persons: "",
    propertyType: apt.propertyType || "unit", authority: apt.authority || "",
    vaadActive: apt.vaadActive !== false, contents: apt.contents || apt.inventory || "",
    // ברירת מחדל: מה שנמסר לדייר הוא גם רשימת הציוד של הנכס.
    updateInventory: true,
    arnona: apt.arnona || "", arnonaActive: apt.arnonaActive === true,
    municipal: apt.municipal || "", municipalActive: apt.municipalActive === true,
    municipalLabel: apt.municipalLabel || "",
    rateElec: apt.rates?.elec ?? "", rateWS: apt.rates?.waterSewage ?? "", rateGas: apt.rates?.gas ?? "",
    rentSteps: (apt.rentSteps || []).length ? apt.rentSteps : [],
    /* ערבים של השכירות הזו. עד כאן הם נכתבו ישירות למסמך, ובנכס בלי
       תקופה פעילה נשמרו על הנכס — ואז change_tenant יצר תקופה עם רשימה
       ריקה ו-syncApartments מחק אותם. כלומר כל ערב שהוקלד באשף נעלם
       בשמירה. עכשיו הם חלק מהטופס ונשלחים עם הפעולה.
       undefined = טרם נשאל · [] = "אין ערבים" מפורש · מערך = יש. */
    guarantors: undefined,
  });
  /* האם יש בכלל גז בנכס הזה — לפי מה שנבחר באשף עכשיו, ולא לפי מה
     שהיה קודם: מי שסימן "ללא חיוב" לא אמור לראות שדות בלון גז. */
  const gasRelevant = (f.utilities?.gas ?? utilityMode(apt, "gas")) !== "none";
  const [photos, setPhotos] = useState({ tenantId: null, partnerId: null, elec: null, water: null, gas: null });
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const media = (data.media || []).filter((m) => m.aptId === apt.id && m.kind === "movein");

  /* קוד הכניסה נטען מראש, כדי שההודעה שמצרפת את החוזה תכלול גם קישור
     וגם קוד — זו ההודעה הראשונה שהדייר מקבל, ופעמים רבות היחידה שיקרא. */
  const inviteCode = usePortalCode(apt.id);

  /** משבצות הצילום שמזינות שדה קריאת כניסה. */
  const METER_SLOTS = { elec: "entryElec", water: "entryWater", gas: "entryGas" };

  /* צילומי תעודות זהות מוצפנים; צילומי מונה לא — את אלה השרת חייב לקרוא
     כדי להריץ עליהם זיהוי. ראו attachFile. */
  const attach = async (file, slot) => {
    if (!file) return;
    setBusy(slot); setErr("");
    try {
      const isId = slot === "tenantId" || slot === "partnerId";
      const small = await compressForUpload(file);
      const id = await attachFile(small, {
        apartmentId: apt.id,
        kind: isId ? "id" : slot,
        encrypt: isId,
      });
      setPhotos((p) => ({ ...p, [slot]: id }));

      /* קריאות הכניסה הן הבסיס לכל חישובי הצריכה של הדייר החדש, ולכן
         המספר נקרא מהתמונה ונכנס לשדה — עדיין לאישור, לא כעובדה. */
      const field = METER_SLOTS[slot];
      if (field) {
        try {
          const out = await ai.readMeter(slot, small, { scope: "owner" });
          if (out.reading != null) {
            setF((prev) => ({ ...prev, [field]: String(out.reading) }));
            if (out.confidence === "low") {
              setErr(`זיהיתי ${out.reading} — לא בוודאות. ודאו מול התמונה.`);
            }
          } else if (out.note) {
            setErr(`${out.note} הקלידו את הקריאה ידנית.`);
          }
        } catch (e) {
          // התמונה נשמרה; כישלון בזיהוי אינו כישלון של הצירוף.
          setErr(`${e instanceof ApiError ? e.message : "קריאת המונה מהתמונה נכשלה."} הקלידו את הקריאה ידנית.`);
        }
      }
    } catch (e) {
      setErr(e?.message || "שמירת הקובץ נכשלה. נסו קובץ קטן יותר.");
    }
    setBusy("");
  };

  const addCondition = async (file) => {
    if (!file) return;
    setBusy("cond"); setErr("");
    try {
      const id = await attachFile(file, { apartmentId: apt.id, kind: "movein" });
      setData((d) => ({ ...d, media: [...(d.media || []), { id, aptId: apt.id, kind: "movein", label: "מצב הדירה בכניסה", date: TODAY, tenant: f.tenant }] }));
    } catch { setErr("העלאת התיעוד נכשלה."); }
    setBusy("");
  };

  /* צילום ת"ז של ערב — כמו באשף ובעורך הערבים: מוצפן כשההצפנה פעילה,
     נקשר לערב ונשמר איתו על התקופה (change_tenant מעביר אותו כמות שהוא). */
  const attachGuarantorId = async (file, gid) => {
    if (!file) return;
    setBusy(`gid:${gid}`); setErr("");
    try {
      const id = await attachFile(file, { apartmentId: apt.id, kind: "id", encrypt: true });
      setF((prev) => ({ ...prev, guarantors: (prev.guarantors || []).map((x) => (x.id === gid ? { ...x, idPhoto: id } : x)) }));
    } catch (e) {
      setErr(e?.message || "העלאת הצילום נכשלה. נסו שוב.");
    }
    setBusy("");
  };

  const buildActions = (withContract) => {
    // סך הביטחונות נשמר על החוזה לתצוגה; הפירוט חי על השכירות עצמה.
    const secTotal = securityTotal({ ...apt, rent: Number(f.rent) || 0, securities: f.securities }, s);
    const acts = [];
    /* הוחלט להחליף רישום עתידי קיים: מחיקה רכה — הרישום הקודם עובר
       לארכיון התקופות וניתן לשחזור, לא נמחק לצמיתות. */
    if (upcomingChoice === "replace") {
      upcoming.forEach((t) => acts.push({
        type: "remove_tenancy", apartment: apt.id, tenancyId: t.id,
        notes: `הוחלף ברישום של ${f.tenant}`,
      }));
    }
    acts.push(
      {
        type: "change_tenant", apartment: apt.id, tenant: f.tenant, phone: f.phone,
        rent: Number(f.rent) || 0, leaseStart: f.leaseStart, leaseEnd: f.leaseEnd,
        prorateFirstMonth: !!f.prorateFirstMonth,
        ...(f.prorateFirstMonth && f.firstMonthAmount != null ? { firstMonthAmount: Number(f.firstMonthAmount) } : {}),
        moveOut: replacing ? f.moveOut : null,
        // הערבים נוצרים יחד עם התקופה, ולכן לא נמחקים ב-syncApartments.
        guarantors: (f.guarantors || []).filter((g) => String(g.name || "").trim()),
        contents: f.contents,
      },
      {
        type: "update_apartment", apartment: apt.id, __useNewTenancy: true,
        vaad: Number(f.vaad) || 0, rentDay: Number(f.rentDay) || 1, billsDay: Number(f.billsDay) || 15,
        billsCycle: Number(f.billsCycle) || 1, billsAnchor: Number(f.billsAnchor) || 1,
        propertyType: f.propertyType, authority: f.authority,
        vaadActive: f.vaadActive, contents: f.contents,
        ...(f.updateInventory !== false ? { inventory: f.contents } : {}),
        securities: f.securities,
        payMethodRent: f.payMethodRent || null,
        rentChecks: f.payMethodRent === "check" ? (Number(f.rentChecks) || leaseMonthsOf(f)) : 0,
        arnona: Number(f.arnona) || 0, arnonaActive: !!f.arnonaActive,
        municipal: Number(f.municipal) || 0, municipalActive: !!f.municipalActive,
        municipalLabel: f.municipalLabel || "",
        vaadProrateFirstMonth: !!f.vaadProrateFirstMonth,
        ...(f.vaadProrateFirstMonth && f.vaadFirstMonthAmount != null ? { vaadFirstMonthAmount: Number(f.vaadFirstMonthAmount) } : {}),
        arnonaProrateFirstMonth: !!f.arnonaProrateFirstMonth,
        ...(f.arnonaProrateFirstMonth && f.arnonaFirstMonthAmount != null ? { arnonaFirstMonthAmount: Number(f.arnonaFirstMonthAmount) } : {}),
        municipalProrateFirstMonth: !!f.municipalProrateFirstMonth,
        ...(f.municipalProrateFirstMonth && f.municipalFirstMonthAmount != null ? { municipalFirstMonthAmount: Number(f.municipalFirstMonthAmount) } : {}),
        rates: {
          elec: f.rateElec === "" ? null : Number(f.rateElec),
          waterSewage: f.rateWS === "" ? null : Number(f.rateWS),
          gas: f.rateGas === "" ? null : Number(f.rateGas),
        },
        rentSteps: (f.rentSteps || []).filter((x) => x.from && x.rent).map((x) => ({ from: x.from, rent: Number(x.rent) })),
        intake: {
          tenantId: f.tenantId, email: f.email,
          partnerName: f.partnerName, partnerId: f.partnerId, partnerPhone: f.partnerPhone,
          tenantIdPhoto: photos.tenantId, partnerIdPhoto: photos.partnerId,
          keys: Number(f.keys) || 0, gasBalloon: f.gasBalloon, gasAmount: Number(f.gasAmount) || 0,
          gasSmallAmount: Number(f.gasSmallAmount) || 0, persons: Number(f.persons) || 0,
          handoverNotes: f.handoverNotes, deposit: secTotal,
          entryElec: f.entryElec, entryWater: f.entryWater, entryGas: f.entryGas,
          intakeDate: TODAY,
        },
      },
    );
    [["elec", f.entryElec], ["water", f.entryWater], ["gas", f.entryGas]].forEach(([meter, val]) => {
      if (val !== "" && val != null) acts.push({ type: "set_meter", apartment: apt.id, meter, reading: Number(val), date: f.leaseStart || TODAY, note: "כניסת דייר", photoId: photos[meter] });
    });
    if (f.gasBalloon && Number(f.gasAmount) > 0) {
      acts.push({ type: "set_bill", apartment: apt.id, month: (f.leaseStart || TODAY).slice(0, 7), bill: "gas", amount: Number(f.gasAmount) });
    }
    if (withContract) {
      acts.push({
        type: "new_contract", apartment: apt.id, __useNewTenancy: true, from: f.leaseStart, to: f.leaseEnd,
        rent: Number(f.rent) || 0, deposit: secTotal, notes: f.extraTerms,
      });
    }
    return acts;
  };

  const save = (withContract) => {
    if (!f.tenant.trim()) { setErr("צריך להזין את שם הדייר"); return; }
    if (!f.rent) { setErr("צריך להזין שכר דירה"); return; }
    if (upcoming.length && !upcomingChoice) {
      setErr(`${upcoming[0].tenant} כבר רשום/ה להיכנס לנכס הזה ב-${heDate(upcoming[0].leaseStart)} — בחרו בכרטיס שבראש העמוד אם להחליף את הרישום הקיים או להשאיר אותו.`);
      return;
    }
    const r = run(buildActions(withContract));
    setSaved({ withContract, logs: r.logs });
  };

  const contractPreview = {
    id: "preview", type: "original", from: f.leaseStart, to: f.leaseEnd,
    rent: Number(f.rent) || 0, deposit: Number(f.deposit) || Number(f.rent) * (s.depositMonths || 2),
    paymentDay: Number(f.rentDay) || 1, status: "draft", notes: f.extraTerms,
  };
  const previewApt = {
    ...apt, tenant: f.tenant || apt.tenant, phone: f.phone,
    propertyType: f.propertyType, authority: f.authority, vaadActive: f.vaadActive,
    contents: f.contents, vaad: Number(f.vaad) || 0,
    // שכר הדירה החדש, לא זה שבנכס — ביטחונות לפי חודשים מחושבים ממנו.
    rent: Number(f.rent) || apt.rent || 0,
    securities: f.securities,
    payMethodRent: f.payMethodRent || null,
    rentChecks: f.payMethodRent === "check" ? (Number(f.rentChecks) || leaseMonthsOf(f)) : 0,
    arnona: Number(f.arnona) || 0, arnonaActive: !!f.arnonaActive,
    municipal: Number(f.municipal) || 0, municipalActive: !!f.municipalActive,
    municipalLabel: f.municipalLabel || "",
    rates: { elec: Number(f.rateElec) || null, waterSewage: Number(f.rateWS) || null, gas: Number(f.rateGas) || null },
    rentSteps: (f.rentSteps || []).filter((x) => x.from && x.rent),
    guarantors: (f.guarantors || []).filter((g) => String(g.name || "").trim()),
    rooms: apt.rooms,
    intake: { tenantId: f.tenantId, partnerName: f.partnerName, partnerId: f.partnerId, partnerPhone: f.partnerPhone,
      keys: f.keys, gasBalloon: f.gasBalloon, gasAmount: Number(f.gasAmount) || 0,
      gasSmallAmount: Number(f.gasSmallAmount) || 0, persons: Number(f.persons) || 0,
      entryElec: f.entryElec, entryWater: f.entryWater, entryGas: f.entryGas, handoverNotes: f.handoverNotes },
  };

  const signLink = `שלום ${f.tenant}, מצורף חוזה השכירות ל${apt.name} לתקופה ${heDate(f.leaseStart)}–${heDate(f.leaseEnd)} בדמי שכירות ${nis(Number(f.rent) || 0)}. נא להיכנס לאזור האישי, לעבור על החוזה ולחתום.${inviteLines(inviteCode)}`;

  return (
    <div dir="rtl" className="min-h-screen w-full pb-16" style={{ background: C.surface, color: C.ink, fontFamily: "'Assistant', system-ui, sans-serif" }}>
      <div className="em-appbar">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <header className="pt-6 pb-4 flex items-start justify-between gap-3">
            <div>
              <div className="em-eyebrow tracking-widest">{replacing ? "החלפת דייר" : "קליטת דייר"}</div>
              <h1 className="disp text-2xl font-bold">{apt.name}</h1>
              {replacing && <p className="em-sub">הדייר היוצא: {apt.tenant}</p>}
            </div>
            <button onClick={onExit} className="em-iconbtn shrink-0">
              <ArrowRight size={14} /> חזרה
            </button>
          </header>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">

        {/* דייר שכבר רשום להיכנס בעתיד: אי אפשר לפספס את זה ואי אפשר
            לשמור בלי להכריע — החלפה או תוספת. אחרי השמירה הכרטיס יורד,
            אחרת הדייר שנשמר הרגע היה מוצג בעצמו כ"רישום קיים". */}
        {upcoming.length > 0 && !saved && (
          <Card title="כבר רשום דייר לנכס הזה">
            {upcoming.map((t) => (
              <div key={t.id} className="rounded-lg p-3 text-sm mb-2 flex items-start gap-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <div>
                  <div style={{ fontWeight: 600 }}>{t.tenant} אמור/ה להיכנס ב-{heDate(t.leaseStart)}</div>
                  <div className="text-[11px]">
                    עד {t.leaseEnd ? heDate(t.leaseEnd) : "ללא מועד סיום"} · {nis(t.rent)} לחודש{t.phone ? ` · ${t.phone}` : ""}
                  </div>
                </div>
              </div>
            ))}
            <div className="text-xs mb-2" style={{ fontWeight: 600 }}>מה לעשות עם הרישום הקיים?</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setUpcomingChoice("replace")}
                className="flex-1 rounded-lg px-3 py-2 text-xs"
                style={upcomingChoice === "replace"
                  ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` }
                  : { border: `1px solid ${C.line}`, color: C.ink }}>
                להחליף — הרישום הקיים יבוטל
              </button>
              <button type="button" onClick={() => setUpcomingChoice("keep")}
                className="flex-1 rounded-lg px-3 py-2 text-xs"
                style={upcomingChoice === "keep"
                  ? { background: C.teal, color: "#fff", border: `1px solid ${C.teal}` }
                  : { border: `1px solid ${C.line}`, color: C.ink }}>
                להשאיר — הדייר החדש יתווסף לצידו
              </button>
            </div>
            <p className="text-[11px] mt-2" style={{ color: C.muted }}>
              החלפה מעבירה את הרישום הקיים לארכיון התקופות — אפשר לשחזר אותו בכל רגע ממסך "תקופות שכירות".
            </p>
          </Card>
        )}

        {replacing && (
          <Card title="הדייר הנוכחי">
            <div className="rounded-lg p-3 text-sm mb-3" style={{ background: "#F4FAF6" }}>
              <div style={{ fontWeight: 600 }}>{cur.tenant}</div>
              <div className="text-[11px]" style={{ color: C.muted }}>
                {heDate(cur.leaseStart)} — {cur.leaseEnd ? heDate(cur.leaseEnd) : "ללא מועד סיום"} · {nis(cur.rent)}
              </div>
            </div>
            <Field label="מועד סיום השכירות שלו"><DateField value={f.moveOut} onChange={(v) => setF({ ...f, moveOut: v })} /></Field>
            <p className="text-[11px] mt-2" style={{ color: C.muted }}>
              {cur.tenant} ממשיך להיות הדייר הפעיל עד למועד הזה. התשלומים, הקריאות והחוזים שלו נשמרים ואינם נמחקים.
              הגישה שלו לאזור האישי נחסמת רק כשתסגרו את התקופה שלו במסך "תקופות שכירות".
            </p>
            {f.leaseStart > TODAY && (
              <div className="mt-2 rounded-lg p-2.5 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
                תאריך הכניסה שבחרתם הוא {heDate(f.leaseStart)} — הדייר החדש יישמר כתקופה עתידית ויהפוך לפעיל באותו תאריך.
              </div>
            )}
          </Card>
        )}

        <Card title="פרטי הדייר">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="שם מלא"><input style={inputStyle} value={f.tenant} onChange={set("tenant")} /></Field>
            <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={f.tenantId} onChange={set("tenantId")} /></Field>
            <Field label="טלפון"><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
            <Field label="דוא״ל"><input style={inputStyle} value={f.email} onChange={set("email")} /></Field>
          </div>
          <PhotoSlot label="צילום תעודת זהות" id={photos.tenantId} busy={busy === "tenantId"} onPick={(file) => attach(file, "tenantId")} secure />

          <div className="mt-4 pt-3 border-t" style={{ borderColor: C.line }}>
            <div className="text-xs mb-2" style={{ fontWeight: 600 }}>בן/בת זוג או שוכר נוסף</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="שם"><input style={inputStyle} value={f.partnerName} onChange={set("partnerName")} /></Field>
              <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={f.partnerId} onChange={set("partnerId")} /></Field>
              <Field label="טלפון"><PhoneField value={f.partnerPhone} onChange={(v) => setF({ ...f, partnerPhone: v })} /></Field>
            </div>
            <PhotoSlot label="צילום תעודת זהות" id={photos.partnerId} busy={busy === "partnerId"} onPick={(file) => attach(file, "partnerId")} secure />
          </div>
        </Card>

        <Card title="סוג הנכס והחשבונות">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="סוג הנכס">
              <select style={inputStyle} value={f.propertyType} onChange={(e) => setF({ ...f, propertyType: e.target.value })}>
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.k} value={t.k}>{t.label} — {t.hint}</option>
                ))}
              </select>
            </Field>
            {f.propertyType !== "unit" && (
              <Field label="הרשות המקומית לארנונה">
                <input style={inputStyle} value={f.authority} onChange={set("authority")} placeholder="עיריית / מועצה אזורית" />
              </Field>
            )}
            <Field label="מספר נפשות במושכר"><input style={inputStyle} type="number" placeholder="0" value={f.persons} onChange={set("persons")} /></Field>
          </div>
          <div className="mt-3 rounded-lg border p-3" style={{ borderColor: C.line }}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.vaadActive} onChange={(e) => setF({ ...f, vaadActive: e.target.checked })} />
              ועד בית — האם חל על הדייר?
            </label>
            {f.vaadActive ? (
              <div className="mt-2">
                <Field label="סכום ועד בית לחודש">
                  <NumInput value={f.vaad} placeholder="0" onCommit={(v) => setF({ ...f, vaad: v })} />
                </Field>
                <FirstMonthProrate f={f} setF={setF} prefix="vaad" amount={f.vaad} />
              </div>
            ) : (
              <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>בחוזה ייכתב במפורש שלא חל תשלום ועד בית, והסעיף לא יופיע בחשבונות.</p>
            )}
          </div>

          {/* ארנונה והיטלים עירוניים.
              כבוי כברירת מחדל: ברוב השכירויות בישראל הדייר משלם ארנונה
              ישירות לרשות, ולא דרך בעל הנכס. הדלקה כאן משמעה שבעל הנכס
              משלם וגובה מהדייר — וזה מה שייכתב בחוזה. */}
          <div className="mt-3 rounded-lg border p-3" style={{ borderColor: C.line }}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.arnonaActive}
                onChange={(e) => setF({ ...f, arnonaActive: e.target.checked })} />
              ארנונה — נגבית דרכי מהדייר?
            </label>
            {f.arnonaActive ? (
              <div className="mt-2">
                <Field label="סכום ארנונה לחודש">
                  <NumInput value={f.arnona} placeholder="0" onCommit={(v) => setF({ ...f, arnona: v })} />
                </Field>
                <FirstMonthProrate f={f} setF={setF} prefix="arnona" amount={f.arnona} />
              </div>
            ) : (
              <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>
                הדייר משלם ארנונה ישירות לרשות. הסעיף לא יופיע בחשבונות.
              </p>
            )}

            <label className="flex items-center gap-2 text-sm mt-3">
              <input type="checkbox" checked={!!f.municipalActive}
                onChange={(e) => setF({ ...f, municipalActive: e.target.checked })} />
              היטל עירוני נוסף (שמירה, ביוב וכדומה)
            </label>
            {f.municipalActive && (
              <div className="mt-2">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="שם ההיטל">
                    <input style={inputStyle} value={f.municipalLabel || ""} placeholder="היטל שמירה"
                      onChange={(e) => setF({ ...f, municipalLabel: e.target.value })} />
                  </Field>
                  <Field label="סכום לחודש">
                    <NumInput value={f.municipal} placeholder="0" onCommit={(v) => setF({ ...f, municipal: v })} />
                  </Field>
                </div>
                <FirstMonthProrate f={f} setF={setF} prefix="municipal" amount={f.municipal} />
              </div>
            )}
          </div>

          <div className="mt-3 rounded-lg border p-3" style={{ borderColor: C.line }}>
            <div className="text-xs mb-2" style={{ fontWeight: 600 }}>תעריפי הצריכה לנכס הזה</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="חשמל ₪ לקוט״ש">
                <NumInput value={f.rateElec} placeholder={String(s.elecRate)} onCommit={(v) => setF({ ...f, rateElec: v })} />
              </Field>
              <Field label="מים וביוב ₪ למ״ק">
                <NumInput value={f.rateWS} placeholder={String(s.waterSewageRate)} onCommit={(v) => setF({ ...f, rateWS: v })} />
              </Field>
              <Field label="גז ₪ למ״ק (לא חובה)">
                <NumInput value={f.rateGas} placeholder={String(s.gasRate || 0)} onCommit={(v) => setF({ ...f, rateGas: v })} />
              </Field>
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>
              ריק = ברירת המחדל ({s.elecRate} לקוט״ש, {s.waterSewageRate} למ״ק). התעריפים נכנסים לחוזה ומשמשים לחישוב החשבונות.
            </p>
          </div>
          <p className="text-[11px] mt-1" style={{ color: C.muted }}>
            {f.propertyType === "unit"
              ? "בחוזה ייכתבו התעריפים מלשונית התעריפים, יחד עם סעיף שהם עשויים להשתנות במהלך השכירות."
              : "בחוזה ייכתב שהשוכר פותח חשבונות על שמו מול חברת החשמל, תאגיד המים, הרשות המקומית וחברת הגז."}
          </p>
        </Card>

        <Card title="מטלטלין שנמסרים לדייר">
          <p className="text-xs mb-2" style={{ color: C.muted }}>
            פריט בכל שורה — הרשימה תופיע כנספח א׳ לחוזה.
            {apt.inventory ? " מולאה מרשימת הציוד של הנכס; אפשר להוסיף או להוריד לדייר הזה." : ""}
          </p>
          <textarea value={f.contents} onChange={set("contents")} placeholder={"תנור בנוי"}
            style={{ ...inputStyle, height: 110 }} />
          {/* מה שנמסר לדייר הוא בדרך כלל גם מה שיש בנכס. סימון אחד חוסך
              הקלדה כפולה, ומי שמסר משהו חד-פעמי יכול לכבות אותו. */}
          <label className="flex items-start gap-2 text-[12px] mt-2 cursor-pointer" style={{ color: C.muted }}>
            <input type="checkbox" className="mt-0.5" checked={f.updateInventory !== false}
              onChange={(e) => setF({ ...f, updateInventory: e.target.checked })} />
            <span>לעדכן גם את רשימת הציוד של הנכס — כך היא תמולא מראש לדייר הבא</span>
          </label>
        </Card>

        <Card title="תנאי השכירות">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="שכר דירה חודשי"><input style={inputStyle} type="number" placeholder="0" value={f.rent} onChange={set("rent")} /></Field>
            <Field label="תחילת החוזה"><DateField value={f.leaseStart} onChange={(v) => setF({ ...f, leaseStart: v, leaseEnd: addYears(v, 1) })} /></Field>
            <Field label="סיום החוזה"><DateField value={f.leaseEnd} onChange={(v) => setF({ ...f, leaseEnd: v })} /></Field>
            {/* חיוב יחסי לחודש הראשון — רק כשהכניסה לא ב-1 לחודש. תצוגה
                מקדימה של הסכום היחסי, כדי שההחלטה תתקבל עם המספר.
                שדה חופשי מאפשר לדרוס בסכום אחר שסוכם בעל פה (למשל
                עגול) — ריק ממשיך לפי prorateFirstMonth כרגיל. */}
            {(() => {
              const p = firstMonthProration(f.rent, f.leaseStart);
              if (!p) return null;
              return (
                <div className="sm:col-span-2 space-y-1.5">
                  <label className="flex items-start gap-2 text-xs rounded-lg border p-2.5 cursor-pointer" style={{ borderColor: C.line, background: "#FBFCFB" }}>
                    <input type="checkbox" className="mt-0.5" checked={!!f.prorateFirstMonth}
                      onChange={(e) => setF({ ...f, prorateFirstMonth: e.target.checked })} />
                    <span>
                      חיוב יחסי לחודש הראשון — {p.occupiedDays} מתוך {p.daysInMonth} ימים
                      {f.prorateFirstMonth ? <> · <b>{nis(p.prorated)}</b> במקום חודש מלא</> : " (חודש מלא כברירת מחדל)"}
                    </span>
                  </label>
                  {f.prorateFirstMonth && (
                    <Field label="סכום אחר לחודש הראשון (אם סוכם בעל פה)">
                      <input style={inputStyle} type="number" placeholder={String(p.prorated)}
                        value={f.firstMonthAmount ?? ""} onChange={(e) => setF({ ...f, firstMonthAmount: e.target.value === "" ? null : Number(e.target.value) })} />
                    </Field>
                  )}
                </div>
              );
            })()}
            <div className="sm:col-span-2">
              <SecuritiesPicker value={f.securities} rent={Number(f.rent) || 0} settings={s}
                onChange={(v) => setF({ ...f, securities: v })}
                months={leaseMonthsOf(f)}
                rentPay={{ method: f.payMethodRent || "", checks: Number(f.rentChecks) || 0 }}
                onRentPay={(rp) => setF({ ...f, payMethodRent: rp.method, rentChecks: rp.method === "check" ? (rp.checks || "") : "" })} />
            </div>
            <Field label="יום תשלום שכר דירה"><input style={inputStyle} type="number" min="1" max="28" value={f.rentDay} onChange={set("rentDay")} /></Field>
            <Field label="יום תשלום חשבונות"><input style={inputStyle} type="number" min="1" max="28" value={f.billsDay} onChange={set("billsDay")} /></Field>
            <Field label="מחזור חשבונות">
              <select style={inputStyle} value={f.billsCycle} onChange={(e) => setF({ ...f, billsCycle: Number(e.target.value) })}>
                <option value={1}>חודשי</option><option value={2}>דו-חודשי</option>
              </select>
            </Field>
            {Number(f.billsCycle) === 2 && (
              <Field label="חודשי הגבייה">
                <select style={inputStyle} value={f.billsAnchor} onChange={(e) => setF({ ...f, billsAnchor: Number(e.target.value) })}>
                  <option value={1}>אי-זוגיים</option><option value={2}>זוגיים</option>
                </select>
              </Field>
            )}
          </div>

          <div className="mt-4 pt-3 border-t" style={{ borderColor: C.line }}>
            <div className="text-xs mb-1" style={{ fontWeight: 600 }}>עליית שכר דירה מדורגת</div>
            <p className="text-[11px] mb-2" style={{ color: C.muted }}>אופציונלי — מאיזה תאריך ומה יהיה שכר הדירה. יופיע בחוזה ויחול אוטומטית.</p>
            {(f.rentSteps || []).map((st, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <DateField value={st.from} ariaLabel="מתאריך" onChange={(v) => { const r = [...f.rentSteps]; r[i] = { ...r[i], from: v }; setF({ ...f, rentSteps: r }); }} style={{ padding: "6px 8px", fontSize: 13 }} />
                <input type="number" placeholder="שכר דירה" value={st.rent} onChange={(e) => { const r = [...f.rentSteps]; r[i] = { ...r[i], rent: e.target.value }; setF({ ...f, rentSteps: r }); }} style={{ ...inputStyle, padding: "6px 8px", fontSize: 13 }} />
                <button onClick={() => setF({ ...f, rentSteps: f.rentSteps.filter((_, j) => j !== i) })} className="rounded-lg px-2" style={{ border: `1px solid ${C.line}`, color: C.late }}><Trash2 size={14} /></button>
              </div>
            ))}
            <button onClick={() => setF({ ...f, rentSteps: [...(f.rentSteps || []), { from: addYears(f.leaseStart, 1), rent: "" }] })}
              className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
              <Plus size={13} /> הוספת מדרגה
            </button>
          </div>
        </Card>

        <Card title="שיקים שהתקבלו לשכר הדירה">
          <ChecksSection apt={apt} data={data} setData={setData} tenant={f.tenant} />
        </Card>

        <Card title="קריאות מונה במועד הכניסה">
          <p className="text-xs mb-3" style={{ color: C.muted }}>אלה הקריאות שמהן יחושב החיוב הראשון. מומלץ לצלם כל מונה.</p>
          {[{ k: "entryElec", slot: "elec", label: "מונה חשמל", Icon: Zap }, { k: "entryWater", slot: "water", label: "מונה מים וביוב", Icon: Droplets }, { k: "entryGas", slot: "gas", label: "מונה גז", Icon: Flame }].map(({ k, slot, label, Icon }) => (
            <div key={k} className="rounded-lg border p-3 mb-2" style={{ borderColor: C.line }}>
              <div className="flex items-center gap-1.5 text-sm mb-2" style={{ fontWeight: 600 }}><Icon size={14} style={{ color: C.muted }} /> {label}</div>
              <input style={readingInputStyle} type="number" inputMode="numeric" aria-label={label}
                placeholder="קריאה" value={f[k]} onChange={set(k)} />
              <PhotoSlot label="צילום המונה" id={photos[slot]} busy={busy === slot} onPick={(file) => attach(file, slot)} />
            </div>
          ))}
        </Card>

        <Card title="מסירת הנכס">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="מספר מפתחות שנמסרו"><input style={inputStyle} type="number" placeholder="0" value={f.keys} onChange={set("keys")} /></Field>
            {/* בלוני גז מוצגים רק כשיש גז בנכס. ביחידה שבה גז = "ללא
                חיוב" אלה שני שדות שאין להם משמעות, וגם הסעיף בחוזה
                נגזר מהם. */}
            {gasRelevant && (
              <>
                <Field label="בלון גז גדול — לחיוב">
                  <input style={inputStyle} type="number" placeholder="0" value={f.gasAmount} onChange={set("gasAmount")} disabled={!f.gasBalloon} />
                </Field>
                <Field label="בלון גז קטן — לחיוב">
                  <input style={inputStyle} type="number" placeholder="0" value={f.gasSmallAmount} onChange={set("gasSmallAmount")} disabled={!f.gasBalloon} />
                </Field>
              </>
            )}
          </div>
          {gasRelevant && (
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={f.gasBalloon} onChange={(e) => setF({ ...f, gasBalloon: e.target.checked })} />
              נמסר בלון גז מלא במעמד קבלת המפתח
            </label>
          )}
          <div className="mt-3"><Field label="הערות מסירה"><input style={inputStyle} value={f.handoverNotes} onChange={set("handoverNotes")} placeholder="למשל: שלט לחניה, ריהוט שנשאר בדירה" /></Field></div>
        </Card>

        <Card title="תיעוד מצב הדירה">
          <p className="text-xs mb-2" style={{ color: C.muted }}>תמונות או סרטון מיום המסירה — הראיה למצב הנכס בתום החוזה.</p>
          <div className="flex gap-2 mb-3">
            <FilePick capture accept="image/*,video/*" title="צילום" onPick={addCondition} className="text-xs rounded-lg px-2.5 py-2 gap-1" style={{ background: C.ochre, color: "#fff" }}>
              <Camera size={14} /> {busy === "cond" ? "מעלה…" : "צילום"}
            </FilePick>
            <FilePick accept="image/*,video/*" title="העלאה" onPick={addCondition} className="text-xs rounded-lg px-2.5 py-2 gap-1" style={{ border: `1px solid ${C.line}` }}>
              <Upload size={14} /> העלאה מהגלריה
            </FilePick>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {media.map((m) => (
              <div key={m.id} className="relative">
                <StoredImage id={m.id} className="w-full rounded-lg" style={{ height: 80, objectFit: "cover" }} />
                <button onClick={() => setData((d) => ({ ...d, media: d.media.filter((x) => x.id !== m.id) }))}
                  className="absolute top-1 left-1 rounded-full w-5 h-5 flex items-center justify-center" style={{ background: "rgba(20,38,43,0.6)", color: "#fff" }}><X size={10} /></button>
              </div>
            ))}
            {media.length === 0 && <div className="col-span-full text-xs py-4 text-center" style={{ color: C.muted }}>אין עדיין תיעוד</div>}
          </div>
        </Card>

        <Card title="ערבים">
          {/* שאלה מפורשת ולא רשימה ריקה: "לא הוספתי" ו"סוכם בלי ערבים"
              נראים אותו דבר, ורק אחד מהם אמור להיכנס לחוזה כהצהרה. */}
          <div className="flex flex-wrap gap-2 mb-3">
            {[["none", "אין ערבים לשכירות הזו"], ["some", "יש ערבים"]].map(([k, label]) => {
              const on = k === "some" ? Array.isArray(f.guarantors) && f.guarantors.length > 0
                : Array.isArray(f.guarantors) && f.guarantors.length === 0;
              return (
                <button key={k} type="button"
                  onClick={() => setF({ ...f, guarantors: k === "none" ? [] : (f.guarantors?.length ? f.guarantors : [blankGuarantor()]) })}
                  className="text-sm rounded-lg px-3 py-2"
                  style={on ? { background: C.teal, color: "#fff" } : { border: `1px solid ${C.line}`, color: C.muted }}>
                  {label}
                </button>
              );
            })}
          </div>

          {Array.isArray(f.guarantors) && f.guarantors.length > 0 && (
            <>
              <p className="text-xs mb-2" style={{ color: C.muted }}>
                הפרטים נכנסים לכתב הערבות (נספח ב׳). אחרי השמירה אפשר לשלוח לכל ערב
                קישור אישי — הוא קורא את החוזה, מצלם תעודת זהות וחותם מהטלפון,
                והחתימה חוזרת לכאן.
              </p>
              {f.guarantors.map((g, i) => (
                <div key={g.id} className="rounded-lg border p-3 mb-2" style={{ borderColor: C.line, background: "#FBFCFB" }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs" style={{ fontWeight: 600 }}>ערב {i + 1}</span>
                    <button type="button" onClick={() => setF({ ...f, guarantors: f.guarantors.filter((x) => x.id !== g.id) })}
                      aria-label="הסרת הערב" style={{ color: C.late }}><Trash2 size={14} /></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="שם מלא">
                      <input style={inputStyle} value={g.name}
                        onChange={(e) => setF({ ...f, guarantors: f.guarantors.map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x)) })} />
                    </Field>
                    <Field label="תעודת זהות">
                      <input style={inputStyle} inputMode="numeric" value={g.idNumber}
                        onChange={(e) => setF({ ...f, guarantors: f.guarantors.map((x) => (x.id === g.id ? { ...x, idNumber: e.target.value } : x)) })} />
                    </Field>
                    <Field label="טלפון">
                      <PhoneField value={g.phone}
                        onChange={(v) => setF({ ...f, guarantors: f.guarantors.map((x) => (x.id === g.id ? { ...x, phone: v } : x)) })} />
                    </Field>
                    <Field label="כתובת">
                      <input style={inputStyle} value={g.address}
                        onChange={(e) => setF({ ...f, guarantors: f.guarantors.map((x) => (x.id === g.id ? { ...x, address: e.target.value } : x)) })} />
                    </Field>
                  </div>
                  {/* צילום ת"ז — אותם כפתורים כמו באשף; התצוגה מוכיחה שנשמר. */}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {g.idPhoto && <SecureImage id={g.idPhoto} alt="ת.ז." zoom className="rounded" style={{ height: 40 }} />}
                    <FilePick capture title="צילום תעודת זהות" disabled={busy === `gid:${g.id}`} onPick={(file) => attachGuarantorId(file, g.id)}
                      className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                      <Camera size={13} /> {busy === `gid:${g.id}` ? "שומר…" : g.idPhoto ? "החלפת צילום ת.ז." : 'צילום ת"ז'}
                    </FilePick>
                    <FilePick title="העלאה מהגלריה" disabled={busy === `gid:${g.id}`} onPick={(file) => attachGuarantorId(file, g.id)}
                      className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                      <Upload size={13} />
                    </FilePick>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setF({ ...f, guarantors: [...f.guarantors, blankGuarantor()] })}
                className="text-xs rounded-lg px-3 py-2 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                <Plus size={13} /> הוספת ערב
              </button>
            </>
          )}

          {f.guarantors === undefined && (
            <p className="text-[11px]" style={{ color: C.muted }}>
              בלי בחירה — נשתמש בברירת המחדל: בלי ערבים, והחוזה יאמר זאת במפורש.
            </p>
          )}
        </Card>

        <Card title="החוזה">
          <Field label="סעיפים נוספים שסוכמו">
            <textarea value={f.extraTerms} onChange={set("extraTerms")} placeholder="כל סעיף בשורה נפרדת — למשל: השוכר רשאי להחזיק חיית מחמד."
              style={{ ...inputStyle, height: 80 }} />
          </Field>
          <div className="mt-3 rounded-lg border p-3 text-[12px] whitespace-pre-wrap leading-relaxed overflow-y-auto"
            style={{ borderColor: C.line, maxHeight: 240, background: "#FBFCFB" }}>
            {contractText(previewApt, contractPreview, s)}
          </div>
          {(!s.ownerName || !s.ownerId || !s.ownerPhone) && (
            <div className="mt-2 rounded-lg p-2.5 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
              פרטי בעל הנכס חסרים ולכן יופיעו בחוזה כקווים ריקים. השלימו אותם בלשונית "אזור אישי", והם ייכנסו לכל החוזים אוטומטית.
            </div>
          )}
          <p className="text-[11px] mt-2" style={{ color: C.muted }}>תבנית כללית — מומלץ שעורך דין יעבור עליה לפני החתמה.</p>
        </Card>

        {err && <div className="rounded-lg p-3 text-sm mb-3" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>}

        {saved ? (
          <div className="rounded-xl border p-4 mb-4" style={{ background: C.card, borderColor: C.line }}>
            <div className="flex items-center gap-2 mb-2" style={{ color: C.paid, fontWeight: 600 }}><CheckCircle2 size={18} /> הקליטה נשמרה</div>
            <ul className="text-[11px] space-y-0.5 mb-3" style={{ color: C.muted }}>
              {saved.logs.map((l, i) => <li key={i}>• {l.text}</li>)}
            </ul>
            <div className="flex flex-wrap gap-2">
              {saved.withContract && f.phone && (
                <a href={waLink(f.phone, signLink)} target="_blank" rel="noreferrer"
                  className="text-sm rounded-lg px-3 py-2 flex items-center gap-1.5" style={{ background: "#128C7E", color: "#fff" }}>
                  <Send size={15} /> שליחת החוזה לחתימה
                </a>
              )}
              <button onClick={onExit} className="text-sm rounded-lg px-3 py-2" style={{ border: `1px solid ${C.line}` }}>סיום</button>
            </div>

            {/* הערבים נשמרו יחד עם התקופה, ורק עכשיו אפשר להנפיק להם
                קישור — הוא מצביע על רשומה שקיימת. כל ערב מקבל קישור
                אישי, קורא את החוזה, מצלם ת"ז וחותם; החתימה חוזרת לכאן
                ונכנסת לנספח ב׳. */}
            {(f.guarantors || []).some((g) => String(g.name || "").trim()) && (
              <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
                <div className="text-sm mb-1" style={{ fontWeight: 600 }}>שליחת כתב הערבות לחתימה</div>
                <p className="text-[11px] mb-2" style={{ color: C.muted }}>
                  החתימה נשמרת אצלכם ומופיעה בנספח ב׳ של החוזה, יחד עם הפרטים המלאים.
                </p>
                <GuarantorsEditor apt={data.apartments.find((a) => a.id === apt.id) || apt}
                  setData={setData} ownerPhone={s.ownerPhone} linksOnly />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => save(true)} className="flex-1 rounded-lg py-3 text-sm" style={{ background: C.teal, color: "#fff" }}>
              שמירה ויצירת חוזה לחתימה
            </button>
            <button onClick={() => save(false)} className="rounded-lg px-4 py-3 text-sm" style={{ border: `1px solid ${C.line}` }}>
              שמירה בלבד
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border p-4 mb-3" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-lg font-bold mb-3">{title}</h2>
      {children}
    </div>
  );
}

/* secure=true לצילומי תעודות זהות: הם מוצפנים ולכן נטענים ומפוענחים
   בדפדפן במקום להיטען ישירות מ-src. */
function PhotoSlot({ label, id, busy, onPick, secure = false }) {
  return (
    <div className="flex items-center gap-2 mt-2">
      {id && secure ? <SecureImage id={id} className="rounded" style={{ height: 44 }} /> : null}
      {id && !secure ? <StoredImage id={id} className="rounded" style={{ height: 44 }} /> : null}
      <FilePick capture title={label} onPick={onPick} className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
        <Camera size={13} /> {busy ? "שומר…" : id ? "החלפה" : label}
      </FilePick>
      <FilePick title={label} onPick={onPick} className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
        <Upload size={13} />
      </FilePick>
    </div>
  );
}

/* מודאל ניהול הגישה של דייר לאזור האישי */
/**
 * בחירת אופן המחיקה של דייר.
 *
 * שתי האפשרויות אינן "רכה וקשה" אלא שתי החלטות שונות, וההבדל ביניהן
 * חשבונאי: מחיקה מלאה מוציאה את הכסף שנגבה מדוחות השנים הקודמות. לכן
 * היא לא ברירת המחדל, והסכום שייעלם מוצג במפורש לפני האישור.
 */
function DeleteTenancy({ tenant, income, notes, setNotes, purge, setPurge, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const months = (tenant.contracts || []).length;
  const ready = !purge || typed.trim() === "מחק";

  const Choice = ({ value, title, body, tone }) => (
    <button
      type="button"
      onClick={() => { setPurge(value); setTyped(""); }}
      className="w-full text-right rounded-lg border p-2.5 mb-2"
      style={{
        borderColor: purge === value ? tone : C.line,
        background: purge === value ? `${tone}12` : C.card,
        borderWidth: purge === value ? 2 : 1,
      }}
    >
      <div className="text-[12px] flex items-center gap-1.5" style={{ fontWeight: 600, color: purge === value ? tone : C.ink }}>
        <span style={{
          width: 11, height: 11, borderRadius: 99, display: "inline-block",
          border: `2px solid ${purge === value ? tone : C.line}`,
          background: purge === value ? tone : "transparent",
        }} />
        {title}
      </div>
      <div className="text-[11px] mt-1 pr-4" style={{ color: C.muted }}>{body}</div>
    </button>
  );

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
      <div className="text-[12px] mb-2" style={{ fontWeight: 600 }}>
        מחיקת {tenant.tenant || "הדייר"}
      </div>

      <Choice
        value={false}
        tone={C.teal}
        title="מחיקה עם שמירת ההיסטוריה"
        body={`הדייר יורד מכל המסכים, אבל התשלומים, החשבונות והקריאות שלו נשארים ודוחות השנים הקודמות לא משתנים. אפשר לשחזר בכל רגע.`}
      />
      <Choice
        value
        tone={C.late}
        title="מחיקה לצמיתות, כולל ההיסטוריה"
        body={`נמחקים גם ${nis(income)} שנגבו, החשבונות, קריאות המונה${months ? `, ${months} חוזים` : ""}, הערבים וכל ההתכתבות איתו בתיבת ההודעות (ההודעות שלו, התשובות שלך והאירועים שלו). אי אפשר לשחזר.`}
      />

      <div className="mt-2">
        <label className="text-[11px]" style={{ color: C.muted }}>הערת מחיקה (לא חובה)</label>
        <input
          style={{ ...inputStyle, marginTop: 2 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="נרשם בטעות / עזב בלי הודעה / כפילות"
        />
      </div>

      {/* אישור בהקלדה רק במסלול שאין ממנו חזרה. */}
      {purge && (
        <div className="mt-2 rounded-lg p-2.5" style={{ background: "#FBEDED" }}>
          <div className="text-[11px] mb-1.5" style={{ color: C.late }}>
            הפעולה אינה הפיכה. להמשך הקלידו <b>מחק</b>:
          </div>
          <input
            style={{ ...inputStyle, textAlign: "center" }}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="מחק"
          />
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <button
          onClick={onConfirm}
          disabled={!ready}
          className="flex-1 rounded-lg py-2 text-sm"
          style={{
            background: purge ? C.late : C.teal,
            color: "#fff",
            opacity: ready ? 1 : 0.45,
          }}
        >
          {purge ? "מחיקה לצמיתות" : "מחיקה"}
        </button>
        <button onClick={onCancel} className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}` }}>
          ביטול
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
   כרטיס החוב של הנכס.

   התיעוד המלא: כל חודש, מה נצרך, מה התקבל ומה נותר — כולל חודשים שנסגרו.
   חודש ששולם לא נעלם מכאן, כי "מה שולם ומתי" הוא בדיוק מה שצריך כשיש
   מחלוקת, ולא רק "כמה חייבים עכשיו".

   תקופות קודמות מוצגות בנפרד: חוב של דייר שעזב אינו החוב של הנוכחי,
   אבל הוא גם לא נעלם.
   ============================================================================ */
/* שורת סעיף בפירוט חודש: תווית, נוסחה (כשיש), יתרה, ושדה גביה חלקית —
   input+כפתור נפרדים, לא ב-MonthItemized עצמה, כדי שהקלדה לא תאבד
   פוקוס בכל רינדור מחדש של ההורה (קומפוננטה מוגדרת בפנים הייתה
   נוצרת מחדש בכל רינדור, וריאקט הייתה מתייחסת אליה כאל טיפוס חדש). */
function LedgerItemRow({ label, formula, charged, paid, onSave }) {
  const [val, setVal] = useState(paid || "");
  const due = Math.max(0, Number(charged) - Number(paid));
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 text-[12px]" style={{ borderTop: `1px solid ${C.line}` }}>
      <div style={{ minWidth: 84, fontWeight: 600 }}>{label}</div>
      <div className="flex-1 min-w-0" style={{ color: C.muted }}>
        {formula || (charged > 0 ? `לחיוב ${nis(charged)}` : "אין חיוב")}
      </div>
      <div style={{ color: due > 0 ? C.late : C.paid, whiteSpace: "nowrap" }}>
        {due > 0 ? `יתרה ${nis(due)}` : "שולם"}
      </div>
      <input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="שולם"
        aria-label={`${label} — שולם`}
        style={{ ...inputStyle, width: 90, padding: "4px 6px", fontSize: 12 }} />
      <button onClick={() => onSave(Number(val) || 0)}
        className="text-[11px] rounded-md px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
        עדכון
      </button>
    </div>
  );
}

/* פירוט חודש: שכר דירה וכל סעיף חשבון בנפרד — עם הנוסחה (קריאה קודמת
   ← נוכחית = צריכה × תעריף) כשיש מונה, וגביה חלקית לכל סעיף לחוד.
   x.bills.record הוא חשבון (נכס, חודש) הגולמי מ-debtStatement — לא
   תלוי בדייר, ולכן עובד באותה צורה גם על חודש של דייר שכבר עזב. */
function MonthItemized({ apt, data, run, x }) {
  const s = data.settings || defTariffs();
  const b = x.bills.record || emptyBill();
  const num = (n) => Number(n).toLocaleString("he-IL");
  const METER_KEYS = { elec: "elec", water: "water", gas: "gas" };

  const meterFormula = (meter, k) => {
    const cons = consumptionForMonth(data, apt, s, meter, x.month);
    const amt = Number(b[k]?.amount || 0);
    if (!(cons && cons.diff > 0 && cons.from && cons.to)) return null;
    const rate = rateFor(apt, s, meter, cons.to.date);
    return `קריאה ${num(cons.from.value)} → ${num(cons.to.value)} = ${num(Math.round(cons.diff * 10) / 10)} ${UNIT[meter]} × ₪${rate}${amt ? ` = ${nis(amt)}` : ""}`;
  };

  const billLines = BILLS.filter(({ k }) => Number(b[k]?.amount || 0) > 0 || itemPaid(b[k]) > 0);
  const hasRent = Number(x.rent.charged) > 0 || Number(x.rent.paid) > 0;

  return (
    <div className="px-3 pb-2" style={{ background: "#FBFCFB" }}>
      {hasRent && (
        <LedgerItemRow label="שכר דירה" formula={x.rent.charged ? `לחיוב ${nis(x.rent.charged)}` : null}
          charged={x.rent.charged} paid={x.rent.paid}
          onSave={(v) => run([{ type: "set_rent_paid", apartment: apt.id, month: x.month, paid: true, amount: v }])} />
      )}
      {billLines.map(({ k, label }) => (
        <LedgerItemRow key={k} label={label} formula={METER_KEYS[k] ? meterFormula(k, k) : null}
          charged={Number(b[k]?.amount || 0)} paid={itemPaid(b[k])}
          onSave={(v) => run([{ type: "set_bill", apartment: apt.id, month: x.month, bill: k, paidAmount: v }])} />
      ))}
      {!hasRent && !billLines.length && (
        <div className="py-2 text-[12px]" style={{ color: C.muted }}>אין פירוט רשום לחודש הזה.</div>
      )}
    </div>
  );
}

/** מפתחות "YYYY-MM" מ-from עד to, כולל שני הקצוות. ריק כשהטווח הפוך או חסר. */
function monthsBetween(from, to) {
  if (!from || !to || from > to) return [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (!fy || !fm || !ty || !tm) return [];
  const out = [];
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(mKey(y, m));
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return out;
}

/* ============================================================================
   עריכת חשבונות עבר — דייר ותיק שרק עכשיו נרשם במערכת, שהסכומים שלו
   היו שונים בתקופות שונות בעבר (לא רק מה שמשולם היום).

   לכל קטגוריה פעילה (שכר דירה, ועד בית, ארנונה) רשימת תקופות נפרדת
   משלה — לא טווח אחד משותף לכולן, כי הן בדרך כלל השתנו בתאריכים שונים
   לגמרי. שכר דירה שונה מהשאר: הפעולה (set_rent_history) קובעת גם
   "כמה היה מחויב" (מדרגת שכר דירה), לא רק "כמה התקבל" — כי בשכר דירה,
   בניגוד לחשבונות, שני הדברים חיים במקומות נפרדים לגמרי; בלעדי זה
   נוצר חוב מדומה בדיוק כמו שקרה בנתונים האמיתיים. ועד בית וארנונה
   משתמשים ב-set_bill הרגיל, ששומר את שניהם יחד ולכן בלי הפער הזה
   מלכתחילה. */
function yearsFromTo(fromYear, toYear) {
  const out = [];
  for (let y = fromYear; y <= toYear; y++) out.push(y);
  return out;
}

/** בחירת חודש+שנה בשני בוררים נפרדים — מחזירה "YYYY-MM". נוח יותר
    לקפיצה בין שנים מבורר תאריך רגיל, בעיקר בטלפון. */
function MonthYearPick({ value, onChange, years }) {
  const [y, m] = String(value || "").split("-").map(Number);
  return (
    <span className="flex items-center gap-1">
      <select value={m || 1} onChange={(e) => onChange(`${y || years[0]}-${String(e.target.value).padStart(2, "0")}`)}
        style={{ ...inputStyle, padding: "4px 2px", fontSize: 11, width: 66 }}>
        {MONTHS.map((label, i) => <option key={label} value={i + 1}>{label}</option>)}
      </select>
      <select value={y || years[0]} onChange={(e) => onChange(`${e.target.value}-${String(m || 1).padStart(2, "0")}`)}
        style={{ ...inputStyle, padding: "4px 2px", fontSize: 11, width: 62 }}>
        {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </span>
  );
}

/** רשימת התקופות של קטגוריה אחת — "הוספת תקופה" ממשיכה מסוף הקודמת. */
const HISTORY_TABS = {
  rent: { label: "שכר דירה", color: "#2F8F6B" },
  vaad: { label: "ועד בית", color: "#3D6B99" },
  arnona: { label: "ארנונה", color: "#B8860B" },
};

/** שורת חודש אחת מ-debt.months, מצומצמת לקטגוריה אחת בלבד. */
function historyMonthRow(kind, x) {
  if (kind === "rent") return { month: x.month, charged: x.rent.charged, paid: x.rent.paid, due: x.rent.due };
  const it = x.bills.record?.[kind];
  return { month: x.month, charged: Number(it?.amount || 0), paid: itemPaid(it), due: itemDue(it) };
}

/**
 * קטגוריה אחת, לגמרי בפני עצמה: עורך תקופה אחת מעל, טבלת החודשים שלה
 * (רק שלה — לא מעורבבת עם קטגוריות אחרות) מתחת. שינוי בתאריכים צובע
 * מיד את השורות שהתקופה תכסה — תצוגה מקדימה חיה, בלי לשמור, כדי לראות
 * פיזית מה עומד לקרות לפני שלוחצים על הכפתור.
 *
 * שמירה היא של תקופה אחת בכל פעם, לא אצווה: ברגע שתקופה נשמרת בהצלחה
 * היא נרשמת כשורת אישור קבועה למעלה, והעורך נפתח מיד מחדש על התקופה
 * הבאה (מהחודש שאחרי זו שנשמרה) — כך אפשר להמשיך ולכסות רצף שלם בלי
 * להקליד "עוד תקופה" בכל פעם. key={kind} ב-PastAccountsEditor מאפס
 * את כל זה בכל מעבר לשונית, כך שאין דליפת מצב בין קטגוריה לקטגוריה. */
function CategoryHistoryPanel({ kind, initialFrom, defaultAmount, years, months, onSave }) {
  const meta = HISTORY_TABS[kind];
  // to מברירת מחדל CUR_KEY, אבל אם ה"מ" עצמו כבר אחרי החודש הנוכחי
  // (המשך אוטומטי אחרי תקופה ששמרה עד החודש הזה) — עד לא יכול לצאת
  // לפני מ; נופל לחודש בודד במקום טווח לא תקין.
  const mkPeriod = (from) => ({ from, to: from > CUR_KEY ? from : CUR_KEY, amount: defaultAmount ? String(defaultAmount) : "" });
  const [period, setPeriod] = useState(() => mkPeriod(initialFrom));
  const [saved, setSaved] = useState([]); // תקופות שכבר נשמרו בסשן הזה — לתצוגה בלבד
  const [status, setStatus] = useState(null); // null | "saving" | "error"

  const valid = period.from && period.to && period.from <= period.to && Number(period.amount) > 0;
  const previewSet = useMemo(() => new Set(monthsBetween(period.from, period.to)), [period.from, period.to]);

  /* שמירה מיידית עם המתנה לתוצאה אמיתית מהשרת (לא ה-debounce הרגיל) —
     דריסה קשה-לחזור-עליה כזו לא יכולה "להיראות" שנשמרה בעוד שהתנגשות
     עם טאב/מכשיר אחר בלעה אותה בשקט. */
  const save = async () => {
    if (!valid || status === "saving") return;
    const label = `${monthLabel(period.from)}–${monthLabel(period.to)}: ${nis(Number(period.amount))} לחודש`;
    if (!window.confirm(`לעדכן ${meta.label} ל-${monthsBetween(period.from, period.to).length} חודשים?\n${label}\nזה דורס כל תשלום קיים באותם חודשים.`)) return;
    setStatus("saving");
    const r = await onSave([{ from: period.from, to: period.to, amount: Number(period.amount) }]);
    if (r?.ok) {
      setStatus(null);
      setSaved((list) => [...list, label]);
      setPeriod(mkPeriod(nextMonth(period.to)));
    } else {
      setStatus("error");
    }
  };

  return (
    <div>
      {saved.length > 0 && (
        <div className="mb-2 space-y-0.5">
          {saved.map((label, i) => (
            <div key={i} className="text-[11px] flex items-center gap-1.5" style={{ color: C.paid }}>
              <CheckCircle2 size={12} className="shrink-0" /> {label}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <MonthYearPick value={period.from} years={years} onChange={(v) => setPeriod((p) => ({ ...p, from: v }))} />
        <span className="text-[11px]" style={{ color: C.muted }}>עד</span>
        <MonthYearPick value={period.to} years={years} onChange={(v) => setPeriod((p) => ({ ...p, to: v }))} />
        <input type="number" inputMode="numeric" value={period.amount} onChange={(e) => setPeriod((p) => ({ ...p, amount: e.target.value }))}
          placeholder="סכום לחודש" style={{ ...inputStyle, width: 90, padding: "4px 6px", fontSize: 12 }} />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={save} disabled={!valid || status === "saving"} className="text-[11px] rounded-md px-2.5 py-1.5 flex items-center gap-1"
          style={{ background: meta.color, color: "#fff", opacity: (!valid || status === "saving") ? 0.6 : 1 }}>
          {status === "saving" && <Loader2 size={12} className="animate-spin" />}
          {status === "saving" ? "שומר…" : `שמירת ${meta.label}`}
        </button>
      </div>
      {status === "error" && (
        <div className="mb-2 text-[11px] flex items-center gap-1" style={{ color: C.late }}>
          <AlertTriangle size={12} className="shrink-0" />
          השמירה נכשלה (כנראה טאב/מכשיר אחר פתוח על אותו חשבון) — התקופה נשארה כאן, אפשר ללחוץ שוב.
        </div>
      )}

      {months.length > 0 && (
        <div className="overflow-x-auto -mx-1 px-1" style={{ maxHeight: 260, overflowY: "auto" }}>
          <table className="w-full text-[11px]" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: C.muted }}>
                <th className="text-right py-1 font-normal">חודש</th>
                <th className="text-right py-1 font-normal">לחיוב</th>
                <th className="text-right py-1 font-normal">שולם</th>
                <th className="text-right py-1 font-normal">יתרה</th>
              </tr>
            </thead>
            <tbody>
              {months.map((x) => {
                const row = historyMonthRow(kind, x);
                const isPreview = previewSet.has(row.month);
                const isPaid = row.due <= 0 && row.charged > 0;
                const bg = isPreview ? "#E3EEFB" : isPaid ? "#EFF7F1" : row.due > 0 ? "#FDF1EF" : "transparent";
                return (
                  <tr key={row.month} style={{ background: bg, borderTop: `1px solid ${C.line}` }}>
                    <td className="py-1">{monthLabel(row.month)}</td>
                    <td>{row.charged ? nis(row.charged) : "—"}</td>
                    <td style={{ color: C.paid }}>{row.paid ? nis(row.paid) : "—"}</td>
                    <td style={{ color: row.due > 0 ? C.late : C.muted, fontWeight: row.due > 0 ? 600 : 400 }}>
                      {row.due > 0 ? nis(row.due) : "שולם"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PastAccountsEditor({ apt, tenancy, months, saveNow }) {
  const startYear = Math.min(Number((tenancy.leaseStart || CUR_KEY).slice(0, 4)) || CUR_Y, CUR_Y);
  const years = yearsFromTo(startYear, CUR_Y);
  const leaseMonth = (tenancy.leaseStart || CUR_KEY).slice(0, 7);
  const hasVaad = tenancy.vaadActive !== false && Number(tenancy.vaad || 0) > 0;
  const hasArnona = tenancy.arnonaActive !== false && Number(tenancy.arnona || 0) > 0;
  const tabs = ["rent", ...(hasVaad ? ["vaad"] : []), ...(hasArnona ? ["arnona"] : [])];
  const [tab, setTab] = useState("rent");

  const saveRent = (periods) => saveNow(periods.map((p) => ({
    type: "set_rent_history", apartment: apt.id, tenancyId: tenancy.id, from: p.from, to: p.to, rent: p.amount,
  })));
  const saveBill = (bill) => (periods) => saveNow(periods.flatMap((p) => monthsBetween(p.from, p.to).map((mo) => ({
    type: "set_bill", apartment: apt.id, month: mo, bill, paid: true, amount: p.amount,
  }))));
  const defaultAmountOf = (kind) => (kind === "rent" ? tenancy.rent : kind === "vaad" ? tenancy.vaad : tenancy.arnona);
  const saveOf = (kind) => (kind === "rent" ? saveRent : saveBill(kind));
  // סך החוב הפתוח של הקטגוריה — מוצג על הלשונית עצמה, כדי לדעת מיד
  // איפה יש מה לתקן בלי לפתוח כל אחת בנפרד.
  const debtOf = (kind) => months.reduce((t, x) => t + historyMonthRow(kind, x).due, 0);

  return (
    <div className="rounded-lg mb-3" style={{ border: `1px solid ${C.line}` }}>
      <div className="text-[12px] px-3 pt-2.5" style={{ fontWeight: 600 }}>עריכת חשבונות עבר</div>
      {/* לשוניות — כל קטגוריה נפרדת לחלוטין, טבלה משלה ותקופות משלה,
          עם סך החוב הפתוח שלה מוצג ישירות על הלשונית. */}
      <div className="flex items-center gap-1 px-3 mt-1.5 mb-2 flex-wrap">
        {tabs.map((k) => {
          const due = debtOf(k);
          return (
            <button key={k} onClick={() => setTab(k)}
              className="text-[12px] rounded-md px-3 py-1.5 flex items-center gap-1"
              style={{
                background: tab === k ? HISTORY_TABS[k].color : "transparent",
                color: tab === k ? "#fff" : C.muted,
                fontWeight: tab === k ? 600 : 400,
              }}>
              {HISTORY_TABS[k].label}
              {due > 0 && (
                <span style={{ color: tab === k ? "#fff" : C.late, fontWeight: 700 }}>· {nis(due)}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="px-3 pb-3">
        <CategoryHistoryPanel key={tab} kind={tab} initialFrom={leaseMonth} defaultAmount={defaultAmountOf(tab)}
          years={years} months={months} onSave={saveOf(tab)} />
      </div>
    </div>
  );
}

/**
 * tenancyId — רשות: פותח את הכרטיס ישר על תקופה מסוימת (מ"דיירים
 * וחובות"), במקום ברירת המחדל "הנוכחית, או האחרונה שעזבה". חובה
 * כשבנכס יש כמה תקופות שעזבו — בלעדיו הכרטיס תמיד קפץ לאחרונה מביניהן,
 * וזו היחידה שיכלה להיות מסומנת כשולמה.
 */
/**
 * historyMode — נפתח מ"היסטוריית דיירים וחובות": מציג ברירת מחדל את כל
 * חודשי התקופה (כולל ששולמו במלואם), לא רק את הפתוחים — כדי שכרטיס של
 * דייר שכבר שילם הכול לא ייראה ריק. ברירת המחדל (false) — כמו קודם.
 *
 * initialOpenMonth — נפתח מרשימת הפירוט של כרטיסי הדשבורד: מרחיב מיד את
 * החודש הרלוונטי (כדי שהפירוט לפי סעיף ייראה בלי לחיצה נוספת), וגם
 * מציג חודשים ששולמו במלואם — אחרת חודש "נגבה החודש" שנבחר לא היה
 * מופיע בטבלה בכלל.
 */
function LedgerModal({ apt, data, run, saveNow, onClose, tenancyId = null, historyMode = false, initialOpenMonth = null, tabs = null }) {
  const [showPaid, setShowPaid] = useState(historyMode || !!initialOpenMonth);
  const [receiptLightbox, setReceiptLightbox] = useState(null);
  // אילו חודשים פתוחים לפירוט: שכר דירה וכל סעיף חשבון בנפרד, עם הנוסחה
  // (קריאה קודמת/נוכחית/צריכה/תעריף) וגביה חלקית לכל סעיף בנפרד.
  const [openMonths, setOpenMonths] = useState(() => new Set(initialOpenMonth ? [initialOpenMonth] : []));

  /* הטבלה הראשית: הדייר הנוכחי. כשהנכס פנוי — התקופה האחרונה שהייתה
     בו, כדי שמי שעזב עם חוב עדיין ייראה ויוכל להיות מסומן כשולם. התקופה
     שמוצגת למעלה לא חוזרת גם ברשימת "תקופות קודמות" למטה — כך היא
     הופיעה פעמיים ונראתה כחוב כפול. */
  const cur = currentDebt(data, apt, CUR_KEY);
  const past = pastDebts(data, apt, CUR_KEY);
  const resolved = resolvedDebts(data, apt, CUR_KEY);
  const picked = tenancyId
    ? ([...past, ...resolved].find((x) => x.tenancy.id === tenancyId)
      || (cur?.tenancy.id === tenancyId ? cur : null))
    : null;
  const main = picked || cur || past[past.length - 1] || null;   // האחרונה שעזבה — כמו ברירת המחדל של debtStatement
  const debt = main ? main.statement : debtStatement(data, apt, CUR_KEY, { tenancyId: null });
  const mainTenancy = main?.tenancy || null;
  const mainIsPast = !!mainTenancy && mainTenancy.id !== cur?.tenancy?.id;
  const previous = tenancyId ? [] : past.filter((x) => x.tenancy.id !== mainTenancy?.id).map(({ tenancy, statement }) => ({ t: tenancy, st: statement }));

  const rows = showPaid ? debt.months : debt.months.filter((x) => x.due > 0);

  const Money = ({ v, tone }) => (
    <span style={{ color: tone, fontVariantNumeric: "tabular-nums" }}>{v ? nis(v) : "—"}</span>
  );

  return (
    <Modal title={`${historyMode ? "היסטוריית תשלומים וחובות" : "כרטיס חוב"} — ${apt.name}`} onClose={onClose} wide tabs={tabs}>
      <div className="text-sm mb-3" style={{ color: C.muted }}>
        {mainIsPast
          ? <>{mainTenancy.tenant} <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: "#FBF2E3", color: "#6B4E12" }}>עזב/ה {heDate(mainTenancy.closedAt || mainTenancy.leaseEnd)} · חוב של הדייר</span></>
          : (apt.tenant || "אין דייר פעיל")}
        {debt.since && ` · חוב פתוח מ${monthLabel(debt.since)}`}
      </div>

      <div className="rounded-lg p-3 mb-3 flex flex-wrap items-center justify-between gap-3"
        style={{ background: debt.total > 0 ? "#FBF2E3" : "#EAF3EE" }}>
        <div className="text-sm" style={{ color: debt.total > 0 ? "#6B4E12" : C.paid }}>
          {debt.total > 0 ? "סך החוב הפתוח" : "אין חוב פתוח"}
        </div>
        {debt.total > 0 && (
          <div className="flex items-center gap-4 text-sm">
            <span style={{ color: C.muted }}>שכר דירה <b style={{ color: C.ink }}>{nis(debt.rentDue)}</b></span>
            <span style={{ color: C.muted }}>חשבונות <b style={{ color: C.ink }}>{nis(debt.billsDue)}</b></span>
            <b className="text-lg" style={{ color: "#6B4E12" }}>{nis(debt.total)}</b>
          </div>
        )}
      </div>

      {/* דיווחי תשלום מהדייר — האסמכתאות שהוא צירף, לצפייה קבועה כאן
          ולא רק ברגע שההתראה הופיעה בתיבת ההודעות. לא "שולם ✓" — זו
          הטענה של הדייר; הסימון בפועל נשאר פעולה נפרדת בטבלה למטה. */}
      {(data.paymentReports || []).filter((p) => p.aptId === apt.id).length > 0 && (
        <div className="rounded-lg border p-3 mb-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>דיווחי תשלום מהדייר</div>
          <div className="space-y-1.5">
            {[...(data.paymentReports || [])].filter((p) => p.aptId === apt.id)
              .sort((a, b) => (a.date < b.date ? 1 : -1)).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-[12px] rounded-lg p-2" style={{ background: C.surface }}>
                <div className="min-w-0">
                  <span style={{ fontWeight: 600 }}>{nis(p.amount)}</span>
                  <span style={{ color: C.muted }}> · {p.category === "bills" ? "חשבונות" : "שכר דירה"} · {methodLabel(p.method) || p.method} · {heDate(p.date)}</span>
                  {p.note && <span style={{ color: C.muted }}> · {p.note}</span>}
                </div>
                {p.photoId && (
                  <button type="button" onClick={() => setReceiptLightbox(p.photoId)} title="לחיצה להגדלה" aria-label="הגדלת האסמכתא" className="shrink-0">
                    <StoredImage id={p.photoId} className="rounded" style={{ height: 36 }} scope="owner" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {receiptLightbox && <ZoomLightbox id={receiptLightbox} scope="owner" onClose={() => setReceiptLightbox(null)} />}

      {mainTenancy && <PastAccountsEditor apt={apt} tenancy={mainTenancy} months={debt.months} saveNow={saveNow} />}

      {/* הטבלה המקורית (כל הקטגוריות מעורבבות בשורה אחת) מיותרת כשיש
         "עריכת חשבונות עבר" — לכל קטגוריה שם כבר יש טבלה משלה, ברורה
         יותר. נשארת רק כשאין תקופה לערוך איתה (mainTenancy null). */}
      {!mainTenancy && (debt.months.length === 0 ? (
        <p className="text-sm" style={{ color: C.muted }}>עדיין אין חיובים רשומים לנכס הזה.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: C.muted }}>
                <th className="text-right py-1.5 font-normal">חודש</th>
                <th className="text-right py-1.5 font-normal">שכר דירה</th>
                <th className="text-right py-1.5 font-normal">חשבונות</th>
                <th className="text-right py-1.5 font-normal">התקבל</th>
                <th className="text-right py-1.5 font-normal">איך</th>
                <th className="text-right py-1.5 font-normal">יתרה</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => {
                const open = openMonths.has(x.month);
                const toggle = () => setOpenMonths((s) => {
                  const next = new Set(s);
                  if (next.has(x.month)) next.delete(x.month); else next.add(x.month);
                  return next;
                });
                return (
                <Fragment key={x.month}>
                <tr style={{ borderTop: `1px solid ${C.line}` }}>
                  <td className="py-1.5">
                    <button onClick={toggle} className="flex items-center gap-1" style={{ color: C.ink }}>
                      <ChevronDown size={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                      {monthLabel(x.month)}
                    </button>
                    {x.month === CUR_KEY && <span className="text-[10px]" style={{ color: C.muted }}> · החודש</span>}
                  </td>
                  <td><Money v={x.rent.charged} /></td>
                  <td><Money v={x.bills.charged} /></td>
                  <td><Money v={x.paid} tone={C.paid} /></td>
                  <td>
                    {x.methods.length === 0 ? <span style={{ color: C.muted }}>—</span> : (
                      <span className="flex flex-wrap gap-1">
                        {x.methods.map((m) => (
                          <MethodTag key={m.k} value={m.k} />
                        ))}
                      </span>
                    )}
                  </td>
                  <td><b><Money v={x.due} tone={x.due > 0 ? C.late : C.muted} /></b></td>
                  <td className="text-left">
                    {x.due > 0 && (
                      <button
                        onClick={() => run([
                          ...(x.rent.due > 0 ? [{ type: "set_rent_paid", apartment: apt.id, month: x.month, paid: true, amount: x.rent.charged }] : []),
                          ...(x.bills.due > 0 ? [{ type: "set_bills_paid", apartment: apt.id, month: x.month, paid: true }] : []),
                        ])}
                        className="rounded-md px-2 py-1 text-[11px] whitespace-nowrap"
                        style={{ border: `1px solid ${C.line}`, color: C.paid }}>
                        סימון כשולם
                      </button>
                    )}
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <MonthItemized apt={apt} data={data} run={run} x={x} />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {!mainTenancy && debt.months.some((x) => x.due === 0) && (
        <button onClick={() => setShowPaid(!showPaid)} className="mt-2 text-[11px]" style={{ color: C.teal }}>
          {showPaid ? "הצגת חודשים פתוחים בלבד" : `הצגת כל החודשים (${debt.months.length})`}
        </button>
      )}

      {previous.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
          <div className="text-sm mb-2" style={{ fontWeight: 600 }}>חוב שנשאר מתקופות קודמות</div>
          <p className="text-[11px] mb-2" style={{ color: C.muted }}>
            אינו נכלל בחוב של הדייר הנוכחי ואינו נשלח אליו.
          </p>
          {previous.map(({ t, st }) => (
            <div key={t.id} className="flex items-center justify-between text-[12px] py-1"
              style={{ borderTop: `1px solid ${C.line}` }}>
              <span>
                {t.tenant}
                <span style={{ color: C.muted }}> · {heDate(t.leaseStart)}–{t.leaseEnd ? heDate(t.leaseEnd) : "…"}</span>
                <span style={{ color: C.muted }}> · {st.months.filter((x) => x.due > 0).length} חודשים</span>
              </span>
              <b style={{ color: C.late }}>{nis(st.total)}</b>
            </div>
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
          <div className="text-sm mb-2" style={{ fontWeight: 600 }}>חובות שלא ייגבו</div>
          {resolved.map(({ tenancy: t, statement: st }) => (
            <div key={t.id} className="flex items-center justify-between text-[12px] py-1" style={{ borderTop: `1px solid ${C.line}` }}>
              <span>
                {t.tenant}
                <span style={{ color: C.muted }}> · {DEBT_RESOLUTIONS.find((x) => x.k === t.debtResolution?.kind)?.label}{t.debtResolution?.date ? ` · ${heDate(t.debtResolution.date)}` : ""}{t.debtResolution?.notes ? ` · ${t.debtResolution.notes}` : ""}</span>
              </span>
              <b style={{ color: C.muted, textDecoration: "line-through" }}>{nis(st.total)}</b>
            </div>
          ))}
        </div>
      )}

      {/* ריכוז החוב נשלח לדייר שהחוב שלו מוצג — גם אם כבר עזב: הטלפון
          שלו נשמר על התקופה. */}
      {(mainIsPast ? mainTenancy.phone : apt.tenant && apt.phone) && debt.total > 0 && (
        <a href={waLink(mainIsPast ? mainTenancy.phone : apt.phone, statementText(mainIsPast ? { ...apt, ...mainTenancy, id: apt.id } : apt, data, CUR_KEY, data.settings))} target="_blank" rel="noreferrer"
          className="mt-4 w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
          style={{ background: "#128C7E", color: "#fff" }}>
          <MessageCircle size={15} /> שליחת ריכוז החוב ל{mainIsPast ? mainTenancy.tenant : "דייר"}
        </a>
      )}
    </Modal>
  );
}

/* לוח תקופות השכירות של נכס — נוכחית, עתידיות וסגורות */
function TenanciesModal({ apt, data, run, onClose, onIntake, tabs = null }) {
  const cur = currentTenancy(data, apt.id);
  const upcoming = upcomingTenancies(data, apt.id);
  const past = pastTenancies(data, apt.id);
  const [closing, setClosing] = useState(null);
  const [closeDate, setCloseDate] = useState(TODAY);
  const [closeNotes, setCloseNotes] = useState("");
  const [deposit, setDeposit] = useState("");
  const [resolution, setResolution] = useState("tenant");   // ייעוד החוב שנשאר בסגירה
  const [resolutionNotes, setResolutionNotes] = useState("");

  /* החוב שיישאר פתוח אם התקופה תיסגר בתאריך שנבחר: מחושב על עותק שבו
     התקופה כבר מקוצרת, כדי שחודשים שאחרי היציאה לא ייספרו. */
  const remainingAt = (t, date) => {
    const tmp = { ...data, tenancies: (data.tenancies || []).map((x) => (x.id === t.id ? { ...x, closed: true, closedAt: date, leaseEnd: date } : x)) };
    return debtStatement(tmp, apt, CUR_KEY, { tenancyId: t.id });
  };
  const [deleting, setDeleting] = useState(null);
  const [delNotes, setDelNotes] = useState("");
  const [purge, setPurge] = useState(false);
  const [editing, setEditing] = useState(null);   // תקופה שנפתחה לעריכה
  const removed = deletedTenancies(data, apt.id);

  const incomeOf = (t) => {
    let sum = 0;
    (data.tenancies || []).length;
    for (let y = 2020; y <= CUR_Y + 1; y++) {
      for (let m = 1; m <= 12; m++) {
        const mk_ = mKey(y, m);
        if (tenancyForMonth(data, apt.id, mk_)?.id !== t.id) continue;
        const inc = monthIncome(data, apt.id, mk_);
        sum += REPORT_SOURCES.reduce((t, x) => t + (inc[x.k] || 0), 0);
      }
    }
    return sum;
  };

  /* שורת תקופה. נקראת כפונקציה רגילה — Row({...}) — ולא כקומפוננטה <Row/>:
     הפונקציה נוצרת מחדש בכל רינדור של המודל, וקומפוננטה שהטיפוס שלה מתחלף
     גורמת ל-React לפרק ולהרכיב את השורה מחדש בכל הקלדה — שדה הערת המחיקה
     איבד פוקוס אחרי כל אות, ושדה האישור "מחק" התאפס. */
  const Row = ({ t, tone }) => (
    <div className="rounded-lg border p-3 mb-2" style={{ borderColor: tone.border, background: tone.bg }}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm" style={{ fontWeight: 600 }}>{t.tenant}</div>
          <div className="text-[11px]" style={{ color: C.muted }}>
            {heDate(t.leaseStart)} — {t.leaseEnd ? heDate(t.leaseEnd) : "ללא מועד סיום"}
            {t.closed && t.contractEnd && t.contractEnd !== t.leaseEnd ? ` (חוזה עד ${heDate(t.contractEnd)})` : ""} · {nis(t.rent)} לחודש
          </div>
          {t.phone && <div className="text-[11px]" style={{ color: C.muted }}>{t.phone}</div>}
          {t.closeNotes && <div className="text-[11px] mt-1" style={{ color: C.muted }}>{t.closeNotes}</div>}
          {t.closed && t.debtResolution && (
            <div className="text-[11px] mt-1" style={{ color: t.debtResolution.kind === "tenant" ? C.late : C.muted }}>
              {DEBT_RESOLUTIONS.find((x) => x.k === t.debtResolution.kind)?.label}
              {t.debtResolution.amount ? ` · ${nis(t.debtResolution.amount)}` : ""}
              {t.debtResolution.notes ? ` · ${t.debtResolution.notes}` : ""}
            </div>
          )}
          {t.deleteNotes && <div className="text-[11px] mt-1" style={{ color: C.late }}>הערת מחיקה: {t.deleteNotes}</div>}
        </div>
        <div className="text-left shrink-0">
          <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: tone.chipBg, color: tone.chip }}>{tone.label}</span>
          <div className="text-[11px] mt-1" style={{ color: C.muted }}>נגבה {nis(incomeOf(t))}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-2">
        {(t.contracts || []).length > 0 && (
          <span className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
            {t.contracts.filter((c) => c.status === "signed").length} מתוך {t.contracts.length} חוזים חתומים
          </span>
        )}
        {(t.guarantors || []).length > 0 && (
          <span className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
            {t.guarantors.length} ערבים
          </span>
        )}
        {/* עריכה לכל תקופה — נוכחית, עתידית או שהסתיימה. עד כאן אפשר
            היה לערוך רק את הדייר הפעיל, ומכרטיס הנכס בלבד: טעות בתאריך
            או בטלפון של דייר עתידי נשארה במסמך ובחוזה בלי דרך לתקן. */}
        {!t.deleted && (
          <button onClick={() => setEditing(t)}
            className="text-[11px] rounded-lg px-2 py-1 flex items-center gap-1"
            style={{ border: `1px solid ${C.line}`, color: C.teal }}>
            <Pencil size={11} /> עריכה
          </button>
        )}
        {!t.closed && (
          <button onClick={() => { setClosing(t.id); setCloseDate(t.leaseEnd || TODAY); setResolution("tenant"); setResolutionNotes(""); }}
            className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.late }}>
            סגירת התקופה
          </button>
        )}
        {t.closed && !t.deleted && (
          <button onClick={() => run([{ type: "reopen_tenancy", apartment: apt.id, tenancyId: t.id }])}
            className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
            פתיחה מחדש
          </button>
        )}
        {/* ייעוד החוב ניתן לשינוי גם אחרי הסגירה — חוב שסומן "של הדייר"
            ואחרי שנה ברור שלא ייגבה, או להפך. */}
        {t.closed && !t.deleted && debtStatement(data, apt, CUR_KEY, { tenancyId: t.id }).total > 0 && (
          <select value={t.debtResolution?.kind || "tenant"} aria-label="ייעוד החוב"
            onChange={(e) => run([{ type: "resolve_tenancy_debt", apartment: apt.id, tenancyId: t.id, kind: e.target.value, amount: debtStatement(data, apt, CUR_KEY, { tenancyId: t.id }).total }])}
            className="text-[11px] rounded-lg px-2 py-1" style={{ border: `1px solid ${C.line}`, color: C.ink, background: "#fff" }}>
            {DEBT_RESOLUTIONS.map((x) => <option key={x.k} value={x.k}>{x.label}</option>)}
          </select>
        )}
        {t.deleted ? (
          <button onClick={() => run([{ type: "restore_tenancy", apartment: apt.id, tenancyId: t.id }])}
            className="text-[11px] rounded-lg px-2 py-1 flex items-center gap-1"
            style={{ border: `1px solid ${C.line}`, color: C.teal }}>
            <Undo2 size={11} /> שחזור
          </button>
        ) : (
          <button onClick={() => { setDeleting(t.id); setDelNotes(""); setPurge(false); }}
            className="text-[11px] rounded-lg px-2 py-1 flex items-center gap-1"
            style={{ border: `1px solid ${C.line}`, color: C.late }}>
            <Trash2 size={11} /> מחיקה
          </button>
        )}
      </div>

      {deleting === t.id && (
        <DeleteTenancy
          tenant={t}
          income={incomeOf(t)}
          notes={delNotes} setNotes={setDelNotes}
          purge={purge} setPurge={setPurge}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            /* מחיקה לצמיתות: גם ההתכתבות עם הדייר נמחקת בשרת — ההודעות
               שלו, התשובות שלי והאירועים שלו בתיבת ההודעות. החלון: מסוף
               התקופה הקודמת בדירה ועד תחילת הבאה (גם חתימה והודעות
               שלפני הכניסה הן שלו). התקופות כוללות מחוקות-רכות — גם הן
               דיירים אחרים. */
            if (purge) {
              const others = (data.tenancies || []).filter((x) => x.aptId === apt.id && x.id !== t.id);
              const prevEnd = others.filter((x) => x.leaseStart < t.leaseStart)
                .map((x) => x.closedAt || x.leaseEnd || x.leaseStart).sort().slice(-1)[0] || null;
              const nextStart = others.filter((x) => x.leaseStart > t.leaseStart).map((x) => x.leaseStart).sort()[0] || null;
              const iso = (d) => { const x = d ? new Date(`${d}T00:00:00`) : null; return x && !Number.isNaN(x.getTime()) ? x.toISOString() : null; };
              dataApi.notificationsForgetTenancy({ apartmentId: apt.id, tenancyId: t.id, from: iso(prevEnd), until: iso(nextStart) })
                .catch(() => { /* ההתכתבות תישאר בתיבה — אפשר למחוק משם ידנית */ });
            }
            run([{ type: "remove_tenancy", apartment: apt.id, tenancyId: t.id, purge, notes: delNotes }]);
            setDeleting(null); setDelNotes(""); setPurge(false);
          }}
        />
      )}

      {closing === t.id && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: C.line }}>
          <p className="text-[11px] mb-2" style={{ color: C.muted }}>
            סגירה חוסמת את הדייר מהאזור האישי ומפנה את הנכס להשכרה מתאריך היציאה. כל התשלומים, הקריאות והחוזים שלו נשמרים ויופיעו בדוחות.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            <Field label="תאריך היציאה בפועל"><DateField value={closeDate} onChange={setCloseDate} /></Field>
            <Field label="ביטחונות שהוחזרו"><input style={inputStyle} type="number" placeholder="0" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></Field>
          </div>
          <div className="mt-2"><Field label="הערות סגירה"><input style={inputStyle} value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} placeholder="מצב הדירה, ניכויים, מפתחות שהוחזרו" /></Field></div>

          {(() => {
            const rem = remainingAt(t, closeDate);
            const early = t.leaseEnd && closeDate < t.leaseEnd;
            return (
              <div className="mt-3 rounded-lg p-3" style={{ background: rem.total > 0 ? "#FBF2E3" : "#EAF3EE" }}>
                {early && (
                  <div className="text-[11px] mb-1" style={{ color: "#6B4E12" }}>
                    יציאה לפני תום החוזה ({heDate(t.leaseEnd)}). מ-{heDate(closeDate)} הנכס פנוי להשכרה ושכר הדירה מפסיק להיצבר.
                  </div>
                )}
                {rem.total > 0 ? (
                  <>
                    <div className="text-sm flex items-center justify-between" style={{ color: "#6B4E12" }}>
                      <span>חוב שנשאר עד היציאה</span>
                      <b>{nis(rem.total)}</b>
                    </div>
                    <div className="text-[11px] mb-2" style={{ color: C.muted }}>
                      שכר דירה {nis(rem.rentDue)} · חשבונות {nis(rem.billsDue)} · {rem.months.filter((x) => x.due > 0).length} חודשים
                    </div>
                    <div className="text-[11px] mb-1" style={{ fontWeight: 600 }}>מה עושים עם החוב?</div>
                    <div className="space-y-1">
                      {DEBT_RESOLUTIONS.map((x) => (
                        <label key={x.k} className="flex items-start gap-2 text-[12px] cursor-pointer">
                          <input type="radio" name={`res-${t.id}`} checked={resolution === x.k} onChange={() => setResolution(x.k)} className="mt-0.5" />
                          <span><b>{x.label}</b> <span style={{ color: C.muted }}>— {x.hint}</span></span>
                        </label>
                      ))}
                    </div>
                    {resolution !== "tenant" && (
                      <input style={{ ...inputStyle, marginTop: 8 }} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} placeholder="סיבה (לא חובה): הסכמה, קיזוז מהפיקדון, אין סיכוי לגבות…" />
                    )}
                  </>
                ) : (
                  <div className="text-[12px]" style={{ color: C.paid }}>אין חוב פתוח עד תאריך היציאה.</div>
                )}
              </div>
            );
          })()}

          <div className="flex gap-2 mt-2">
            <button onClick={() => {
              const rem = remainingAt(t, closeDate);
              run([{
                type: "close_tenancy", apartment: apt.id, tenancyId: t.id, date: closeDate, notes: closeNotes, depositReturned: Number(deposit) || 0,
                ...(rem.total > 0 ? { debtResolution: { kind: resolution, amount: rem.total, notes: resolutionNotes } } : {}),
              }]);
              setClosing(null); setCloseNotes(""); setDeposit(""); setResolution("tenant"); setResolutionNotes("");
            }} className="flex-1 rounded-lg py-2 text-sm" style={{ background: C.late, color: "#fff" }}>סגירת התקופה</button>
            <button onClick={() => setClosing(null)} className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal title="דיירים ותקופות שכירות" onClose={onClose} wide tabs={tabs}>
      <div className="text-sm mb-3" style={{ color: C.muted }}>{apt.name}</div>

      {((apt.rateSteps || []).length > 0 || (apt.rentSteps || []).length > 0) && (
        <div className="rounded-lg border p-3 mb-3" style={{ borderColor: C.line, background: "#FBFCFB" }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>היסטוריית מחירים</div>
          {(apt.rentSteps || []).map((st, i) => (
            <div key={`r${i}`} className="text-[11px] flex justify-between py-0.5" style={{ color: C.muted }}>
              <span>שכר דירה מ-{heDate(st.from)}</span><span>{nis(st.rent)}</span>
            </div>
          ))}
          {(apt.rateSteps || []).map((st, i) => (
            <div key={`t${i}`} className="text-[11px] flex justify-between py-0.5" style={{ color: C.muted }}>
              <span>תעריפים מ-{heDate(st.from)}</span>
              <span>{[st.elec != null && `חשמל ${st.elec}`, st.waterSewage != null && `מים ${st.waterSewage}`, st.gas != null && `גז ${st.gas}`].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
          <div className="text-[10px] mt-1.5" style={{ color: C.muted }}>
            חיובים שכבר נרשמו נשמרים בסכום שחושב במועדם ואינם משתנים.
          </div>
        </div>
      )}

      <div className="text-xs mb-1" style={{ fontWeight: 600 }}>דייר נוכחי</div>
      {cur ? Row({ t: cur, tone: { label: "פעיל", border: "#CBE2D6", bg: "#F4FAF6", chip: C.paid, chipBg: "#EAF3EE" } })
        : <div className="text-sm py-3 text-center mb-2" style={{ color: C.muted }}>הנכס פנוי</div>}

      {upcoming.length > 0 && (
        <>
          <div className="text-xs mb-1 mt-3" style={{ fontWeight: 600 }}>נכנסים בעתיד</div>
          {upcoming.map((t) => <Fragment key={t.id}>{Row({ t, tone: { label: `מתחיל ${heDate(t.leaseStart)}`, border: "#E8D9B5", bg: "#FBF7EE", chip: "#6B4E12", chipBg: "#FBF2E3" } })}</Fragment>)}
        </>
      )}

      <button onClick={onIntake} className="w-full rounded-lg py-2.5 text-sm mt-2 flex items-center justify-center gap-1.5" style={{ background: C.teal, color: "#fff" }}>
        <UserPlus size={15} /> קליטת דייר {cur ? "נוסף / מחליף" : "חדש"}
      </button>

      {past.length > 0 && (
        <>
          <div className="text-xs mb-1 mt-4" style={{ fontWeight: 600 }}>דיירים קודמים</div>
          {past.map((t) => <Fragment key={t.id}>{Row({ t, tone: { label: `הסתיים ${heDate(t.closedAt || t.leaseEnd)}`, border: C.line, bg: "#FBFCFB", chip: C.muted, chipBg: "#F1F4F1" } })}</Fragment>)}
        </>
      )}

      {/* מחוקים עם שמירת היסטוריה. הם אינם מופיעים בשום מסך אחר, אבל הם
          לא נעלמו — הכסף שלהם עדיין בדוחות, ואפשר להחזיר אותם. */}
      {removed.length > 0 && (
        <>
          <div className="text-xs mb-1 mt-4 flex items-center gap-1.5" style={{ fontWeight: 600, color: C.muted }}>
            <Trash2 size={12} /> נמחקו — ההיסטוריה נשמרה
          </div>
          {removed.map((t) => (
            <Fragment key={t.id}>
              {Row({ t, tone: { label: `נמחק ${heDate(t.deletedAt)}`, border: C.line, bg: "#F7F7F6", chip: C.muted, chipBg: "#EEEFED" } })}
            </Fragment>
          ))}
        </>
      )}

      {/* עורך הדייר נפתח מעל הרשימה. הוא כותב לתקופה שנבחרה ולא לנכס,
          ולכן עריכה של דייר עתידי או של מי שעזב לא נוגעת בפעיל. */}
      {editing && (
        <TenantEditModal apt={apt} tenancy={editing} run={run} settings={data.settings} onClose={() => setEditing(null)} />
      )}
    </Modal>
  );
}

/**
 * גישת הדייר.
 *
 * הסיסמה מונפקת בשרת: ברירת המחדל היא 6 הספרות האחרונות של תעודת הזהות
 * של הדייר (כשהיא רשומה), ואם אין — קוד אקראי בן 8. היא נשמרת מוצפנת,
 * כך שבעל הנכס רואה אותה כאן בכל רגע — גם כשהדייר שכח — ואפשר לבטל
 * אותה בלי לגעת בנכס.
 */
/** 6 הספרות האחרונות של ת"ז הדייר — ברירת המחדל של הסיסמה, כשהיא רשומה. */
const idLast6 = (apt) => {
  const digits = String(apt?.intake?.tenantId || "").replace(/\D/g, "");
  return digits.length >= 6 ? digits.slice(-6) : null;
};

function AccessModal({ apt, setData, onClose, tabs = null }) {
  const enabled = apt.portalEnabled !== false;
  const [copied, setCopied] = useState("");
  const [status, setStatus] = useState("loading"); // loading | active | none
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [info, setInfo] = useState(null);   // setBy, lastUsedAt

  useEffect(() => {
    let alive = true;
    dataApi.accessCode(apt.id)
      .then((r) => {
        if (!alive) return;
        setStatus(r.active ? "active" : "none");
        setCode(r.code || null);
        setInfo(r.active ? { setBy: r.setBy, lastUsedAt: r.lastUsedAt } : null);
      })
      .catch(() => alive && setStatus("none"));
    return () => { alive = false; };
  }, [apt.id]);

  /* כניסה אמיתית לאזור של הדייר, לא תצוגה מקדימה: השרת פותח סשן דייר
     מסומן כהתחזות, וכל פעולה משם נרשמת ביומן על שמך. הרענון נדרש כדי
     ש-Root יזהה את העוגייה החדשה ויציג את האזור האישי. */
  const enterPortal = async () => {
    setBusy(true); setErr("");
    try {
      await dataApi.enterPortal(apt.id);
      window.location.reload();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const setField = (k, v) => setData((d) => ({
    ...d, apartments: d.apartments.map((a) => (a.id === apt.id ? { ...a, [k]: v } : a)),
  }));

  const issue = async () => {
    setBusy(true); setErr("");
    try {
      /* כשהסיסמה הנוכחית היא ממילא זו שנגזרת מהת"ז, "סיסמה חדשה" חייבת
         להיות אקראית — אחרת השרת היה גוזר שוב את אותן 6 ספרות. */
      const wantRandom = !!code && code === idLast6(apt);
      const r = await dataApi.issueCode(apt.id, null, wantRandom);
      setCode(r.code);
      setStatus("active");
      setInfo({ setBy: "owner", lastUsedAt: null });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const revoke = async () => {
    setBusy(true); setErr("");
    try {
      await dataApi.revokeCode(apt.id);
      setCode(null);
      setStatus("none");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const invite = code ? `שלום ${apt.tenant},
פתחתי לך אזור אישי לניהול השכירות ב${apt.name}.
שם תוכל לראות מה לתשלום, לדווח קריאות מונה, לצפות בחוזה ולחתום עליו.${inviteLines(code)}` : "";

  const copy = async (t, what) => {
    try { await navigator.clipboard.writeText(t); setCopied(what); setTimeout(() => setCopied(""), 2000); }
    catch { setCopied("fail"); }
  };

  return (
    <Modal title="גישת הדייר לאזור האישי" onClose={onClose} tabs={tabs}>
      <div className="text-sm mb-3" style={{ color: C.muted }}>{apt.tenant} · {apt.name}</div>

      <div className="rounded-lg border p-4 text-center" style={{ borderColor: C.line, background: "#FBFCFB" }}>
        {code ? (
          <>
            <div className="text-xs mb-1" style={{ color: C.muted }}>הסיסמה לאזור האישי</div>
            <div className="disp text-3xl font-bold" style={{ letterSpacing: 6 }}>{code}</div>
            {info && (
              <div className="text-[11px] mt-1" style={{ color: C.muted }}>
                {info.setBy === "tenant" ? "הדייר בחר את הסיסמה"
                  : code === idLast6(apt) ? "6 הספרות האחרונות של תעודת הזהות של הדייר"
                    : "הסיסמה הונפקה על ידך"}
                {info.lastUsedAt ? ` · שימוש אחרון ${heDate(info.lastUsedAt)}` : " · טרם הייתה בשימוש"}
              </div>
            )}
            <div className="flex justify-center gap-2 mt-3">
              <button onClick={() => copy(code, "code")} className="text-xs rounded-lg px-2.5 py-1.5"
                style={{ border: `1px solid ${C.line}` }}>
                {copied === "code" ? "הועתקה" : "העתקת הסיסמה"}
              </button>
              <button onClick={issue} disabled={busy} className="text-xs rounded-lg px-2.5 py-1.5"
                style={{ border: `1px solid ${C.line}` }}>
                {idLast6(apt) && code !== idLast6(apt) ? "איפוס ל-6 ספרות הת״ז" : "סיסמה חדשה"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm" style={{ color: C.muted }}>
              {status === "loading" ? "בודק…"
                : status === "active" ? "לדייר יש סיסמה פעילה שהונפקה לפני שהסיסמאות נשמרו לצפייה. הנפיקו חדשה כדי לראות אותה."
                : "עדיין לא הונפקה סיסמה לדירה הזו."}
            </div>
            {status !== "loading" && (
              <button onClick={issue} disabled={busy} className="mt-3 rounded-lg px-3 py-2 text-sm"
                style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}>
                {busy ? "מנפיק…" : status === "active" ? "הנפקת סיסמה חדשה (הקודמת תבוטל)" : "הנפקת סיסמה"}
              </button>
            )}
            {status !== "loading" && (
              <div className="text-[11px] mt-2" style={{ color: C.muted }}>
                {idLast6(apt)
                  ? "הסיסמה שתונפק: 6 הספרות האחרונות של תעודת הזהות של הדייר."
                  : "לא רשומה תעודת זהות לדייר — תונפק סיסמה אקראית בת 8 ספרות."}
              </div>
            )}
          </>
        )}

        {status === "active" && (
          <button onClick={revoke} disabled={busy} className="block mx-auto mt-2 text-xs rounded-lg px-2.5 py-1.5"
            style={{ border: `1px solid ${C.line}`, color: C.late }}>
            ביטול הגישה
          </button>
        )}
      </div>

      {err && (
        <div className="mt-2 rounded-lg p-2.5 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>
      )}

      <label className="flex items-start gap-2 text-sm mt-4">
        <input type="checkbox" checked={enabled} onChange={(e) => setField("portalEnabled", e.target.checked)} className="mt-1" />
        <span>
          האזור האישי פתוח לדייר
          <span className="block text-[11px]" style={{ color: C.muted }}>
            כיבוי חוסם כניסה חדשה וגם מנתק מכשירים שכבר נכנסו.
          </span>
        </span>
      </label>

      <div className="mt-4 space-y-2">
        {code && apt.phone && (
          <a href={waLink(apt.phone, invite)} target="_blank" rel="noreferrer"
            className="w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2" style={{ background: "#128C7E", color: "#fff" }}>
            <MessageCircle size={15} /> שליחת הזמנה לדייר בוואטסאפ
          </a>
        )}
        {code && (
          <button onClick={() => copy(invite, "invite")} className="w-full rounded-lg py-2.5 text-sm" style={{ border: `1px solid ${C.line}` }}>
            {copied === "invite" ? "ההזמנה הועתקה" : "העתקת נוסח ההזמנה"}
          </button>
        )}
        {/* כניסה אמיתית לאזור של הדייר — סשן דייר מסומן כהתחזות, כל פעולה
            בו נכתבת ונרשמת על שמך. (התצוגה המקדימה המקומית בוטלה: היא לא
            עברה דרך השרת ובלבלה בכל בדיקה.) */}
        <button onClick={enterPortal} disabled={busy || !enabled}
          className="w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
          style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: busy || !enabled ? 0.6 : 1 }}>
          <Eye size={15} /> כניסה לאזור של הדייר
        </button>
        <p className="text-[11px] -mt-1" style={{ color: C.muted }}>
          כניסה אמיתית לחשבון של {apt.tenant || "הדייר"}, לשעתיים. פעולות שתעשו שם
          נשמרות אצלו ונרשמות ביומן על שמכם. ליציאה — "יציאה" בראש האזור האישי.
        </p>
      </div>

      <div className="mt-4 rounded-lg p-3 text-[11px]" style={{ background: "#F1F4F1", color: C.muted }}>
        הדייר פותח את הכתובת, בוחר "כניסה לאזור הדייר" ומזין את הסיסמה. הוא רואה
        רק את הדירה שלו — לא נכסים אחרים, לא דיירים אחרים ולא לשונית ניהול.
      </div>
    </Modal>
  );
}

/* מסכי הכניסה והנעילה של הדייר עברו ל-Root.jsx: הם נשענים עכשיו על
   עוגיית סשן חתומה מהשרת ולא על דגל מקומי שאפשר למחוק מכלי הפיתוח. */

/* ---------------- אזור הדייר ---------------- */
export function TenantPortal({ apt, data, setData, run, onExit, locked,
  scope = "owner", onReading = null, onPaymentReport = null, impersonating = false, onExtension = null,
  onGuarantor = null }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  /* איזו מארבע הפעולות החודשיות פתוחה כרגע — מונה מסוים, "pay-rent" או
     "pay-bills". אחת בכל פעם (אקורדיון): פתיחת חדשה סוגרת את הקודמת
     ומנקה את התוצאה שלה, כדי שלא יוצג בטעות אישור של מונה אחר בתוך
     הכרטיס הפתוח. */
  const [openPanel, setOpenPanel] = useState(null);
  const [payForm, setPayForm] = useState(null);   // null עד שנפתח, אז מאותחל עם היתרה
  const [payPhoto, setPayPhoto] = useState(null);  // File גולמי — מועלה רק בשליחה
  const [payBusy, setPayBusy] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [manual, setManual] = useState({ elec: "", water: "", gas: "" });
  const [photoFor, setPhotoFor] = useState({ elec: null, water: null, gas: null });
  const tenantInstall = useInstallPrompt();
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const bill = data.bills[rowKey(apt.id, CUR_KEY)] || emptyBill();
  const billingNow = isBillingMonth(apt, data.settings, CUR_KEY);
  /* בלי צילומי מונים ובלי שיקים: השיקים הם תיעוד פיננסי פנימי של בעל
     הנכס ולא חלק ממה שהדייר רואה באזור האישי. */
  const media = (data.media || []).filter((m) => m.aptId === apt.id && m.kind !== "meter" && m.kind !== "check");
  const contracts = apt.contracts || [];
  const [openContract, setOpenContract] = useState(null);
  const [billsDetail, setBillsDetail] = useState(false);
  // תמונה מוגדלת (תמונות הדירה, צילום מונה) — לחיצה פותחת על כל המסך.
  const [lightbox, setLightbox] = useState(null);
  /* לשונית "הודעות" של הדייר: השיחה עם בעל הנכס (ההודעות שלו והתשובות),
     עם ספירת תשובות חדשות (נשמר במכשיר). בתצוגה המקדימה של בעל הנכס
     השיחה נטענת דרך נתיב הבעלים — אין שם סשן דייר — כדי שיראה בדיוק מה
     הדייר רואה. */
  /* חמש לשוניות: חוזה, ערבים, תיעוד, הודעות, קריאות (שם גם החשבון והחוב).
     נפתח בקריאות — הפעולה החודשית — וכשיש חוזה שממתין לחתימה, בחוזה. */
  const [tenantTab, setTenantTab] = useState(() => (
    (apt.contracts || []).some((c) => c.status !== "signed") ? "contract" : "reads"
  ));
  const [thread, setThread] = useState([]);
  const loadThread = async () => {
    try {
      const r = scope === "tenant" ? await portalApi.messages() : await dataApi.tenantThread(apt.id);
      setThread(r.items || []);
    } catch { /* שרת ישן או אין רשת — הלשונית פשוט ריקה */ }
  };
  useEffect(() => { loadThread(); }, [scope, apt.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // תשובה של בעל הנכס מגיעה כשחוזרים ללשונית — בלי לרענן את הדף.
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && loadThread();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [scope, apt.id]); // eslint-disable-line react-hooks/exhaustive-deps
  /* "נקרא" של הדייר נשמר בשרת (tenant_seen_at) — כך בעל הנכס רואה שהתשובה
     שלו נקראה, וזה נכון בכל מכשיר של הדייר, לא רק בזה שפתח את הלשונית. */
  const unseenReplyIds = thread.filter((m) => m.from === "owner" && !m.tenantSeenAt).map((m) => m.id);
  const newReplies = unseenReplyIds.length;
  const markThreadSeen = () => {
    if (!unseenReplyIds.length || scope !== "tenant") return;
    const now = new Date().toISOString();
    setThread((cur) => cur.map((m) => (unseenReplyIds.includes(m.id) ? { ...m, tenantSeenAt: now } : m)));
    portalApi.markMessagesSeen(unseenReplyIds).catch(() => {});
  };
  // כשהלשונית פתוחה, כל תשובה שמגיעה נחשבת נקראה.
  useEffect(() => { if (tenantTab === "messages") markThreadSeen(); }, [thread, tenantTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const openMessages = () => { setTenantTab("messages"); markThreadSeen(); };
  const s = data.settings || defTariffs();
  /* תשלום חלקי של שכר דירה: הכותרת הראתה "לתשלום ₪761" ו"שכר דירה ✓" כששולמו
     2,000 מתוך 5,200 — כי נבדק רק אם קיימת רשומת תשלום. */
  /* חודש ההחלפה שייך לתקופה היוצאת — לדייר שנכנס באמצע חודש אין בו
     חיוב: לא שכר הדירה של הקודם ולא חשבונות שנצברו לפני כניסתו.
     debtStatement ממילא סופר רק את התקופה שלו; כאן מגודרים גם רכיבי
     "החודש" שבכותרת. כשאין נתוני תקופות בפרוסה — שני הצדדים null
     והשוואתם true, כלומר ההתנהגות הרגילה. */
  const myMonth = tenancyForMonth(data, apt.id, CUR_KEY)?.id === currentTenancy(data, apt.id)?.id;
  const rentPaidAmt = myMonth ? rentPaidOf(data, apt.id, CUR_KEY) : 0;
  const rentOwedNow = myMonth ? Math.max(0, Number(apt.rent || 0) - rentPaidAmt) : 0;
  const rentPaid = !myMonth || (Number(apt.rent || 0) > 0 ? (rentPaidAmt > 0 && rentOwedNow === 0) : !!data.payments[rowKey(apt.id, CUR_KEY)]);
  const rentPartial = rentPaidAmt > 0 && rentOwedNow > 0;
  const due = myMonth
    ? rentOwedNow + BILLS.reduce((t, x) => t + (bill[x.k].paid ? 0 : Number(bill[x.k].amount || 0)), 0)
    : 0;
  const debt = debtStatement(data, apt, CUR_KEY);

  /* דייר שעוד לא חתם על שום חוזה נכנס ישירות לאזור החוזה — בלי חשבונות,
     קריאות והודעות. רק אחרי החתימה נפתח הממשק המלא. חל רק על סשן דייר
     אמיתי (כולל כניסת בעל הנכס לחשבון הדייר — זה מה שהדייר רואה).
     כשהוגדרו ערבים השער נשאר סגור עד שגם כולם חתמו על כתב הערבות —
     החוזה שלם רק עם הערבויות, והדייר הוא זה שמחתים אותם. */
  const pendingSign = contracts.find((c) => c.status !== "signed") || null;
  const tenantSigned = contracts.some((c) => c.status === "signed");
  const pendingGuarantors = (apt.guarantors || []).filter((g) => !(g.signature || g.signatureId));
  const signGate = scope === "tenant" && contracts.length > 0
    && (!tenantSigned || pendingGuarantors.length > 0);

  /* מוצגים רק מונים שמשולמים דרך בעל הנכס — מונה שמוגדר "ישירות לספק"
     או "ללא חיוב" לא מופיע לדייר: אין לו למי לדווח עליו כאן. גז, גם
     כשהוא דרך בעל הנכס, רק כשהוא רלוונטי — הוגדר במפורש, יש תעריף או
     שכבר נרשמה קריאה; ברוב הדירות הגז מול חברת הגז ושדה מיותר מבלבל. */
  const tenantMeters = [
    ...(utilityMode(apt, "elec") === "landlord" ? [{ m: "elec", label: "מונה חשמל", noun: "חשמל", Icon: Zap }] : []),
    ...(utilityMode(apt, "water") === "landlord" ? [{ m: "water", label: "מונה מים וביוב", noun: "מים וביוב", Icon: Droplets }] : []),
    ...(utilityMode(apt, "gas") === "landlord"
      && (apt?.utilities?.gas === "landlord" || rateGas(apt, s) > 0 || lastReading(data, apt.id, "gas"))
      ? [{ m: "gas", label: "מונה גז", noun: "גז", Icon: Flame }]
      : []),
  ];

  /**
   * שליחת קריאה — ידנית או מצילום.
   *
   * אצל דייר אמיתי (scope === "tenant") הקריאה נשלחת לשרת, ושם מחושב גם
   * הסכום לחיוב. זה לא עניין של ארכיטקטורה בלבד: אם הלקוח היה מחשב את
   * החיוב, דייר יכול היה להגיש לעצמו חשבון חשמל של אפס.
   * במצב תצוגה מקדימה של בעל הנכס הפעולה מוחלת מקומית כמו קודם.
   */
  /** מנקה את השדה ואת התמונה אחרי שהקריאה נקלטה בפועל. */
  const clearMeter = (meter) => {
    setManual((m) => ({ ...m, [meter]: "" }));
    setPhotoFor((p) => ({ ...p, [meter]: null }));
  };

  const submitReading = async (meter, value, photoId, confirm = false) => {
    const n = Number(value);
    if (!n || Number.isNaN(n)) { setResult({ ok: false, text: "צריך להזין מספר" }); return false; }
    /* תמונת מונה חובה — הכפתור כבר נעול בלי תמונה, אבל Enter בשדה
       המספר לא עובר דרכו, ולכן הבדיקה חוזרת גם כאן. */
    if (!photoId) { setResult({ ok: false, text: "יש לצרף תמונת מונה לפני השליחה" }); return false; }

    if (onReading) {
      setBusy(`send-${meter}`);
      try {
        const r = await onReading({ meter, value: n, photoId, confirm });
        setResult({ ok: true, text: r.message || `הקריאה נקלטה: ${n}` });
        clearMeter(meter);
        return true;
      } catch (err) {
        // קריאה שנראית חריגה חוזרת כ-409 ומחכה לאישור מפורש של הדייר.
        if (err.code === "needs_confirmation") {
          setResult({
            ok: false,
            text: err.message,
            confirm: () => submitReading(meter, n, photoId, true),
          });
        } else {
          setResult({ ok: false, text: err.message });
        }
        return false;
      } finally {
        setBusy("");
      }
    }

    /* תצוגה מקדימה של בעל הנכס. אותן בדיקות בדיוק שהשרת מריץ על הדייר —
       אחרת המסך הזה מתנהג אחרת מהאמיתי ומסתיר בעיות במקום להראות אותן. */
    const check = readingSanity(data, apt.id, meter, n, TODAY);
    if (check.code === "below_previous") {
      setResult({ ok: false, text: `הקריאה נמוכה מהקודמת (${check.previous}). בדקו את המספר.` });
      return false;
    }
    if (check.code === "jump" && !confirm) {
      setResult({
        ok: false,
        text: `לפי הקריאה הזו נצרכו ${Math.round(check.diff)} ${UNIT[meter]} ב-${check.days} ימים — הרבה מעל הרגיל. ודאו שהמספר נכון.`,
        confirm: () => submitReading(meter, n, photoId, true),
      });
      return false;
    }

    const diff = check.diff ?? null;
    const rate = rateFor(apt, s, meter);
    /* סעיף שהשוכר משלם ישירות לספק, או שאין לו תעריף (גז בבלונים) —
       הקריאה נשמרת לתיעוד ולא נוצר חיוב. */
    const charges = billsMeter(apt, s, meter);
    const acts = [{ type: "set_meter", apartment: apt.id, meter, reading: n, photoId: photoId || null, date: TODAY, note: "מהדייר" }];
    if (billingNow && diff > 0 && charges) {
      acts.push({ type: "set_bill", apartment: apt.id, bill: meter, amount: Math.round(diff * rate) });
    }
    run(acts);
    setResult({
      ok: true,
      text: `הקריאה נקלטה: ${n}` + (diff > 0
        ? ` · צריכה ${fmtQty(diff)} ${UNIT[meter]}` + (!charges
          ? " · לתיעוד בלבד, החשבון משולם ישירות לספק"
          : billingNow ? ` · חשבון ${nis(diff * rate)}` : " · תחויב בחיוב הבא")
        : " · הקריאה הבאה תחושב מולה"),
    });
    clearMeter(meter);
    return true;
  };

  const sendMeter = async (file, meter) => {
    if (!file) return;
    setBusy(meter); setResult(null);
    let photoId = null;
    try {
      const small = await compressForUpload(file);
      // התמונה נשמרת גם אם הקריאה האוטומטית תיכשל — היא הראיה למה שנמסר.
      try { photoId = await uploadImage(small, { scope, kind: "meter", apartmentId: apt.id }); }
      catch { photoId = null; }

      const parsed = await ai.readMeter(meter, small, { scope });

      if (parsed.reading == null) {
        setPhotoFor((p) => ({ ...p, [meter]: photoId }));
        setResult({ ok: false, text: parsed.note || "לא הצלחתי לקרוא את המונה מהתמונה. הקלידו את המספר בשדה והתמונה תישמר יחד איתו." });
      } else if (parsed.confidence === "low") {
        // ספרה שנקראה לא נכון מכפילה חשבון. כשהמודל לא בטוח — הדייר מאשר.
        setManual((m) => ({ ...m, [meter]: String(parsed.reading) }));
        setPhotoFor((p) => ({ ...p, [meter]: photoId }));
        setResult({ ok: false, text: `קראתי ${parsed.reading}, אבל לא בוודאות. בדקו מול המונה ואשרו.` });
      } else {
        // גם בביטחון גבוה לא שולחים אוטומטית — הדייר צריך לראות את התמונה
        // והמספר שנקראו ולאשר בעצמו לפני שהקריאה נשלחת.
        setManual((m) => ({ ...m, [meter]: String(parsed.reading) }));
        setPhotoFor((p) => ({ ...p, [meter]: photoId }));
      }
    } catch (err) {
      setPhotoFor((p) => ({ ...p, [meter]: photoId }));
      setResult({ ok: false, text: `${err.message} אפשר להקליד את המספר ידנית.` });
    }
    setBusy("");
  };

  /* פותח את הטופס עם היתרה הפתוחה **של הקטגוריה הזו** כברירת מחדל —
     שכ"ד וחשבונות מדווחים בנפרד, כל אחד עם האסמכתא שלו, כי בפועל אלה
     לרוב שתי העברות נפרדות (למשל שכ"ד בהוראת קבע וחשבונות בביט). */
  const openPayReport = (category) => {
    const owed = category === "rent" ? debt.rentDue : debt.billsDue;
    setPayForm({ category, method: "transfer", amount: owed > 0 ? String(owed) : "", date: TODAY, note: "" });
    setPayPhoto(null);
    setPayResult(null);
  };

  const submitPayReport = async () => {
    const amount = Number(payForm?.amount);
    if (!amount || amount <= 0) { setPayResult({ ok: false, text: "צריך להזין סכום" }); return; }
    /* תמונת אסמכתא חובה — הכפתור כבר נעול בלי תמונה, הבדיקה כאן היא
       רשת ביטחון נוספת. */
    if (!payPhoto) { setPayResult({ ok: false, text: "יש לצרף תמונת אסמכתא לפני השליחה" }); return; }
    const category = payForm.category;
    setPayBusy(true);
    setPayResult(null);
    try {
      const small = await compressForUpload(payPhoto);
      const photoId = await uploadImage(small, { scope, kind: "payment", apartmentId: apt.id });
      const r = await onPaymentReport({
        amount, category, method: payForm.method,
        date: payForm.date || TODAY, note: payForm.note || "", photoId,
      });
      setPayResult({ ok: true, category, text: r?.message || `הדיווח נשלח: ${nis(amount)}` });
      setPayForm(null);
      setPayPhoto(null);
      setOpenPanel(null);
    } catch (err) {
      setPayResult({ ok: false, category, text: err.message });
    }
    setPayBusy(false);
  };

  return (
    <div dir="rtl" className="min-h-screen w-full pb-16" style={{ background: C.surface, color: C.ink, fontFamily: "'Assistant', system-ui, sans-serif" }}>
      {impersonating && (
        <div className="w-full text-center text-[12px] py-2 px-3" style={{ background: C.ochre, color: "#fff" }}>
          אתם צופים כבעל הנכס בחשבון של {apt.tenant || "הדייר"}. כל פעולה כאן נשמרת אצלו ונרשמת ביומן.
        </div>
      )}
      {/* "תצוגת הדייר" מתוך הניהול — הדמיה מקומית בלי סשן דייר: קריאות
          מונים ובקשות נשמרות ישירות במסמך שלך ולא עוברות דרך השרת, ולכן
          לא נוצרות מהן התראות בלשונית "הודעות". בלי השורה הזו זה נראה
          כמו באג. */}
      {scope !== "tenant" && !impersonating && (
        <div className="w-full text-center text-[12px] py-2 px-3" style={{ background: "#F1F4F1", color: C.muted }}>
          תצוגה מקדימה — כך הדייר רואה את האזור האישי. פעולות כאן נשמרות אצלך ישירות ולא יוצרות התראות;
          לבדיקת הזרימה המלאה (הודעות, צליל, לשונית "הודעות"): "גישה לדייר" ← "כניסה לאזור של הדייר".
        </div>
      )}
      {/* הכותרת הכהה של אזור הדייר — מי אני ואיפה אני, והלשוניות עליה. */}
      <div className="em-appbar">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <header className="pt-6 pb-1 flex items-center justify-between gap-3">
            <div>
              <div className="em-eyebrow tracking-widest">אזור הדייר</div>
              <h1 className="disp text-2xl font-bold">{apt.tenant}</h1>
              <p className="em-sub">{apt.name}</p>
            </div>
            {locked ? (
              <div className="flex flex-col items-end gap-1 shrink-0">
                <button onClick={() => { if (confirm("לשחרר את המכשיר ולחזור למסך הניהול?")) onExit(); }}
                  className="em-iconbtn">
                  יציאה
                </button>
                <FeedbackPanel role="tenant" />
                {!tenantInstall.isInstalled && (tenantInstall.canInstall || tenantInstall.isIOS) && (
                  <button onClick={() => { if (tenantInstall.canInstall) tenantInstall.promptInstall(); else setShowIOSHelp(true); }}
                    className="em-iconbtn" title="התקנת האפליקציה">
                    <Smartphone size={13} /> התקנה
                  </button>
                )}
              </div>
            ) : (
              <button onClick={onExit} className="em-iconbtn shrink-0">
                <ArrowRight size={14} /> חזרה לניהול
              </button>
            )}
          </header>

          {/* לפני החתימה הראשונה אין סרגל — הדייר נכנס ישירות לאזור החוזה,
              ורק אחרי שחתם נפתח שאר הממשק (חשבונות, קריאות, תיעוד, הודעות). */}
          {!signGate && (
            <div className="em-tabs mt-2">
              {[
                { k: "contract", label: contracts.length > 1 ? "חוזים" : "חוזה" },
                { k: "guarantors", label: "ערבים" },
                { k: "docs", label: "תיעוד" },
                { k: "messages", label: "הודעות", badge: newReplies },
                { k: "reads", label: "קריאות וחשבון" },
              ].map((t) => (
                <button key={t.k} onClick={() => (t.k === "messages" ? openMessages() : setTenantTab(t.k))}
                  className={`em-tab${tenantTab === t.k ? " active" : ""}`}>
                  {t.label}
                  {t.badge > 0 && (
                    <span className="text-[10px] rounded-full px-1.5 leading-4 mr-1 inline-block align-middle" aria-label={`${t.badge} תשובות חדשות`}
                      style={{ background: C.late, color: "#fff", minWidth: 16, textAlign: "center", fontWeight: 700 }}>
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
        {signGate && (
          <div className="rounded-xl border p-4 mb-4" style={{ background: "#FBF2E3", borderColor: "#E7D2A9" }}>
            <div className="text-sm" style={{ fontWeight: 600, color: "#6B4E12" }}>
              {!tenantSigned
                ? (pendingSign?.type === "extension" ? "נספח ההארכה ממתין לחתימתך" : "החוזה ממתין לחתימתך")
                : pendingGuarantors.length === 1
                  ? `כתב הערבות ממתין לחתימת ${pendingGuarantors[0].name || "הערב"}`
                  : `כתב הערבות ממתין לחתימת ${pendingGuarantors.length} ערבים`}
            </div>
            <p className="text-xs mt-1" style={{ color: "#6B4E12" }}>
              {!tenantSigned
                ? `קראו את החוזה וחתמו למטה${(apt.guarantors || []).length ? ", צלמו תעודת זהות והחתימו את הערבים" : ""}. `
                : "חתמתם על החוזה — נשאר להחתים את הערבים: שלחו לכל אחד את הקישור האישי שלו למטה, והוא ימלא פרטים, יצלם ת\"ז ויחתום מהטלפון. "}
              האזור האישי המלא ייפתח אחרי שכל החתימות יושלמו.
            </p>
          </div>
        )}

        {!signGate && tenantTab !== "messages" && newReplies > 0 && (
          <button onClick={openMessages} className="w-full text-right rounded-xl border p-3 mb-4 text-sm flex items-center gap-2"
            style={{ background: "#E9F5F0", borderColor: "#C9E5DA", color: C.teal }}>
            <MessageCircle size={16} /> {newReplies === 1 ? "תשובה חדשה" : `${newReplies} תשובות חדשות`} מבעל הנכס — לחצו לפתיחה
          </button>
        )}

        {!signGate && tenantTab === "reads" && (<>
        {/* הסכום שבשבילו נכנסים — קודם המספר, אחר כך הפירוט. */}
        <div className="em-hero">
          <div className="text-xs" style={{ opacity: .8 }}>לתשלום ב{monthLabel(CUR_KEY)}</div>
          <div className="em-amount">{nis(due)}</div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <span className="em-pill-ghost">שכר דירה עד ה-{rentDayOf(apt, s)} בחודש</span>
            <span className="em-pill-ghost">חשבונות עד ה-{billsDayOf(apt, s)} בחודש{billsCycleOf(apt, s) === 2 ? " · דו-חודשי" : ""}</span>
          </div>
        </div>

        {/* שלוש הפעולות החודשיות — נגישות מיד למעלה, בלי לגלול. לחיצה
            פותחת את הטופס מתחתיה (אקורדיון, אחת בכל פעם); תמונה חובה
            בכל אחת (צילום מונה / אסמכתת תשלום) לפני שאפשר לשלוח. */}
        {(tenantMeters.length > 0 || (scope === "tenant" && onPaymentReport)) && (
        <div className="space-y-2.5 mt-3">
          {tenantMeters.map(({ m, label, noun, Icon }) => {
            const lastR = lastReading(data, apt.id, m);
            const sending = busy === `send-${m}`;
            const open = openPanel === m;
            /* תצוגה בלבד לפני שמירה — לא הערך שנשלח, ואינו נכתב לשום
               מקום: השרת הוא זה שבפועל שומר את הקריאה ומחשב את החשבון.
               אבל החישוב עצמו מדויק, לא קירוב: מריצים את אותה applyAction
               (set_meter) על עותק מקומי של המסמך, בדיוק כמו שהשרת עושה
               ב-portal.js, ואז consumptionForMonth על פני כל תקופת החיוב
               (לא רק ההפרש מהקריאה הקודמת) — כך שהמספר שהדייר רואה כאן
               זהה למה שייכתב בפועל כשילחץ "שליחה". */
            let estimate = null;
            if (open && billsMeter(apt, s, m)) {
              const n = Number(manual[m]);
              if (n && !Number.isNaN(n)) {
                const r0 = applyActionFocused(data, {
                  type: "set_meter", apartment: apt.id, meter: m, reading: n,
                  photoId: photoFor[m] || null, date: TODAY, note: "מהדייר",
                }, apt.id);
                if (!r0.fail) {
                  const apt2 = (r0.data.apartments || []).find((a) => a.id === apt.id) || apt;
                  const cons = consumptionForMonth(r0.data, apt2, s, m, CUR_KEY);
                  if (cons && cons.diff > 0) {
                    const rate = rateFor(apt2, s, m, cons.to.date);
                    estimate = { diff: cons.diff, rate, amount: Math.round(cons.diff * rate) };
                  }
                }
              }
            }
            return (
              <div key={m} className={`em-meter ${m} rounded-xl border overflow-hidden`} style={{ borderColor: C.line, background: C.card }}>
                <button type="button"
                  onClick={() => { setOpenPanel(open ? null : m); setResult(null); }}
                  className="w-full flex items-center gap-3 p-3.5 text-right">
                  <span className="rounded-xl flex items-center justify-center shrink-0"
                    style={{ width: 42, height: 42, background: METER_HUES[m].bg, color: METER_HUES[m].main }}>
                    <Icon size={19} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm" style={{ fontWeight: 700 }}>דיווח קריאת {noun}</div>
                    <div className="text-[11px]" style={{ color: C.muted }}>
                      {lastR ? `קריאה קודמת: ${lastR.value} · ${heDate(lastR.date)}` : "אין קריאה קודמת"}
                    </div>
                  </div>
                  <ChevronDown size={18} style={{ color: C.muted, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                </button>

                {open && (
                  <div className="px-3.5 pb-3.5">
                    {lastR?.photoId && (
                      <button type="button" onClick={() => setLightbox(lastR.photoId)} aria-label="הגדלת צילום הקריאה האחרונה" title="לחיצה להגדלה"
                        className="flex items-center gap-1.5 mb-2.5">
                        <StoredImage id={lastR.photoId} className="rounded" style={{ height: 32 }} scope={scope} />
                        <span className="text-[11px]" style={{ color: C.teal }}>צילום הקריאה הקודמת</span>
                      </button>
                    )}

                    <input type="number" inputMode="numeric" value={manual[m]} placeholder="הקלידו את הקריאה"
                      aria-label={`קריאת ${label}`}
                      onChange={(e) => setManual({ ...manual, [m]: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") submitReading(m, manual[m], photoFor[m]); }}
                      style={readingInputStyle} />

                    {estimate && (
                      <div className="mt-1.5 text-[11px]" style={{ color: C.muted }}>
                        צריכה {fmtQty(estimate.diff)} {UNIT[m]} × ₪{estimate.rate.toFixed(2)} ={" "}
                        <span style={{ fontWeight: 700, color: C.teal }}>{nis(estimate.amount)}</span>
                      </div>
                    )}

                    <div className="flex gap-1.5 mt-1.5">
                      <button onClick={() => submitReading(m, manual[m], photoFor[m])} disabled={sending || !photoFor[m]}
                        className="rounded-lg py-2.5 text-sm flex-1 flex items-center justify-center"
                        style={{ background: C.teal, color: "#fff", opacity: sending || !photoFor[m] ? 0.5 : 1 }}>
                        {sending ? <Loader2 size={16} className="animate-spin" /> : "שליחה"}
                      </button>
                      <FilePick capture title="צילום המונה" disabled={!!busy} onPick={(f) => sendMeter(f, m)}
                        className="rounded-lg px-3 shrink-0 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                        {busy === m ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                      </FilePick>
                      <FilePick title="בחירת תמונה מהגלריה" disabled={!!busy} onPick={(f) => sendMeter(f, m)}
                        className="rounded-lg px-3 shrink-0 flex items-center" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                        <Upload size={16} />
                      </FilePick>
                    </div>

                    <div
                      tabIndex={0}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) sendMeter(f, m); }}
                      onPaste={(e) => { const f = e.clipboardData.files && e.clipboardData.files[0]; if (f) { e.preventDefault(); sendMeter(f, m); } }}
                      className="mt-2 rounded-lg text-[11px] text-center py-2"
                      style={{ border: `1px dashed ${C.line}`, color: C.muted, outline: "none" }}
                    >
                      אפשר גם לגרור לכאן תמונה של המונה, או ללחוץ כאן ולהדביק אותה
                    </div>

                    {photoFor[m] ? (
                      <div className="flex items-center gap-2 mt-2 text-[11px]" style={{ color: C.muted }}>
                        <button onClick={() => setLightbox(photoFor[m])} aria-label="הגדלת צילום המונה" title="לחיצה להגדלה">
                          <StoredImage id={photoFor[m]} className="rounded" style={{ height: 40 }} scope={scope} />
                        </button>
                        התמונה תישמר יחד עם הקריאה
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px]" style={{ color: C.ochre, fontWeight: 600 }}>
                        חובה לצרף תמונת מונה כדי לשלוח את הקריאה
                      </div>
                    )}

                    {!billingNow && (
                      <div className="mt-1.5 text-[11px]" style={{ color: C.muted }}>
                        החודש אינו חודש חיוב — הקריאה תישמר לחיוב הבא.
                      </div>
                    )}

                    {result && (
                      <div className="mt-3 rounded-lg p-2.5 text-sm" style={{ background: result.ok ? "#EAF3EE" : "#FBF2E3", color: result.ok ? C.paid : "#6B4E12" }}>
                        {result.text}
                        {result.confirm && (
                          <div className="flex gap-1.5 mt-2">
                            <button onClick={result.confirm}
                              className="rounded-md px-3 py-1.5 text-xs" style={{ background: C.ochre, color: "#fff" }}>
                              כן, זו הקריאה
                            </button>
                            <button onClick={() => setResult(null)}
                              className="rounded-md px-3 py-1.5 text-xs" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                              אתקן
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <a href={`https://wa.me/?text=${encodeURIComponent(`שלום, מצורף צילום המונה של ${apt.name} מתאריך ${heDate(TODAY)}.`)}`}
                      target="_blank" rel="noreferrer"
                      className="mt-3 w-full rounded-lg py-2 text-[12px] flex items-center justify-center gap-1.5" style={{ border: `1px solid ${C.line}`, color: "#128C7E" }}>
                      <MessageCircle size={13} /> אם המצלמה לא נפתחת — שליחת הצילום בוואטסאפ
                    </a>
                  </div>
                )}
              </div>
            );
          })}

          {/* דיווח תשלום שכר דירה ודיווח תשלום חשבונות — שני כפתורים
              נפרדים, כל אחד עם המסגרת הצבעונית שלו (כמו המונים למעלה)
              ואקורדיון משלו. שניהם משתמשים באותו טופס/מצב (payForm),
              עם category קבוע מראש לפי איזה כפתור פתח אותו. */}
          {scope === "tenant" && onPaymentReport && [
            { key: "rent", panel: "pay-rent", label: "דיווח תשלום שכר דירה", Icon: Wallet, bg: "#E9F5F0", color: C.teal, due: debt.rentDue },
            { key: "bills", panel: "pay-bills", label: "דיווח תשלום חשבונות", Icon: Receipt, bg: "#FBF2E3", color: C.ochre, due: debt.billsDue },
          ].map(({ key, panel, label, Icon, bg, color, due }) => {
            const open = openPanel === panel;
            const reports = [...(data.paymentReports || [])]
              .filter((p) => p.aptId === apt.id && p.category === key)
              .sort((a, b) => (a.date < b.date ? 1 : -1));
            return (
              <div key={key} className={`em-pay ${key} rounded-xl border overflow-hidden`} style={{ borderColor: C.line, background: C.card }}>
                <button type="button"
                  onClick={() => { if (!open) openPayReport(key); setOpenPanel(open ? null : panel); }}
                  className="w-full flex items-center gap-3 p-3.5 text-right">
                  <span className="rounded-xl flex items-center justify-center shrink-0"
                    style={{ width: 42, height: 42, background: bg, color }}>
                    <Icon size={19} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm" style={{ fontWeight: 700 }}>{label}</div>
                    <div className="text-[11px]" style={{ color: C.muted }}>
                      {due > 0 ? `חוב פתוח: ${nis(due)}` : "אין חוב פתוח"}
                    </div>
                  </div>
                  <ChevronDown size={18} style={{ color: C.muted, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                </button>

                {open && payForm && (
                  <div className="px-3.5 pb-3.5 space-y-2.5">
                    <div className="grid grid-cols-2 gap-2.5">
                      <Field label="סכום ששולם">
                        <input type="number" inputMode="decimal" style={inputStyle}
                          value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
                      </Field>
                      <Field label="תאריך התשלום">
                        <DateField value={payForm.date} onChange={(v) => setPayForm({ ...payForm, date: v })} />
                      </Field>
                    </div>
                    <div>
                      <label className="text-[12px]" style={{ color: C.muted }}>איך שולם</label>
                      <div className="flex gap-2 mt-1">
                        {[["transfer", "העברה בנקאית"], ["bit", "ביט"], ["paybox", "פייבוקס"]].map(([k, l]) => (
                          <button key={k} type="button" onClick={() => setPayForm({ ...payForm, method: k })}
                            className="text-xs rounded-lg px-3 py-1.5"
                            style={payForm.method === k
                              ? { background: C.teal, color: "#fff" }
                              : { border: `1px solid ${C.line}`, color: C.muted }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Field label="הערה (לא חובה)">
                      <input style={inputStyle} value={payForm.note} placeholder="למשל: שולם מהחשבון של בן/בת הזוג"
                        onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
                    </Field>
                    <div>
                      <label className="text-[12px]" style={{ color: C.muted }}>תמונת אסמכתא</label>
                      <div className="flex items-center gap-2 mt-1">
                        <FilePick capture accept="image/*" title="צילום האסמכתא" disabled={payBusy}
                          onPick={setPayPhoto}
                          className="rounded-lg px-3 py-2.5 text-sm flex items-center gap-1.5"
                          style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                          <Camera size={15} /> {payPhoto ? "החלפת תמונה" : "צילום"}
                        </FilePick>
                        <FilePick accept="image/*" title="העלאה מהגלריה" disabled={payBusy}
                          onPick={setPayPhoto}
                          className="rounded-lg px-3 py-2.5 text-sm flex items-center gap-1.5"
                          style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                          <Upload size={15} /> גלריה
                        </FilePick>
                        {payPhoto && <span className="text-[11px]" style={{ color: C.paid }}>תמונה נבחרה ✓</span>}
                      </div>
                      {!payPhoto && (
                        <div className="mt-1.5 text-[11px]" style={{ color: C.ochre, fontWeight: 600 }}>
                          חובה לצרף תמונת אסמכתא כדי לשלוח את הדיווח
                        </div>
                      )}
                    </div>
                    {payResult && !payResult.ok && (
                      <div className="rounded-lg p-2.5 text-sm" style={{ background: "#FBEDE9", color: "#7A2E1E" }}>{payResult.text}</div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={submitPayReport} disabled={payBusy || !payPhoto}
                        className="flex-1 rounded-lg py-2.5 text-sm flex items-center justify-center"
                        style={{ background: C.teal, color: "#fff", opacity: payBusy || !payPhoto ? 0.5 : 1 }}>
                        {payBusy ? <Loader2 size={16} className="animate-spin" /> : "שליחת הדיווח"}
                      </button>
                      <button onClick={() => { setOpenPanel(null); setPayForm(null); setPayPhoto(null); }} disabled={payBusy}
                        className="rounded-lg px-4 py-2.5 text-sm" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                        ביטול
                      </button>
                    </div>
                  </div>
                )}

                {!open && payResult?.ok && payResult.category === key && (
                  <div className="mx-3.5 mb-3.5 rounded-lg p-2.5 text-sm" style={{ background: "#EAF3EE", color: C.paid }}>{payResult.text}</div>
                )}

                {/* הדיווחים שלכם על הקטגוריה הזו — תמיד גלוי, גם כשהטופס
                    סגור, כדי שתראו שהם נשלחו בלי לפתוח שוב. לא "שולם ✓":
                    זה מה שדיווחתם, לא מה שבעל הנכס אישר — זה מוצג
                    ב"תשלומים וחשבונות" למטה, אחרי שהוא מסמן בעצמו. */}
                {reports.length > 0 && (
                  <div className="px-3.5 pb-3.5 pt-1">
                    <div className="text-xs mb-1.5" style={{ fontWeight: 600 }}>הדיווחים שלכם</div>
                    <div className="space-y-1.5">
                      {reports.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2 text-[12px] rounded-lg p-2" style={{ background: C.surface }}>
                          <div className="min-w-0">
                            <span style={{ fontWeight: 600 }}>{nis(p.amount)}</span>
                            <span style={{ color: C.muted }}> · {methodLabel(p.method) || p.method} · {heDate(p.date)}</span>
                          </div>
                          {p.photoId && (
                            <button type="button" onClick={() => setLightbox(p.photoId)} title="לחיצה להגדלה" aria-label="הגדלת האסמכתא" className="shrink-0">
                              <StoredImage id={p.photoId} className="rounded" style={{ height: 36 }} scope={scope} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        <div className="rounded-xl border p-4 mt-3" style={{ background: C.card, borderColor: C.line }}>
          <div className="space-y-1.5 text-sm">
            {myMonth ? (<>
              <BillRow label={rentPartial ? `שכר דירה — שולם ${nis(rentPaidAmt)}, נותר` : "שכר דירה"} amount={rentPartial ? rentOwedNow : apt.rent} paid={rentPaid} />
              {BILLS.map(({ k, label }) => bill[k].amount ? <BillRow key={k} label={label} amount={bill[k].amount} paid={bill[k].paid} /> : null)}
            </>) : (
              <div className="text-[12px] rounded-lg p-2.5" style={{ background: "#F1F4F1", color: C.muted }}>
                חיובי {monthLabel(CUR_KEY)} שייכים לתקופת השכירות הקודמת — החיוב שלך מתחיל ב{monthLabel(nextMonth(CUR_KEY))}.
              </div>
            )}
          </div>

          {/* החוב בחלוקה לשכר דירה ולחשבונות צריכה — על פני כל החודשים,
              לא רק החודש הזה. מתעדכן מהדיווח של בעל הנכס ("התקבל"), כך
              שהדייר רואה מה מתוך החוב הוא מה בלי לשאול. */}
          <div className="mt-3 pt-3 grid grid-cols-2 gap-2" style={{ borderTop: `1px solid ${C.line}` }}>
            <div className="rounded-lg p-2.5" style={{ background: debt.rentDue > 0 ? "#FBEDEA" : "#E9F5F0" }}>
              <div className="text-[11px]" style={{ color: debt.rentDue > 0 ? C.late : C.teal }}>חוב שכר דירה</div>
              <div className="text-sm" style={{ fontWeight: 700, color: debt.rentDue > 0 ? C.late : C.paid }}>
                {debt.rentDue > 0 ? nis(debt.rentDue) : "אין חוב"}
              </div>
            </div>
            {(() => {
              const Tag = debt.billsDue > 0 ? "button" : "div";
              return (
                <Tag type={debt.billsDue > 0 ? "button" : undefined}
                  onClick={debt.billsDue > 0 ? () => setBillsDetail(true) : undefined}
                  className="rounded-lg p-2.5 text-right w-full"
                  style={{ background: debt.billsDue > 0 ? "#FBEDEA" : "#E9F5F0", cursor: debt.billsDue > 0 ? "pointer" : "default" }}>
                  <div className="text-[11px]" style={{ color: debt.billsDue > 0 ? C.late : C.teal }}>חוב חשבונות צריכה</div>
                  <div className="text-sm" style={{ fontWeight: 700, color: debt.billsDue > 0 ? C.late : C.paid }}>
                    {debt.billsDue > 0 ? nis(debt.billsDue) : "אין חוב"}
                  </div>
                </Tag>
              );
            })()}
          </div>

          {/* חוב מחודשים קודמים. בלי זה המסך מציג סכום קטן מהאמת, הדייר
              משלם אותו ובטוח שסגר — וזו בדיוק אי-ההבנה שמייצרת מחלוקת. */}
          {debt.arrears.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
              <div className="text-xs mb-1.5" style={{ color: C.ochre, fontWeight: 600 }}>
                חודשים קודמים שטרם שולמו
              </div>
              <div className="space-y-1 text-[12px]">
                {debt.arrears.map((x) => (
                  <div key={x.month} className="flex items-center justify-between">
                    <span style={{ color: C.muted }}>
                      {monthLabel(x.month)}
                      {x.rent.due > 0 && x.bills.due > 0 ? " · שכ״ד וחשבונות"
                        : x.rent.due > 0 ? " · שכר דירה" : " · חשבונות"}
                    </span>
                    <b style={{ color: C.late }}>{nis(x.due)}</b>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 flex items-center justify-between text-sm"
                style={{ borderTop: `1px solid ${C.line}` }}>
                <span style={{ fontWeight: 600 }}>סה״כ חוב פתוח</span>
                <b className="disp text-lg" style={{ color: C.late }}>{nis(debt.total)}</b>
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: C.muted }}>
                אם כבר העברת חלק מהסכום ועדיין לא עודכן, אפשר לפנות לבעל הנכס.
              </p>
            </div>
          )}

          {/* לאן משלמים. הסכום הופיע כאן מהיום הראשון, אבל פרטי התשלום
              היו רק בהודעת הוואטסאפ — דייר שנכנס לאזור האישי ראה כמה
              הוא חייב ולא למי להעביר. */}
          {(s.payBank || s.payBit) && (
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
              <div className="text-xs mb-1" style={{ fontWeight: 600 }}>איך משלמים</div>
              {s.payBank && (
                <div className="text-[12px]" style={{ color: C.muted }}>
                  העברה בנקאית · בנק {s.payBank}{s.payBranch ? ` · סניף ${s.payBranch}` : ""}{s.payAccount ? ` · חשבון ${s.payAccount}` : ""}
                  {s.ownerName ? ` · ע״ש ${s.ownerName}` : ""}
                </div>
              )}
              {s.payBit && (
                <div className="text-[12px]" style={{ color: C.muted }}>ביט או פייבוקס · {s.payBit}</div>
              )}
            </div>
          )}
        </div>

        {/* מה שולם ומה פתוח, חודש אחרי חודש — כדי שהדייר יראה את
            ההיסטוריה בלי לבקש אותה מבעל הנכס. */}
        <PaidHistory months={debt.months} />

        {scope === "tenant" && !impersonating && <MyAccessCode />}
        </>)}

        {(signGate || tenantTab === "contract") && (
        <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
          <h2 className="disp text-lg font-bold mb-3">החוזה שלי</h2>
          {contracts.length === 0 && <div className="text-sm" style={{ color: C.muted }}>עדיין לא הועלה חוזה.</div>}
          <div className="space-y-2">
            {contracts.map((c) => (
              <div key={c.id} className="rounded-lg border p-3 flex items-center justify-between gap-2" style={{ borderColor: C.line }}>
                <div>
                  {/* חוזה-קובץ שהעלה בעל הנכס מוצג בשם הקובץ — שהדייר יידע מה הוא חותם. */}
                  <div className="text-sm" style={{ fontWeight: 600 }}>{c.fileId ? (c.fileName || "קובץ חוזה") : c.type === "extension" ? "נספח הארכה" : "חוזה שכירות"}</div>
                  <div className="text-[11px]" style={{ color: C.muted }}>{heDate(c.from)} — {heDate(c.to)} · {nis(c.rent)}</div>
                </div>
                <button onClick={() => setOpenContract(c)} className="text-xs rounded-lg px-3 py-1.5 flex items-center gap-1"
                  style={c.status === "signed" ? { border: `1px solid ${C.line}` } : { background: C.teal, color: "#fff" }}>
                  {c.status === "signed" ? <><FileText size={13} /> צפייה</> : <><PenTool size={13} /> קריאה וחתימה</>}
                </button>
              </div>
            ))}
          </div>
          {/* בקשת הארכת חוזה — נשלחת לבעל הנכס, שמאשר (נספח הארכה לחתימה)
              או דוחה עם סיבה. המצב מוצג מהמסמך עצמו אחרי כל רענון. */}
          {/* בקשת הארכה — רק אחרי שיש חוזה חתום; לפני החתימה אין מה להאריך. */}
          {!signGate && (
            <ExtensionRequestBlock apt={apt} data={data} onExtension={onExtension} run={run}
              contracts={contracts} onOpenContract={setOpenContract} />
          )}
        </div>
        )}

        {(signGate || tenantTab === "guarantors") && (
        <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
          <h2 className="disp text-lg font-bold mb-1">הערבים שלי</h2>
          <GuarantorsEditor apt={apt} setData={setData} tenantMode ownerPhone={s.ownerPhone} onGuarantor={onGuarantor} />
        </div>
        )}

        {!signGate && tenantTab === "docs" && (
        <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
          <h2 className="disp text-lg font-bold mb-3">תמונות הדירה בכניסה</h2>
          <div className="grid grid-cols-3 gap-2">
            {media.map((m) => (
              <button key={m.id} onClick={() => setLightbox(m.id)} className="block w-full" aria-label="הגדלת התמונה" title="לחיצה להגדלה">
                <StoredImage id={m.id} className="w-full rounded-lg" style={{ height: 92, objectFit: "cover" }} scope={scope} />
              </button>
            ))}
            {media.length === 0 && <div className="col-span-3 text-sm py-4 text-center" style={{ color: C.muted }}>אין תמונות.</div>}
          </div>
        </div>
        )}

        {(signGate || tenantTab !== "messages") && (
        <a href={s.ownerPhone ? waLink(s.ownerPhone, `שלום${s.ownerName ? " " + s.ownerName : ""}, מדבר ${apt.tenant} מ${apt.name}. `) : `https://wa.me/?text=${encodeURIComponent(`שלום, מדבר ${apt.tenant} מ${apt.name}.`)}`}
          target="_blank" rel="noreferrer"
          className="mt-4 w-full rounded-xl py-3 text-sm flex items-center justify-center gap-2" style={{ background: "#128C7E", color: "#fff" }}>
          <MessageCircle size={16} /> פנייה לבעל הדירה בוואטסאפ
        </a>
        )}

        {/* לשונית ההודעות: השיחה עם בעל הנכס + שליחת הודעה חדשה. בתצוגה
            המקדימה — השיחה האמיתית לקריאה, השליחה כבויה עם הסבר. */}
        {!signGate && tenantTab === "messages" && (
          <>
            <TenantMessages preview={scope !== "tenant"} impersonating={impersonating} items={thread} reload={loadThread} />
            {thread.length === 0 && (
              <p className="text-xs mt-2 text-center" style={{ color: C.muted }}>
                עדיין אין הודעות. הודעה שתשלחו תופיע כאן, וגם התשובות של בעל הנכס.
              </p>
            )}
          </>
        )}

        {/* הדייר הוא נושא המידע — הקישור למדיניות נגיש לו מהאזור שלו. */}
        <LegalLinks className="mt-5" />
      </div>

      {/* תמונה מוגדלת על כל המסך, עם הגדלה — כדי לקרוא את הספרות על המונה. */}
      {lightbox && <ZoomLightbox id={lightbox} scope={scope} onClose={() => setLightbox(null)} />}

      {openContract && (
        <ContractModal apt={apt} contract={openContract} settings={s} setData={setData} tenantMode onClose={() => setOpenContract(null)} />
      )}

      {billsDetail && <BillsDebtModal months={debt.months} onClose={() => setBillsDetail(false)} />}
      {showIOSHelp && <IOSInstallHelp onClose={() => setShowIOSHelp(false)} />}
      {/* רק בסשן דייר אמיתי — כולל impersonating (בעל הנכס בתוך "כניסה
          לאזור של הדייר"), שגם לו יש סשן אמיתי. בתצוגה המקדימה
          (scope==="owner") אין סשן דייר ו-requireTenant היה נכשל. */}
      {scope === "tenant" && <TenantAssistant />}
    </div>
  );
}

/**
 * עוזר שאלות-ותשובות באזור האישי — read-only, רק על הנתונים של הדייר הזה.
 * בכוונה לא שכפול של Assistant (בעל הנכס): אין אשפים, אין actions, אין
 * היסטוריית פקודות — רק שיחה. אותה תבנית מיקום/עיצוב (כפתור צף מקופל →
 * פאנל עוגן-תחתון) לפי בקשה מפורשת, לא מודל מכותרת.
 */
function useTenantKeyboardInset() {
  const zoomOf = () => Number(document.documentElement.style.zoom) || 1;
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setInset(Math.max(0, Math.round((window.innerHeight - vv.height - vv.offsetTop) / zoomOf())));
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);
  return inset;
}

function TenantAssistant() {
  const [collapsed, setCollapsed] = useState(true);
  const [log, setLog] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const kbInset = useTenantKeyboardInset();

  const ask = async () => {
    const text = q.trim();
    if (!text || busy) return;
    const history = [...log, { role: "user", text }];
    setLog(history);
    setQ("");
    setBusy(true);
    try {
      const r = await ai.portalAssistant(history.slice(-16));
      setLog((l) => [...l, { role: "assistant", text: r.reply || "" }]);
    } catch (err) {
      setLog((l) => [...l, { role: "assistant", text: `לא הצלחתי לענות: ${err.message}` }]);
    }
    setBusy(false);
  };

  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} dir="rtl" aria-label="פתיחת העוזר"
        className="fixed z-40 flex items-center gap-1.5 shadow-lg no-print"
        style={{
          left: 14, bottom: "max(18px, env(safe-area-inset-bottom, 0px) + 14px)",
          background: C.teal, color: "#fff",
          borderRadius: 999, padding: "12px 16px", fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 14px rgba(20,38,43,0.35)",
        }}>
        <Sparkles size={16} style={{ color: "#F0C987" }} /> עוזר
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 no-print" dir="rtl"
      style={kbInset > 0 ? { bottom: kbInset } : undefined}>
      <div className="max-w-lg w-full mx-auto px-4 pb-4">
        <div className="border shadow-lg rounded-t-2xl overflow-hidden" style={{ background: C.card, borderColor: C.line }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-2 text-sm" style={{ fontWeight: 600 }}>
              <Sparkles size={15} style={{ color: C.ochre }} /> עוזר
            </div>
            <button onClick={() => setCollapsed(true)} className="text-xs" style={{ color: C.muted }}>סגירה</button>
          </div>

          <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-2">
            {log.length === 0 && (
              <p className="text-xs" style={{ color: C.muted }}>
                אפשר לשאול על שכר הדירה, יתרת החוב, תאריכי החוזה או קריאות המונה שלכם.
              </p>
            )}
            {log.map((m, i) => (
              <div key={i} className={`text-sm rounded-xl px-3 py-2 max-w-[85%] whitespace-pre-line ${m.role === "user" ? "mr-auto" : "ml-auto"}`}
                style={m.role === "user"
                  ? { background: C.teal, color: "#fff" }
                  : { background: C.surface, color: C.ink, border: `1px solid ${C.line}` }}>
                {m.text}
              </div>
            ))}
            {busy && <div className="text-xs" style={{ color: C.muted }}>חושב…</div>}
          </div>

          <div className="flex items-center gap-2 px-3 py-2.5 border-t" style={{ borderColor: C.line }}>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="שאלה על הנתונים שלכם…" disabled={busy}
              style={{ ...inputStyle, flex: 1 }} />
            <button onClick={ask} disabled={busy || !q.trim()} aria-label="שליחה"
              className="rounded-lg p-2.5 shrink-0" style={{ background: C.teal, color: "#fff", opacity: busy || !q.trim() ? 0.6 : 1 }}>
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * הדייר בוחר לעצמו סיסמת כניסה.
 *
 * ברירת המחדל היא 6 הספרות האחרונות של תעודת הזהות — סיסמה שהדייר
 * באמת זוכר. מי שמעדיף אחרת בוחר כאן 6–8 ספרות משלו. הבדיקות (לא רצף,
 * לא ספרה חוזרת, לא תפוס) רצות בשרת.
 */
function MyAccessCode() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    const clean = code.replace(/\D/g, "");
    if (clean.length < 6 || clean.length > 8) { setMsg({ ok: false, text: "הסיסמה חייבת להיות 6–8 ספרות" }); return; }
    setBusy(true); setMsg(null);
    try {
      await portalApi.setCode(clean);
      setMsg({ ok: true, text: "הסיסמה עודכנה. הקודמת כבר לא עובדת." });
      setCode("");
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
      <div className="flex items-center justify-between">
        <h2 className="disp text-lg font-bold">הסיסמה שלי</h2>
        <button onClick={() => { setOpen(!open); setMsg(null); }} className="text-xs" style={{ color: C.teal }}>
          {open ? "סגירה" : "שינוי הסיסמה"}
        </button>
      </div>

      {open && (
        <div className="mt-3">
          <p className="text-xs mb-2" style={{ color: C.muted }}>
            בחרו 6–8 ספרות שתזכרו. לא רצף (123456) ולא ספרה אחת חוזרת.
            הסיסמה הנוכחית תפסיק לעבוד, והמכשיר הזה יישאר מחובר.
          </p>
          <div className="flex gap-1.5">
            <input value={code} onChange={(e) => { setCode(e.target.value); setMsg(null); }}
              inputMode="numeric" maxLength={8} placeholder="000000"
              style={{ ...inputStyle, textAlign: "center", fontSize: 20, letterSpacing: 6, padding: "8px 10px" }} />
            <button onClick={save} disabled={busy}
              className="rounded-lg px-4 text-sm shrink-0" style={{ background: C.teal, color: "#fff", opacity: busy ? 0.6 : 1 }}>
              {busy ? "שומר…" : "שמירה"}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div className="mt-2 rounded-lg p-2.5 text-sm"
          style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/**
 * "הודעה לבעל הנכס" — מהאזור האישי של דייר אמיתי בלבד. ההודעה נכנסת
 * לתיבת ההודעות של בעל הנכס (עם צליל, כמו חתימה); כאן רואים אם נקראה.
 * התשובה חוזרת בוואטסאפ/טלפון — לא צ'אט דו-כיווני בתוך האפליקציה.
 */
/**
 * @param preview תצוגה מקדימה של בעל הנכס (אין סשן דייר): הכרטיס מוצג
 *        כדי שיראה מה הדייר רואה, אבל השליחה כבויה עם הסבר איך בודקים
 *        באמת. @param impersonating בעל הנכס בתוך חשבון הדייר: שולח באמת,
 *        וההודעה מסומנת אצלו ככזו — זו דרך לבדוק את הזרימה מקצה לקצה.
 */
function TenantMessages({ preview = false, impersonating = false, items = [], reload = null }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // בחירה מרובה ומחיקה — מסתירה אצל הדייר בלבד; היומן של בעל הנכס לא משתנה.
  const [selected, setSelected] = useState(() => new Set());
  const [selectMode, setSelectMode] = useState(false);
  const removeIds = async (ids) => {
    if (!ids.length || preview) return;
    const q = ids.length === 1
      ? "להסיר את ההודעה הזו מהרשימה שלכם? אצל בעל הנכס היא נשארת."
      : `להסיר ${ids.length} הודעות מהרשימה שלכם? אצל בעל הנכס הן נשארות.`;
    if (!window.confirm(q)) return;
    try {
      await portalApi.deleteMessages(ids);
      setSelected(new Set());
      if (reload) await reload();
    } catch (e) {
      setMsg({ ok: false, text: e.message || "המחיקה נכשלה" });
    }
  };
  const toggleSelect = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const send = async () => {
    const t = text.trim();
    if (!t || preview) return;
    setBusy(true); setMsg(null);
    try {
      await portalApi.sendMessage(t);
      setText("");
      setMsg({ ok: true, text: "ההודעה נשלחה. בעל הנכס יקבל התראה." });
      if (reload) await reload();
    } catch (e) {
      setMsg({ ok: false, text: e.status === 429 ? "יותר מדי הודעות בשעה האחרונה. נסו שוב מאוחר יותר." : (e.message || "השליחה נכשלה") });
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-lg font-bold mb-1">הודעה לבעל הנכס</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        ההודעה נכנסת לתיבת ההודעות של בעל הנכס והוא מקבל התראה. תשובה תחזור אליכם בוואטסאפ או בטלפון.
      </p>
      {preview && (
        <div className="mb-3 rounded-lg p-2.5 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <b>תצוגה מקדימה</b> — כך הדייר רואה את זה. השליחה פעילה רק מתוך אזור דייר מחובר: מכרטיס הנכס ←
          "עוד ⋯" ← <b>"גישה לדייר"</b> ← "כניסה לאזור של הדייר", או כניסה בקוד הדייר ממסך הכניסה. ההודעה
          תופיע אצלך בלשונית "הודעות" עם צליל.
        </div>
      )}
      {!preview && impersonating && (
        <div className="mb-3 rounded-lg p-2.5 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          אתם בחשבון של הדייר — ההודעה תישלח באמת ותופיע אצלכם ב"הודעות", מסומנת ככזו שנשלחה על ידיכם מתוך האזור של הדייר.
        </div>
      )}
      <textarea value={text} rows={3} maxLength={1000} onChange={(e) => setText(e.target.value)} disabled={preview}
        placeholder="למשל: הברז במטבח מטפטף, אשמח לתיאום תיקון"
        aria-label="הודעה לבעל הנכס" style={{ ...inputStyle, resize: "vertical", opacity: preview ? 0.6 : 1 }} />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px]" style={{ color: C.muted }}>{text.length}/1000</span>
        <button onClick={send} disabled={preview || busy || !text.trim()} className="rounded-lg px-4 py-2 text-sm flex items-center gap-1.5"
          style={{ background: C.teal, color: "#fff", opacity: preview || busy || !text.trim() ? 0.6 : 1 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} שליחה
        </button>
      </div>
      {msg && (
        <div className="mt-2 rounded-lg p-2 text-[12px]" style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
      {items.length > 0 && (
        <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: `1px solid ${C.line}` }}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px]" style={{ color: C.muted }}>השיחה עם בעל הנכס — החדשות למעלה</div>
            {!preview && (
              <div className="flex gap-1.5">
                <button onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }} className="text-[11px] rounded-lg px-2 py-1"
                  style={{ border: `1px solid ${C.line}`, color: C.muted, background: selectMode ? "#F1F4F1" : "transparent" }}>
                  {selectMode ? "ביטול" : "בחירה"}
                </button>
                {selectMode && (
                  <button onClick={() => removeIds([...selected])} disabled={!selected.size}
                    className="text-[11px] rounded-lg px-2 py-1 flex items-center gap-1"
                    style={{ background: C.late, color: "#fff", opacity: selected.size ? 1 : 0.5 }}>
                    <Trash2 size={11} /> מחיקה{selected.size ? ` (${selected.size})` : ""}
                  </button>
                )}
              </div>
            )}
          </div>
          {items.map((m) => {
            const mine = m.from !== "owner";
            return (
              <div key={m.id} className="rounded-lg border px-3 py-2 text-[12px] flex items-start gap-2"
                style={{ borderColor: mine ? C.line : "#BFD7E0", background: mine ? C.card : "#F3F8FA" }}>
                {selectMode && !preview && (
                  <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} className="mt-1" aria-label="בחירת הודעה" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] mb-0.5" style={{ color: mine ? C.muted : "#2E8FCC", fontWeight: 600 }}>
                    {mine ? "אתם" : "בעל הנכס"}{m.requestId ? " · בנוגע לבקשת ההארכה" : ""}
                  </div>
                  <div className="break-words">{m.text}</div>
                  <div className="flex items-center justify-between mt-0.5 text-[11px]" style={{ color: C.muted }}>
                    {/* תאריך ושעה מקומיים — לא רק היום. */}
                    <span>{(() => { const d = new Date(m.createdAt); return `${heDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`; })()}</span>
                    {mine && <span style={{ color: m.seenAt ? C.paid : C.muted }}>{m.seenAt ? "נקרא ✓" : "נשלח"}</span>}
                  </div>
                </div>
                {!preview && (
                  <button onClick={() => removeIds([m.id])} aria-label="מחיקת ההודעה" title="מחיקה"
                    className="shrink-0 rounded-lg px-1.5 py-1" style={{ color: C.muted }}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * תשלומים וחשבונות לפי חודש, מהחדש לישן — מה נרשם כהתקבל (מהדיווח של
 * בעל הנכס) ומה עדיין פתוח. אותם חודשים שמרכיבים את כרטיס החוב.
 */
function PaidHistory({ months }) {
  const [all, setAll] = useState(false);
  const rows = [...(months || [])].reverse();
  if (!rows.length) return null;
  const shown = all ? rows : rows.slice(0, 6);
  const part = (label, x) => (
    <span>{label} {x.due > 0 ? `${nis(x.paid)} מתוך ${nis(x.charged)}` : `${nis(x.charged)} ✓`}</span>
  );
  return (
    <div className="rounded-xl border p-4 mt-4" style={{ background: C.card, borderColor: C.line }}>
      <h2 className="disp text-lg font-bold mb-1">תשלומים וחשבונות</h2>
      <p className="text-xs mb-3" style={{ color: C.muted }}>
        מה נרשם כהתקבל ומה עדיין פתוח, לפי חודש. מתעדכן כשבעל הנכס מסמן שהתשלום הגיע.
      </p>
      <div className="space-y-1.5">
        {shown.map((x) => (
          <div key={x.month} className="rounded-lg border px-3 py-2 text-[12px]"
            style={{ borderColor: C.line, background: x.due > 0 ? "#FFFBF3" : "#FBFCFB" }}>
            <div className="flex items-center justify-between">
              <span style={{ fontWeight: 600 }}>{monthLabel(x.month)}{x.month === CUR_KEY ? " (החודש)" : ""}</span>
              {x.due > 0
                ? <span style={{ color: C.late, fontWeight: 600 }}>פתוח {nis(x.due)}</span>
                : <span className="flex items-center gap-1" style={{ color: C.paid }}><CheckCircle2 size={13} /> שולם</span>}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5" style={{ color: C.muted }}>
              {x.rent.charged > 0 && part("שכ״ד", x.rent)}
              {x.bills.charged > 0 && part("חשבונות", x.bills)}
              {x.rent.charged === 0 && x.bills.charged === 0 && x.paid > 0 && <span>התקבל {nis(x.paid)}</span>}
            </div>
          </div>
        ))}
      </div>
      {rows.length > 6 && (
        <button onClick={() => setAll((v) => !v)} className="mt-2 text-xs" style={{ color: C.teal }}>
          {all ? "הצגת החודשים האחרונים בלבד" : `הצגת כל ${rows.length} החודשים`}
        </button>
      )}
    </div>
  );
}

/**
 * פירוט חוב חשבונות הצריכה — נפתח בלחיצה על כרטיס "חוב חשבונות צריכה"
 * באזור האישי. אותם months כמו PaidHistory, אבל ברמת הסעיף הבודד (מים,
 * חשמל, גז, ועד בית וכו') כדי שהדייר יראה על מה בדיוק הסכום מורכב ולא
 * רק את הסך הכול שכבר מוצג בכרטיס.
 */
function BillsDebtModal({ months, onClose }) {
  const rows = [...(months || [])].reverse()
    .map((x) => ({
      month: x.month,
      due: x.bills.due,
      items: BILLS.map(({ k, label }) => ({ k, label, amount: itemDue(x.bills.record?.[k]) })).filter((it) => it.amount > 0),
    }))
    .filter((x) => x.due > 0);
  return (
    <Modal title="פירוט חוב חשבונות" onClose={onClose}>
      {rows.length === 0 ? (
        <div className="text-sm" style={{ color: C.muted }}>אין חוב פתוח.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((m) => (
            <div key={m.month} className="rounded-lg border p-3" style={{ borderColor: C.line }}>
              <div className="flex items-center justify-between text-sm mb-1.5" style={{ fontWeight: 600 }}>
                <span>{monthLabel(m.month)}{m.month === CUR_KEY ? " (החודש)" : ""}</span>
                <span style={{ color: C.late }}>{nis(m.due)}</span>
              </div>
              <div className="space-y-1 text-[12px]" style={{ color: C.muted }}>
                {m.items.map((it) => (
                  <div key={it.k} className="flex items-center justify-between">
                    <span>{it.label}</span><span>{nis(it.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * בקשת הארכת חוזה מהאזור האישי.
 *
 * אצל דייר אמיתי (onExtension) הבקשה נשלחת לשרת, שכותב אותה למסמך ורושם
 * התראה לבעל הנכס; בתצוגה המקדימה של בעל הנכס אותה פעולה רצה מקומית.
 * המצב (ממתינה / אושרה / נדחתה) נקרא מהמסמך, ולכן זהה בשני המסלולים.
 */
function ExtensionRequestBlock({ apt, data, onExtension, run, contracts, onOpenContract }) {
  const pending = pendingExtensionRequest(data, apt.id);
  const all = extensionRequestsOf(data, apt.id);
  const last = all.length ? all[all.length - 1] : null;
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(apt.leaseEnd ? addYears(apt.leaseEnd, 1) : addYears(TODAY, 1));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  if (!apt.tenant || !apt.leaseEnd) return null;

  const act = async (payload, localAction, okText) => {
    setBusy(true); setMsg(null);
    try {
      if (onExtension) {
        await onExtension(payload);
      } else {
        const r = run([localAction]);
        const bad = (r.logs || []).find((l) => !l.ok);
        if (bad) throw new Error(bad.text);
      }
      setOpen(false); setNote("");
      setMsg({ ok: true, text: okText });
    } catch (err) {
      setMsg({ ok: false, text: err.message || "הפעולה נכשלה" });
    }
    setBusy(false);
  };

  const submit = () => act(
    { action: "request", to, note },
    { type: "request_extension", apartment: apt.id, to, notes: note, by: "owner" },
    onExtension
      ? "הבקשה נשלחה לבעל הנכס. תקבלו תשובה כאן ובהודעה."
      : "הבקשה נרשמה אצלך (תצוגה מקדימה — לא עברה דרך השרת). היא מופיעה בלשונית \"הודעות\" תחת \"ממתין לטיפול\" ובכרטיס הנכס, לאישור או דחייה.",
  );
  const withdraw = () => act(
    { action: "withdraw", requestId: pending.id },
    { type: "withdraw_extension_request", apartment: apt.id, requestId: pending.id },
    "הבקשה בוטלה.",
  );

  const approvedContract = last?.status === "approved" && last.contractId
    ? (contracts || []).find((c) => c.id === last.contractId) : null;

  return (
    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
      <div className="text-sm mb-1.5" style={{ fontWeight: 600 }}>הארכת החוזה</div>

      {pending ? (
        <div className="rounded-lg p-3 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <div className="flex items-center gap-1.5" style={{ fontWeight: 600 }}>
            <CalendarPlus size={13} /> ביקשתם להאריך את החוזה עד {heDate(pending.to)}
          </div>
          <div className="mt-0.5">
            נשלח ב-{heDate(pending.createdAt)} · {EXTENSION_STATUS.pending}
            {pending.note ? ` · "${pending.note}"` : ""}
          </div>
          <button onClick={withdraw} disabled={busy} className="mt-2 text-xs rounded-lg px-2.5 py-1.5"
            style={{ border: "1px solid #E8C98A", background: "#fff", color: "#6B4E12", opacity: busy ? 0.6 : 1 }}>
            ביטול הבקשה
          </button>
        </div>
      ) : last?.status === "approved" ? (
        <div className="rounded-lg p-3 text-[12px]" style={{ background: "#EAF3EE", color: C.paid }}>
          <div className="flex items-center gap-1.5" style={{ fontWeight: 600 }}>
            <CheckCircle2 size={13} /> בקשת ההארכה עד {heDate(last.to)} אושרה{last.decidedAt ? ` ב-${heDate(last.decidedAt)}` : ""}
          </div>
          {approvedContract && approvedContract.status !== "signed" ? (
            <button onClick={() => onOpenContract(approvedContract)} className="mt-2 text-xs rounded-lg px-3 py-1.5 flex items-center gap-1"
              style={{ background: C.teal, color: "#fff" }}>
              <PenTool size={13} /> נספח ההארכה ממתין לחתימתכם
            </button>
          ) : (
            <div className="mt-0.5">{approvedContract ? "נספח ההארכה חתום." : "נספח ההארכה יופיע כאן ברשימת החוזים."}</div>
          )}
        </div>
      ) : last?.status === "declined" ? (
        <div className="rounded-lg p-3 text-[12px]" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <div style={{ fontWeight: 600 }}>
            בקשת ההארכה עד {heDate(last.to)} נדחתה{last.decidedAt ? ` ב-${heDate(last.decidedAt)}` : ""}
          </div>
          {last.reason && <div className="mt-0.5">הסיבה: {last.reason}</div>}
        </div>
      ) : null}

      {!pending && !open && (
        <button onClick={() => { setOpen(true); setMsg(null); }}
          className="mt-2 text-xs rounded-lg px-3 py-2 flex items-center gap-1"
          style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          <CalendarPlus size={13} /> {last ? "בקשת הארכה חדשה" : "בקשת הארכת חוזה"}
        </button>
      )}

      {open && !pending && (
        <div className="mt-2 rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-[11px] mb-2" style={{ color: C.muted }}>
            החוזה הנוכחי מסתיים ב-{heDate(apt.leaseEnd)}. עד מתי תרצו להאריך?
          </div>
          <Field label="סיום התקופה המבוקשת">
            <DateField value={to} onChange={setTo} />
          </Field>
          <div className="mt-2">
            <Field label="הודעה לבעל הנכס (לא חובה)">
              <textarea value={note} rows={2} onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="למשל: נשמח להישאר עוד שנה באותם תנאים"
                style={{ ...inputStyle, resize: "vertical" }} />
            </Field>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={submit} disabled={busy} className="flex-1 rounded-lg py-2 text-sm flex items-center justify-center"
              style={{ background: C.teal, color: "#fff", opacity: busy ? 0.6 : 1 }}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : "שליחת הבקשה לבעל הנכס"}
            </button>
            <button onClick={() => setOpen(false)} className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
              ביטול
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div className="mt-2 rounded-lg p-2 text-[12px]" style={{ background: msg.ok ? "#EAF3EE" : "#FBF2E3", color: msg.ok ? C.paid : "#6B4E12" }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function BillRow({ label, amount, paid }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: paid ? C.muted : C.ink }}>{label}</span>
      <span className="flex items-center gap-2" style={{ color: paid ? C.paid : C.ink }}>
        {nis(amount)} {paid && <CheckCircle2 size={14} />}
      </span>
    </div>
  );
}

/* ---------------- חוזה: צפייה וחתימה ---------------- */
/* ניהול ערבים — משמש גם את בעל הנכס בקליטה וגם את הדייר באזור שלו */
/**
 * @param linksOnly מצב תצוגה מצומצם: הפרטים כבר הוקלדו (באשף), וכאן
 *        רק שולחים קישור לחתימה ורואים מי חתם. בלי עריכה ובלי מחיקה,
 *        כדי שהמסך שאחרי השמירה יישאר מסך אחד עם פעולה אחת.
 */
function GuarantorsEditor({ apt, setData, tenantMode, ownerPhone, linksOnly = false, onGuarantor = null }) {
  /* onGuarantor — סשן דייר אמיתי (TenantApp): שם setData הוא no-op במכוון,
     וכל פעולה עוברת בנקודות הקצה של האזור האישי. הקלדה נשמרת מקומית
     (patches) ונשלחת בעזיבת השדות; הוספה, מחיקה וחתימה נשלחות מיד. */
  const server = !!onGuarantor;
  const [patches, setPatches] = useState({});
  const [serverErr, setServerErr] = useState("");
  const baseList = apt.guarantors || [];
  const list = server ? baseList.map((g) => ({ ...g, ...(patches[g.id] || {}) })) : baseList;
  const [signing, setSigning] = useState(null);
  const [busy, setBusy] = useState("");
  const [deedView, setDeedView] = useState(null);   // ערב שכתב הערבות החתום שלו פתוח לצפייה
  /* קישורי החתימה שהונפקו בביקור הזה: { [gid]: {url, expiresAt} | {error} }.
     הם לא נשמרים במסמך — הטוקן חי בבסיס הנתונים, וכאן מחזיקים רק את מה
     שצריך כדי להציג את הכפתור. */
  const [links, setLinks] = useState({});
  const [linking, setLinking] = useState("");
  /* הודעת הוואטסאפ לדייר — מה נדרש ממנו והקישור האישי של כל ערב שטרם
     חתם. שני שלבים (הכנה ← לחיצה על קישור אמיתי): פתיחת חלון אחרי
     await נחסמת בדפדפני מובייל. */
  const [tenantMsg, setTenantMsg] = useState(null);   // "busy" | { href } | { error }
  const prepareTenantMsg = async () => {
    setTenantMsg("busy");
    try {
      const pending = list.filter((g) => !g.signature);
      const rows = [];
      for (const g of pending) {
        if (!String(g.name || "").trim()) throw new Error("צריך למלא שם לכל ערב לפני שליחת ההודעה לדייר");
        const r = links[g.id]?.url ? links[g.id] : await issueGuarantorLink({ tenantMode: false, aptId: apt.id, guarantorId: g.id });
        setLinks((s) => ({ ...s, [g.id]: r }));
        rows.push(`${g.name}: ${r.url}`);
      }
      const text = `שלום ${apt.tenant || ""}, לחוזה השכירות ב${apt.name} הוגדרו ערבים.\n`
        + `כדי להשלים את החתימות:\n`
        + `1. היכנסו לאזור האישי, קראו וחתמו על החוזה וצלמו תעודת זהות.\n`
        + (rows.length
          ? `2. העבירו לכל ערב את הקישור האישי שלו — הוא ימלא פרטים, יצלם תעודת זהות ויחתום מהטלפון:\n${rows.join("\n")}\n`
          : "")
        + `האזור האישי ייפתח במלואו אחרי שכל החתימות יושלמו. תודה!`;
      setTenantMsg({ href: waLink(apt.phone, text) });
    } catch (ex) {
      setTenantMsg({ error: ex.message });
    }
  };

  /* דרך setGuarantors ולא כתיבה ישירה ל-apt.guarantors: מקור האמת הוא
     תקופת השכירות, וכתיבה לנכס נמחקת בטעינה הבאה. */
  const update = (next) => setData((d) => setGuarantors(d, apt.id, next));

  /** שולח לשרת את מה שהוקלד לערב הזה וטרם נשמר. זורק הלאה בכישלון. */
  const flush = async (id) => {
    if (!server) return;
    const p = patches[id];
    if (!p || !Object.keys(p).length) return;
    await onGuarantor({ action: "save", guarantor: { guarantorId: id, ...p } });
    setPatches((s) => { const n = { ...s }; delete n[id]; return n; });
  };
  const flushQuiet = (id) => flush(id).catch((ex) => setServerErr(ex.message));

  const setField = (id, k, v) => {
    if (server) {
      setServerErr("");
      setPatches((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));
      return;
    }
    update(list.map((g) => (g.id === id ? { ...g, [k]: v } : g)));
  };
  const add = () => {
    if (server) {
      setServerErr("");
      onGuarantor({ action: "save", guarantor: {} }).catch((ex) => setServerErr(ex.message));
      return;
    }
    update([...list, { id: uid(), name: "", idNumber: "", phone: "", address: "", idPhoto: null, signature: null, signedAt: null }]);
  };
  const remove = (g) => {
    if (server) {
      setServerErr("");
      onGuarantor({ action: "delete", guarantorId: g.id }).catch((ex) => setServerErr(ex.message));
      return;
    }
    update(list.filter((x) => x.id !== g.id));
  };

  /* צילום תעודת זהות עולה כקובץ ונשמר כמזהה. הטמעתו כ-dataURL בתוך המסמך
     הייתה מגדילה כל שמירה במאות קילובייטים, ומשכפלת מסמך מזהה בכל גיבוי. */
  const attachId = async (file, gid) => {
    if (!file) return;
    setBusy(gid);
    setServerErr("");
    try {
      /* זיהוי מספר הת"ז רץ במקביל להעלאה, על העותק שלפני ההצפנה.
         כישלון זיהוי אינו כישלון העלאה — מקלידים ידנית. */
      const ocr = compressForUpload(file)
        .then((dataUrl) => ai.readId(dataUrl, { scope: tenantMode ? "tenant" : "owner" }))
        .catch(() => null);
      const id = await attachFile(file, {
        scope: tenantMode ? "tenant" : "owner",
        kind: "id",
        encrypt: true,
        ...(tenantMode ? {} : { apartmentId: apt.id }),
      });
      // בסשן דייר הצילום נקשר לערב דרך השרת — setField שם מקומי בלבד.
      if (server) await onGuarantor({ action: "save", guarantor: { guarantorId: gid, idPhotoId: id } });
      else setField(gid, "idPhoto", id);
      const out = await ocr;
      const cur = list.find((x) => x.id === gid);
      if (out?.idNumber && !String(cur?.idNumber || "").trim()) {
        if (server) await onGuarantor({ action: "save", guarantor: { guarantorId: gid, idNumber: out.idNumber } });
        else setField(gid, "idNumber", out.idNumber);
      }
    } catch (ex) {
      /* בליעה שקטה כאן היא שהסתירה את אובדן הצילומים — השגיאה חייבת להיראות. */
      setServerErr(ex?.message || "העלאת הצילום נכשלה. נסו שוב.");
    }
    setBusy("");
  };

  /**
   * מנפיק קישור חתימה לערב.
   *
   * הקישור לא נפתח כאן ישירות לוואטסאפ: פתיחת חלון אחרי await נחסמת
   * בדפדפני מובייל, וזה בדיוק המכשיר שממנו שולחים. במקום זה הקישור מוצג,
   * והשליחה היא לחיצה שנייה על קישור אמיתי.
   */
  const makeLink = async (g, force = false) => {
    if (!g.name.trim()) {
      setLinks((s) => ({ ...s, [g.id]: { error: "צריך למלא את שם הערב לפני שליחת הקישור" } }));
      return;
    }
    setLinking(g.id);
    try {
      // מה שהוקלד וטרם נשמר חייב להגיע לשרת לפני הקישור — הערב יראה אותו.
      await flush(g.id);
      const r = await issueGuarantorLink({
        tenantMode, aptId: apt.id, guarantorId: g.id, force,
      });
      setLinks((s) => ({ ...s, [g.id]: r }));
    } catch (ex) {
      setLinks((s) => ({ ...s, [g.id]: { error: ex.message } }));
    }
    setLinking("");
  };

  return (
    <div>
      {!linksOnly && (
        <p className="text-xs mb-2" style={{ color: C.muted }}>
          {tenantMode
            ? "הוסיפו את פרטי הערבים שלכם. אפשר לשלוח לכל אחד קישור אישי — הוא יקרא את החוזה, יצלם תעודת זהות ויחתום מהטלפון שלו — או להחתים אותם כאן."
            : "הערבים יופיעו בסעיף הערבות ובנספח ב' לחוזה, עם חתימותיהם."}
        </p>
      )}

      {list.length === 0 && !linksOnly && <div className="text-sm py-3 text-center" style={{ color: C.muted }}>אין ערבים רשומים</div>}

      {list.map((g, i) => (
        <div key={g.id} className="rounded-lg border p-3 mb-2" style={{ borderColor: g.signature ? "#CBE2D6" : C.line, background: g.signature ? "#F4FAF6" : C.card }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm" style={{ fontWeight: 600 }}>
              {linksOnly ? (g.name || `ערב ${i + 1}`) : `ערב ${i + 1}`}
              {linksOnly && g.idNumber && <span className="text-[11px] font-normal" style={{ color: C.muted }}> · ת.ז. {g.idNumber}</span>}
            </div>
            <div className="flex items-center gap-2">
              {g.signature && <span className="text-[11px] flex items-center gap-1" style={{ color: C.paid }}><CheckCircle2 size={12} /> חתם {heDate(g.signedAt)}</span>}
              {/* ערב שחתם נעול בסשן דייר — כתב ערבות חתום לא נמחק מהאזור האישי. */}
              {!linksOnly && !(server && g.signature) && (
                <button onClick={() => remove(g)} aria-label="מחיקה" style={{ color: C.late }}><Trash2 size={14} /></button>
              )}
            </div>
          </div>
          {!linksOnly && (
            /* בסשן דייר ההקלדה נשמרת בעזיבת השדה — האירוע מבעבע מהשדות. */
            <div className="grid sm:grid-cols-2 gap-2" onBlur={server ? () => flushQuiet(g.id) : undefined}>
              <input style={inputStyle} placeholder="שם מלא" value={g.name} disabled={server && !!g.signature} onChange={(e) => setField(g.id, "name", e.target.value)} />
              <input style={inputStyle} inputMode="numeric" placeholder="תעודת זהות" value={g.idNumber} disabled={server && !!g.signature} onChange={(e) => setField(g.id, "idNumber", e.target.value)} />
              <PhoneField value={g.phone} onChange={(v) => setField(g.id, "phone", v)} placeholder="טלפון" disabled={server && !!g.signature} />
              <input style={inputStyle} placeholder="כתובת" value={g.address} disabled={server && !!g.signature} onChange={(e) => setField(g.id, "address", e.target.value)} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-2">
            {g.idPhoto && !tenantMode && (
              <SecureImage id={g.idPhoto} alt="ת.ז." zoom className="rounded" style={{ height: 40 }} />
            )}
            {g.idPhoto && tenantMode && (
              /* הדייר מצפין אל בעל הנכס ולכן אינו יכול לפתוח בחזרה. */
              <span className="text-[11px] flex items-center gap-1" style={{ color: C.muted }}>
                <Check size={12} /> צילום נשמר
              </span>
            )}
            {!linksOnly && (
              <>
                <FilePick capture title="צילום תעודת זהות" onPick={(f) => attachId(f, g.id)} className="text-xs rounded-lg px-2.5 py-1.5 gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                  <Camera size={13} /> {busy === g.id ? "שומר…" : g.idPhoto ? "החלפת צילום ת.ז." : "צילום ת.ז."}
                </FilePick>
                <FilePick title="העלאת ת.ז." onPick={(f) => attachId(f, g.id)} className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                  <Upload size={13} />
                </FilePick>
              </>
            )}
            {!g.signature && (
              <button onClick={() => makeLink(g)} disabled={linking === g.id}
                className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
                style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: linking === g.id ? 0.6 : 1 }}>
                <Send size={13} /> {linking === g.id ? "מכין…" : links[g.id]?.url ? "קישור חדש" : "קישור לחתימה מרחוק"}
              </button>
            )}
            {!g.signature && !linksOnly && (
              <button onClick={() => setSigning(g.id)} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ background: C.teal, color: "#fff" }}>
                <PenTool size={13} /> החתמה כאן
              </button>
            )}
            {g.signature && (
              <div className="flex items-center gap-2">
                <img src={g.signature} alt="חתימה" style={{ height: 34 }} />
                {/* המסמך הסופי — הנוסח שנחתם, חותמת הזמן, המזהה וה-hash. */}
                {g.deed && (
                  <button onClick={() => setDeedView(g)} className="text-[11px] rounded-lg px-2 py-1 flex items-center gap-1"
                    style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                    <FileText size={11} /> המסמך החתום
                  </button>
                )}
                {/* בסשן דייר אין ביטול חתימה — כתב ערבות חתום נעול. */}
                {!server && (
                  <button onClick={() => setField(g.id, "signature", null)} className="text-[11px]" style={{ color: C.muted }}>חתימה מחדש</button>
                )}
              </div>
            )}
          </div>

          {links[g.id] && !g.signature && (
            <GuarantorLink
              link={links[g.id]}
              guarantor={g}
              apt={apt}
              onClear={() => setLinks((s) => ({ ...s, [g.id]: undefined }))}
            />
          )}

          {signing === g.id && (
            <div className="mt-2">
              <div className="text-[11px] mb-1" style={{ color: C.muted }}>חתימת {g.name || `ערב ${i + 1}`} על כתב הערבות</div>
              <SignaturePad
                saveLabel="שמירת חתימת הערב"
                guard={() => (g.name ? null : "צריך למלא את שם הערב לפני החתימה")}
                onSave={async (dataUrl) => {
                  if (server) {
                    /* קודם הפרטים שהוקלדו, ואז החתימה — השרת דוחה חתימה
                       לערב בלי שם. כישלון מוצג במשטח החתימה עצמו. */
                    await flush(g.id);
                    await onGuarantor({ action: "sign", guarantorId: g.id, signature: dataUrl });
                  } else {
                    update(list.map((x) => (x.id === g.id ? { ...x, signature: dataUrl, signedAt: TODAY } : x)));
                  }
                  setSigning(null);
                }}
              />
              <button onClick={() => setSigning(null)} className="mt-2 text-xs" style={{ color: C.muted }}>ביטול</button>
            </div>
          )}
        </div>
      ))}

      {serverErr && (
        <div className="rounded-lg p-2 mb-2 text-[12px]" style={{ background: "#FBEDEA", color: C.late }}>{serverErr}</div>
      )}

      <button onClick={add} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
        <Plus size={13} /> הוספת ערב
      </button>

      {/* עדכון הדייר: הוגדרו ערבים — עליו לחתום, לצלם ת"ז ולהחתים אותם.
          ההודעה נבנית עם הקישור האישי של כל ערב שטרם חתם. */}
      {!tenantMode && !linksOnly && list.length > 0 && apt.phone && (
        <div className="mt-3">
          {tenantMsg?.href ? (
            <a href={tenantMsg.href} target="_blank" rel="noreferrer" onClick={() => setTenantMsg(null)}
              className="w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
              style={{ background: "#128C7E", color: "#fff" }}>
              <MessageCircle size={15} /> שליחת ההודעה ל{apt.tenant || "דייר"} בוואטסאפ
            </a>
          ) : (
            <button onClick={prepareTenantMsg} disabled={tenantMsg === "busy"}
              className="w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
              style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: tenantMsg === "busy" ? 0.6 : 1 }}>
              <MessageCircle size={15} /> {tenantMsg === "busy" ? "מכין את הקישורים…" : "עדכון הדייר בוואטסאפ — חתימות וצילומי ת\"ז"}
            </button>
          )}
          {tenantMsg?.error && <div className="text-[11px] mt-1" style={{ color: C.late }}>{tenantMsg.error}</div>}
          <p className="text-[11px] mt-1" style={{ color: C.muted }}>
            ההודעה מסבירה לדייר שעליו לחתום על החוזה, לצלם ת"ז ולהחתים את הערבים, וכוללת את הקישור האישי של כל ערב שטרם חתם.
          </p>
        </div>
      )}

      {tenantMode && ownerPhone && list.some((g) => g.signature) && (
        <a href={waLink(ownerPhone, `שלום, הערבים שלי חתמו על כתב הערבות עבור ${apt.name}.`)} target="_blank" rel="noreferrer"
          className="mt-3 w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2" style={{ background: "#128C7E", color: "#fff" }}>
          <Send size={15} /> עדכון בעל הדירה שהערבים חתמו
        </a>
      )}

      {/* המסמך הסופי של ערב שחתם: הנוסח שנשמר ברגע החתימה — לא נוסח
          שנבנה עכשיו מחדש — עם החתימה, הזמן המלא, מזהה המסמך וה-hash. */}
      {deedView && (
        <Modal title={`כתב ערבות חתום — ${deedView.name || ""}`} onClose={() => setDeedView(null)} wide>
          <div className="print-area">
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.8 }}>{deedView.deed.text}</div>
            <div className="mt-3 text-[12px]" style={{ color: C.muted }}>{deedView.deed.consent}</div>
            <div className="mt-3">
              <div className="text-[11px]" style={{ color: C.muted }}>חתימת הערב/ה:</div>
              <img src={deedView.signature} alt="חתימה" style={{ height: 64 }} />
            </div>
            <div className="text-[11px] mt-4 pt-2" style={{ borderTop: `1px solid ${C.line}`, color: C.muted, lineHeight: 1.8 }}>
              נחתם דיגיטלית: {new Date(deedView.deed.signedAt).toLocaleString("he-IL")}
              {deedView.deed.otp?.verified ? ` · זהות הטלפון אומתה בקוד SMS (${deedView.deed.otp.phone || ""})` : ""}
              {deedView.idPhoto ? " · צורף צילום תעודת זהות" : ""}
              <br />מזהה מסמך: <span dir="ltr">{deedView.deed.id}</span>
              <br />טביעת אצבע (SHA-256): <span dir="ltr" style={{ fontSize: 9, wordBreak: "break-all" }}>{deedView.deed.sha256}</span>
            </div>
          </div>
          <button onClick={() => printContract(`כתב ערבות — ${deedView.name || ""}`)}
            className="no-print mt-4 w-full rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
            style={{ background: C.teal, color: "#fff" }}>
            <FileText size={15} /> הדפסה / שמירה כ-PDF
          </button>
        </Modal>
      )}
    </div>
  );
}

/**
 * הקישור שהונפק לערב, מוכן לשליחה.
 *
 * הוא מוצג ולא נשלח אוטומטית: המכשיר שממנו שולחים הוא כמעט תמיד טלפון,
 * ופתיחת חלון אחרי קריאת רשת נחסמת שם. לחיצה שנייה על קישור אמיתי עובדת
 * בכל מקום — וגם נותנת לשולח לראות מה בדיוק הוא מעביר.
 */
function GuarantorLink({ link, guarantor, apt, onClear }) {
  const [copied, setCopied] = useState(false);

  if (link.error) {
    return (
      <div className="mt-2 rounded-lg p-2 text-[11px] flex items-start gap-1.5"
        style={{ background: "#FBF2E3", color: "#6B4E12" }}>
        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
        <span>{link.error}</span>
      </div>
    );
  }

  const text = guarantorMessage({
    name: guarantor.name,
    tenant: apt.tenant,
    apartment: apt.name,
    url: link.url,
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // דפדפן ללא הרשאת לוח — הקישור ממילא מוצג ואפשר לסמן אותו ידנית.
    }
  };

  return (
    <div className="mt-2 rounded-lg border p-2.5" style={{ borderColor: "#CBE2D6", background: "#F4FAF6" }}>
      <div className="text-[11px] mb-1.5" style={{ color: C.muted }}>
        {link.reused ? "הקישור שכבר נשלח (עדיין בתוקף)" : "קישור אישי לחתימה"} —
        {" "}תקף עד {heDate(String(link.expiresAt).slice(0, 10))}
      </div>

      <div className="text-[11px] rounded p-1.5 mb-2 break-all" dir="ltr"
        style={{ background: "#fff", border: `1px solid ${C.line}`, textAlign: "left" }}>
        {link.url}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {guarantor.phone && (
          <a href={waLink(guarantor.phone, text)} target="_blank" rel="noreferrer"
            className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
            style={{ background: "#128C7E", color: "#fff" }}>
            <MessageCircle size={13} /> שליחה בוואטסאפ
          </a>
        )}
        <button onClick={copy} className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
          style={{ border: `1px solid ${C.line}`, color: copied ? C.paid : C.muted }}>
          {copied ? <><Check size={13} /> הועתק</> : "העתקת הקישור"}
        </button>
        <button onClick={onClear} className="text-[11px]" style={{ color: C.muted }}>הסתרה</button>
      </div>

      {!guarantor.phone && (
        <div className="text-[11px] mt-1.5" style={{ color: C.muted }}>
          מלאו טלפון כדי לשלוח בוואטסאפ ישירות, או העתיקו את הקישור.
        </div>
      )}
    </div>
  );
}

/* src — חתימה כ-data URL (ערבים, חתימה שזה עתה צוירה). fileId — חתימה
   שנשמרה כקובץ (חתימת הדייר על החוזה, חתימת המשכיר מההגדרות). */
function SigBlock({ label, name, src, fileId, scope = "owner", date }) {
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: 10, minHeight: 96 }}>
      <div style={{ fontSize: 11, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{name || "________"}</div>
      {src
        ? <img src={src} alt="חתימה" style={{ height: 46, marginTop: 4 }} />
        : fileId
          ? <StoredImage id={fileId} scope={scope} eager alt="חתימה" style={{ height: 46, marginTop: 4, background: "transparent" }} muted="transparent" />
          : <div style={{ height: 46, borderBottom: `1px solid ${C.line}`, marginTop: 4 }} />}
      <div style={{ fontSize: 10, color: C.muted }}>{date ? `נחתם ${heDate(date)}` : "טרם נחתם"}</div>
    </div>
  );
}

/* מסמך החוזה כפי שהוא מודפס — כולל החתימות בגוף המסמך */
function ContractDocument({ apt, contract, settings, scope = "owner" }) {
  const gs = apt.guarantors || [];
  /* קובץ חוזה פרטי שהועלה: תמונה מוצגת בגוף המסמך; PDF נפתח בקישור —
     הדפדפן בטלפון ממילא לא מציג PDF בתוך העמוד. החתימות עדיין למטה. */
  const isFileImage = contract.fileId && String(contract.fileMime || "").startsWith("image/");
  return (
    <div className="print-area" style={{ background: "#fff", color: C.ink, padding: 14 }}>
      {contract.fileId ? (
        isFileImage ? (
          <StoredImage id={contract.fileId} scope={scope} eager alt={contract.fileName || "קובץ החוזה"}
            style={{ maxWidth: "100%", borderRadius: 6 }} />
        ) : (
          <a href={fileUrl(contract.fileId, scope)} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 rounded-lg p-3 text-sm no-print-link"
            style={{ border: `1px solid ${C.line}`, color: C.teal, textDecoration: "none" }}>
            <FileText size={16} /> {contract.fileName || "קובץ החוזה"} — לחיצה לפתיחה
          </a>
        )
      ) : (
        <div style={{ whiteSpace: "pre-wrap", fontSize: 12.5, lineHeight: 1.75 }}>
          {contractText(apt, contract, settings)}
        </div>
      )}
      {/* החתימות בגוף המסמך — גם ב-PDF. החתימה השמורה היא קובץ, ולכן
          נטענת דרך StoredImage ולא רק מ-data URL שקיים רק ברגע החתימה. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 18 }}>
        <SigBlock label="השוכר" name={apt.tenant} src={contract.signature} fileId={contract.signatureId} scope={scope} date={contract.signedAt} />
        <SigBlock label="המשכיר" name={settings.ownerName} src={settings.ownerSignature} fileId={settings.ownerSignatureId} scope={scope}
          date={contract.status === "signed" ? contract.signedAt : null} />
      </div>
      {gs.length > 0 && (
        <>
          <div style={{ fontSize: 12, marginTop: 16, fontWeight: 600 }}>חתימות הערבים</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 6 }}>
            {gs.map((g, i) => (
              <SigBlock key={g.id || i} label={`ערב ${i + 1}${g.idNumber ? ` · ת.ז. ${g.idNumber}` : ""}`} name={g.name} src={g.signature} date={g.signedAt} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* כותרת המסמך היא שם הקובץ שהדפדפן מציע ב"שמירה כ-PDF". */
function printContract(title) {
  const prev = document.title;
  try {
    if (title) document.title = title;
    window.print();
    return true;
  } catch {
    return false;
  } finally {
    if (title) setTimeout(() => { document.title = prev; }, 1500);
  }
}

/* נוסח ההזמנה לחתימה — אותו טקסט שנשלח בוואטסאפ, כדי ששיתוף בדרך אחרת
   (SMS, מייל, טלגרם) יביא את הדייר לאותו מקום. */
function contractInvite(apt, c, code) {
  return `שלום ${apt.tenant || ""}, מצורף החוזה ${c.type === "extension" ? "המוארך" : ""} לתקופה ${heDate(c.from)}–${heDate(c.to)} בדמי שכירות ${nis(c.rent)}. נא להיכנס לאזור האישי, לעבור על החוזה ולחתום.${inviteLines(code)}`;
}

/* ניווט מחוזה שנשמר ביומן השיחה אל האובייקטים החיים — הנכס והחוזה
   מחפשים מחדש בכל פתיחה, כי היומן שומר מזהים בלבד והחוזה עשוי להיחתם
   או להימחק בינתיים. חוזה של תקופה עתידית לא מופיע על הנכס המסונכרן
   (הוא משקף את התקופה הנוכחית), ולכן נבנית תצוגה של הנכס כפי שאותה
   תקופה רואה אותו. */
function findContract(d, aptId, contractId) {
  const apt = (d.apartments || []).find((a) => a.id === aptId);
  if (!apt) return null;
  const own = (apt.contracts || []).find((c) => c.id === contractId);
  if (own) return { apt, contract: own };
  const t = tenanciesOf(d, aptId).find((x) => (x.contracts || []).some((c) => c.id === contractId));
  if (!t) return null;
  const view = { ...apt };
  TEN_FIELDS.forEach((k) => { if (t[k] !== undefined) view[k] = t[k]; });
  return { apt: view, contract: t.contracts.find((c) => c.id === contractId) };
}

/* טיוטה שנשלחה לדייר הופכת ל"נשלח לחתימה". נכתב גם לתקופה (המקור שממנו
   הנכס מסונכרן בטעינה) וגם לנכס (התצוגה שעל המסך עכשיו). */
function markContractSent(d, aptId, contractId) {
  const bump = (list) => (list || []).map((x) => (x.id === contractId && x.status === "draft" ? { ...x, status: "sent" } : x));
  return {
    ...d,
    apartments: d.apartments.map((a) => (a.id === aptId ? { ...a, contracts: bump(a.contracts) } : a)),
    tenancies: (d.tenancies || []).map((t) => (t.aptId === aptId && (t.contracts || []).some((c) => c.id === contractId)
      ? { ...t, contracts: bump(t.contracts) } : t)),
  };
}

const canNativeShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

/**
 * חלופות לוואטסאפ לשליחת החוזה: גיליון השיתוף של המכשיר (SMS, מייל,
 * טלגרם…) כשהדפדפן מציע אותו, העתקת הנוסח ללוח, ופתיחת המסמך להורדה
 * כ-PDF. onSent נקרא אחרי שיתוף או העתקה — כמו הלחיצה על וואטסאפ, שמסמנת
 * את החוזה כ"נשלח לחתימה".
 */
function ContractSendRow({ text, title = "חוזה שכירות לחתימה", onDoc = null, onSent = null, size = 12 }) {
  const [fb, setFb] = useState("");
  const flash = (t) => { setFb(t); setTimeout(() => setFb(""), 2500); };

  const share = async () => {
    try {
      await navigator.share({ title, text });
      flash("שותף");
      onSent?.();
    } catch (e) {
      if (e?.name === "AbortError") return;           // המשתמש סגר את הגיליון
      copy();                                         // דפדפן שלא שיתף — נופלים ללוח
    }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); flash("הנוסח הועתק — הדביקו בהודעה או במייל"); onSent?.(); }
    catch { flash("ההעתקה נחסמה — סמנו את הטקסט ידנית"); }
  };

  const btn = { border: `1px solid ${C.line}`, background: "#fff", color: C.ink };
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {canNativeShare() && (
          <button onClick={share} className="rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ ...btn, fontSize: size }}>
            <Share2 size={size + 1} /> שיתוף בדרך אחרת
          </button>
        )}
        <button onClick={copy} className="rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ ...btn, fontSize: size }}>
          <Copy size={size + 1} /> העתקת הנוסח
        </button>
        {onDoc && (
          <button onClick={onDoc} className="rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ ...btn, fontSize: size, color: C.teal }}>
            <Download size={size + 1} /> הורדת החוזה (PDF)
          </button>
        )}
      </div>
      {fb && <div className="mt-1 text-[11px]" style={{ color: C.paid }}>{fb}</div>}
    </div>
  );
}

function ContractModal({ apt, contract, settings, setData, tenantMode, onClose }) {
  const [agreed, setAgreed] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [signing, setSigning] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(contract.status === "signed");
  const [sigId, setSigId] = useState(contract.signatureId || null);
  const [sigData, setSigData] = useState(contract.signature || null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  /* אימות זהות לפני חתימת הדייר (הגדרות → אימות לפני חתימת הדייר על
     החוזה): מי שקיבל את קישור החוזה — במקום הדייר עצמו — לא יכול לחתום
     בלי לדעת ת"ז/טלפון שרשומים אצל בעל הנכס. digitsOnly משווה בלי
     תלות בפורמט (מקפים, רווחים, אפס מוביל). ברירת המחדל "none" משאירה
     את ההתנהגות הקיימת — בלי שדה אימות בכלל. */
  const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
  const verifyMode = tenantMode ? (settings.contractSignVerify || "none") : "none";
  const verifyExpected = verifyMode === "tenantId" ? apt.intake?.tenantId : verifyMode === "phone" ? apt.phone : null;
  const [verifyInput, setVerifyInput] = useState("");
  const verified = verifyMode === "none" || (!!verifyExpected && digitsOnly(verifyInput) === digitsOnly(verifyExpected));

  // המרת מיקום העכבר/אצבע לקואורדינטות הקנבס עצמו — בלי זה הקו נמרח
  // במקום אחר במסך קטן, ונראה כאילו החתימה לא נקלטה.
  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    const src = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = canvasRef.current.getContext("2d"); const { x, y } = pos(e); c.beginPath(); c.moveTo(x, y); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"), { x, y } = pos(e);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = C.ink;
    ctx.lineTo(x, y); ctx.stroke();
    if (!hasSig) setHasSig(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false); };

  const sign = async () => {
    if (verifyMode !== "none" && !verifyExpected) { setErr("בעל הנכס לא רשם עדיין את הפרט הדרוש לאימות — יש לפנות אליו."); return; }
    if (verifyMode !== "none" && !verified) {
      setErr(verifyMode === "tenantId" ? "מספר תעודת הזהות לא תואם למה שרשום — בדקו ונסו שוב." : "מספר הטלפון לא תואם למה שרשום — בדקו ונסו שוב.");
      return;
    }
    if (!agreed) { setErr("צריך לסמן שקראתם את החוזה ומסכימים לתנאיו."); return; }
    if (!canvasRef.current || !canvasHasInk(canvasRef.current)) { setErr("צריך לחתום במסגרת הלבנה לפני השליחה."); return; }
    setErr(""); setSigning(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");

      if (tenantMode) {
        // הדייר לא כותב למסמך. החתימה עולה כקובץ והשרת הוא שמסמן את החוזה
        // כחתום — כך גם מתועד מי חתם ומתי, ולא רק שהשדה השתנה.
        const id = await uploadImage(dataUrl, { scope: "tenant", kind: "signature" });
        await portalApi.sign(contract.id, id);
        setSigId(id);
      } else {
        const id = await uploadImage(dataUrl, {
          scope: "owner", kind: "signature", apartmentId: apt.id,
        });
        setData((d) => ({
          ...d,
          apartments: d.apartments.map((a) => a.id === apt.id ? {
            ...a, contracts: (a.contracts || []).map((c) => c.id === contract.id
              ? { ...c, status: "signed", signedAt: TODAY, signatureId: id } : c),
          } : a),
        }));
        setSigId(id);
      }
      setSigData(null);
      setDone(true);
    } catch (e) {
      setErr("שמירת החתימה נכשלה: " + (e.message || "שגיאה לא ידועה"));
    }
    setSigning(false);
  };

  const signedNote = `שלום, חתמתי על ${contract.type === "extension" ? "נספח ההארכה" : "החוזה"} עבור ${apt.name} לתקופה ${heDate(contract.from)}–${heDate(contract.to)}.`;

  /* נספח הארכה לא עומד לבדו: מי שחותם עליו צריך לראות על מה הוא מאריך.
     החוזה המקורי (האחרון שאינו נספח) מוצג לעיון מעל הנספח — גם בהדפסה
     ל-PDF שני המסמכים יוצאים יחד. החתימה עצמה חלה על הנספח. */
  const baseContract = contract.type === "extension"
    ? (apt.contracts || []).filter((c) => c.type !== "extension" && c.id !== contract.id).slice(-1)[0] || null
    : null;

  return (
    <Modal title={contract.type === "extension" ? "נספח הארכת חוזה" : "חוזה שכירות"} onClose={onClose} wide>
      <div className="rounded-lg border overflow-y-auto print-area" style={{ borderColor: C.line, maxHeight: baseContract ? 440 : 320, background: "#FBFCFB" }}>
        {baseContract && (
          <>
            <div className="px-3 pt-2 text-[11px]" style={{ color: C.muted }}>
              החוזה המקורי ({heDate(baseContract.from)} — {heDate(baseContract.to)}) — לעיון. הנספח שלמטה הוא מה שנחתם עכשיו.
            </div>
            <ContractDocument apt={apt} contract={baseContract} settings={settings} scope={tenantMode ? "tenant" : "owner"} />
            <div className="mx-3 my-2 text-center text-[12px]" style={{ borderTop: `2px dashed ${C.line}`, paddingTop: 8, fontWeight: 600 }}>
              נספח הארכה
            </div>
          </>
        )}
        <ContractDocument apt={apt} scope={tenantMode ? "tenant" : "owner"}
          contract={{
            ...contract,
            signature: sigData || contract.signature,
            signatureId: sigId || contract.signatureId,
            status: done ? "signed" : contract.status,
            signedAt: contract.signedAt || (done ? TODAY : null),
          }}
          settings={settings} />
      </div>
      <div className="flex items-center justify-between mt-2 no-print">
        <p className="text-[11px]" style={{ color: C.muted }}>תבנית כללית — מומלץ שעורך דין יעבור עליה לפני חתימה.</p>
        <button onClick={() => printContract(`${contract.type === "extension" ? "נספח הארכה" : "חוזה שכירות"} — ${apt.name}${apt.tenant ? ` — ${apt.tenant}` : ""}`)}
          className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          <FileText size={13} /> הדפסה / שמירה כ-PDF
        </button>
      </div>

      {done ? (
        <div className="mt-4 no-print">
          <div className="rounded-lg p-3 flex items-start gap-3" style={{ background: "#EAF3EE" }}>
            <CheckCircle2 size={18} style={{ color: C.paid }} />
            <div className="text-sm">
              <div style={{ fontWeight: 600 }}>החוזה נחתם</div>
              <div className="text-[11px]" style={{ color: C.muted }}>
                {heDate(contract.signedAt || TODAY)}
                {tenantMode && " · בעל הנכס קיבל התראה על החתימה"}
              </div>
              {sigData
                ? <img src={sigData} alt="חתימת השוכר" className="mt-1" style={{ height: 48 }} />
                : sigId ? <StoredImage id={sigId} className="mt-1" style={{ height: 48 }} scope={tenantMode ? "tenant" : "owner"} /> : null}
              {(settings.ownerSignature || settings.ownerSignatureId) && (
                <div className="mt-2">
                  <div className="text-[11px]" style={{ color: C.muted }}>חתימת המשכיר</div>
                  {settings.ownerSignature
                    ? <img src={settings.ownerSignature} alt="חתימת המשכיר" style={{ height: 40 }} />
                    : <StoredImage id={settings.ownerSignatureId} style={{ height: 40 }} scope={tenantMode ? "tenant" : "owner"} />}
                </div>
              )}
            </div>
          </div>
          {err && <div className="mt-2 text-[11px]" style={{ color: C.late }}>{err}</div>}
          <div className="flex gap-2 mt-3">
            {tenantMode && (
              <a href={settings.ownerPhone ? waLink(settings.ownerPhone, signedNote) : `https://wa.me/?text=${encodeURIComponent(signedNote)}`}
                target="_blank" rel="noreferrer"
                className="flex-1 rounded-lg py-2.5 text-sm flex items-center justify-center gap-2" style={{ background: "#128C7E", color: "#fff" }}>
                <MessageCircle size={15} /> עדכון בעל הדירה בוואטסאפ
              </a>
            )}
            <button onClick={onClose} className="flex-1 rounded-lg py-2.5 text-sm" style={{ border: `1px solid ${C.line}` }}>סגירה</button>
          </div>
        </div>
      ) : tenantMode ? (
        <div className="mt-4 no-print">
          {verifyMode !== "none" && (
            <div className="rounded-lg border p-3 mb-3" style={{ borderColor: verified ? C.paid : C.line, background: "#FBFCFB" }}>
              <label className="block text-sm mb-1.5" style={{ fontWeight: 600 }}>
                {verifyMode === "tenantId" ? "לפני החתימה — הקלידו את מספר תעודת הזהות שלכם" : "לפני החתימה — הקלידו את מספר הטלפון שלכם"}
              </label>
              <input value={verifyInput} onChange={(e) => { setVerifyInput(e.target.value); setErr(""); }}
                inputMode={verifyMode === "tenantId" ? "numeric" : "tel"}
                placeholder={verifyMode === "tenantId" ? "תעודת זהות" : "טלפון"}
                aria-label={verifyMode === "tenantId" ? "תעודת זהות לאימות" : "טלפון לאימות"}
                style={{ ...inputStyle, maxWidth: 220 }} />
              {verified && <div className="text-[11px] mt-1" style={{ color: C.paid }}>תואם ✓</div>}
            </div>
          )}
          <label className="flex items-start gap-2 text-sm mb-3">
            <input type="checkbox" checked={agreed} onChange={(e) => { setAgreed(e.target.checked); setErr(""); }} className="mt-1" />
            קראתי את החוזה ואני מסכים/ה לתנאיו
          </label>
          <div className="text-xs mb-1" style={{ color: C.muted }}>חתימה בכתב יד — ציירו במסגרת</div>
          <canvas ref={canvasRef} width={640} height={180} className="w-full rounded-lg border touch-none"
            style={{ borderColor: hasSig ? C.teal : C.line, background: "#fff", height: 150 }}
            onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={draw} onTouchEnd={end} onTouchCancel={end} />
          {err && <div className="mt-2 text-sm rounded-lg p-2" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>}
          <div className="flex gap-2 mt-3">
            <button onClick={sign} disabled={signing} className="flex-1 rounded-lg py-2.5 text-sm"
              style={{ background: agreed && hasSig && verified ? C.teal : "#8FA9A7", color: "#fff" }}>
              {signing ? "שומר ושולח…" : "שמירה ושליחה לבעל הדירה"}
            </button>
            <button onClick={clear} className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}` }}>ניקוי</button>
          </div>
        </div>
      ) : (
        <div className="mt-4 text-sm rounded-lg p-3 no-print" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          החוזה ממתין לחתימת הדייר באזור הדייר.
        </div>
      )}
    </Modal>
  );
}

/* ---------------- עוזר AI ---------------- */
/* ============ אשף כניסת דייר ============
   רצף שאלות קבוע — שאלה אחת בכל פעם, בסדר הגיוני, עד שכל מה שצריך
   בנכס מלא: פרטי הדייר, מועדי תשלום, מחזור חיוב, קריאות מונה ביום
   הכניסה ותעריפים. האשף רץ בקוד ולא במודל, כדי שהסדר לא ישתנה. */
const parseNum = (t) => { const m = String(t).replace(/,/g, "").match(/-?\d+(\.\d+)?/); return m ? Number(m[0]) : null; };
/* "מחר" ביחס לתאריך נתון (מחרוזת YYYY-MM-DD) — מחושב מהתאריך המקומי,
   לא מ-Date.now() ± יום, כדי לא להיתקל בגלישת יום סביב חצות ב-UTC. */
const tomorrowOf = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
/* כמה חודשים בחוזה — ברירת המחדל למספר השיקים ("12 שיקים" לשנה). */
function leaseMonthsOf(a) {
  const from = new Date(a?.leaseStart || ""), to = new Date(a?.leaseEnd || "");
  const m = Math.round((to - from) / 2629800000);
  return Number.isFinite(m) && m > 0 ? m : 12;
}
const rentPayLabel = (v) => (!v ? "" : v.method === "check" ? `${v.checks} שיקים` : methodLabel(v.method));
const parseDate = (t) => {
  const raw = String(t).trim();
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
  m = raw.match(/^(\d{1,2})[./](\d{1,2})$/);
  if (m) return `${CUR_Y}-${pad(m[2])}-${pad(m[1])}`;
  return null;
};

/* תיאור בחירת הערבים בשורה — ללוג ולסיכום. */
const guarantorsText = (list) => (!Array.isArray(list) || !list.length
  ? "בלי ערבים — ייכתב בחוזה במפורש"
  : `${list.length === 1 ? "ערב אחד" : `${list.length} ערבים`}: ${list.map((g) => g.name + (g.idPhoto ? ' (צורף צילום ת"ז)' : "")).join(", ")}`);

/* ============ ערבים באשף ============
   "האם יהיו ערבים לחוזה?" נענית ברכיב: בלי ערבים (החוזה אומר זאת
   במפורש) או קליטת הערבים — שם, ת"ז, טלפון, כתובת וצילום תעודת זהות
   (מוצפן, כמו בטופס). מה שלא צולם כאן מושלם אחר כך בקישור האישי של
   הערב מכרטיס הדייר. */
function GuarantorsPick({ initial = null, aptId, onPick }) {
  const [list, setList] = useState(() => (Array.isArray(initial) ? initial : []));
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const patch = (id, k, v) => setList((l) => l.map((g) => (g.id === id ? { ...g, [k]: v } : g)));

  const attachIdPhoto = async (file, gid) => {
    if (!file) return;
    setBusy(gid); setErr("");
    try {
      const id = await attachFile(file, { scope: "owner", kind: "id", encrypt: true, apartmentId: aptId });
      patch(gid, "idPhoto", id);
    } catch (e) {
      setErr(`${e?.message || "העלאת הצילום נכשלה"} — אפשר גם בלי צילום: שולחים לערב קישור אישי אחרי השמירה.`);
    }
    setBusy("");
  };

  const ready = list.every((g) => String(g.name || "").trim());

  return (
    <div className="mt-2 rounded-lg border p-3" style={{ borderColor: C.line }}>
      <div className="text-sm font-medium">ערבים לחוזה</div>
      <div className="text-[11px] mb-2" style={{ color: C.muted }}>
        נכנסים לסעיף הערבות ולנספח ב' (כתב הערבות). צילום ת"ז אפשר כבר כאן,
        או אחרי השמירה — קישור אישי לכל ערב: הוא קורא את החוזה, מצלם ת"ז וחותם מהטלפון שלו.
      </div>

      {list.map((g, i) => (
        <div key={g.id} className="rounded-lg border p-2.5 mb-2" style={{ borderColor: C.line, background: "#FBFCFB" }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[12px]" style={{ fontWeight: 600 }}>ערב {i + 1}</span>
            <button type="button" onClick={() => setList((l) => l.filter((x) => x.id !== g.id))} aria-label="הסרת הערב" style={{ color: C.late }}>
              <Trash2 size={13} />
            </button>
          </div>
          <div className="grid gap-1.5">
            <input style={inputStyle} placeholder="שם מלא" value={g.name} onChange={(e) => patch(g.id, "name", e.target.value)} />
            <input style={inputStyle} inputMode="numeric" placeholder="תעודת זהות" value={g.idNumber} onChange={(e) => patch(g.id, "idNumber", e.target.value)} />
            <PhoneField value={g.phone} onChange={(v) => patch(g.id, "phone", v)} placeholder="טלפון" />
            <input style={inputStyle} placeholder="כתובת" value={g.address} onChange={(e) => patch(g.id, "address", e.target.value)} />
            <div className="flex flex-wrap items-center gap-2">
              <FilePick capture title="צילום תעודת זהות" disabled={busy === g.id} onPick={(f) => attachIdPhoto(f, g.id)}
                className="rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                {busy === g.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {g.idPhoto ? "צילום מחדש" : 'צילום ת"ז'}
              </FilePick>
              <FilePick title="העלאה מהגלריה" disabled={busy === g.id} onPick={(f) => attachIdPhoto(f, g.id)}
                className="rounded-lg px-2.5 py-1.5 text-[12px] flex items-center" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                <Upload size={14} />
              </FilePick>
              {g.idPhoto && <span className="text-[11px]" style={{ color: C.paid }}>✓ צורף צילום</span>}
            </div>
          </div>
        </div>
      ))}

      {err && <div className="text-[11px] mb-1.5" style={{ color: C.late }}>{err}</div>}

      <button type="button" onClick={() => setList((l) => [...l, blankGuarantor()])}
        className="text-[12px] rounded-lg px-3 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
        <Plus size={13} /> {list.length ? "עוד ערב" : "הוספת ערב"}
      </button>

      <button type="button" disabled={!ready} onClick={() => onPick(list.filter((g) => String(g.name || "").trim()))}
        className="mt-2 w-full rounded-lg px-3 py-2 text-[12px]" style={{ background: C.teal, color: "#fff", opacity: ready ? 1 : 0.5 }}>
        {list.length ? `אישור ${list.length === 1 ? "הערב" : `${list.length} הערבים`}` : "בלי ערבים — ייכתב בחוזה"}
      </button>
      {!ready && <div className="text-[11px] mt-1" style={{ color: C.ochre }}>לכל ערב צריך לפחות שם.</div>}
    </div>
  );
}

/* ============ צילום מונה באשף ============
   שאלת "מה הקריאה?" מקבלת גם צילום: התמונה נשמרת כתיעוד (kind "meter"),
   נשלחת לזיהוי, והמספר שזוהה נכנס כתשובה. זיהוי לא ודאי לא נכנס לבד —
   כמו בכרטיס המונה של הדייר: ספרה שגויה מכפילה חשבון, אז המשתמש מאשר
   או מקליד. הצילום נשמר גם כשהזיהוי נכשל — הוא התיעוד למצב המונה. */
function MeterPhotoPick({ aptId, meter, onPick, onPhoto }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(null); // { reading, photoId } — זיהוי לא ודאי שממתין לאישור

  const read = async (file) => {
    if (!file || busy) return;
    setBusy(true); setNote(""); setPending(null);
    let photoId = null;
    try {
      const small = await compressForUpload(file);
      // התמונה נשמרת גם אם הזיהוי ייכשל — כישלון העלאה לא מפיל את הזיהוי.
      try {
        photoId = await uploadImage(small, { scope: "owner", kind: "meter", apartmentId: aptId });
        onPhoto?.(photoId);
      } catch { photoId = null; }

      const parsed = await ai.readMeter(meter, small, { scope: "owner" });
      if (parsed.reading == null) {
        setNote(parsed.note || "לא הצלחתי לקרוא את המונה מהתמונה. הקלידו את המספר — הצילום כבר נשמר ויצורף לקריאה.");
      } else if (parsed.confidence === "low") {
        setPending({ reading: parsed.reading, photoId });
        setNote(`קראתי ${parsed.reading}, אבל לא בוודאות. בדקו מול המונה ואשרו, או הקלידו את המספר.`);
      } else {
        onPick({ reading: parsed.reading, photoId });
      }
    } catch (err) {
      setNote(`${err?.message || "זיהוי הקריאה נכשל."} אפשר להקליד את המספר ידנית.`);
    }
    setBusy(false);
  };

  return (
    <div className="mt-2 rounded-lg border p-2.5" style={{ borderColor: C.line, minWidth: 240 }}>
      <div className="flex flex-wrap items-center gap-2">
        <FilePick capture title="צילום המונה" disabled={busy} onPick={read}
          className="rounded-lg px-3 py-1.5 text-[12px] flex items-center gap-1"
          style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} {busy ? "מזהה…" : "צילום המונה"}
        </FilePick>
        <FilePick title="בחירה מהגלריה" disabled={busy} onPick={read}
          className="rounded-lg px-2.5 py-1.5 text-[12px] flex items-center"
          style={{ border: `1px solid ${C.line}`, color: C.muted }}>
          <Upload size={14} />
        </FilePick>
        {pending && (
          <button type="button" onClick={() => onPick(pending)}
            className="rounded-lg px-3 py-1.5 text-[12px]" style={{ background: C.teal, color: "#fff" }}>
            אישור {pending.reading}
          </button>
        )}
      </div>
      {note && <div className="text-[11px] mt-1.5" style={{ color: C.ochre }}>{note}</div>}
    </div>
  );
}

/* חודש ראשון חלקי — כמה ימים נשארו בחודש הכניסה, וכמה יוצא מזה יחסית
   משכר הדירה. null כשהכניסה כבר ב-1 לחודש (אין מה לפרוס). משותף
   לשאלת האשף (WIZ_STEPS) ולתצוגה המקדימה בטופס הידני (TenantIntake). */
function firstMonthProration(rent, leaseStart) {
  const startDay = Number((leaseStart || "").slice(8, 10)) || 1;
  if (startDay <= 1) return null;
  const [y, m] = (leaseStart || "").split("-").map(Number);
  if (!y || !m) return null;
  const daysInMonth = new Date(y, m, 0).getDate();
  const occupiedDays = daysInMonth - startDay + 1;
  const prorated = Math.round((Number(rent) || 0) * occupiedDays / daysInMonth);
  return { daysInMonth, occupiedDays, prorated };
}

/* אותו רעיון בדיוק כמו חיוב יחסי לשכר דירה (למעלה), לסעיף קבוע אחד
   (ועד בית / ארנונה / היטל עירוני) — prefix הוא "vaad"/"arnona"/
   "municipal", וקובע את שמות שני השדות על f: ${prefix}ProrateFirstMonth
   ו-${prefix}FirstMonthAmount. 0 בשדה הסכום האחר = לא לחייב על החודש
   הראשון בכלל; ריק = ממשיך לפי החישוב היחסי. */
function FirstMonthProrate({ f, setF, prefix, amount }) {
  const p = firstMonthProration(amount, f.leaseStart);
  if (!p) return null;
  const prorateKey = `${prefix}ProrateFirstMonth`, amountKey = `${prefix}FirstMonthAmount`;
  return (
    <div className="mt-2 space-y-1.5">
      <label className="flex items-start gap-2 text-xs rounded-lg border p-2 cursor-pointer" style={{ borderColor: C.line, background: "#FBFCFB" }}>
        <input type="checkbox" className="mt-0.5" checked={!!f[prorateKey]}
          onChange={(e) => setF({ ...f, [prorateKey]: e.target.checked })} />
        <span>
          חיוב יחסי לחודש הראשון — {p.occupiedDays} מתוך {p.daysInMonth} ימים
          {f[prorateKey] ? <> · <b>{nis(p.prorated)}</b> במקום סכום מלא</> : " (סכום מלא כברירת מחדל)"}
        </span>
      </label>
      {f[prorateKey] && (
        <Field label="סכום אחר לחודש הראשון (0 = לא לחייב בכלל)">
          <NumInput value={f[amountKey] ?? ""} placeholder={String(p.prorated)}
            onCommit={(v) => setF({ ...f, [amountKey]: v === "" ? null : v })} />
        </Field>
      )}
    </div>
  );
}

/* יש בכלל מה לגבות מהדייר חוץ משכר דירה? אם לא — אין טעם לשאול על מועד
   ומחזור החיוב של "החשבונות", כי אין חשבון שדרכו עוברת. */
const anyLandlordBill = (a) => ["vaadMode", "arnonaMode", "elecMode", "waterMode", "gasMode"]
  .some((k) => a[k] === "landlord");

const WIZ_STEPS = [
  /* שם הנכס + הדייר שגר בו — "הרצל 12 — דנה לוי" / "חנות — פנוי": כך רואים
     מיד איפה כבר יש דייר לפני שבוחרים. findApt מזהה את הנכס גם עם הסיומת. */
  { k: "apt", q: () => "לאיזו יחידה?", opts: (d) => d.apartments.map((a) => (a.tenant ? `${a.name} — ${a.tenant}` : `${a.name} — פנוי`)) },
  { k: "tenant", q: () => "מה שם הדייר?" },
  { k: "phone", q: () => "מה הטלפון שלו?", skip: true, phone: true },
  /* ת"ז ובן/בת זוג — נכתבים ל-intake ומופיעים בשורת "לבין" של החוזה.
     בלעדיהם החוזה יוצא עם קו ריק במקום ת.ז., והשוכר השני לא מופיע כלל. */
  { k: "tenantId", q: () => "מה מספר תעודת הזהות של הדייר?", skip: true },
  { k: "hasPartner", q: () => "יש בן/בת זוג שיגור בדירה ויופיע בחוזה?", yesNo: true, skip: true, opts: () => ["כן", "לא"] },
  { k: "partnerName", q: () => "מה שם בן/בת הזוג?", when: (a) => a.hasPartner === true },
  { k: "partnerId", q: () => "מה תעודת הזהות של בן/בת הזוג?", skip: true, when: (a) => a.hasPartner === true },
  { k: "partnerPhone", q: () => "ומה הטלפון של בן/בת הזוג?", skip: true, phone: true, when: (a) => a.hasPartner === true },
  /* מציעים כצ׳יפ את שכר הדירה האחרון שהיה רשום על הנכס (הדייר הקודם,
     בהחלפת דייר) — כדי שלא יצטרכו להיזכר בו, ואפשר גם לגעת ולעדכן. */
  {
    k: "rent", q: () => "מה שכר הדירה החודשי?", num: true,
    opts: (d, a) => {
      const apt = d.apartments.find((x) => x.id === a.apt);
      return apt?.rent ? [String(apt.rent)] : [];
    },
  },
  { k: "leaseStart", q: () => "מתי תאריך הכניסה?", date: true },
  { k: "leaseEnd", q: () => "מתי מסתיים החוזה?", date: true },
  /* חודש ראשון חלקי — רק כשהכניסה לא ב-1 לחודש. מציגים כצ'יפ את הסכום
     היחסי המדויק לפי ימים, אבל שאלה מספרית רגילה — אפשר גם להקליד
     סכום אחר אם סוכם בעל פה משהו שונה (למשל סכום עגול). "דלג" = חודש
     מלא, כרגיל. */
  {
    k: "firstMonthAmount",
    q: (a) => {
      const p = firstMonthProration(a.rent, a.leaseStart);
      return p
        ? `הכניסה ב-${heDate(a.leaseStart)} — לא ה-1 לחודש. כמה שכר דירה לחודש הראשון? מוצע ${nis(p.prorated)} (יחסי — ${p.occupiedDays} מתוך ${p.daysInMonth} ימים), אפשר גם להקליד סכום אחר אם סוכם בעל פה משהו שונה`
        : "כמה שכר דירה לחודש הראשון?";
    },
    num: true, skip: true,
    opts: (d, a) => { const p = firstMonthProration(a.rent, a.leaseStart); return p ? [String(p.prorated)] : []; },
    when: (a) => !!firstMonthProration(a.rent, a.leaseStart),
  },
  { k: "rentDay", q: () => "באיזה יום בחודש משולם שכר הדירה?", num: true, opts: () => ["1", "5", "10", "15"] },
  /* ועד בית: קודם האם, אחר כך למי משולם, ורק אז כמה. "כמה ועד בית?"
     בנכס שאין בו ועד הוא שאלה בלי תשובה נכונה, ודילוג עליה לא מבחין בין
     "אין" ל"לא ידוע". */
  { k: "hasVaad", q: () => "האם לנכס הזה יש ועד בית?", yesNo: true, opts: () => ["כן", "לא"] },
  /* "ישירות לוועד" לא הופך את vaadActive לדלוק (הגבייה בפועל לא עוברת
     דרך המשכיר, בדיוק כמו ארנונה ישירה) — אבל הסכום עדיין נשאל, כי הוא
     נכתב לחוזה ומעניין את המשכיר לדעת גם כשלא הוא זה שגובה. */
  { k: "vaadMode", q: () => "ועד הבית — למי משולם?", utility: "vaad", opts: () => ["לי", "ישירות לוועד"], when: (a) => a.hasVaad === true },
  { k: "vaad", q: () => "כמה ועד בית לחודש?", num: true, when: (a) => a.hasVaad === true },
  /* חודש ראשון חלקי לועד בית — אותו רעיון כמו שכר הדירה למעלה: מוצע
     הסכום היחסי, ואפשר גם 0 (לא לחייב בכלל) או כל סכום אחר. "דלג" =
     סכום מלא, כרגיל. רלוונטי רק כשהמשכיר בפועל גובה — בתשלום ישיר לוועד
     אין חיוב דרכו לפרוס. */
  {
    k: "vaadFirstMonthAmount",
    q: (a) => {
      const p = firstMonthProration(a.vaad, a.leaseStart);
      return p
        ? `כמה ועד בית לחודש הראשון? מוצע ${nis(p.prorated)} (יחסי — ${p.occupiedDays} מתוך ${p.daysInMonth} ימים), אפשר גם 0 כדי לא לחייב על החודש הזה בכלל, או סכום אחר`
        : "כמה ועד בית לחודש הראשון?";
    },
    num: true, skip: true,
    opts: (d, a) => { const p = firstMonthProration(a.vaad, a.leaseStart); return p ? [String(p.prorated), "0"] : []; },
    when: (a) => a.hasVaad === true && a.vaadMode !== "direct" && !!firstMonthProration(a.vaad, a.leaseStart),
  },

  { k: "arnonaMode", q: () => "והארנונה — למי משולמת?", utility: "arnona", opts: () => ["משולמת לי, בסכום חודשי", "ישירות לרשות המקומית", "ללא חיוב"] },
  { k: "arnona", q: () => "כמה ארנונה לחודש הדייר מחזיר לך?", num: true, when: (a) => a.arnonaMode === "landlord" },
  {
    k: "arnonaFirstMonthAmount",
    q: (a) => {
      const p = firstMonthProration(a.arnona, a.leaseStart);
      return p
        ? `כמה ארנונה לחודש הראשון? מוצע ${nis(p.prorated)} (יחסי — ${p.occupiedDays} מתוך ${p.daysInMonth} ימים), אפשר גם 0 כדי לא לחייב על החודש הזה בכלל, או סכום אחר`
        : "כמה ארנונה לחודש הראשון?";
    },
    num: true, skip: true,
    opts: (d, a) => { const p = firstMonthProration(a.arnona, a.leaseStart); return p ? [String(p.prorated), "0"] : []; },
    when: (a) => a.arnonaMode === "landlord" && !!firstMonthProration(a.arnona, a.leaseStart),
  },

  /* לכל סעיף צריכה: למי הוא משולם. שלוש אפשרויות, וכל אחת גוררת המשך
     אחר — "משולם לי" שואל תעריף, "ישירות לספק" מתעד מונה בלי חיוב,
     ו"ללא חיוב" מדלג גם על המונה, כי אין מה למדוד. */
  { k: "elecMode", q: () => "חשבון החשמל — למי משולם?", utility: "elec", opts: () => ["משולם לי, לפי מונה", "ישירות לחברת החשמל", "ללא חיוב"] },
  /* meterPhoto בשאלות הכניסה: שם המונה קבוע לשאלה (בזרימת "קריאת מונה"
     הוא true ונלקח מהתשובה). הצילום נשמר כתיעוד ומצורף לקריאת הכניסה. */
  { k: "elecRead", q: (a) => (a.elecMode === "direct"
    ? "מה קריאת מונה החשמל ביום הכניסה? (תיעוד בלבד, בלי חיוב)"
    : "מה קריאת מונה החשמל ביום הכניסה?"), num: true, skip: true, meterPhoto: "elec", when: (a) => a.elecMode !== "none" },
  { k: "elecRate", q: () => `מה תעריף החשמל לקוט"ש?`, num: true, skip: true, opts: (d) => [String(d.settings.elecRate)], when: (a) => a.elecMode === "landlord" },

  { k: "waterMode", q: () => "והמים והביוב — למי משולמים?", utility: "water", opts: () => ["משולם לי, לפי מונה", "ישירות לתאגיד המים", "ללא חיוב"] },
  { k: "waterRead", q: (a) => (a.waterMode === "direct"
    ? "מה קריאת מונה המים ביום הכניסה? (תיעוד בלבד)"
    : "ומה קריאת מונה המים?"), num: true, skip: true, meterPhoto: "water", when: (a) => a.waterMode !== "none" },
  { k: "waterSewageRate", q: () => `מה תעריף המים והביוב למ"ק?`, num: true, skip: true, opts: (d) => [String(d.settings.waterSewageRate)], when: (a) => a.waterMode === "landlord" },

  { k: "gasMode", q: () => "והגז — למי משולם?", utility: "gas", opts: () => ["משולם לי, לפי מונה", "ישירות לספק הגז", "ללא חיוב"] },
  { k: "gasRead", q: () => "מה קריאת מונה הגז ביום הכניסה?", num: true, skip: true, meterPhoto: "gas", when: (a) => a.gasMode !== "none" },
  { k: "gasRate", q: () => `מה תעריף הגז למ"ק?`, num: true, skip: true, when: (a) => a.gasMode === "landlord" },

  /* מועד ומחזור החיוב על "החשבונות" — רלוונטי רק אם יש בכלל סעיף שהמשכיר
     גובה (ועד בית, ארנונה או צריכה). בלי זה השאלה נשארת בלי תשובה נכונה,
     כמו "כמה ועד בית" בנכס בלי ועד. */
  { k: "billsDay", q: () => "ובאיזה יום משולמים החשבונות?", num: true, opts: () => ["1", "5", "10", "15"], when: anyLandlordBill },
  { k: "billsCycle", q: () => "החיוב על החשבונות חודשי או דו-חודשי?", opts: () => ["חודשי", "דו-חודשי"], when: anyLandlordBill },
  { k: "billsAnchor", q: () => "באילו חודשים גובים?", opts: () => ["אי-זוגיים", "זוגיים"], when: (a) => a.billsCycle === 2 },

  // בחירה מרובה — פיקדון בחודשים, ערבות בנקאית, שיק לנזקים וכו׳.
  // נענית ברכיב ולא בטקסט, כי ניחוש סכומים ממשפט חופשי שובר חוזים.
  /* באותו מסך גם תשלום שכר הדירה: בלי ברירת מחדל — מה שלא נבחר במפורש
     נשאר על ברירת המחדל מההגדרות. נשמר על התקופה (payMethodRent / rentChecks). */
  { k: "securities", q: () => "אילו ביטחונות סוכמו, ואיך ישולם שכר הדירה?", pick: "securities", skip: true },
  /* ערבים: האם, כמה ומי — נענה ברכיב (שם, ת"ז, טלפון, כתובת, צילום ת"ז).
     נשמר על התקופה עם change_tenant ונכנס לסעיף הערבות ולנספח ב'. */
  { k: "guarantors", q: () => "האם יהיו ערבים לחוזה?", pick: "guarantors", skip: true },
  /* נשמר ב-intake.keys — אותו שדה שהטופס הידני ממלא, והחוזה מציין
     ("קיבל X מפתחות שאותם ישיב בתום התקופה"). */
  { k: "keys", q: () => "כמה מפתחות נמסרים לדייר?", num: true, skip: true, opts: () => ["1", "2"] },
  /* קביעה לנכס עצמו: עוקפת את ההגדרה הכללית בחוזים של הנכס הזה. */
  { k: "clauseShabbat", q: () => "להוסיף לחוזה של הנכס סעיף שמירת שבת ומועדי ישראל?", yesNo: true, opts: () => ["כן", "לא"] },
  { k: "contract", q: () => "ליצור חוזה לחתימה?", opts: () => ["כן", "לא"] },
];

/* ============ אשף נכס חדש ============
   האשף שואל אך ורק על הנכס עצמו — מה שנכון לו בלי קשר למי גר בו.

   כל השאר (שכר דירה, יום התשלום, ועד בית, מועדי החשבונות) הם שדות של
   תקופת השכירות ולא של הנכס — ראו TEN_FIELDS ב-shared/domain.js. הם
   נקבעים מול דייר, משתנים בין דייר לדייר, ולכן נשאלים באשף כניסת הדייר.
   שאלה עליהם כאן גם מיותרת וגם מטעה: התשובה נכתבת לתקופה הפעילה, ואם
   אין דייר — היא פשוט נעלמת. */
const PROP_STEPS = [
  { k: "name", q: () => "מה הכתובת של הנכס, או איך תרצה לקרוא לו?" },
  {
    k: "propertyType",
    q: () => "איזה סוג נכס זה?",
    opts: () => PROPERTY_TYPES.map((t) => t.label),
  },
  {
    k: "rooms",
    q: (a) => (propertyType(a).residential ? "כמה חדרים?" : "מה הגודל במ״ר?"),
    skip: true,
  },
  {
    k: "authority",
    q: () => "מול איזו רשות מקומית משולמת הארנונה?",
    skip: true,
    when: (a) => a.propertyType !== "unit",
  },
  /* ציוד ומטלטלין — אחרי כל הפרטים. נענה ברכיב (רשימה מוכנה + הוספה
     ידנית), נשמר ב-inventory של הנכס ונכנס לחוזה של כל דייר כנספח א'. */
  {
    k: "inventory",
    q: () => "איזה ציוד ומטלטלין יש בנכס? הרשימה תיכנס לחוזה (נספח א')",
    pick: "inventory",
    skip: true,
  },
];

function propActions(a) {
  return [{
    type: "add_apartment",
    name: a.name,
    propertyType: a.propertyType || "unit",
    rooms: a.rooms == null ? "" : String(a.rooms),
    authority: a.authority || "",
    // רשימת הציוד של הנכס — ממולאת מראש לכל דייר ונכנסת לחוזה (נספח א').
    inventory: a.inventory || "",
    // שכר דירה, יום תשלום וועד בית נקבעים בכניסת הדייר.
    rent: 0,
  }];
}

function propSummary(a) {
  const t = propertyType(a);
  return [
    `${a.name} · ${t.label}`,
    a.rooms ? (t.residential ? `${a.rooms} חדרים` : `${a.rooms} מ״ר`) : null,
    a.authority ? `ארנונה מול ${a.authority}` : null,
    a.inventory ? `ציוד ומטלטלין — ${inventorySummary(a.inventory)} (נספח א' בחוזה)` : null,
    t.residential ? null : "נכס שאינו למגורים — כדאי שעורך דין יעבור על החוזה",
    "שכר הדירה, יום התשלום וועד הבית ייקבעו כשתכניס דייר",
  ].filter(Boolean);
}

/* ============ אשפים קצרים ============
   כל נושא שהמשתמש בוחר מהתפריט הוא אשף משלו, עם השאלות שלו בלבד. זה
   מה שגורם לשאלות "להשתנות לפי הנושא": אין רשימת שאלות אחת שמנסה לכסות
   הכול, אלא רשימה קצרה וממוקדת לכל פעולה.

   השאלות מסודרות לפי מה שהמשתמש כבר יודע: קודם על איזה נכס מדובר, ורק
   אחר כך הפרטים — כי בלי הנכס אי אפשר להציע ברירות מחדל נכונות. */

const occupied = (d) => (d.apartments || []).filter((a) => a.tenant);

/* ---- דיווח תשלום שכר דירה ---- */
const PAY_STEPS = [
  {
    k: "apt",
    q: () => "על איזו יחידה?",
    /* מציעים קודם את מי שטרם שילם החודש — זה הרוב המכריע של המקרים,
       ומי שכבר שילם עדיין ניתן לבחירה בהקלדה. */
    opts: (d) => {
      const open = occupied(d).filter((a) => !d.payments[rowKey(a.id, CUR_KEY)]);
      return (open.length ? open : occupied(d)).map((a) => a.name);
    },
  },
  {
    k: "month",
    q: () => "עבור איזה חודש?",
    month: true,
    opts: () => ["החודש", "החודש שעבר"],
  },
  {
    k: "amount",
    q: (a, d) => {
      const apt = d.apartments.find((x) => x.id === a.apt);
      return `כמה התקבל?${apt?.rent ? ` (שכר הדירה הוא ${nis(apt.rent)})` : ""}`;
    },
    num: true,
    opts: (d, a) => {
      const apt = d.apartments.find((x) => x.id === a.apt);
      return apt?.rent ? [String(apt.rent)] : [];
    },
  },
];

const payActions = (a) => [{
  type: "set_rent_paid",
  apartment: a.apt,
  month: a.month || CUR_KEY,
  paid: true,
  amount: Number(a.amount) || 0,
}];

const paySummary = (a, d) => {
  const apt = d.apartments.find((x) => x.id === a.apt);
  return [
    `${apt?.name || ""}${apt?.tenant ? ` · ${apt.tenant}` : ""}`,
    `${nis(a.amount)} עבור ${monthLabel(a.month || CUR_KEY)}`,
  ];
};

/* ---- קריאת מונה ---- */
const METER_STEPS = [
  { k: "apt", q: () => "על איזו יחידה?", opts: (d) => occupied(d).map((a) => a.name) },
  { k: "meter", q: () => "איזה מונה?", opts: () => ["חשמל", "מים", "גז"] },
  {
    k: "reading",
    q: (a, d) => {
      const prev = lastReading(d, a.apt, a.meter);
      return `מה הקריאה?${prev ? ` (הקודמת: ${prev.value} מ-${heDate(prev.date)})` : ""}`;
    },
    num: true,
    // אפשר גם לצלם את המונה — הזיהוי ממלא את המספר והתמונה נשמרת כתיעוד.
    meterPhoto: true,
  },
];

const meterActions = (a) => [{
  type: "set_meter",
  apartment: a.apt,
  meter: a.meter,
  reading: Number(a.reading),
  date: TODAY,
  // הצילום שממנו זוהתה (או תועדה) הקריאה — מוצג לצד הקריאה, כמו ביחידה.
  ...(a.readingPhoto ? { photoId: a.readingPhoto } : {}),
}];

const meterSummary = (a, d) => {
  const apt = d.apartments.find((x) => x.id === a.apt);
  const prev = lastReading(d, a.apt, a.meter);
  const diff = prev ? Number(a.reading) - prev.value : null;
  return [
    `${apt?.name || ""} · מונה ${METER_LABEL[a.meter]}`,
    `קריאה ${a.reading}${diff != null ? ` · צריכה ${fmtQty(diff)} ${UNIT[a.meter]}` : ""}${a.readingPhoto ? " · הצילום נשמר" : ""}`,
  ];
};

/* ---- החלפת דייר ---- */
/* החלפה = קליטה מלאה של הדייר הנכנס: בדיוק אותם שלבים כמו "כניסת דייר" —
   ת"ז ובן/בת זוג, מועדי תשלום, למי משולם כל סעיף וכמה, קריאות הכניסה
   של המונים, ביטחונות ואופן תשלום שכר הדירה, ערבים, מפתחות, סעיף שבת
   וחוזה. עד 25.8.2026 האשף שאל רק שם/טלפון/שכ"ד/תאריכים — והדייר החדש
   נקלט בלי קריאות מונה, בלי ערבים ובלי חוזה. שונה רק השלב הראשון:
   בוחרים יחידה מאוישת, והתקופה הקודמת נסגרת מעצמה עם הכניסה. */
const SWAP_STEPS = [
  {
    k: "apt",
    q: () => "באיזו יחידה מתחלף הדייר?",
    opts: (d) => occupied(d).map((a) => `${a.name} — ${a.tenant}`),
  },
  ...WIZ_STEPS.slice(1),
];

const swapSummary = (a, d) => {
  const apt = d.apartments.find((x) => x.id === a.apt);
  return [
    apt?.tenant && apt.tenant !== a.tenant
      ? `במקום ${apt.tenant} — התקופה הקודמת תיסגר והחדשה תיפתח`
      : "התקופה הקודמת תיסגר והחדשה תיפתח",
    ...tenantSummary(a, d),
  ].filter(Boolean);
};

/* ---- סיכום כניסת דייר ---- */
const tenantSummary = (a, d) => {
  const apt = d.apartments.find((x) => x.id === a.apt);
  return [
    `${a.tenant} · ${apt?.name || ""}`,
    `${nis(a.rent)} לחודש · ${heDate(a.leaseStart)} — ${heDate(a.leaseEnd)}`,
    a.firstMonthAmount != null ? `חודש ראשון (${monthLabel(a.leaseStart.slice(0, 7))}): ${nis(a.firstMonthAmount)} במקום חודש מלא` : null,
    a.phone ? `טלפון ${a.phone}` : null,
    a.tenantId ? `ת"ז ${a.tenantId}` : null,
    a.hasPartner === true && a.partnerName
      ? `בן/בת זוג: ${a.partnerName}${a.partnerId ? ` · ת"ז ${a.partnerId}` : ""}${a.partnerPhone ? ` · טלפון ${a.partnerPhone}` : ""}`
      : null,
    `שכ״ד עד ה-${a.rentDay} · חשבונות עד ה-${a.billsDay}${a.billsCycle === 2 ? " (דו-חודשי)" : ""}`,
    [a.elecRead != null && `חשמל ${a.elecRead}`,
     a.waterRead != null && `מים ${a.waterRead}`,
     a.gasRead != null && `גז ${a.gasRead}`].filter(Boolean).join(" · ") || null,
    securitiesLabel(a.securities, a.rent, d.settings),
    a.rentPay ? `שכר הדירה ב${a.rentPay.method === "check" ? `-${a.rentPay.checks} שיקים חודשיים` : methodLabel(a.rentPay.method)}` : null,
    Array.isArray(a.guarantors) ? guarantorsText(a.guarantors) : null,
    a.keys != null ? `נמסרים ${a.keys} מפתחות` : null,
    a.clauseShabbat === true ? "החוזה יכלול סעיף שמירת שבת"
      : a.clauseShabbat === false ? "בלי סעיף שמירת שבת" : null,
    a.contract ? "כולל יצירת חוזה לחתימה" : "בלי חוזה",
  ].filter(Boolean);
};

function wizParse(step, raw, a, d) {
  const t = raw.trim();
  if (step.k === "apt") { const hit = findApt(d.apartments, t); return hit ? hit.id : null; }
  if (step.k === "propertyType") {
    const n = norm(t);
    const hit = PROPERTY_TYPES.find((x) => norm(x.label) === n)
      || PROPERTY_TYPES.find((x) => n.includes(norm(x.label)));
    return hit ? hit.k : null;
  }
  if (step.k === "meter") {
    // meterOf נופל ל-"elec" על כל קלט לא מוכר, ולכן אסור להסתמך עליו כאן:
    // תשובה מבולבלת הייתה נרשמת בשקט כקריאת חשמל.
    const hit = BILL_ALIASES[norm(t)] || BILL_ALIASES[t];
    return METERS.includes(hit) ? hit : null;
  }
  if (step.month) {
    if (/שעבר|קודם/.test(t)) return prevMonth(CUR_KEY);
    if (/החודש|עכשיו|נוכחי/.test(t)) return CUR_KEY;
    const m = /^(\d{4})-(\d{1,2})$/.exec(t);
    return m ? `${m[1]}-${String(m[2]).padStart(2, "0")}` : null;
  }
  if (step.k === "billsCycle") return /דו/.test(t) ? 2 : /חודש/.test(t) ? 1 : null;
  if (step.k === "billsAnchor") return /זוגי/.test(t) ? (/אי/.test(t) ? 1 : 2) : parseNum(t);
  if (step.k === "contract" || step.yesNo) {
    return /^(כן|yes|בטח|אפשר|נכון|יש)/.test(t) ? true
      : /^(לא|אין|no)/.test(t) ? false
        : null;
  }
  /* שלוש האפשרויות של סעיף צריכה. "ללא" נבדק ראשון: "ללא חיוב" מכיל
     "חיוב", והבדיקה ההפוכה הייתה קוראת אותו כגבייה דרך המשכיר. */
  if (step.utility) {
    if (/ללא|אין חיוב|לא מחוי|כלול|לא רלוונ|לא קיים/.test(t)) return "none";
    if (/ישיר|חברת החשמל|תאגיד|ספק|לרשות|עיריי|מועצה/.test(t)) return "direct";
    if (/^(לי|אלי)|משולם לי|משולמת לי|אליי|בעל הנכס|משכיר|מונה|לפי מונה|סכום חודשי/.test(t)) return "landlord";
    return null;
  }
  if (step.date) {
    if (/היום/.test(t)) return TODAY;
    if (/מחר/.test(t)) return tomorrowOf(TODAY);
    if (/שנה/.test(t) && a.leaseStart) return addYears(a.leaseStart, 1);
    return parseDate(t);
  }
  if (step.k === "rentPay") {
    const n = parseNum(t);
    if (/קבע|הו"ק|הו״ק|הוק/.test(t)) return { method: "standing", checks: 0 };
    if (/העברה|בנק/.test(t)) return { method: "transfer", checks: 0 };
    if (/ביט/.test(t)) return { method: "bit", checks: 0 };
    if (/פייבוקס/.test(t)) return { method: "paybox", checks: 0 };
    if (/מזומן/.test(t)) return { method: "cash", checks: 0 };
    if (/אשראי|כרטיס/.test(t)) return { method: "card", checks: 0 };
    if (/שיק|שק|צ'ק|צ׳ק|צק|check/i.test(t) || n != null) {
      return { method: "check", checks: n != null && n > 0 ? Math.round(n) : leaseMonthsOf(a) };
    }
    return null;
  }
  if (step.num) { const n = parseNum(t); return n == null ? null : n; }
  return t || null;
}

function wizActions(a, d) {
  const aptId = a.apt;
  const acts = [{
    type: "change_tenant", apartment: aptId, tenant: a.tenant, phone: a.phone || "",
    rent: a.rent, leaseStart: a.leaseStart, leaseEnd: a.leaseEnd,
    // הערבים נוצרים יחד עם התקופה (כמו בטופס הידני) — כך הם לא נמחקים ב-syncApartments.
    ...(Array.isArray(a.guarantors) ? { guarantors: a.guarantors.filter((g) => String(g.name || "").trim()) } : {}),
    // חודש ראשון חלקי — סכום שנענה (מוצע או הוקלד). "דלג" משאיר undefined, חודש מלא כרגיל.
    ...(a.firstMonthAmount != null ? { firstMonthAmount: a.firstMonthAmount } : {}),
  }];
  /* __useNewTenancy: הפרטים שייכים לתקופה שנפתחה הרגע — בלעדיו, ביחידה
     שעדיין מאוכלסת הם היו נכתבים לתקופה של הדייר היוצא. */
  const upd = { type: "update_apartment", apartment: aptId, __useNewTenancy: true };
  if (a.rentDay) upd.rentDay = a.rentDay;
  if (a.billsDay) upd.billsDay = a.billsDay;
  if (a.billsCycle) upd.billsCycle = a.billsCycle;
  if (a.billsAnchor) upd.billsAnchor = a.billsAnchor;
  /* ועד בית: "לא" הוא תשובה ולא היעדר תשובה — הוא מכבה את הסעיף במפורש
     ולא משאיר סכום ישן מדייר קודם. "ישירות לוועד" משאיר את הסכום לתיעוד
     ולחוזה, אבל לא מדליק גבייה דרך המשכיר — בדיוק כמו ארנונה ישירה. */
  if (a.hasVaad === true) { upd.vaad = a.vaad || 0; upd.vaadActive = a.vaadMode !== "direct"; }
  else if (a.hasVaad === false) { upd.vaad = 0; upd.vaadActive = false; }
  if (a.vaadFirstMonthAmount != null) upd.vaadFirstMonthAmount = a.vaadFirstMonthAmount;

  /* מי משלם למי — landlord | direct | none, כפי שנבחר באשף. */
  const utilities = {};
  for (const [key, answer] of [["elec", a.elecMode], ["water", a.waterMode],
    ["gas", a.gasMode], ["arnona", a.arnonaMode], ["vaad", a.vaadMode]]) {
    if (UTILITY_MODE_KEYS.includes(answer)) utilities[key] = answer;
  }
  if (Object.keys(utilities).length) upd.utilities = utilities;

  // ארנונה נגבית דרך המשכיר רק כשנבחר "משולמת לי", ואז יש לה סכום.
  if (a.arnonaMode === "landlord") { upd.arnona = a.arnona || 0; upd.arnonaActive = true; }
  else if (a.arnonaMode) { upd.arnonaActive = false; }
  if (a.arnonaFirstMonthAmount != null) upd.arnonaFirstMonthAmount = a.arnonaFirstMonthAmount;

  // מערך ריק הוא בחירה מפורשת של "בלי ביטחונות" ולכן חייב לעבור.
  if (Array.isArray(a.securities)) upd.securities = a.securities;
  // איך משלמים שכר דירה — על התקופה החדשה; מספר השיקים רק כשמשלמים בשיקים.
  // בלי בחירה מפורשת (method ריק) לא כותבים כלום — ברירת המחדל מההגדרות.
  if (a.rentPay?.method) { upd.payMethodRent = a.rentPay.method; upd.rentChecks = a.rentPay.method === "check" ? a.rentPay.checks : 0; }
  /* ת"ז, בן/בת זוג ומספר המפתחות — נכתבים ל-intake לפני יצירת החוזה,
     כדי ששורת "לבין ... ת.ז. ..." וסעיף "קיבל X מפתחות" ייכנסו לנוסח
     כבר בחוזה הראשון. */
  const intake = {};
  if (a.tenantId) intake.tenantId = String(a.tenantId);
  if (a.hasPartner === true) {
    if (a.partnerName) intake.partnerName = String(a.partnerName);
    if (a.partnerId) intake.partnerId = String(a.partnerId);
    if (a.partnerPhone) intake.partnerPhone = String(a.partnerPhone);
  }
  if (a.keys != null) intake.keys = Number(a.keys) || 0;
  if (Object.keys(intake).length) upd.intake = intake;
  // סעיף שבת לנכס — "לא" הוא תשובה מפורשת, לא היעדר תשובה.
  if (a.clauseShabbat === true || a.clauseShabbat === false) upd.clauseShabbat = a.clauseShabbat;
  // שלושה מפתחות קבועים (type, apartment, __useNewTenancy) — מעבר להם יש תוכן.
  if (Object.keys(upd).length > 3) acts.push(upd);

  /* קריאות הכניסה נשמרות תמיד, גם כשהסעיף משולם ישירות לספק: הן הראיה
     למצב המונה במסירה, וזה בדיוק מה שנחוץ במחלוקת על חוב שנצבר לפני. */
  const moveMonth = a.leaseStart ? a.leaseStart.slice(0, 7) : CUR_KEY;
  const entry = (meter, value, photoId) => {
    if (value == null) return;
    acts.push({ type: "set_meter", apartment: aptId, month: moveMonth,
      date: a.leaseStart || TODAY, note: "כניסת דייר", meter, reading: value,
      // צילום המונה מהאשף — הראיה למצב המונה במסירה.
      ...(photoId ? { photoId } : {}) });
  };
  entry("elec", a.elecRead, a.elecReadPhoto);
  entry("water", a.waterRead, a.waterReadPhoto);
  entry("gas", a.gasRead, a.gasReadPhoto);

  const rates = {};
  if (a.elecRate != null) rates.elec = a.elecRate;
  if (a.waterSewageRate != null) rates.waterSewage = a.waterSewageRate;
  if (a.gasRate != null) rates.gas = a.gasRate;
  if (Object.keys(rates).length) acts.push({ type: "update_apartment", apartment: aptId, __useNewTenancy: true, rates });
  /* חוזה מקורי לתקופה החדשה — לא נספח הארכה: extend_contract יוצר תמיד
     "נספח הארכה", וכך הדייר החדש קיבל נספח במקום חוזה. */
  if (a.contract && a.leaseStart && a.leaseEnd) acts.push({ type: "new_contract", apartment: aptId, __useNewTenancy: true, from: a.leaseStart, to: a.leaseEnd, rent: a.rent });
  return acts;
}

/* נוסח החוזה המלא, לפני ששואלים "ליצור חוזה לחתימה?" — מדמה את
   הפעולות שהאשף כבר אסף (wizActions, בלי new_contract עצמה — a.contract
   עוד לא נענתה בשלב הזה) על עותק זמני של המסמך, דרך אותו צינור עיבוד
   אמיתי (applyActionFocused/syncApartments) שמריץ אותן בפועל בסוף
   האשף. כך הנוסח לעולם לא יכול לסטות מהחוזה שבאמת ייווצר — אין כאן
   בנייה ידנית שנייה ומקבילה של "מה יהיה בנכס". applyActionFocused לא
   נוגע ב-d המקורי (מחזירה מסמך חדש), ולכן אין שום שינוי בפועל. */
function wizContractPreviewText(a, d) {
  if (!a?.apt || !a.leaseStart || !a.leaseEnd || !d) return null;
  try {
    let sim = d;
    for (const act of wizActions(a, d)) {
      const r = applyActionFocused(sim, act, null);
      if (r.fail) return null;
      sim = syncApartments(r.data);
    }
    const previewApt = sim.apartments.find((x) => x.id === a.apt);
    if (!previewApt) return null;
    const contractPreview = {
      id: "preview", type: "original", from: a.leaseStart, to: a.leaseEnd,
      rent: Number(a.rent) || 0,
      deposit: securityTotal(previewApt, sim.settings),
      paymentDay: rentDayOf(previewApt, sim.settings),
      status: "draft", notes: "",
    };
    return contractText(previewApt, contractPreview, sim.settings);
  } catch {
    return null;
  }
}

/* יומן הרישומים — כל מה שנרשם במערכת, לצפייה ולשימוש חוזר */
function JournalView({ data, onReuse, pastCommands, onClear = null }) {
  const [q, setQ] = useState("");
  // מחיקה בשני שלבים: היומן הוא תיעוד, ולחיצה בודדת לא מוחקת תיעוד.
  const [confirmClear, setConfirmClear] = useState(false);
  const journal = data.journal || [];
  const aptName = (id) => data.apartments.find((a) => a.id === id)?.name || "";

  const filtered = journal.filter((e) =>
    !q || e.text.includes(q) || aptName(e.aptId).includes(q));

  const groups = filtered.reduce((acc, e) => {
    const d = (e.at || "").slice(0, 10);
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});

  /* רישומים עוקבים זהים מקופלים לשורה אחת עם מונה: לחיצות סימון/ביטול
     חוזרות על אותו תא מילאו את היומן ב"סומן כשולם" שש פעמים ברצף,
     והיומן נראה כאילו הוא מציג דברים שלא קרו. */
  const collapse = (items) => {
    const out = [];
    for (const e of items) {
      const last = out[out.length - 1];
      if (last && last.text === e.text) last.count++;
      else out.push({ ...e, count: 1 });
    }
    return out;
  };

  const dayLabel = (d) => (d === TODAY ? "היום" : d === new Date(Date.now() - 86400000).toISOString().slice(0, 10) ? "אתמול" : heDate(d));

  return (
    <div className="px-4 py-3 overflow-y-auto" style={{ maxHeight: 300 }}>
      {pastCommands.length > 0 && (
        <>
          <div className="text-[11px] mb-1.5" style={{ color: C.muted }}>פקודות שכבר השתמשת בהן — לחיצה מריצה שוב</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {pastCommands.map((t) => (
              <button key={t} onClick={() => onReuse(t)} className="text-[11px] rounded-full px-2.5 py-1 truncate"
                style={{ border: `1px solid ${C.line}`, background: "#fff", maxWidth: 240 }} title={t}>↻ {t}</button>
            ))}
          </div>
        </>
      )}

      {/* מה בכלל מוצג כאן — היומן כולל כל פעולה בחשבון, גם כאלה שנעשו
          בלחיצה במסכים ולא דרך העוזר; בלי ההסבר זה נראה "דברים אחרים". */}
      <div className="text-[11px] mb-1.5" style={{ color: C.muted }}>
        יומן הפעולות של החשבון — כל פעולה שבוצעה, מהעוזר (<Sparkles size={10} className="inline" style={{ color: C.ochre }} />) או בלחיצה במסכים.
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="חיפוש ביומן הרישומים"
        style={{ ...inputStyle, padding: "7px 9px", fontSize: 13, marginBottom: 10 }} />

      {/* מחיקת ההיסטוריה — היומן, הפקודות והשיחה השמורה במכשיר. */}
      {onClear && (journal.length > 0 || pastCommands.length > 0) && (
        <div className="flex items-center gap-2 mb-3">
          {confirmClear ? (
            <>
              <span className="text-[11px]" style={{ color: C.late }}>למחוק את כל ההיסטוריה לצמיתות?</span>
              <button onClick={() => { setConfirmClear(false); onClear(); }}
                className="text-[11px] rounded-lg px-2.5 py-1" style={{ background: C.late, color: "#fff" }}>
                מחיקה
              </button>
              <button onClick={() => setConfirmClear(false)}
                className="text-[11px] rounded-lg px-2.5 py-1" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                ביטול
              </button>
            </>
          ) : (
            <button onClick={() => setConfirmClear(true)}
              className="text-[11px] rounded-lg px-2.5 py-1 flex items-center gap-1"
              style={{ border: `1px solid ${C.line}`, color: C.late }}>
              <Trash2 size={11} /> מחיקת ההיסטוריה
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-sm py-6 text-center" style={{ color: C.muted }}>
          {journal.length ? "לא נמצאו רישומים מתאימים" : "עדיין אין רישומים. כל פעולה שתבצעו תישמר כאן."}
        </div>
      )}

      {Object.entries(groups).map(([day, items]) => (
        <div key={day} className="mb-3">
          <div className="text-[11px] mb-1" style={{ color: C.ochre, fontWeight: 600 }}>{dayLabel(day)}</div>
          {collapse(items).map((e) => (
            <div key={e.id} className="flex items-start gap-2 py-1 text-[12px] border-b" style={{ borderColor: "#EEF1EE" }}>
              <span style={{ color: C.muted, minWidth: 38 }}>{(e.at || "").slice(11, 16)}</span>
              <span className="flex-1">
                {e.text}
                {e.count > 1 && <span className="text-[10px] rounded-full px-1.5 mr-1" style={{ background: "#F1F4F1", color: C.muted }}>×{e.count}</span>}
              </span>
              {e.source === "ai" && <Sparkles size={11} style={{ color: C.ochre, marginTop: 3 }} />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* בחירת מספר טלפון: מאנשי הקשר של המכשיר אם ה-API זמין, אחרת הקלדה.
   Contact Picker נתמך בכרום אנדרואיד ודורש חיבור מאובטח; בכל מקרה אחר
   נשארת ההקלדה, ובנוסף מוצעים מספרים שכבר קיימים במערכת. */
const contactsAvailable = () => typeof navigator !== "undefined" && "contacts" in navigator && "select" in (navigator.contacts || {});

/* שדה טלפון עם כפתור בחירה מאנשי הקשר, לשימוש בטפסים */
function PhoneField({ value, onChange, placeholder = "050-0000000", disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const pick = async () => {
    setNote(""); setBusy(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      const tel = picked?.[0]?.tel?.[0];
      if (tel) onChange(String(tel).replace(/[^\d+]/g, ""));
      else setNote("לא נבחר מספר");
    } catch { setNote("בחירה מאנשי הקשר לא זמינה במכשיר הזה"); }
    setBusy(false);
  };
  return (
    <div>
      <div className="flex gap-1.5">
        {/* dir="ltr" חובה: בתוך טופס RTL מספר טלפון עם מקף או + מוצג
            בסדר הפוך והסמן קופץ, וקשה בכלל לראות מה הוקלד.

            minWidth ולא רוחב מהרשת: השדה יושב לפעמים בתוך grid של שתי
            עמודות, ולצידו כפתור אנשי הקשר. בלי רצפה מפורשת נשארו לו כ-90
            פיקסלים ומספר טלפון שלם פשוט לא נכנס למסגרת. min(150px, 100% -
            46px) ולא 150 קשיח: ה-46px שמורים לכפתור עצמו (padding+אייקון+
            מסגרת+המרווח ביניהם) — בלי זה הרצפה "100%" כוללת גם את השטח
            שהכפתור צריך, והכפתור נדחק מחוץ למסגרת (כמו בעמודה אחת בתוך
            בועת הצ'אט הצרה של העוזר, GuarantorsPick). */}
        <input type="tel" inputMode="tel" dir="ltr" value={value || ""} placeholder={placeholder}
          autoComplete="tel" disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, textAlign: "left", minWidth: "min(150px, calc(100% - 46px))", letterSpacing: 0.3 }} />
        <button onClick={pick} disabled={busy || disabled} title="בחירה מאנשי הקשר"
          className="rounded-lg px-3 flex items-center shrink-0" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <User size={15} />}
        </button>
      </div>
      {note && <div className="text-[11px] mt-1" style={{ color: C.muted }}>{note}</div>}
    </div>
  );
}

function PhonePick({ onPick }) {
  const [v, setV] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const fromContacts = async () => {
    setErr(""); setBusy(true);
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      const c = picked && picked[0];
      const tel = c?.tel?.[0];
      if (!tel) { setErr("לא נבחר מספר"); }
      else {
        const clean = String(tel).replace(/[^\d+]/g, "");
        setV(clean);
        onPick(clean);
      }
    } catch (e) {
      setErr("בחירת איש קשר לא זמינה במכשיר הזה — אפשר להקליד את המספר");
    }
    setBusy(false);
  };

  return (
    <div className="mt-2">
      <div className="flex gap-1.5">
        <input type="tel" inputMode="tel" dir="ltr" value={v} placeholder="050-0000000"
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && v.trim() && onPick(v.trim())}
          style={{ ...inputStyle, padding: "7px 9px", fontSize: 13, background: "#fff", textAlign: "right" }} />
        <button onClick={fromContacts} disabled={busy} title="בחירה מאנשי הקשר"
          className="rounded-lg px-3 flex items-center shrink-0" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <User size={15} />}
        </button>
        <button onClick={() => v.trim() && onPick(v.trim())} className="rounded-lg px-3 text-sm shrink-0" style={{ background: C.teal, color: "#fff" }}>אישור</button>
      </div>
      {err && <div className="text-[11px] mt-1" style={{ color: C.muted }}>{err}</div>}
      {!err && contactsAvailable() && <div className="text-[11px] mt-1" style={{ color: C.muted }}>אפשר לבחור מאנשי הקשר בכפתור האדם</div>}
    </div>
  );
}

/* מדגישה את המילה "פנוי" בתוך תווית אפשרות (בחירת יחידה באשף) — כך
   רואים מיד איזו יחידה פנויה בלי לקרוא את כל התווית מילה-מילה. */
function chipLabel(text) {
  const parts = String(text).split(/(פנוי)/);
  return parts.map((p, i) => (p === "פנוי" ? <b key={i} style={{ color: C.late }}>{p}</b> : p));
}

/* תצוגה מקדימה של נוסח סעיף שמירת השבת — נפתחת לפני שעונים כן/לא, עם
   הטקסט המדויק כפי שהוא ייכנס לחוזה (מקור אחד משותף ב-shared/domain.js,
   כדי שהתצוגה לעולם לא תסטה מהחוזה בפועל). קריאה בלבד — בלי עריכה. */
function ClausePreview() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="text-[12px] flex items-center gap-1" style={{ color: C.teal }}>
        <FileText size={12} /> {open ? "הסתרת הנוסח" : "קריאת הנוסח לפני שעונים"}
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border p-2.5 text-[12px] whitespace-pre-line" style={{ borderColor: C.line, background: "#FBFCFB", color: C.ink }}>
          <div className="mb-1" style={{ fontWeight: 600 }}>{SHABBAT_CLAUSE_TITLE}</div>
          {SHABBAT_CLAUSE_TEXT}
        </div>
      )}
    </div>
  );
}

/* תצוגה מקדימה של נוסח החוזה המלא — נפתחת לפני שעונים כן/לא על יצירת
   חוזה, עם הטקסט כפי שהוא ייכנס בפועל (wizContractPreviewText מדמה
   את הפעולות שהאשף אסף דרך אותו צינור עיבוד אמיתי). קריאה בלבד —
   בלי עריכה; תיבה גלילה כי הנוסח ארוך בהרבה מסעיף בודד. */
function ContractPreview({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="text-[12px] flex items-center gap-1" style={{ color: C.teal }}>
        <FileText size={12} /> {open ? "הסתרת החוזה" : "קריאת החוזה לפני שעונים"}
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border p-2.5 text-[12px] whitespace-pre-wrap leading-relaxed overflow-y-auto"
          style={{ borderColor: C.line, background: "#FBFCFB", color: C.ink, maxHeight: 240 }}>
          {text}
        </div>
      )}
    </div>
  );
}

/* בורר תאריך בתוך השיחה — נפתח כשהעוזר שואל על תאריך */
function DatePick({ initial, onPick, quick = null }) {
  const [v, setV] = useState(initial || TODAY);
  /* הקיצורים ניתנים להחלפה: לסיום חוזה הם נמדדים מתאריך הכניסה, לא מהיום. */
  const quickDates = quick || [
    { label: "היום", d: TODAY },
    { label: "תחילת החודש הבא", d: new Date(CUR_Y, CUR_M, 1).toISOString().slice(0, 10) },
    { label: "עוד שנה", d: addYears(TODAY, 1) },
  ];
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {quickDates.map((q) => (
          <button key={q.label} onClick={() => onPick(q.d)} className="text-[11px] rounded-full px-2.5 py-1"
            style={{ border: `1px solid ${C.line}`, background: "#fff" }}>{q.label}</button>
        ))}
      </div>
      <div className="flex gap-1.5">
        <DateField value={v} onChange={setV} onEnter={() => onPick(v)} ariaLabel="תאריך"
          style={{ padding: "7px 9px", fontSize: 13, background: "#fff" }} />
        <button onClick={() => onPick(v)} className="rounded-lg px-3 text-sm shrink-0" style={{ background: C.teal, color: "#fff" }}>אישור</button>
      </div>
    </div>
  );
}

/* מצב ה-viewport הוויזואלי — גובה המקלדת שמכסה את תחתית החלון, והגובה
   שנשאר גלוי בפועל. באנדרואיד ה-meta interactive-widget=resizes-content
   מכווץ את ה-viewport (inset נשאר 0 אבל height קטן); ב-iOS המקלדת שוכבת
   מעל הדף ו-fixed bottom נשאר מאחוריה — inset מרים את שורת ההקלדה.
   height משמש לקיצוץ גוף הצ'אט: גובה קבוע (280px) גבוה מהשטח שנשאר מעל
   המקלדת דחף את סרגל הכותרת של העוזר אל מחוץ למסך. */
function useKeyboardInset() {
  /* מידות ה-viewport מדווחות בפיקסלים לא-מזומים, אבל הפיקסלים של הפאנל
     מוכפלים ב-zoom של הגדלת הטקסט — בלי החלוקה כאן, הגדלה ל-125% הייתה
     דוחפת את סרגל העוזר החוצה מהמסך בדיוק כמו הבאג שתוקן. */
  const zoomOf = () => Number(document.documentElement.style.zoom) || 1;
  const [state, setState] = useState(() => ({
    inset: 0,
    height: typeof window !== "undefined" ? Math.round(window.innerHeight / zoomOf()) : 800,
    width: typeof window !== "undefined" ? Math.round(window.innerWidth / zoomOf()) : 1024,
  }));
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const z = zoomOf();
      setState({
        inset: Math.max(0, Math.round((window.innerHeight - vv.height - vv.offsetTop) / z)),
        height: Math.round(vv.height / z),
        width: Math.round(vv.width / z),
      });
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    // שינוי גודל הטקסט בהגדרות משנה את הזום בלי אירוע resize.
    window.addEventListener("rentals:text-scale", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("rentals:text-scale", update);
    };
  }, []);
  return state;
}

function Assistant({ data, run, setData, undo, setUndo, startFlow = null, accountId = null,
  fullScreen = false, onExit = null }) {
  const CHAT_KEY = chatKeyFor(accountId);
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const [wiz, setWiz] = useState(null);
  const wizRef = useRef(null);
  useEffect(() => { wizRef.current = wiz; }, [wiz]);
  const [open, setOpen] = useState(fullScreen), [text, setText] = useState("");
  /* שורת העוזר גלויה כברירת מחדל. כשהייתה מקופלת ללשונית צרה בצד, מי
     שלא ידע על הלשונית חשב שהעוזר נעלם. מי שמקפל — הבחירה נשמרת.
     במסך מלא אין מצב מקופל בכלל: העוזר הוא כל המסך. */
  const [collapsed, setCollapsed] = useState(false);
  // שורת ההקלדה תמיד מעל המקלדת, וגוף הצ'אט נמתח לפי מה שבאמת נראה.
  const { inset: kbInset, height: viewH, width: viewW } = useKeyboardInset();
  const [busy, setBusy] = useState(false), [log, setLog] = useState([]);
  const [view, setView] = useState("chat");     // chat | history
  const [ready2, setReady2] = useState(false);
  const [history, setHistory] = useState([]);   // התכתבות קודמת — מה שנותן רצף
  const [focus, setFocus] = useState(null);     // הנכס/דייר שטופל לאחרונה
  const [doc, setDoc] = useState(null);         // { apt, contract } — חוזה שנפתח מהשיחה להורדה

  // השיחה נטענת מהמכשיר ונשמרת אחרי כל הודעה, כך שההקשר לא אובד
  useEffect(() => {
    (async () => {
      try {
        // המפתח הישן לא ממופתח לחשבון — נזרק, לא מועבר.
        if (accountId) localStorage.removeItem(CHAT_KEY_LEGACY);
        const saved = JSON.parse(localStorage.getItem(CHAT_KEY) || "null") || {};
        /* רצף הודעות-משתמש זהות מצטמצם לאחת: באג הרקורסיה של תפריט
           הפתיחה הציף שיחות שמורות בעשרות "כמה חוב פתוח יש לי?" — הניקוי
           כאן מציל את מי שכבר נפגע, בלי שיצטרך "שיחה חדשה". */
        if (Array.isArray(saved.log)) {
          setLog(saved.log.slice(-60).filter((m, i, arr) => {
            const prev = arr[i - 1];
            return !(prev && m.role === "user" && prev.role === "user" && prev.text === m.text);
          }));
        }
        if (Array.isArray(saved.history)) setHistory(saved.history.slice(-8));
        if (saved.focus) setFocus(saved.focus);
        if (!fullScreen && typeof saved.collapsed === "boolean") setCollapsed(saved.collapsed);
      } catch { /* אין שיחה שמורה */ }
      setReady2(true);
    })();
  }, []);

  useEffect(() => {
    if (!ready2) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(CHAT_KEY, JSON.stringify({
          log: log.slice(-60), history: history.slice(-8), focus, collapsed, savedAt: new Date().toISOString(),
        }));
      } catch { /* מכסת האחסון מלאה — השיחה פשוט לא תישמר */ }
    }, 400);
    return () => clearTimeout(t);
  }, [log, history, focus, ready2]);

  // פקודות שכבר השתמשת בהן — מהשיחה ומיומן הפעולות
  const pastCommands = useMemo(() => {
    const seen = new Map();
    [...log].reverse().filter((m) => m.role === "user").forEach((m) => {
      const t = m.text.trim();
      if (t.length > 2 && !seen.has(t)) seen.set(t, (seen.get(t) || 0) + 1);
    });
    return [...seen.keys()].slice(0, 12);
  }, [log]);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  /* גם viewH: כשהמקלדת נפתחת וגוף הצ'אט מתקצר, בלי הגלילה הזו השיחה
     נתקעת באמצע והשורה האחרונה — זו שעונים עליה — מחוץ לתצוגה.
     כשיש שדה טקסט עם פוקוס בתוך הקופסה (טופס ארוך כמו GuarantorsPick,
     לא רק שאלה קצרה) — גוללים אליו במקום לתחתית המוחלטת: תחתית ההודעה
     האחרונה יכולה לשבת הרבה מתחת לשדה שבו בדיוק מקלידים, וגלילה
     לתחתית דוחפת אותו מעל לתצוגה, בדיוק ברגע שהמקלדת נפתחת.
     דווקא שדה טקסט, לא כל אלמנט עם פוקוס: כפתור אפשרות שנלחץ (m.options)
     נשאר עם פוקוס גם אחרי הלחיצה, ונשאר בהודעה ישנה בתוך הקופסה — אם
     היינו גוללים לכל אלמנט עם פוקוס, מעבר לשאלה הבאה היה נתקע על
     הכפתור הישן במקום לדחוף למעלה ולחשוף את השאלה החדשה. */
  useEffect(() => {
    if (!boxRef.current) return;
    const active = document.activeElement;
    const typing = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
      && !["button", "submit", "checkbox", "radio", "file"].includes(active.type);
    if (typing && boxRef.current.contains(active)) active.scrollIntoView({ block: "center" });
    else boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [log, busy, viewH]);

  // לחיצה על הצעה שולחת אותה מיד — העוזר ישאל את מה שחסר, שאלה אחת בכל פעם
  const expand = () => { setCollapsed(false); setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); };
  const quick = (t) => { setCollapsed(false); setOpen(true); send(t); };

  const pushAI = (text, options, extra = {}) => setLog((l) => [...l, { role: "ai", text, options: options || [], ...extra }]);
  /* שאלת תאריך פותחת בורר עם קיצורים משלו ("היום" וכו׳), ולכן השלב עצמו
     לא מוסיף צ'יפ "היום" — פעם אחת הוא הופיע פעמיים, אחד מכל מקור.
     לסיום החוזה הקיצורים נגזרים מתאריך הכניסה ולא מהיום. */
  const dateHint = (st, a) => {
    if (!st?.date) return {};
    const cur = a?.[st.k] || null;
    if (st.k === "leaseEnd" && a?.leaseStart) {
      return {
        askDate: cur || addYears(a.leaseStart, 1),
        dateQuick: [
          { label: "שנה מהכניסה", d: addYears(a.leaseStart, 1) },
          { label: "שנתיים מהכניסה", d: addYears(a.leaseStart, 2) },
        ],
      };
    }
    // תאריך כניסה: "עוד שנה" (הקיצור הכללי) לא רלוונטי כאן — "מחר" כן.
    if (st.k === "leaseStart") {
      return { askDate: cur || TODAY, dateQuick: [{ label: "היום", d: TODAY }, { label: "מחר", d: tomorrowOf(TODAY) }, { label: "תחילת החודש הבא", d: new Date(CUR_Y, CUR_M, 1).toISOString().slice(0, 10) }] };
    }
    return { askDate: cur || TODAY };
  };
  // היה כאן hintFor שקרא לעצמו — כל פתיחה של אשף נפלה על גלישת מחסנית.
  const hintFor = (st, a) => ({
    ...dateHint(st, a),
    ...(st?.phone ? { askPhone: true } : {}),
    ...(st?.pick === "securities"
      ? { askSecurities: { rent: Number(a?.rent) || 0, initial: Array.isArray(a?.securities) ? a.securities : null, months: leaseMonthsOf(a), initialRentPay: a?.rentPay || null } }
      : {}),
    ...(st?.pick === "inventory" ? { askInventory: { initial: a?.inventory || "" } } : {}),
    ...(st?.pick === "guarantors"
      ? { askGuarantors: { initial: Array.isArray(a?.guarantors) ? a.guarantors : null, aptId: a?.apt || null } }
      : {}),
    /* meterPhoto: true — המונה מהתשובה (זרימת "קריאת מונה"); מחרוזת —
       שם המונה קבוע לשאלה (קריאות הכניסה באשף כניסת הדייר). */
    ...(st?.meterPhoto && a?.apt && (st.meterPhoto === true ? a?.meter : true)
      ? { askMeterPhoto: { aptId: a.apt, meter: st.meterPhoto === true ? a.meter : st.meterPhoto } }
      : {}),
    // אפשרות לקרוא את נוסח סעיף השבת המדויק לפני שעונים כן/לא.
    ...(st?.k === "clauseShabbat" ? { askClausePreview: true } : {}),
    // אפשרות לקרוא את נוסח החוזה המלא לפני שעונים כן/לא על יצירתו.
    ...(st?.k === "contract" ? { askContractPreview: wizContractPreviewText(a, dataRef.current) } : {}),
  });
  const stepText = (st, a, i, steps) => `${st.q(a, dataRef.current)}  (${i + 1}/${steps.length})`;

  /* שני אשפים על אותו מנוע. flow נשמר במצב האשף, ולכן כל שלב יודע לאיזו
     רשימת שאלות הוא שייך ומה לעשות בסוף. */
  const FLOWS = {
    tenant: { title: "כניסת דייר", steps: WIZ_STEPS, actions: wizActions, summary: tenantSummary },
    property: { title: "נכס חדש", steps: PROP_STEPS, actions: propActions, summary: propSummary },
    // אותן פעולות כמו קליטה מלאה — change_tenant סוגר את התקופה הקודמת.
    swap: { title: "החלפת דייר", steps: SWAP_STEPS, actions: wizActions, summary: swapSummary },
    payment: { title: "דיווח תשלום", steps: PAY_STEPS, actions: payActions, summary: paySummary },
    meter: { title: "קריאת מונה", steps: METER_STEPS, actions: meterActions, summary: meterSummary },
  };

  /* ============ ניווט בין השאלות ============
     תשובה שנכנסה לשאלה הלא נכונה (טלפון במקום שכר דירה) הייתה בעבר
     סופית — הדרך היחידה לתקן הייתה לצאת ולהתחיל מחדש. לכן בכל שאלה יש
     "חזרה", ובסיכום יש "תיקון" שמציג את כל התשובות ומאפשר לקפוץ לכל אחת.

     "נענה" הוא מפתח שקיים ב-a, גם אם הערך null (דילוג). כך חזרה אחורה
     ותיקון לא מאלצים לענות שוב על מה שכבר נענה: המעבר קדימה עוצר רק
     בשאלה הבאה שטרם נענתה ורלוונטית, ואם אין כזו — חוזר לסיכום. */
  const answered = (st, a) => Object.prototype.hasOwnProperty.call(a, st.k);
  const applies = (st, a) => !st.when || st.when(a);
  const nextStep = (steps, from, a) => {
    let i = from + 1;
    while (i < steps.length && (!applies(steps[i], a) || answered(steps[i], a))) i++;
    return i;
  };
  const prevStep = (steps, from, a) => {
    let i = from - 1;
    while (i >= 0 && !applies(steps[i], a)) i--;
    return i;
  };
  /* שינוי תשובה מוקדמת יכול להוציא שאלות מאוחרות מתוקף ("יש ועד בית?"
     → "לא" מייתר את "כמה"). התשובות שלהן נמחקות, אחרת הסיכום והפעולות
     היו נשענים על ערך שכבר לא נשאל עליו. */
  const pruneStale = (steps, a) => {
    for (const s of steps) if (!applies(s, a) && answered(s, a)) delete a[s.k];
    return a;
  };

  /* התשובה הנוכחית כטקסט — לשורת "כרגע:" ולתפריט התיקון. */
  const answerLabel = (st, v, a) => {
    const d = dataRef.current;
    if (v == null) return "דילוג";
    if (st.k === "apt") return d.apartments.find((x) => x.id === v)?.name || String(v);
    if (st.k === "propertyType") return PROPERTY_TYPES.find((x) => x.k === v)?.label || String(v);
    if (st.k === "meter") return METER_LABEL[v] || String(v);
    if (st.month) return monthLabel(v);
    if (st.k === "billsCycle") return v === 2 ? "דו-חודשי" : "חודשי";
    if (st.k === "billsAnchor") return v === 1 ? "אי-זוגיים" : "זוגיים";
    if (st.k === "contract" || st.yesNo) return v ? "כן" : "לא";
    if (st.utility) return { landlord: "משולם לי", direct: "ישירות לספק", none: "ללא חיוב" }[v] || String(v);
    if (st.date) return heDate(v);
    if (st.pick === "securities") return securitiesLabel(v, a.rent, d.settings);
    if (st.pick === "inventory") return inventorySummary(v) || "בלי רשימת ציוד";
    if (st.pick === "guarantors") return guarantorsText(v);
    if (st.k === "rentPay") return rentPayLabel(v);
    if (st.num && /^(rent|amount|vaad|arnona|firstMonthAmount)$/.test(st.k)) return nis(v);
    return String(v);
  };
  /* כותרת קצרה לשאלה: בלי הסוגריים ובלי סימן השאלה. */
  const stepLabel = (st, a) => st.q(a, dataRef.current).replace(/\s*\([^)]*\)/g, " ").replace(/[?？]\s*$/, "").replace(/\s+/g, " ").trim();

  const BACK_RE = /^(חזרה|חזור|אחורה|תחזור|שאלה קודמת|הקודמת?)$/;
  // לא \b: בעברית \b לא מזהה גבול מילה (אותיות עבריות אינן \w).
  const FIX_RE = /^(תיקון|תקן|לתקן|שינוי|שנה|לשנות|עריכה|ערוך|לערוך)(?=\s|$)/;
  const KEEP_RE = /^(בלי שינוי|להשאיר|השאר|אותו דבר)$/;

  /* מה בדיוק מצפים מהמשתמש בשאלה הזו. הצ'יפים לבדם נראו כמו "שתי
     אפשרויות בלבד" ולא היה ברור שאפשר גם להקליד — ולכן כל שאלה אומרת
     את זה במפורש, ובצ'יפים ההצעות לתשובה נפרדות מכפתורי הניווט. */
  const answerHint = (st, suggestions) => {
    if (st.pick) return "";   // יש רכיב בחירה משלו (ביטחונות, ציוד)
    if (st.meterPhoto) return "צלמו את המונה או בחרו תמונה — הקריאה תזוהה לבד. אפשר גם להקליד את המספר";
    if (st.date) return "בחרו תאריך למטה, או הקלידו אותו";
    if (st.phone) return "הקלידו מספר טלפון בשורה למטה";
    if (st.num) return suggestions.length
      ? "הקלידו מספר בשורה למטה, או לחצו על ההצעה"
      : "הקלידו מספר בשורה למטה";
    if (st.yesNo || st.utility || (st.opts && suggestions.length > 1)) return "בחרו אפשרות, או הקלידו תשובה בשורה למטה";
    if (suggestions.length) return "לחצו על הצעה, או הקלידו תשובה בשורה למטה";
    return "הקלידו תשובה בשורה למטה";
  };

  /* הצגת שאלה. השאלה שכבר נענתה מראה את התשובה הנוכחית ומציעה להשאיר. */
  const ask = (flow, i, a, prefix = "") => {
    const steps = FLOWS[flow].steps;
    const st = steps[i];
    const was = answered(st, a);
    const suggestions = [...new Set(st.opts ? st.opts(dataRef.current, a) : [])];
    // כפתורי ניווט — בנפרד מהתשובות, כדי שלא ייראו כמו אחת מהן.
    const nav = [
      ...(was ? ["בלי שינוי"] : []),
      ...(st.skip && !was ? ["דלג"] : []),
      ...(prevStep(steps, i, a) >= 0 ? ["חזרה"] : []),
    ];
    const cur = was ? `\nכרגע: ${answerLabel(st, a[st.k], a)}` : "";
    setWiz({ flow, i, a });
    // wizStep — מאיזו שאלה ההודעה. לחיצה על צ'יפ ישן עונה על השאלה ההיא.
    pushAI(`${prefix}${stepText(st, a, i, steps)}${cur}`, suggestions, {
      ...hintFor(st, a), wizStep: { flow, i }, nav, answerHint: answerHint(st, suggestions),
    });
    /* מקלדת אוטומטית — רק כששאלה נענית בהקלדה בפועל: אין לה צ'יפים
       להצעה (suggestions.length===0), ואין לה רכיב ייעודי (ביטחונות,
       ציוד, ערבים) או בורר תאריך. שאלה עם הצעות/כפתורים — יום תשלום,
       כן/לא, למי משולם, חודשי/דו-חודשי — נענית בלחיצה, ומקלדת שנפתחת
       בכל זאת רק מסתירה את הכפתורים. הקריאה סינכרונית בתוך ask()
       ולא ב-useEffect: ask() תמיד נקראת באותה שרשרת קריאות כמו הלחיצה
       או ה-Enter של המשתמש, ורק כך מובייל מסכים לפתוח מקלדת מחדש
       אחרי שנסגרה (ראו התיקון הקודם, 6e4d113). */
    if (!st.pick && !st.date && !suggestions.length) inputRef.current?.focus();
  };

  /* תשובה שמגיעה מהודעה של שאלה אחרת (צ'יפ, תאריך או טלפון שנלחצו
     בהודעה ישנה). קודם הייתה נדחפת לשאלה הנוכחית — "ישירות לספק הגז"
     שנלחץ בגלילה אחורה נחת על שאלת הביטחונות ונדחה. עכשיו חוזרים לשאלה
     שממנה הגיע הצ'יפ, עונים עליה, וממשיכים מהשאלה הראשונה שטרם נענתה. */
  const answerFrom = (m, text) => {
    const w = wizRef.current;
    const s = m?.wizStep;
    if (w && s && s.flow === w.flow && w.i !== s.i && FLOWS[s.flow]?.steps[s.i]) {
      const jumped = { flow: s.flow, i: s.i, a: { ...w.a } };
      wizRef.current = jumped;
      setWiz(jumped);
    }
    quick(text);
  };

  const showConfirm = (flow, a) => {
    const lines = FLOWS[flow].summary(a, dataRef.current);
    /* דייר עתידי שכבר רשום לנכס: זה נאמר לפני השמירה, עם הצעה מפורשת
       להחליף. "שמור" סתם לא מוחק כלום — מחיקה רק לפי בקשה מפורשת. */
    const up = (flow === "tenant" || flow === "swap") && a.apt ? upcomingTenancies(dataRef.current, a.apt) : [];
    if (up.length) {
      const who = up.map((t) => `${t.tenant} כבר רשום/ה להיכנס ב-${heDate(t.leaseStart)}`).join("; ");
      setWiz({ flow, i: -1, a, confirm: true, replaceIds: up.map((t) => t.id) });
      pushAI(`שימו לב: ${who}.\n\nזה מה שאשמור:\n${lines.join("\n")}\n\nלהחליף את הרישום הקיים, או לשמור את שניהם?`,
        ["שמור והחלף את הרישום הקיים", "שמור את שניהם", "תיקון", "בטל"]);
      return;
    }
    setWiz({ flow, i: -1, a, confirm: true });
    pushAI(`זה מה שאשמור:\n${lines.join("\n")}\n\nלשמור?`, ["שמור", "תיקון", "בטל"]);
  };

  /* תפריט התיקון: כל שאלה שנענתה, עם התשובה שלה. בחירה קופצת אליה.
     התאמה חופשית ("תקן שכר דירה") נעשית לפי מילים, בלי ה׳ הידיעה —
     כדי ש"שכר דירה" ימצא את "מה שכר הדירה החודשי". */
  const words = (t) => norm(t).split(" ").filter(Boolean).map((x) => x.replace(/^ה/, ""));
  const findFix = (menu, raw) => {
    const exact = menu.find((m) => m.text === raw);
    if (exact) return exact;
    const ws = words(raw);
    if (!ws.length) return null;
    return menu.find((m) => { const lw = words(m.text); return ws.every((x) => lw.some((y) => y.includes(x))); }) || null;
  };
  const showFixMenu = (flow, a, wanted = "") => {
    const steps = FLOWS[flow].steps;
    const menu = steps
      .map((st, i) => ({ st, i }))
      .filter(({ st }) => applies(st, a) && answered(st, a))
      .map(({ st, i }) => ({ i, text: `${stepLabel(st, a)}: ${answerLabel(st, a[st.k], a)}` }));
    const hit = wanted ? findFix(menu, wanted) : null;
    if (hit) { ask(flow, hit.i, a); return; }
    setWiz({ flow, i: -1, a, fixMenu: menu });
    pushAI("מה לתקן?", menu.map((m) => m.text).concat("חזרה לסיכום"));
  };

  /* אשף שנפתח מבחוץ (כפתור במסך הפתיחה). ה-n במפתח הוא מונה לחיצות, כך
     שלחיצה שנייה מתחילה אשף חדש ולא נבלעת כי הערך לא השתנה. */
  const startedFlow = useRef(0);
  useEffect(() => {
    if (!startFlow || startFlow.n === startedFlow.current) return;
    startedFlow.current = startFlow.n;
    startWiz(startFlow.flow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startFlow]);

  /* אזהרה מוקדמת: אם לנכס שנבחר כבר רשום דייר עתידי, זה נאמר מיד עם
     בחירת הנכס — ולא רק בסיכום, אחרי שכבר נענו כל השאלות. */
  const upcomingNote = (aptId) => {
    const up = upcomingTenancies(dataRef.current, aptId);
    return up.length
      ? `שימו לב: ${up.map((t) => `${t.tenant} כבר רשום/ה להיכנס ב-${heDate(t.leaseStart)}`).join("; ")}. בסוף האשף אציע להחליף את הרישום הקיים או לשמור את שניהם. `
      : "";
  };

  const startWiz = (flow = "tenant", aptId = null) => {
    const a = {};
    let i = 0;
    if (flow === "tenant" && aptId) { a.apt = aptId; i = 1; }
    setCollapsed(false); setOpen(true);
    ask(flow, i, a, flow === "tenant" && aptId ? upcomingNote(aptId) : "");
  };

  /* ============ פתיחת השיחה ============
     שתי התנהגויות שונות, כי המצב שונה:

     חשבון ריק — אין על מה לדבר לפני שיש נכס, ולכן העוזר נפתח מעצמו
     ומתחיל את אשף הנכס. תפריט נושאים במצב הזה רק היה מבקש מהמשתמש
     לבחור בין פעולות שאף אחת מהן לא אפשרית.

     חשבון עם נכסים — לא נפתח מעצמו (זה היה מפריע בכל טעינה), אבל
     ההודעה הראשונה כשפותחים אותו היא תפריט הנושאים ולא שדה ריק. */

  const MENU = [
    { label: "הכנסת דייר חדש", flow: "tenant", when: (d) => d.apartments.some((a) => !a.tenant) },
    { label: "החלפת דייר", flow: "swap", when: (d) => d.apartments.some((a) => a.tenant) },
    { label: "דיווח תשלום", flow: "payment", when: (d) => d.apartments.some((a) => a.tenant) },
    { label: "קריאת מונה", flow: "meter", when: (d) => d.apartments.some((a) => a.tenant) },
    { label: "הוספת נכס", flow: "property" },
    { label: "כמה חוב פתוח יש לי?", ask: "כמה חוב פתוח יש לי?" },
  ];

  const menuFor = (d) => MENU.filter((m) => !m.when || m.when(d));

  const openMenu = () => {
    const items = menuFor(dataRef.current);
    pushAI("במה נתחיל?", items.map((m) => m.label));
  };

  /* רץ פעם אחת, אחרי שהשיחה השמורה נטענה — אחרת התפריט היה נדחף מעל
     שיחה קיימת בכל רענון. */
  const greeted = useRef(false);
  useEffect(() => {
    if (!ready2 || greeted.current || !data) return;
    greeted.current = true;
    if (log.length) return;                     // יש כבר שיחה שמורה

    /* חשבון ריק: האשף מתחיל רק כשהעוזר הוא כל המסך. קודם הוא נפתח
       מעצמו כחלונית תחתונה מעל מסך "ברוך הבא" וכיסה את חציו — שני
       מסכי פתיחה שונים בו-זמנית, אף אחד מהם לא שלם. */
    if (!data.apartments.length) {
      if (!fullScreen) return;
      pushAI("נתחיל מהנכס הראשון. אשאל כמה שאלות קצרות.");
      startWiz("property");
    } else {
      openMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready2, data]);

  /** בחירה מהתפריט: או שהיא פותחת אשף, או שהיא שאלה חופשית למודל.
      פריט ask פונה ל-askModel ישירות ולא ל-send — send היה מזהה את
      אותו טקסט שוב כפריט תפריט ונכנס לרקורסיה אינסופית. הבועה נדחפת
      שם, ולכן כאן נדחפת רק לפריטי flow. */
  const pickFromMenu = (label) => {
    const hit = menuFor(dataRef.current).find((m) => m.label === label);
    if (!hit) return false;
    if (hit.flow) {
      setLog((l) => [...l, { role: "user", text: label }]);
      startWiz(hit.flow);
    } else {
      askModel(hit.ask);
    }
    return true;
  };

  const stepAnswer = (ans) => {
    const w = wizRef.current;
    const raw0 = ans.trim();

    // אחרי השמירה: לשלוח את החוזה לדייר?
    if (w.sendStage) {
      if (/^(כן|שלח|שליחה|בטח)/.test(raw0) || raw0.startsWith("כן,")) {
        const apt = dataRef.current.apartments.find((x) => x.id === w.a.apt);
        const ten = apt ? currentTenancy(dataRef.current, apt.id) || upcomingTenancies(dataRef.current, apt.id).slice(-1)[0] : null;
        const c = (ten?.contracts || []).slice(-1)[0];
        const phone = w.a.phone || apt?.phone;
        setWiz(null);

        /* הקוד נשלף לפני בניית ההודעה. הקישור מוצג בצ'אט ונלחץ אחר כך,
           ולכן ההמתנה כאן לא פוגעת בפתיחת החלון. */
        (async () => {
          const code = await ensurePortalCode(w.a.apt);
          const msg = `שלום ${w.a.tenant}, מצורף חוזה השכירות ל${apt?.name || ""} לתקופה ${heDate(w.a.leaseStart)}–${heDate(w.a.leaseEnd)} בדמי שכירות ${nis(w.a.rent)}. נא להיכנס לאזור האישי, לעבור על החוזה ולחתום.${inviteLines(code)}`;
          /* לצד וואטסאפ — שיתוף דרך המכשיר, העתקה, והורדה כ-PDF. ביומן
             נשמרים מזהים בלבד; החוזה עצמו נשלף מחדש בלחיצה. */
          pushAI(phone
            ? "פותח וואטסאפ לשליחת החוזה. אפשר גם לשתף בדרך אחרת או להוריד את החוזה."
            : "אין טלפון לדייר — אפשר לשתף את ההזמנה בדרך אחרת, להוריד את החוזה, או לשלוח מלשונית החוזים.",
            [], {
              waLink: phone ? waLink(phone, msg) : null, waLabel: "שליחת החוזה לחתימה",
              shareText: msg, doc: c ? { apt: w.a.apt, contract: c.id } : null,
            });
        })();

        if (c && phone) setData((d) => markContractSent(d, w.a.apt, c.id));
        return;
      }
      setWiz(null);
      pushAI("בסדר, החוזה שמור כטיוטה. אפשר לשלוח אותו מאוחר יותר מלשונית החוזים.");
      return;
    }

    // תפריט התיקון — בחירת השאלה שחוזרים אליה
    if (w.fixMenu) {
      if (/^(בטל|ביטול|חזרה|חזור|לא|חזרה לסיכום)$/.test(raw0)) { showConfirm(w.flow, w.a); return; }
      const hit = findFix(w.fixMenu, raw0);
      if (!hit) { pushAI("לא הבנתי — איזו תשובה לתקן?", w.fixMenu.map((m) => m.text).concat("חזרה לסיכום")); return; }
      ask(w.flow, hit.i, { ...w.a });
      return;
    }

    // שלב האישור לפני השמירה
    if (w.confirm) {
      if (/^(בטל|לא|ביטול)/.test(raw0)) { setWiz(null); pushAI("בוטל — לא נשמר כלום."); return; }
      if (BACK_RE.test(raw0)) {
        const steps = FLOWS[w.flow].steps;
        const i = prevStep(steps, steps.length, w.a);
        if (i >= 0) { ask(w.flow, i, { ...w.a }); return; }
      }
      if (FIX_RE.test(raw0)) { showFixMenu(w.flow, { ...w.a }, raw0.replace(FIX_RE, "").replace(/^\s*את\s+/, "").trim()); return; }
      /* כל טקסט אחר היה נשמר. תשובה שנכתבה בטעות בשלב הזה לא אמורה
         לכתוב לנתונים — רק אישור מפורש. */
      if (!/^(שמור|שמירה|כן|אישור|אשר|מאשר|ok|אוקיי|בסדר)/i.test(raw0)
        && !(w.replaceIds?.length && /^(החלף|להחליף)/.test(raw0))) {
        pushAI("לשמור, לתקן או לבטל?", w.replaceIds?.length
          ? ["שמור והחלף את הרישום הקיים", "שמור את שניהם", "תיקון", "בטל"]
          : ["שמור", "תיקון", "בטל"]);
        return;
      }
      const a = w.a;
      const acts = FLOWS[w.flow].actions(a, dataRef.current);
      /* "שמור והחלף": הרישום העתידי הקיים מבוטל במחיקה רכה — עובר
         לארכיון התקופות וניתן לשחזור — לפני שהדייר החדש נרשם. */
      if (w.replaceIds?.length && /החלף/.test(raw0)) {
        acts.unshift(...w.replaceIds.map((id) => ({
          type: "remove_tenancy", apartment: a.apt, tenancyId: id,
          notes: `הוחלף ברישום של ${a.tenant || ""}`.trim(),
        })));
      }
      const r = run(acts, a.apt || null);
      setUndo(dataRef.current);

      if (w.flow === "property") {
        // הנכס נוצר עכשיו — הפוקוס עובר אליו כדי שההוראה הבאה תתייחס אליו.
        if (r.focus) setFocus(r.focus);
        setWiz(null);
        setLog((l) => [...l, {
          role: "ai",
          text: "הנכס נוסף. אפשר להכניס אליו דייר — פשוט כתוב \"דייר חדש\".",
          steps: r.logs,
          options: ["דייר חדש"],
        }]);
        return;
      }

      setFocus(a.apt);
      if (a.contract) {
        setWiz({ i: -1, a, sendStage: true });
        setLog((l) => [...l, { role: "ai", text: "נשמר. לשלוח את החוזה לדייר לחתימה?", steps: r.logs, options: ["כן, שלח עכשיו", "לא, אחר כך"] }]);
      } else {
        setWiz(null);
        const items = menuFor(dataRef.current);
        setLog((l) => [...l, {
          role: "ai", text: "נשמר. משהו נוסף?", steps: r.logs,
          options: items.map((m) => m.label),
        }]);
      }
      return;
    }

    const steps = FLOWS[w.flow].steps;
    const st = steps[w.i];
    const a = { ...w.a }, raw = raw0;
    if (/^(ביטול|בטל|עצור|יציאה)$/.test(raw)) { setWiz(null); pushAI("יצאנו מהאשף."); return; }
    if (BACK_RE.test(raw)) {
      const i = prevStep(steps, w.i, a);
      if (i < 0) { ask(w.flow, w.i, a, "זו השאלה הראשונה. "); return; }
      ask(w.flow, i, a);
      return;
    }
    if (KEEP_RE.test(raw) && answered(st, a)) { advanceWiz(w, a); return; }
    let skipPrefix = "";
    if (/^(דלג|אין|לא יודע)$/.test(raw)) {
      if (!st.skip) { ask(w.flow, w.i, a, "השדה הזה נחוץ. "); return; }
      /* דילוג הוא תשובה: null נשמר כדי שחזרה אחורה לא תשאל שוב. כל
         הצרכנים בודקים == null / || "" ולכן לא מבחינים בין זה להיעדר. */
      a[st.k] = null;
      // דילוג על קריאת מונה — למשל כי הדייר עוד לא נכנס לנכס באותו רגע.
      // תזכורת קבועה תופיע בדף הבית עד שהקריאה תושלם, עם תמונה או בלעדיה.
      if (st.meterPhoto) skipPrefix = "אין בעיה — תופיע תזכורת בדף הבית עד שתשלימו את הקריאה, אפשר גם עם תמונה. ";
    } else if (st.pick) {
      // שלב שנענה ברכיב. טקסט חופשי כאן היה נשמר כמחרוזת ושובר את הפעולה.
      ask(w.flow, w.i, a, "בחר ברכיב שלמטה ולחץ \"אישור\", או כתוב \"דלג\". ");
      return;
    } else {
      const v = wizParse(st, raw, a, dataRef.current);
      if (v === null) { ask(w.flow, w.i, a, "לא הבנתי — אפשר שוב? "); return; }
      a[st.k] = v;
      // הנכס נבחר הרגע — אם כבר רשום אליו דייר עתידי, זה נאמר מיד.
      if (st.k === "apt" && (w.flow === "tenant" || w.flow === "swap")) {
        advanceWiz(w, pruneStale(steps, a), upcomingNote(v));
        return;
      }
    }
    advanceWiz(w, pruneStale(steps, a), skipPrefix);
  };

  /* המעבר לשאלה הבאה, משותף לתשובות בטקסט ולתשובות שמגיעות מרכיב
     (בורר הביטחונות). ההפרדה כאן היא מה שמאפשר שלב שלא עובר דרך parse.
     המעבר עוצר בשאלה הבאה שטרם נענתה — אחרי חזרה אחורה או תיקון זה
     מחזיר ישר למקום שבו היינו, או לסיכום. */
  const advanceWiz = (w, a, prefix = "") => {
    const steps = FLOWS[w.flow].steps;
    const i = nextStep(steps, w.i, a);
    if (i >= steps.length) { showConfirm(w.flow, a); return; }
    ask(w.flow, i, a, prefix);
  };

  /* תשובה מובנית מהבורר, במקום טקסט. */
  const pickSecurities = (list, rentPay = null) => {
    const w = wizRef.current;
    if (!w || w.i < 0) return;
    const steps = FLOWS[w.flow].steps;
    const st = steps[w.i];
    // תשלום שכר הדירה נבחר באותו מסך — נשמר על התשובות (a.rentPay) ונכנס לפעולות.
    const a = { ...w.a, [st.k]: list, ...(rentPay && rentPay.method ? { rentPay } : {}) };
    const rpText = rentPayText(rentPay);
    setLog((l) => [...l, { role: "user", text: `${securitiesLabel(list, a.rent, dataRef.current.settings)}${rpText ? ` · שכר הדירה ב${rentPay.method === "check" ? "-" : ""}${rpText}` : ""}` }]);
    advanceWiz(w, pruneStale(steps, a));
  };

  /* רשימת הציוד מהרכיב — אותו מסלול כמו הביטחונות: לא עובר דרך parse. */
  const pickInventory = (text) => {
    const w = wizRef.current;
    if (!w || w.i < 0) return;
    const steps = FLOWS[w.flow].steps;
    const st = steps[w.i];
    const clean = inventoryLines(text).join("\n");
    const a = { ...w.a, [st.k]: clean || null };
    setLog((l) => [...l, { role: "user", text: inventorySummary(clean) || "בלי רשימת ציוד" }]);
    advanceWiz(w, pruneStale(steps, a));
  };

  /* הערבים מהרכיב — אותו מסלול: לא עוברים דרך parse. */
  const pickGuarantors = (list) => {
    const w = wizRef.current;
    if (!w || w.i < 0) return;
    const steps = FLOWS[w.flow].steps;
    const st = steps[w.i];
    const a = { ...w.a, [st.k]: Array.isArray(list) ? list : [] };
    setLog((l) => [...l, { role: "user", text: guarantorsText(list) }]);
    advanceWiz(w, pruneStale(steps, a));
  };

  /* הקריאה שזוהתה מצילום המונה — עונה על שאלת הקריאה והתמונה מצורפת
     לקריאה עצמה (set_meter עם photoId), בדיוק כמו בכרטיס המונה.
     מפתח הצילום נגזר מהשאלה: reading → readingPhoto (זרימת קריאת מונה),
     elecRead → elecReadPhoto וכו' (קריאות הכניסה) — כך כל שאלה שומרת
     את הצילום שלה בלי לדרוס אחרות. */
  const pickMeterReading = ({ reading, photoId }) => {
    const w = wizRef.current;
    if (!w || w.i < 0) return;
    const steps = FLOWS[w.flow].steps;
    const st = steps[w.i];
    const a = { ...w.a, [st.k]: reading, ...(photoId ? { [`${st.k}Photo`]: photoId } : {}) };
    setLog((l) => [...l, { role: "user", text: `${reading} — זוהה מהצילום` }]);
    advanceWiz(w, pruneStale(steps, a));
  };

  /* צילום שהועלה אבל המספר יוקלד ידנית (זיהוי כושל או לא ודאי) — נשמר
     על התשובות, כדי שגם קריאה שהוקלדה תקבל את התמונה כתיעוד. */
  const noteMeterPhoto = (photoId) => {
    const w = wizRef.current;
    if (!w || w.i < 0 || !photoId) return;
    const st = FLOWS[w.flow].steps[w.i];
    const next = { ...w, a: { ...w.a, [`${st.k}Photo`]: photoId } };
    wizRef.current = next;
    setWiz(next);
  };

  const focusApt = data.apartments.find((a) => a.id === focus) || null;

  /* שאלת האשף הנוכחית. שורת הקלט של העוזר היא גם המקום שבו נענית השאלה
     "מה הקריאה?", ולכן בשלב מספרי היא מקבלת מקלדת ספרות וספרות גדולות
     יותר — אותה בעיה שהייתה בכרטיס המונה, רק במסך אחר. */
  const activeStep = wiz && wiz.i >= 0 ? FLOWS[wiz.flow]?.steps[wiz.i] : null;
  const numericStep = !!activeStep?.num;

  /* ההקשר ופרומפט המערכת נבנים בשרת מהמסמך עצמו. הוסרו מכאן כדי שלא
     יידרש מפתח API בדפדפן, שהפרומפט לא יהיה ניתן לשינוי מהלקוח, ושמטמון
     הפרומפטים יעבוד — הוא זהה בין כל הפניות. */

  const send = async (override) => {
    const q = (typeof override === "string" ? override : text).trim();
    if (!q || busy) return;
    // אשף פעיל — התשובה נקלטת מקומית, בלי פנייה למודל. המיקוד (אם צריך
    // בכלל) קורה בתוך ask() עצמה — ראו ההערה שם.
    if (wizRef.current) { setText(""); setLog((l) => [...l, { role: "user", text: q }]); stepAnswer(q); return; }
    // בחירה מתפריט הפתיחה — פותחת אשף במקום לפנות למודל
    if (pickFromMenu(q)) { setText(""); return; }
    // התחלת אשף כניסת דייר
    if (/(דייר חדש|הכנס דייר|כניסת דייר|נכניס דייר|להכניס דייר)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      startWiz("tenant", focus && !dataRef.current.apartments.find((x) => x.id === focus)?.tenant ? focus : null);
      return;
    }
    // החלפת דייר
    if (/(החלפת דייר|להחליף דייר|דייר מתחלף|מחליף דייר|דייר עוזב)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      startWiz("swap");
      return;
    }
    // דיווח תשלום
    if (/(דיווח תשלום|לדווח תשלום|קיבלתי שכר|שכר דירה שהתקבל|התקבל תשלום)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      startWiz("payment");
      return;
    }
    // קריאת מונה
    if (/(קריאת מונה|לדווח מונה|להזין מונה|עדכן מונה|עדכון מונה)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      startWiz("meter");
      return;
    }
    // התחלת אשף נכס חדש
    if (/(נכס חדש|הוסף נכס|להוסיף נכס|נוסיף נכס|דירה חדשה|חנות חדשה|מחסן חדש)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      startWiz("property");
      return;
    }
    /* עריכת חשבונות עבר — הכוונה למסך ולא אשף: כמה תקופות וסכומים
       שונים לא מתאימים לזרימת שאלה-תשובה בצ'אט, ולוח מלא נוח יותר
       לראות את כל הפירוט לפני שמירה. */
    if (/(עריכת חשבונות עבר|לתקן חשבונות ישנים|לתקן תשלומים ישנים|לערוך את העבר של|היסטוריית תשלומים של הדייר)/.test(q)) {
      setText(""); setLog((l) => [...l, { role: "user", text: q }]);
      pushAI('כדי לערוך חשבונות עבר של דייר — כמה תקופות שצריך, כל אחת עם הסכום האמיתי שלה — יש לזה מסך משלו: "נכסים" ← הדירה ← "עוד ⋯" ← "כרטיס חוב". שם, מתחת לסיכום החוב, קטע "עריכת חשבונות עבר" עם שכר דירה, ועד בית וארנונה בנפרד, ואפשרות להוסיף כמה תקופות שצריך לפני שמירה.');
      return;
    }
    await askModel(q);
  };

  /* הפנייה למודל עצמה, אחרי שכל הקיצורים המקומיים לא תפסו.
     מופרדת מ-send כדי שתפריט הפתיחה יוכל לפנות אליה ישירות: פריט
     "כמה חוב פתוח יש לי?" שה-label וה-ask שלו זהים נכנס דרך send
     ללולאה — pickFromMenu זיהה אותו שוב ושוב עד קריסת המחסנית,
     והשיחה התמלאה באותה בועה בלי שהמודל נשאל כלל. */
  const askModel = async (q) => {
    if (!q || busy) return;
    setText(""); setBusy(true); setLog((l) => [...l, { role: "user", text: q }]);
    const snapshot = data;
    try {
      // השרת בונה את ההקשר מהמסמך, מוסיף את פרומפט המערכת ומאמת את התשובה
      // מול סכמה — ולכן אין כאן יותר ניסיון שני של "תקן את עצמך".
      const parsed = await ai.assistant([...history.slice(-8), { role: "user", text: q }], focus);
      const actions = Array.isArray(parsed.actions) ? parsed.actions : [];
      /* שליחת הודעה לדייר אינה שינוי במסמך אלא קריאת שרת, ולכן מטופלת
         כאן ולא ב-run. פעולות המסמך רצות קודם — "סמן ששולם ותשלח לו
         אישור" שולח את האישור אחרי שהתשלום כבר נרשם. הודעה שנשלחה
         אינה חלק מה-undo: אי אפשר להחזיר אותה מהדייר. */
      const msgActions = actions.filter((a) => a.type === "send_tenant_message");
      const docActions = actions.filter((a) => a.type !== "send_tenant_message");
      let logs = [], r0Focus = focus, after = null;
      if (docActions.length) {
        const r = run(docActions, focus, "ai");
        logs = r.logs; after = r.data;
        if (r.focus !== undefined) { setFocus(r.focus); r0Focus = r.focus; }
        setUndo(snapshot);
      }
      for (const m of msgActions) {
        const src = after || dataRef.current;
        const apt = findApt(src.apartments, m.apartment) || src.apartments.find((a) => a.id === r0Focus) || null;
        const msgText = String(m.text || "").trim().slice(0, 1000);
        if (!apt) { logs.push({ text: `לא נמצאה דירה בשם "${m.apartment || ""}"`, ok: false }); continue; }
        if (!apt.tenant) { logs.push({ text: `אין דייר בנכס ${apt.name} — ההודעה לא נשלחה`, ok: false }); continue; }
        if (!msgText) { logs.push({ text: "נוסח ההודעה ריק — לא נשלחה", ok: false }); continue; }
        try {
          await dataApi.replyToTenant({ apartmentId: apt.id, text: msgText });
          logs.push({ text: `נשלחה הודעה ל${apt.tenant} (${apt.name})`, ok: true });
        } catch (err) {
          logs.push({ text: `שליחת ההודעה ל${apt.tenant} נכשלה: ${err.message}`, ok: false });
        }
      }
      const failed = logs.filter((x) => !x.ok).length;
      // אפשרויות לבחירה בלחיצה. אם המודל שאל על נכס ולא החזיר אפשרויות — נשלים אותן לבד.
      const src2 = after || dataRef.current;
      let options = Array.isArray(parsed.options) ? parsed.options.filter((x) => typeof x === "string").slice(0, 8) : [];
      // שאלה על תאריך פותחת יומן במקום להקליד
      const asksDate = /\?/.test(parsed.reply || "") && /(מתי|תאריך|מאימתי|החל מ|מאיזה יום)/.test(parsed.reply || "");
      const asksPhone = /\?/.test(parsed.reply || "") && /(טלפון|נייד|מספר של|ליצירת קשר)/.test(parsed.reply || "");
      if (!options.length && /(איזו|איזה|לאיזו|לאיזה|באיזו|באיזה)[^?]*(דיר|יחיד|נכס)/.test(parsed.reply || "")) {
        options = src2.apartments.map((a) => a.name);
      }
      setHistory((h) => [...h.slice(-8),
        { role: "user", text: `הוראה קודמת: "${q}"` },
        { role: "assistant", text: JSON.stringify({ reply: parsed.reply, actions }) }]);
      // הדרכה: מה עוד חסר בנכס שזה עתה טופל
      let missing = [];
      if (r0Focus) {
        // חשוב: הנתונים שאחרי הריצה, לא אלה שלפניה
        const src = after || dataRef.current;
        const aptNow = src.apartments.find((a) => a.id === r0Focus);
        if (aptNow) missing = missingFor(aptNow, src);
      }
      setLog((l) => [...l, {
        role: "ai",
        text: parsed.reply || (actions.length ? "בוצע." : "לא זוהתה פעולה לביצוע."),
        steps: logs,
        warn: failed ? "חלק מהפעולות לא בוצעו — בדקו את שם הנכס." : null,
        options,
        askDate: asksDate ? TODAY : null,
        askPhone: asksPhone,
        missing,
      }]);
    } catch (err) {
      setLog((l) => [...l, { role: "ai", text: `הפעולה נכשלה: ${err.message}` }]);
    }
    setBusy(false);
  };

  const examples = [
    "הכנס דייר חדש", "סמן שכר דירה שהתקבל", "עדכן קריאת מונה",
    "האריך חוזה", "עדכן תעריפים", "כמה חוב פתוח יש לי?",
  ];

  /* "הורדת החוזה" מהשיחה: המסמך נפתח בחלון (עם "הדפסה / שמירה כ-PDF").
     מעל העוזר ולא דרך ה-modal של המסך הראשי, כי העוזר חי גם במסך הפתיחה. */
  const docView = doc ? findContract(data, doc.apt, doc.contract) : null;

  return (
    <>
    {docView && (
      <ContractModal apt={docView.apt} contract={docView.contract} settings={data.settings} setData={setData} onClose={() => setDoc(null)} />
    )}
    {/* מקופל: כפתור צף בפינה. הלשונית האנכית הקודמת נצמדה ל-left:0
        ונבלעה בקצה המסך בטלפון (פינות מעוגלות ומחוות קצה) — נראתה כאילו
        העוזר נעלם. */}
    {collapsed && !fullScreen && (
      <button onClick={expand} dir="rtl" aria-label="פתיחת עוזר הניהול"
        className="fixed z-40 flex items-center gap-1.5 shadow-lg no-print"
        style={{
          left: 14, bottom: "max(18px, env(safe-area-inset-bottom, 0px) + 14px)",
          background: C.teal, color: "#fff",
          borderRadius: 999, padding: "12px 16px", fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 14px rgba(20,38,43,0.35)",
        }}>
        <Sparkles size={16} style={{ color: "#F0C987" }} />
        עוזר
        {wiz && <span style={{ width: 7, height: 7, borderRadius: 99, background: "#F0C987" }} />}
      </button>
    )}

    <div className={fullScreen ? "fixed inset-0 z-40 no-print flex flex-col" : "fixed bottom-0 inset-x-0 z-40 no-print"} dir="rtl"
      style={{
        display: collapsed ? "none" : undefined,
        /* iOS: המקלדת מכסה את תחתית הדף — התחתית של העוזר מוצמדת לקצה
           העליון שלה, כך ששורת ההקלדה תמיד גלויה. */
        ...(kbInset > 0 ? { bottom: kbInset } : {}),
        ...(fullScreen ? { background: C.surface } : {}),
      }}>
      <div className={fullScreen
        ? "max-w-lg w-full mx-auto px-4 py-4 flex-1 flex flex-col min-h-0"
        : "max-w-6xl mx-auto px-4 sm:px-6 pb-4"}>
        {/* "אחורה" בטלפון מקפל את העוזר במקום לצאת מהאתר. האשף נשאר במצבו. */}
        {open && <BackLayer onBack={() => { setOpen(false); setCollapsed(true); }} />}
        {open && (
          <div className={`border shadow-lg overflow-hidden ${fullScreen ? "rounded-2xl flex-1 flex flex-col min-h-0" : "rounded-t-2xl mb-[-8px]"}`}
            style={{ background: C.card, borderColor: C.line }}>
            {/* הסרגל נשבר לשורה שנייה כשצר, במקום להיחתך: הצירוף של הצ'יפ
                עם "שיחה חדשה"/"ביטול אחרון" גלש מעבר לקצה בטלפון, והכפתורים
                פשוט לא נראו (ל-container יש overflow-hidden). */}
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2.5 border-b" style={{ borderColor: C.line }}>
              <div className="flex items-center gap-2 text-sm min-w-0" style={{ fontWeight: 600 }}>
                <Sparkles size={15} className="shrink-0" style={{ color: C.ochre }} /> <span className="whitespace-nowrap">עוזר הניהול</span>
                {wiz ? (
                  <span className="text-[11px] rounded-full px-2 py-0.5 truncate" style={{ background: "#FBF2E3", color: "#6B4E12", fontWeight: 400 }}>
                    {FLOWS[wiz.flow]?.title || "כניסת דייר"} · {wiz.sendStage ? "שליחת החוזה" : wiz.confirm ? "אישור" : wiz.fixMenu ? "תיקון" : `שאלה ${wiz.i + 1} מתוך ${(FLOWS[wiz.flow]?.steps || WIZ_STEPS).length}`}
                  </span>
                ) : focusApt && (
                  <span className="text-[11px] rounded-full px-2 py-0.5 truncate" style={{ background: "#EAF3EE", color: C.paid, fontWeight: 400 }}>
                    ממשיך עם {focusApt.tenant || focusApt.name}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 mr-auto">
                {wiz ? (
                  <button
                    onClick={() => {
                      setWiz(null);
                      /* במסך הפתיחה אין לאן לחזור מאחורי העוזר — יציאה
                         מחזירה למסך "ברוך הבא" ולא למסך ריק. */
                      if (fullScreen && onExit) { onExit(); return; }
                      setLog((l) => [...l, { role: "ai", text: "יצאנו מהאשף." }]);
                    }}
                    className="text-xs whitespace-nowrap" style={{ color: C.muted }}>
                    {fullScreen ? "חזרה" : "יציאה מהאשף"}
                  </button>
                ) : (
                  <button onClick={() => setView(view === "chat" ? "history" : "chat")} className="text-xs whitespace-nowrap" style={{ color: view === "history" ? C.teal : C.muted }}>
                    {view === "chat" ? "היסטוריה" : "חזרה לשיחה"}
                  </button>
                )}
                {/* זמין גם בתוך אשף פעיל — "יציאה מהאשף" רק סוגר את האשף,
                    בלי לאפס את השיחה; מי שרוצה להתחיל מחדש לגמרי לא צריך
                    לצאת קודם בנפרד. */}
                {(wiz || history.length > 0 || focus || log.length > 0) && (
                  <button onClick={() => {
                    setHistory([]); setFocus(null); setWiz(null);
                    localStorage.removeItem(CHAT_KEY);
                    // מתחילים מהתפריט ולא ממסך ריק — אותה נקודת פתיחה
                    // כמו בכניסה לאפליקציה.
                    const items = menuFor(dataRef.current);
                    setLog([{ role: "ai", text: "במה נתחיל?", options: items.map((m) => m.label) }]);
                  }}
                    className="text-xs whitespace-nowrap rounded-full px-2.5 py-1" style={{ background: C.teal, color: "#fff" }}>שיחה חדשה</button>
                )}
                {undo && <button onClick={() => { setData(undo); setUndo(null); setLog((l) => [...l, { role: "ai", text: "הפעולה האחרונה בוטלה." }]); }} className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: C.muted }}><Undo2 size={13} /> ביטול אחרון</button>}
                {/* במסך מלא ה-X מחזיר למסך הפתיחה, לא מקפל לכלום. */}
                <button onClick={() => { if (fullScreen && onExit) { onExit(); return; } setOpen(false); setCollapsed(true); }}
                  aria-label={fullScreen ? "חזרה" : "סגירה"} style={{ color: C.muted }}><X size={16} /></button>
              </div>
            </div>
            {view === "history" ? (
              <JournalView data={data} onReuse={(t) => { setView("chat"); quick(t); }} pastCommands={pastCommands}
                /* מחיקת ההיסטוריה: יומן הרישומים שבמסמך, הפקודות מהעבר
                   והשיחה השמורה במכשיר — הכול, אחרי אישור. */
                onClear={() => {
                  setData?.((d) => ({ ...d, journal: [] }));
                  setHistory([]); setFocus(null); setWiz(null);
                  localStorage.removeItem(CHAT_KEY);
                  setView("chat");
                  const items = menuFor(dataRef.current);
                  setLog([{ role: "ai", text: "ההיסטוריה נמחקה. במה נתחיל?", options: items.map((m) => m.label) }]);
                }} />
            ) : (
            <div ref={boxRef} className={`px-4 py-3 space-y-3 overflow-y-auto ${fullScreen ? "flex-1 min-h-0" : ""}`}
              /* הגובה נגזר ממה שנראה בפועל, לא מ-280 קבועים: גבוה מדי עם
                 מקלדת פתוחה — הסרגל ("שיחה חדשה", ✕) נדחף אל מעל המסך;
                 נמוך מדי — הפאנל צף באמצע. בטלפון הגובה קבוע (height, לא
                 רק תקרה): העוזר פרוס מקצה לקצה ומכסה גם את פס "יציאה"
                 הירוק — בדפדפן שורת הכתובת ממילא יושבת מעל, וכל פס נוסף
                 הוא בזבוז מסך. 116 ≈ הסרגל + שורת ההקלדה והשוליים.
                 בדסקטופ נשארת התקרה של 280 — שיחה בתחתית המסך, לא וילון. */
              style={fullScreen ? undefined : viewW < 640
                ? { height: Math.max(140, viewH - 116) }
                : { maxHeight: Math.max(140, Math.min(280, viewH - 180)) }}>
              {log.length > 0 && pastCommands.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pb-1">
                  {pastCommands.slice(0, 4).map((t) => (
                    <button key={t} onClick={() => quick(t)} className="text-[11px] rounded-full px-2.5 py-1 truncate"
                      style={{ border: `1px dashed ${C.line}`, color: C.muted, maxWidth: 200 }} title={t}>↻ {t}</button>
                  ))}
                </div>
              )}
              {log.length === 0 && (
                <div className="text-sm" style={{ color: C.muted }}>
                  <p className="mb-2">כתבו מה לעשות. אם יחסר לי פרט אשאל עליו. אפשר גם להתחיל מכאן:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {examples.map((e) => <button key={e} onClick={() => quick(e)} className="text-xs rounded-full px-2.5 py-1" style={{ border: `1px solid ${C.line}` }}>{e}</button>)}
                  </div>
                </div>
              )}
              {log.map((m, i) => (
                <div key={i} className="text-sm">
                  {/* pre-wrap: תשובה מפורטת מהעוזר (רשימת ערבים, סטטוס חתימות)
                      מגיעה עם שורות — בלעדיו הכול נדחס לפסקה אחת. */}
                  <div className="inline-block rounded-lg px-3 py-2 max-w-[85%] whitespace-pre-wrap" style={m.role === "user" ? { background: C.teal, color: "#fff" } : { background: "#F1F4F1" }}>
                    {m.text}
                    {m.steps?.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-[11px]" style={{ color: C.muted }}>
                        {m.steps.map((st, j) => (
                          <li key={j} className="flex items-center gap-1">
                            {st.ok ? <Check size={11} style={{ color: C.paid }} /> : <AlertTriangle size={11} style={{ color: C.late }} />} {st.text}
                          </li>
                        ))}
                      </ul>
                    )}
                    {m.waLink && (
                      <a href={m.waLink} target="_blank" rel="noreferrer"
                        className="mt-2 rounded-lg px-3 py-2 text-[12px] flex items-center justify-center gap-1.5"
                        style={{ background: "#128C7E", color: "#fff" }}>
                        <MessageCircle size={13} /> {m.waLabel || "שליחה בוואטסאפ"}
                      </a>
                    )}
                    {m.shareText && (
                      <ContractSendRow text={m.shareText} size={11}
                        onDoc={m.doc && findContract(data, m.doc.apt, m.doc.contract) ? () => setDoc(m.doc) : null}
                        onSent={m.doc ? () => setData((d) => markContractSent(d, m.doc.apt, m.doc.contract)) : null} />
                    )}
                    {m.askPhone && (
                      <PhonePick onPick={(v) => answerFrom(m, v)} />
                    )}
                    {m.askDate && <DatePick initial={m.askDate} quick={m.dateQuick} onPick={(d) => answerFrom(m, d)} />}
                    {m.askSecurities && i === log.length - 1 && (
                      <SecuritiesPick rent={m.askSecurities.rent} settings={data.settings}
                        initial={m.askSecurities.initial} months={m.askSecurities.months || 12}
                        initialRentPay={m.askSecurities.initialRentPay} onPick={pickSecurities} />
                    )}
                    {m.askInventory && i === log.length - 1 && (
                      <InventoryPick initial={m.askInventory.initial} onPick={pickInventory} />
                    )}
                    {m.askGuarantors && i === log.length - 1 && (
                      <GuarantorsPick initial={m.askGuarantors.initial} aptId={m.askGuarantors.aptId} onPick={pickGuarantors} />
                    )}
                    {m.askMeterPhoto && i === log.length - 1 && (
                      <MeterPhotoPick aptId={m.askMeterPhoto.aptId} meter={m.askMeterPhoto.meter}
                        onPick={pickMeterReading} onPhoto={noteMeterPhoto} />
                    )}
                    {m.askClausePreview && <ClausePreview />}
                    {m.askContractPreview && <ContractPreview text={m.askContractPreview} />}
                    {m.answerHint && i === log.length - 1 && (
                      <div className="mt-1.5 text-[11px] flex items-center gap-1" style={{ color: C.ochre }}>
                        <Pencil size={11} /> {m.answerHint}
                      </div>
                    )}
                    {m.options?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {m.options.map((o) => (
                          <button key={o} onClick={() => answerFrom(m, o)} className="text-[12px] rounded-full px-3 py-1.5" style={{ border: `1px solid ${C.teal}`, color: C.teal, background: "#fff", fontWeight: 600 }}>
                            {chipLabel(o)}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.nav?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: "#E1E7E3" }}>
                        {m.nav.map((o) => (
                          <button key={o} onClick={() => answerFrom(m, o)} className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.muted, background: "transparent" }}>
                            {o === "חזרה" ? <Undo2 size={11} /> : o === "דלג" ? <ChevronLeft size={11} /> : <Check size={11} />} {o}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.warn && <div className="mt-1 text-[11px]" style={{ color: C.late }}>{m.warn}</div>}
                    {m.missing?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[11px] mb-1" style={{ color: C.ochre }}>אפשר להמשיך מכאן:</div>
                        <div className="flex flex-wrap gap-1.5">
                          {m.missing.map((x) => (
                            <button key={x.label} onClick={() => quick(x.tpl)} className="text-[11px] rounded-full px-2.5 py-1" style={{ border: `1px solid ${C.line}`, background: "#fff" }}>
                              {x.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {m.debug && <div className="mt-1 text-[10px] whitespace-pre-wrap" style={{ color: C.muted }}>{m.debug}</div>}
                  </div>
                </div>
              ))}
              {busy && <div className="flex items-center gap-2 text-sm" style={{ color: C.muted }}><Loader2 size={14} className="animate-spin" /> מעבד…</div>}
            </div>
            )}
          </div>
        )}
        <div className={`flex items-center gap-2 rounded-2xl border shadow-lg p-2 ${fullScreen ? "mt-2" : ""}`}
          style={{ background: C.card, borderColor: C.line }}>
          {/* במסך מלא אין לאן לקפל — קיפול היה מותיר דף ריק לגמרי. */}
          {!fullScreen && (
            <button onClick={() => { setCollapsed(true); setOpen(false); }} aria-label="קיפול העוזר"
              className="rounded-lg w-8 h-8 flex items-center justify-center shrink-0" style={{ color: C.muted }}>
              <ChevronRight size={16} />
            </button>
          )}
          <Sparkles size={17} style={{ color: C.ochre }} />
          <input ref={inputRef} value={text} onFocus={() => setOpen(true)} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            inputMode={numericStep ? "numeric" : undefined}
            placeholder={numericStep ? "הקלידו מספר כתשובה…" : activeStep ? "הקלידו תשובה לשאלה…" : "מה לעשות? למשל: הכנס דייר ליחידת דיור 3"}
            className="flex-1" aria-label={activeStep ? "תשובה לשאלת האשף" : "הוראה לעוזר"}
            /* 16px ומעלה — מתחת לזה iOS מגדיל את כל הדף בכל מיקוד בשדה. */
            style={{
              border: "none", outline: "none", background: "transparent", padding: "8px 4px",
              fontSize: numericStep ? 18 : 16,
              fontVariantNumeric: numericStep ? "tabular-nums" : undefined,
            }} />
          <button onClick={send} disabled={busy} aria-label="הרצה" className="rounded-xl w-10 h-10 flex items-center justify-center" style={{ background: busy ? C.line : C.teal, color: "#fff" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

/* ---------------- רכיבי עזר ---------------- */

/* onClick — רשות: הופך את הכרטיס לכפתור (כמו "חובות אבודים" שמוביל
   ל"דיירים וחובות"). בלי onClick נשאר בדיוק כמו קודם — div שקוף ללחיצה.
   bg/border — רשות: כרטיס דשבורד בגוון מלא (כחול/ירוק/אדום/כתום) במקום
   כרטיס לבן עם אייקון צבעוני בלבד; ברירת המחדל היא בדיוק מה שהיה. */
function Stat({ label, value, sub, color, icon, onClick = null, bg = C.card, border = C.line }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick || undefined} type={onClick ? "button" : undefined}
      className="rounded-xl border p-3 sm:p-4 min-w-0 text-right w-full"
      style={{ background: bg, borderColor: border, cursor: onClick ? "pointer" : "default" }}>
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs" style={{ color: C.muted }}><span className="shrink-0" style={{ color }}>{icon}</span> <span className="leading-tight">{label}</span></div>
      <div className="disp text-lg sm:text-2xl font-bold mt-2 truncate" style={{ color }}>{value}</div>
      <div className="text-[10px] sm:text-[11px] mt-0.5" style={{ color: C.muted }}>{sub}</div>
    </Tag>
  );
}

/* גוונים לכרטיסי הדשבורד — צבע מלא לכל כרטיס לפי משמעותו, לא רק אייקון
   צבעוני על רקע לבן. אותם זוגות bg/text כבר קיימים במקומות אחרים
   באפליקציה (METER_HUES למים, פילי סטטוס לתשלום/חוב) — לא צבעים חדשים. */
const STAT_TINTS = {
  blue: { bg: "#E6F2FB", line: "#BEDCF0", text: "#1B5E88" },
  green: { bg: "#EAF3EE", line: "#CBE2D6", text: C.paid },
  red: { bg: "#FBEAE6", line: "#F0C9C0", text: C.late },
  orange: { bg: "#FDF3DC", line: "#F0DFAE", text: C.ochre },
};
function IconBtn({ children, onClick, label }) {
  return <button onClick={onClick} aria-label={label} className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted }}>{children}</button>;
}
/* שדה מספרי שמחזיק את מה שהוקלד כטקסט, כדי שאפשר יהיה למחוק את ה-0
   ולהקליד 0.65 בלי שהערך יקפוץ חזרה באמצע ההקלדה. */
function NumInput({ value, onCommit, placeholder, style, step }) {
  const [txt, setTxt] = useState(value == null || value === "" ? "" : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setTxt(value == null || value === "" ? "" : String(value)); }, [value, focused]);
  return (
    <input
      type="text" inputMode="decimal" step={step} placeholder={placeholder} value={txt}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); onCommit(txt === "" ? "" : Number(txt.replace(",", ".")) || 0); }}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d.,-]/g, "");
        setTxt(v);
        const n = Number(v.replace(",", "."));
        if (v !== "" && !Number.isNaN(n)) onCommit(n);
      }}
      style={style || inputStyle}
    />
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="block text-xs mb-1" style={{ color: C.muted }}>{label}</span>{children}</label>;
}
/* ============================================================================
   הגדרות — סדר הנכסים.

   סדר המערך data.apartments הוא סדר התצוגה בכל המערכת: כרטיסי הנכסים,
   לוחות הגבייה, מונים וחשבונות, הדוחות והעוזר — כולם עוברים על אותו
   מערך. לכן "לסדר את הדיירים" הוא פשוט לסדר את המערך ולשמור, בלי שדה
   נוסף ובלי מיון בכל מסך בנפרד. נכס חדש נוסף בסוף.
   ============================================================================ */
function SettingsModal({ data, setData, onClose }) {
  const [order, setOrder] = useState(() => data.apartments.map((a) => a.id));
  const [sortMode, setSortMode] = useState(data.settings?.aptSort === "billsDay" ? "billsDay" : "manual");
  const byId = Object.fromEntries(data.apartments.map((a) => [a.id, a]));
  const list = order.map((id) => byId[id]).filter(Boolean);
  const manual = sortMode === "manual";
  const dirty = order.some((id, i) => data.apartments[i]?.id !== id)
    || sortMode !== (data.settings?.aptSort === "billsDay" ? "billsDay" : "manual");

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  const byName = () => setOrder([...list].sort((a, b) => a.name.localeCompare(b.name, "he")).map((a) => a.id));
  const occupiedFirst = () => setOrder([...list].sort((a, b) => (b.tenant ? 1 : 0) - (a.tenant ? 1 : 0)).map((a) => a.id));

  const save = () => {
    setData((d) => {
      const map = Object.fromEntries(d.apartments.map((a) => [a.id, a]));
      const sorted = order.map((id) => map[id]).filter(Boolean);
      // נכס שנוסף בזמן שהחלון פתוח נשאר בסוף ולא נעלם.
      const rest = d.apartments.filter((a) => !order.includes(a.id));
      // syncApartments מחיל את המיון האוטומטי (אם נבחר) מיד, לא רק בטעינה הבאה.
      return syncApartments({
        ...d,
        settings: { ...d.settings, aptSort: sortMode },
        apartments: [...sorted, ...rest],
      });
    });
    onClose();
  };

  return (
    <Modal title="הגדרות" onClose={onClose}>
      <div className="text-sm" style={{ fontWeight: 600 }}>סדר הנכסים</div>
      <p className="text-[12px] mt-0.5 mb-3" style={{ color: C.muted }}>
        הסדר כאן הוא הסדר בכל המערכת — כרטיסי הנכסים, לוחות הגבייה, מונים וחשבונות והדוחות.
      </p>

      <div className="grid gap-1.5 mb-3">
        {[
          { k: "manual", label: "לפי החלטה שלי", hint: "מסדרים בחצים למטה, והסדר נשמר" },
          { k: "billsDay", label: "לפי יום תשלום החשבונות", hint: "המוקדם בחודש קודם · פנויות בסוף · מתעדכן לבד כשיום התשלום משתנה" },
        ].map((o) => (
          <label key={o.k} className="flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer"
            style={{ borderColor: sortMode === o.k ? C.teal : C.line, background: sortMode === o.k ? "#F1F6F5" : C.card }}>
            <input type="radio" name="aptSort" checked={sortMode === o.k} onChange={() => setSortMode(o.k)} className="mt-1" />
            <span>
              <span className="text-sm block">{o.label}</span>
              <span className="text-[11px] block" style={{ color: C.muted }}>{o.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {manual && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button onClick={byName} className="text-[11px] rounded-full px-2.5 py-1" style={{ border: `1px solid ${C.line}` }}>מיון לפי שם</button>
          <button onClick={occupiedFirst} className="text-[11px] rounded-full px-2.5 py-1" style={{ border: `1px solid ${C.line}` }}>מושכרות קודם</button>
        </div>
      )}

      <div className="rounded-lg border divide-y" style={{ borderColor: C.line }}>
        {list.map((a, i) => (
          <div key={a.id} className="flex items-center gap-2 px-3 py-2">
            <span className="text-[11px] w-5 text-center shrink-0" style={{ color: C.muted }}>{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" style={{ fontWeight: 600 }}>{a.name}</div>
              <div className="text-[11px] truncate" style={{ color: a.tenant ? C.muted : C.ochre }}>
                {a.tenant || "פנויה"}{a.tenant ? ` · חשבונות עד ה-${billsDayOf(a, data.settings)}` : ""}
              </div>
            </div>
            {manual ? (
              <>
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="למעלה"
                  className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted, opacity: i === 0 ? 0.35 : 1 }}>
                  <ArrowUp size={14} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label="למטה"
                  className="w-8 h-8 rounded-lg flex items-center justify-center border" style={{ borderColor: C.line, color: C.muted, opacity: i === list.length - 1 ? 0.35 : 1 }}>
                  <ArrowDown size={14} />
                </button>
              </>
            ) : (
              <span className="text-[11px]" style={{ color: C.muted }}>אוטומטי</span>
            )}
          </div>
        ))}
        {!list.length && <div className="px-3 py-4 text-sm" style={{ color: C.muted }}>אין עדיין נכסים.</div>}
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={!dirty} className="flex-1 rounded-lg py-2.5 text-sm"
          style={{ background: C.teal, color: "#fff", opacity: dirty ? 1 : 0.5 }}>שמירת הסדר</button>
        <button onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm" style={{ border: `1px solid ${C.line}`, color: C.muted }}>ביטול</button>
      </div>
    </Modal>
  );
}

/** ערב ריק. אותה צורה בכל מקום שיוצר ערב — האשף, העורך, והאזור האישי. */
const blankGuarantor = () => ({
  id: uid(), name: "", idNumber: "", phone: "", address: "",
  idPhoto: null, signature: null, signedAt: null,
});

/** נכס ריק לטופס. מקור אחד — הצורה הזו הופיעה קודם כליטרל ארוך בקריאה. */
const blankApt = () => ({
  id: null, name: "", rooms: "", notes: "", inventory: "",
  propertyType: "unit", authority: "",
  /* שדות התקופה נשארים כאן כברירת מחדל כדי שהטופס לא ייתקל ב-undefined,
     אבל הם אינם נשאלים על נכס חדש — ראו ההערה ב-AptModal. */
  tenant: "", phone: "", rent: 0, vaad: 0, leaseStart: "", leaseEnd: "",
  rentDay: 1, billsDay: 15, billsCycle: 1, billsAnchor: 1,
});

/* בורר "איך משולם" לסעיף שסוכם מול הדייר: ריק = ברירת המחדל מההגדרות,
   ערך = מה שסוכם מול הדייר הזה (נשמר על התקופה, payMethodBills/Vaad). */
function AgreedMethodField({ label, value, kind, settings, onChange }) {
  return (
    <Field label={label}>
      <select style={inputStyle} value={PAY_METHOD_BY_KEY[value] ? value : ""}
        onChange={(e) => onChange(e.target.value || null)}>
        <option value="">ברירת המחדל ({methodLabel(defaultMethod(settings, kind))})</option>
        {PAY_METHODS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
      </select>
    </Field>
  );
}

/* שורת הסעיפים המשותפת לכרטיס הנכס המאוחד — מוזרקת דרך ה-prop tabs
   של כל אחד מהמודלים (AptModal/TenanciesModal/AccessModal/LedgerModal),
   כך שהמעבר בין "פרטים"/"דיירים"/"גישה"/"כרטיס חוב" מחליף את התוכן
   בלי לסגור ולפתוח מודל חדש. */
const PROPERTY_CARD_SECTIONS = [
  { k: "details", label: "פרטים", icon: Home },
  { k: "tenants", label: "דיירים", icon: UserPlus },
  { k: "access", label: "גישה", icon: Fingerprint },
  { k: "ledger", label: "כרטיס חוב", icon: Wallet },
  { k: "history", label: "היסטוריה", icon: History },
  { k: "meters", label: "מונים", icon: Gauge },
];

function PropertyCardTabs({ active, onChange }) {
  return PROPERTY_CARD_SECTIONS.map(({ k, label, icon: Icon }) => (
    <button key={k} onClick={() => onChange(k)}
      className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
      style={active === k
        ? { background: C.teal, color: "#fff" }
        : { border: `1px solid ${C.line}`, color: C.muted }}>
      <Icon size={13} /> {label}
    </button>
  ));
}

/* סעיף "היסטוריה" בכרטיס הנכס המאוחד: דיירים שעברו בנכס (כולל תקופות
   שנמחקו רכות — deletedTenancies, כי הן חלק מההיסטוריה האמיתית), ממוינים
   מהחדש לישן, עם קישור מהיר לתיעוד הנכס. לחיצה על שורת דייר עדיין לא
   פותחת תצוגה (Task 7) — רק מסמנת את הדייר הנבחר דרך onOpenTenantHistory. */
function PropertyHistorySection({ apt, data, tabs, onClose, onOpenDocs, onOpenTenantHistory }) {
  const past = pastTenancies(data, apt.id)
    .concat(deletedTenancies(data, apt.id))
    .sort((a, b) => (b.leaseEnd || b.closedAt || "").localeCompare(a.leaseEnd || a.closedAt || ""));
  return (
    <Modal title={`היסטוריית הנכס — ${apt.name}`} onClose={onClose} tabs={tabs} wide>
      <button onClick={onOpenDocs} className="w-full text-right rounded-lg border p-3 mb-3 text-sm flex items-center justify-between"
        style={{ borderColor: C.line }}>
        <span>חוזים ותיעוד של הנכס</span>
        <FileText size={15} style={{ color: C.muted }} />
      </button>
      {past.length === 0 && (
        <div className="text-sm text-center py-6" style={{ color: C.muted }}>אין עדיין דיירים שעברו בנכס הזה.</div>
      )}
      <div className="space-y-2">
        {past.map((t) => (
          <button key={t.id} onClick={() => onOpenTenantHistory(t.id)}
            className="w-full text-right rounded-lg border p-3 flex items-center justify-between" style={{ borderColor: C.line }}>
            <span>
              <div className="text-sm font-bold flex items-center gap-1.5">
                {t.tenant}
                {/* תקופה שנמחקה: אין לה כרטיס חוב (ר' TenantHistory) —
                    התג הזה מסביר מראש למה, לפני שהבעלים בכלל נכנס/ת. */}
                {t.deleted && (
                  <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: "#EEEFED", color: C.muted }}>נמחק</span>
                )}
              </div>
              <div className="text-[11px]" style={{ color: C.muted }}>
                {heDate(t.leaseStart)} – {heDate(t.leaseEnd || t.closedAt)}
              </div>
            </span>
            <ChevronLeft size={15} style={{ color: C.muted }} />
          </button>
        ))}
      </div>
    </Modal>
  );
}

/* "היסטוריית דיירים מהעבר": כרטיס החוב של אותה תקופה (LedgerModal קיים,
   ב-historyMode) + התכתובת מאותה תקופה + קיצור לתיעוד הנכס — כל התמונה
   על דייר שכבר עזב במסך אחד, בלי לגלול בין לשוניות. נכנסים אליו הן
   מ"היסטוריה" בכרטיס הנכס (PropertyHistorySection) והן מ"היסטוריית
   חובות דיירים" (TenantsCenter). */
function TenantHistory({ aptId, tenancyId, data, run, saveNow, onClose, onOpenDocs }) {
  const apt = data.apartments.find((a) => a.id === aptId);
  /* tenanciesOf/deletedTenancies הן שתי תצוגות על אותו data.tenancies —
     אין שדה data.deletedTenancies על המסמך, ולכן חיפוש ישיר ב-tenancies
     מוצא גם תקופה שנמחקה (deleted:true) וגם תקופה רגילה, בלי fallback. */
  const tenancy = (data.tenancies || []).find((t) => t.id === tenancyId);
  /* תקופה שנמחקה: אין לה כרטיס חוב תקין — LedgerModal פותר את התקופה
     דרך tenanciesOf, שמסננת deleted החוצה, ומציגה בשקט את החוב של דייר
     אחר (הנוכחי/האחרון). לכן ברירת המחדל היא "תכתובת", ולשונית "כרטיס
     חוב" לא מוצגת בכלל לתקופה שנמחקה. */
  const [section, setSection] = useState(tenancy?.deleted ? "messages" : "ledger"); // "ledger" | "messages"
  const [messages, setMessages] = useState(null); // null = טרם נטען
  const [error, setError] = useState(null);
  /* שכבת back אחת למסך הזה כולו, לא לכל סעיף (ר' useBackLayer /
     Modal): המסך הזה עצמו נכנס ויוצא מה-DOM לגמרי (ההורה מרכיב/מפרק
     לפי tenantHistory), ולכן true קבוע — נדחף בהרכבה, נשלף בפירוק. */
  useBackLayer(onClose);

  useEffect(() => {
    if (section !== "messages" || messages !== null) return;
    let alive = true;
    setError(null);
    dataApi.tenancyMessages(aptId, tenancyId)
      .then((r) => { if (alive) setMessages(r.items || []); })
      .catch((e) => { if (alive) setError(e.message || "טעינת התכתובת נכשלה"); });
    return () => { alive = false; };
  }, [section, aptId, tenancyId, messages]);

  if (!apt || !tenancy) return null;

  const tabs = (
    <>
      {!tenancy.deleted && (
        <button onClick={() => setSection("ledger")} className="text-xs rounded-lg px-2.5 py-1.5"
          style={section === "ledger" ? { background: C.teal, color: "#fff" } : { border: `1px solid ${C.line}`, color: C.muted }}>כרטיס חוב</button>
      )}
      <button onClick={() => setSection("messages")} className="text-xs rounded-lg px-2.5 py-1.5"
        style={section === "messages" ? { background: C.teal, color: "#fff" } : { border: `1px solid ${C.line}`, color: C.muted }}>תכתובת</button>
      <button onClick={onOpenDocs} className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}`, color: C.muted }}>חוזים ותיעוד</button>
    </>
  );

  if (section === "ledger" && tenancy.deleted) {
    // הגנה כפולה: גם אם איכשהו הגענו למצב הזה, לעולם לא מציגים כרטיס חוב
    // של דייר אחר במקום התקופה שנמחקה.
    return (
      <Modal title={`כרטיס חוב — ${tenancy.tenant}`} onClose={onClose} tabs={tabs} wide>
        <div className="text-sm text-center py-6" style={{ color: C.muted }}>התקופה הזו נמחקה — אין כרטיס חוב זמין.</div>
      </Modal>
    );
  }

  if (section === "ledger") {
    return <LedgerModal apt={{ ...apt, id: aptId }} data={data} run={run} saveNow={saveNow} tenancyId={tenancyId} historyMode tabs={tabs} onClose={onClose} />;
  }

  return (
    <Modal title={`תכתובת — ${tenancy.tenant}`} onClose={onClose} tabs={tabs} wide>
      {error && <div className="text-sm" style={{ color: C.late }}>{error}</div>}
      {!error && messages === null && <div className="text-sm text-center py-6" style={{ color: C.muted }}>טוען…</div>}
      {!error && messages?.length === 0 && <div className="text-sm text-center py-6" style={{ color: C.muted }}>אין תכתובת מהתקופה הזו.</div>}
      {!error && messages?.length > 0 && (
        <div className="space-y-2">
          {messages.map((m) => (
            <div key={m.id} className="rounded-lg p-2.5 text-sm max-w-[85%]"
              style={m.from === "owner" ? { background: C.teal, color: "#fff", marginInlineStart: "auto" } : { background: "#F1F4F1" }}>
              {m.text}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function AptModal({ apt, settings, onSave, onClose, tabs = null }) {
  /* apt = null פירושו "נכס חדש". initial הוא הצילום שממנו התחלנו — הוא
     נחוץ כדי להשוות מולו (שינוי שכר דירה), ולכן הרכיב לא נוגע ב-apt
     ישירות בשום מקום: כל deref כזה קרס על נכס חדש ומחק את המסך. */
  const [initial] = useState(() => apt || blankApt());
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  /* נכס חדש מקבל רק את מה ששייך לנכס עצמו.
     שכר דירה, ועד בית ומועדי תשלום הם שדות של תקופת השכירות (TEN_FIELDS)
     ולא של הנכס: הם משתנים בין דייר לדייר, ובנכס שאין בו עדיין דייר אין
     לאן לכתוב אותם — הם היו נמחקים בטעינה הבאה. לכן הם מופיעים רק
     בעריכת נכס קיים, שם יש תקופה פעילה שמקבלת אותם. */
  const existing = !!initial.id;
  /* נכס קיים בלי דייר: אין תקופת שכירות שתקבל את שדות הדייר —
     update_apartment כותב אותם ו-syncApartments מוחק בטעינה הבאה. שם
     דייר שהוקלד כאן פשוט נעלם, בלי הודעה. לכן השדות מוצגים רק כשיש
     תקופה פעילה, ובנכס פנוי מוצגת הפניה לקליטת דייר. */
  const hasTenancy = existing && !!initial.tenant;

  return (
    <Modal title={existing ? "פרטי הנכס" : "נכס חדש"} onClose={onClose} tabs={tabs}>
      <div className="space-y-3">
        <Field label="כתובת או שם הנכס">
          <input style={inputStyle} value={f.name} onChange={set("name")} placeholder="ביאליק 12, רמת גן" />
          {/* בלי שם אין איך לזהות את הנכס בשום מסך — נכס ריק נכנס בשקט
              והופיע כקוביות אלמוניות בלוחות. */}
          {!String(f.name || "").trim() && (
            <span className="block text-[11px] mt-1" style={{ color: C.ochre }}>חובה — כך הנכס יזוהה בכל המסכים ובחוזה.</span>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="סוג הנכס">
            <select style={inputStyle} value={f.propertyType || "unit"} onChange={(e) => setF({ ...f, propertyType: e.target.value })}>
              {PROPERTY_TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
          </Field>
          <Field label={propertyType(f).residential ? "חדרים" : "גודל במ״ר"}>
            <input style={inputStyle} value={f.rooms} onChange={set("rooms")} />
          </Field>
        </div>

        {!existing && (f.propertyType || "unit") !== "unit" && (
          <Field label="רשות לארנונה"><input style={inputStyle} value={f.authority || ""} onChange={set("authority")} placeholder="עירייה / מועצה" /></Field>
        )}

        {existing && !hasTenancy && (
          <p className="rounded-lg p-3 text-[12px]" style={{ background: "#F1F4F1", color: C.muted }}>
            הנכס פנוי. פרטי הדייר, שכר הדירה ומועדי התשלום נקבעים ב"קליטת דייר" —
            שם נוצרת תקופת השכירות שאליה הם שייכים.
          </p>
        )}

        {hasTenancy && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="שם הדייר"><input style={inputStyle} value={f.tenant} onChange={set("tenant")} placeholder="ריק = פנויה" /></Field>
              <Field label="שכר דירה"><input style={inputStyle} type="number" placeholder="0" value={f.rent || ""} onChange={(e) => setF({ ...f, rent: e.target.value === "" ? 0 : Number(e.target.value) })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="טלפון"><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
              {/* תחילת החוזה נתמכה בשמירה אבל לא הופיעה בטופס, ולכן תאריך
                  כניסה שגוי (שקובע מאיזה חודש נספר חוב) לא היה ניתן לתיקון. */}
              <Field label="תחילת חוזה"><DateField value={f.leaseStart} onChange={(v) => setF({ ...f, leaseStart: v })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="סיום חוזה"><DateField value={f.leaseEnd} onChange={(v) => setF({ ...f, leaseEnd: v })} /></Field>
              <Field label="רשות לארנונה"><input style={inputStyle} value={f.authority || ""} onChange={set("authority")} placeholder="עירייה / מועצה" /></Field>
            </div>
            <div className="rounded-lg border p-3 space-y-3" style={{ borderColor: C.line, background: "#FBFCFB" }}>
              <div className="text-xs" style={{ fontWeight: 600 }}>מועדי תשלום וועד בית</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="יום תשלום שכר דירה">
                  <input style={inputStyle} type="number" min="1" max="28" value={f.rentDay ?? 1} onChange={(e) => setF({ ...f, rentDay: Number(e.target.value) })} />
                </Field>
                <Field label="יום תשלום חשבונות">
                  <input style={inputStyle} type="number" min="1" max="28" value={f.billsDay ?? 15} onChange={(e) => setF({ ...f, billsDay: Number(e.target.value) })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="מחזור חשבונות">
                  <select style={inputStyle} value={f.billsCycle ?? 1} onChange={(e) => setF({ ...f, billsCycle: Number(e.target.value) })}>
                    <option value={1}>חודשי</option>
                    <option value={2}>דו-חודשי</option>
                  </select>
                </Field>
                {Number(f.billsCycle) === 2 && (
                  <Field label="חודשי הגבייה">
                    <select style={inputStyle} value={f.billsAnchor ?? 1} onChange={(e) => setF({ ...f, billsAnchor: Number(e.target.value) })}>
                      <option value={1}>אי-זוגיים (ינו׳, מרץ, מאי…)</option>
                      <option value={2}>זוגיים (פבר׳, אפר׳, יוני…)</option>
                    </select>
                  </Field>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="ועד בית חודשי"><input style={inputStyle} type="number" placeholder="0" value={f.vaad || ""} onChange={(e) => setF({ ...f, vaad: e.target.value === "" ? 0 : Number(e.target.value) })} /></Field>
                <label className="flex items-center gap-2 text-sm pt-5">
                  <input type="checkbox" checked={f.vaadActive !== false} onChange={(e) => setF({ ...f, vaadActive: e.target.checked })} />
                  ועד בית פעיל
                </label>
              </div>
              {/* איך משולם — מה שסוכם מול הדייר הזה, כמו אמצעי שכר הדירה
                  שנקבע בקליטה. משמש כברירת המחדל כשמסמנים "התקבל". */}
              <div className="grid grid-cols-2 gap-3">
                <AgreedMethodField label="חשבונות צריכה — איך משולמים" kind="elec" settings={settings}
                  value={f.payMethodBills} onChange={(v) => setF({ ...f, payMethodBills: v })} />
                <AgreedMethodField label="ועד בית — איך משולם" kind="vaad" settings={settings}
                  value={f.payMethodVaad} onChange={(v) => setF({ ...f, payMethodVaad: v })} />
              </div>
            </div>
          </>
        )}

        {/* ציוד שנשאר בנכס בין דייר לדייר — מקרר, תנור, מזגנים. הרשימה
            שייכת לנכס: עד כאן היא הייתה שדה של תקופת השכירות בלבד, ולכן
            כל דייר חדש התחיל מרשימה ריקה והיא הוקלדה מחדש. */}
        <Field label="מטלטלין וציוד קבוע בנכס">
          <textarea value={f.inventory || ""} rows={3} onChange={(e) => setF({ ...f, inventory: e.target.value })}
            placeholder={"פריט בכל שורה"} style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        <p className="text-[11px]" style={{ color: C.muted, marginTop: -6 }}>
          נכנס לנספח א׳ של החוזה, ומשמש כברירת מחדל בקליטת דייר חדש.
        </p>

        <Field label="הערות"><input style={inputStyle} value={f.notes} onChange={set("notes")} /></Field>

        {/* נשאל באשף המודרך בקליטת דייר, אבל עד כה לא היה שום מקום לתקן
            אם ההחלטה השתנתה — זה סעיף של הנכס עצמו, לא של תקופת שכירות. */}
        <label className="flex items-start gap-2 text-[12px] cursor-pointer" style={{ color: C.ink }}>
          <input type="checkbox" className="mt-0.5" checked={!!f.clauseShabbat}
            onChange={(e) => setF({ ...f, clauseShabbat: e.target.checked })} />
          <span>להוסיף לחוזה של הנכס הזה סעיף שמירת שבת ומועדי ישראל</span>
        </label>

        {!existing && (
          <p className="text-[12px]" style={{ color: C.muted }}>
            שכר הדירה, יום התשלום וועד הבית ייקבעו כשתכניס דייר — הם משתנים
            בין דייר לדייר ולכן נשמרים על תקופת השכירות.
          </p>
        )}
      </div>
      {/* שינוי שכר דירה הוא שני דברים שונים: העלאה מתאריך מסוים, או
          תיקון של סכום שהוקלד לא נכון מלכתחילה. עד כאן תמיד נוצרה
          מדרגה, ומי שתיקן טעות השאיר את כל החודשים הקודמים בסכום השגוי. */}
      {hasTenancy && Number(f.rent) !== Number(initial.rent) && (
        <div className="mt-3 rounded-lg p-3" style={{ background: "#FBF2E3", color: "#6B4E12" }}>
          <div className="text-[12px] mb-2">
            {nis(Number(initial.rent) || 0)} ← {nis(Number(f.rent) || 0)}. מה זה?
          </div>
          <label className="flex items-start gap-2 text-[12px] cursor-pointer mb-1">
            <input type="radio" name="rent-change" className="mt-0.5" checked={f.rentFix !== true}
              onChange={() => setF({ ...f, rentFix: false })} />
            <span><b>עדכון מתאריך</b> — החודשים שלפניו נשארים בסכום הקודם</span>
          </label>
          <label className="flex items-start gap-2 text-[12px] cursor-pointer">
            <input type="radio" name="rent-change" className="mt-0.5" checked={f.rentFix === true}
              onChange={() => setF({ ...f, rentFix: true })} />
            <span><b>תיקון טעות</b> — הסכום היה שגוי מלכתחילה, בכל התקופה</span>
          </label>
          {f.rentFix !== true && (
            <div className="mt-2">
              <Field label="מאיזה תאריך חל השינוי">
                <DateField value={f.effectiveFrom || TODAY} onChange={(v) => setF({ ...f, effectiveFrom: v })} />
              </Field>
            </div>
          )}
        </div>
      )}
      <button onClick={() => onSave({ ...f, name: String(f.name || "").trim() })}
        disabled={!String(f.name || "").trim()}
        className="mt-5 w-full rounded-lg py-2.5 text-sm"
        style={{ background: C.teal, color: "#fff", opacity: String(f.name || "").trim() ? 1 : 0.5 }}>
        {existing ? "שמירת השינויים" : "הוספת הנכס"}
      </button>
    </Modal>
  );
}
function SwapTenantModal({ apt, onSave, onClose }) {
  const [f, setF] = useState({ tenant: "", phone: "", rent: apt.rent, leaseStart: TODAY, leaseEnd: addYears(TODAY, 1), moveOut: TODAY, prorateFirstMonth: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  /* חודש ראשון חלקי — רק כשהכניסה לא ב-1 לחודש. תצוגה מקדימה של הסכום
     היחסי, כדי שההחלטה תתקבל עם המספר לפני השמירה ולא אחריה. */
  const startDay = Number((f.leaseStart || "").slice(8, 10)) || 1;
  const partialMonth = (() => {
    if (startDay <= 1) return null;
    const [y, m] = (f.leaseStart || "").split("-").map(Number);
    if (!y || !m) return null;
    const daysInMonth = new Date(y, m, 0).getDate();
    const occupiedDays = daysInMonth - startDay + 1;
    const prorated = Math.round((Number(f.rent) || 0) * occupiedDays / daysInMonth);
    return { daysInMonth, occupiedDays, prorated };
  })();
  return (
    <Modal title={apt.tenant ? "החלפת דייר" : "כניסת דייר"} onClose={onClose}>
      {apt.tenant && (
        <div className="rounded-lg p-3 mb-4 text-sm" style={{ background: "#F1F4F1" }}>
          <div style={{ fontWeight: 600 }}>{apt.tenant} עוזב/ת את {apt.name}</div>
          <label className="block mt-2 text-xs" style={{ color: C.muted }}>תאריך היציאה
            <DateField style={{ marginTop: 4 }} value={f.moveOut} onChange={(v) => setF({ ...f, moveOut: v })} /></label>
          <div className="text-[11px] mt-2" style={{ color: C.muted }}>הדייר היוצא נשמר בהיסטוריה יחד עם תשלומי העבר. חוזה חדש ייווצר לדייר הנכנס.</div>
        </div>
      )}
      <div className="space-y-3">
        <Field label="הדייר הנכנס"><input style={inputStyle} value={f.tenant} onChange={set("tenant")} placeholder="ריק = הדירה נשארת פנויה" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="טלפון"><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
          <Field label="שכר דירה חדש"><input style={inputStyle} type="number" placeholder="0" value={f.rent || ""} onChange={(e) => setF({ ...f, rent: e.target.value === "" ? 0 : Number(e.target.value) })} /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="תחילת חוזה"><DateField value={f.leaseStart} onChange={(v) => setF({ ...f, leaseStart: v })} /></Field>
          <Field label="סיום חוזה"><DateField value={f.leaseEnd} onChange={(v) => setF({ ...f, leaseEnd: v })} /></Field>
        </div>
        {partialMonth && f.tenant && (
          <label className="flex items-start gap-2 text-xs rounded-lg border p-2.5 cursor-pointer" style={{ borderColor: C.line, background: "#FBFCFB" }}>
            <input type="checkbox" className="mt-0.5" checked={!!f.prorateFirstMonth}
              onChange={(e) => setF({ ...f, prorateFirstMonth: e.target.checked })} />
            <span>
              חיוב יחסי לחודש הראשון — {f.tenant} גר/ה בו {partialMonth.occupiedDays} מתוך {partialMonth.daysInMonth} ימים
              {f.prorateFirstMonth ? <> · <b>{nis(partialMonth.prorated)}</b> במקום חודש מלא</> : " (חודש מלא כברירת מחדל)"}
            </span>
          </label>
        )}
      </div>
      <button onClick={() => onSave(f)} className="mt-5 w-full rounded-lg py-2.5 text-sm" style={{ background: C.teal, color: "#fff" }}>{f.tenant ? "עדכון הדייר" : "סימון הדירה כפנויה"}</button>
    </Modal>
  );
}

/**
 * עריכת הדייר הנוכחי — תיקון פרטים בלי החלפת דייר ובלי לגעת בהיסטוריה.
 *
 * ההבדל מ"החלפת דייר": שם ופרטים מתעדכנים על אותה תקופת שכירות, ולא
 * נפתחת תקופה חדשה. שינוי שכר דירה נרשם דרך update_apartment כמדרגה
 * מתוארכת, כך שחודשי העבר לא מתייקרים רטרואקטיבית.
 */
/**
 * @param tenancy תקופה מפורשת לעריכה. בלעדיה — הדייר הפעיל, כמו קודם.
 *        זה מה שמאפשר לתקן פרטים של דייר עתידי או של מי שכבר עזב:
 *        עד כאן הם לא היו ניתנים לעריכה בשום מסך, וטעות בתאריך או
 *        בטלפון נשארה במסמך ובחוזה.
 */
function TenantEditModal({ apt, run, onClose, tenancy = null, settings = null }) {
  // התקופה גוברת על הנכס: הנכס משקף תמיד את הדייר הפעיל.
  const src = tenancy || apt;
  const intake = src.intake || {};
  const s = settings || defTariffs();
  const [f, setF] = useState({
    tenant: src.tenant || "", phone: src.phone || "",
    email: intake.email || "", tenantId: intake.tenantId || "",
    partnerName: intake.partnerName || "", partnerId: intake.partnerId || "", partnerPhone: intake.partnerPhone || "",
    persons: intake.persons ?? "",
    rent: src.rent ?? "", effectiveFrom: TODAY,
    leaseStart: src.leaseStart || "", leaseEnd: src.leaseEnd || "",
    rentDay: src.rentDay ?? 1, billsDay: src.billsDay ?? 15,
    billsCycle: src.billsCycle ?? 1, billsAnchor: src.billsAnchor ?? 1,
    prorateFirstMonth: !!src.prorateFirstMonth, firstMonthAmount: src.firstMonthAmount ?? null,

    vaadActive: src.vaadActive === true, vaad: src.vaad ?? "",
    vaadProrateFirstMonth: !!src.vaadProrateFirstMonth, vaadFirstMonthAmount: src.vaadFirstMonthAmount ?? null,
    arnonaActive: src.arnonaActive === true, arnona: src.arnona ?? "",
    arnonaProrateFirstMonth: !!src.arnonaProrateFirstMonth, arnonaFirstMonthAmount: src.arnonaFirstMonthAmount ?? null,
    municipalActive: src.municipalActive === true, municipal: src.municipal ?? "", municipalLabel: src.municipalLabel || "",
    municipalProrateFirstMonth: !!src.municipalProrateFirstMonth, municipalFirstMonthAmount: src.municipalFirstMonthAmount ?? null,

    rateElec: src.rates?.elec ?? "", rateWS: src.rates?.waterSewage ?? "", rateGas: src.rates?.gas ?? "",
    utilities: { ...(src.utilities || {}) },

    securities: Array.isArray(src.securities) ? src.securities : [],
    payMethodRent: src.payMethodRent || "", rentChecks: src.rentChecks || "",

    rentSteps: (src.rentSteps || []).map((x) => ({ ...x })),
    contents: src.contents || "",

    keys: intake.keys ?? "", gasBalloon: !!intake.gasBalloon,
    gasAmount: intake.gasAmount ?? "", gasSmallAmount: intake.gasSmallAmount ?? "",
    handoverNotes: intake.handoverNotes || "",
  });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const gasRelevant = (f.utilities?.gas ?? utilityMode(apt, "gas")) !== "none";

  /* הסיסמה לאזור האישי — למקרה שהדייר שכח. נשלפת רק בלחיצה מפורשת,
     כי כל צפייה נרשמת ביומן הביקורת. */
  const [pw, setPw] = useState(null);   // null | "loading" | {code|null, active}
  const showPw = async () => {
    setPw("loading");
    try { setPw(await dataApi.accessCode(apt.id)); }
    catch { setPw({ active: false, code: null }); }
  };
  const resetPw = async () => {
    setPw("loading");
    try {
      const r = await dataApi.issueCode(apt.id);
      setPw({ active: true, code: r.code, setBy: "owner" });
    } catch { setPw({ active: false, code: null }); }
  };

  const rentChanged = Number(f.rent) !== Number(src.rent);

  const save = () => {
    if (!f.tenant.trim()) { setErr("שם הדייר לא יכול להישאר ריק — להוצאת דייר השתמשו ב\"תקופות שכירות\"."); return; }
    const act = {
      type: "update_apartment", apartment: apt.id,
      tenant: f.tenant.trim(), phone: f.phone,
      leaseStart: f.leaseStart, leaseEnd: f.leaseEnd,
      rentDay: Number(f.rentDay) || 1, billsDay: Number(f.billsDay) || 15,
      billsCycle: Number(f.billsCycle) || 1, billsAnchor: Number(f.billsAnchor) || 1,
      intake: {
        email: f.email, tenantId: f.tenantId,
        partnerName: f.partnerName, partnerId: f.partnerId, partnerPhone: f.partnerPhone,
        persons: Number(f.persons) || 0,
        keys: Number(f.keys) || 0, gasBalloon: f.gasBalloon,
        gasAmount: Number(f.gasAmount) || 0, gasSmallAmount: Number(f.gasSmallAmount) || 0,
        handoverNotes: f.handoverNotes,
      },
      // נשלח תמיד במפורש (לא מותנה) כדי שביטול/ניקוי ידרוס ערך קודם ולא
      // רק יימנע מלהוסיף חדש — זו עריכה של תקופה קיימת, לא יצירה חדשה.
      prorateFirstMonth: !!f.prorateFirstMonth,
      firstMonthAmount: f.prorateFirstMonth && f.firstMonthAmount != null ? Number(f.firstMonthAmount) : null,

      vaadActive: !!f.vaadActive, vaad: Number(f.vaad) || 0,
      vaadProrateFirstMonth: !!f.vaadProrateFirstMonth,
      vaadFirstMonthAmount: f.vaadProrateFirstMonth && f.vaadFirstMonthAmount != null ? Number(f.vaadFirstMonthAmount) : null,
      arnonaActive: !!f.arnonaActive, arnona: Number(f.arnona) || 0,
      arnonaProrateFirstMonth: !!f.arnonaProrateFirstMonth,
      arnonaFirstMonthAmount: f.arnonaProrateFirstMonth && f.arnonaFirstMonthAmount != null ? Number(f.arnonaFirstMonthAmount) : null,
      municipalActive: !!f.municipalActive, municipal: Number(f.municipal) || 0, municipalLabel: f.municipalLabel || "",
      municipalProrateFirstMonth: !!f.municipalProrateFirstMonth,
      municipalFirstMonthAmount: f.municipalProrateFirstMonth && f.municipalFirstMonthAmount != null ? Number(f.municipalFirstMonthAmount) : null,

      rates: {
        elec: f.rateElec === "" ? null : Number(f.rateElec),
        waterSewage: f.rateWS === "" ? null : Number(f.rateWS),
        gas: f.rateGas === "" ? null : Number(f.rateGas),
      },
      utilities: f.utilities,

      securities: f.securities,
      payMethodRent: f.payMethodRent || null,
      rentChecks: f.payMethodRent === "check" ? (Number(f.rentChecks) || leaseMonthsOf(f)) : 0,

      rentSteps: (f.rentSteps || []).filter((x) => x.from && x.rent).map((x) => ({ from: x.from, rent: Number(x.rent) })),
      contents: f.contents,

      ...(tenancy ? { tenancyId: tenancy.id } : {}),
    };
    if (rentChanged && f.rent !== "") {
      act.rent = Number(f.rent);
      act.effectiveFrom = f.effectiveFrom || TODAY;
    }
    run([act]);
    onClose();
  };

  return (
    <Modal title={tenancy ? `עריכת ${tenancy.tenant || "דייר"}` : "עריכת דייר"} onClose={onClose}>
      <p className="text-sm mb-4" style={{ color: C.muted }}>
        {apt.name} · {tenancy
          ? `תקופה ${heDate(tenancy.leaseStart)}–${tenancyEnd(tenancy) ? heDate(tenancyEnd(tenancy)) : "…"}${tenancy.closed ? " (הסתיימה)" : tenancy.leaseStart > TODAY ? " (טרם התחילה)" : ""}`
          : 'עדכון פרטי הדייר הנוכחי. להחלפה בדייר אחר — "דייר נוסף".'}
      </p>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="שם מלא"><input style={inputStyle} value={f.tenant} onChange={set("tenant")} /></Field>
          <Field label="טלפון"><PhoneField value={f.phone} onChange={(v) => setF({ ...f, phone: v })} /></Field>
          <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={f.tenantId} onChange={set("tenantId")} /></Field>
          <Field label="דוא״ל"><input style={inputStyle} value={f.email} onChange={set("email")} /></Field>
          <Field label="תחילת חוזה"><DateField value={f.leaseStart} onChange={(v) => setF({ ...f, leaseStart: v })} /></Field>
          <Field label="סיום חוזה"><DateField value={f.leaseEnd} onChange={(v) => setF({ ...f, leaseEnd: v })} /></Field>
          <Field label="יום תשלום שכ״ד"><input style={inputStyle} type="number" min="1" max="28" value={f.rentDay} onChange={set("rentDay")} /></Field>
          <Field label="יום תשלום חשבונות"><input style={inputStyle} type="number" min="1" max="28" value={f.billsDay} onChange={set("billsDay")} /></Field>
          <Field label="מחזור חשבונות">
            <select style={inputStyle} value={f.billsCycle} onChange={(e) => setF({ ...f, billsCycle: Number(e.target.value) })}>
              <option value={1}>חודשי</option><option value={2}>דו-חודשי</option>
            </select>
          </Field>
          {Number(f.billsCycle) === 2 && (
            <Field label="חודשי הגבייה">
              <select style={inputStyle} value={f.billsAnchor} onChange={(e) => setF({ ...f, billsAnchor: Number(e.target.value) })}>
                <option value={1}>אי-זוגיים</option><option value={2}>זוגיים</option>
              </select>
            </Field>
          )}
          <Field label="מספר נפשות במושכר"><input style={inputStyle} type="number" placeholder="0" value={f.persons} onChange={set("persons")} /></Field>
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>בן/בת זוג או שוכר נוסף</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="שם"><input style={inputStyle} value={f.partnerName} onChange={set("partnerName")} /></Field>
            <Field label="תעודת זהות"><input style={inputStyle} inputMode="numeric" value={f.partnerId} onChange={set("partnerId")} /></Field>
            <Field label="טלפון"><PhoneField value={f.partnerPhone} onChange={(v) => setF({ ...f, partnerPhone: v })} /></Field>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="שכר דירה חודשי">
            <input style={inputStyle} type="number" placeholder="0" value={f.rent}
              onChange={(e) => setF({ ...f, rent: e.target.value })} />
          </Field>
          {rentChanged && f.rent !== "" && (
            <Field label="השינוי חל מ־" hint="נשמר כמדרגה — חודשי עבר לא משתנים">
              <DateField value={f.effectiveFrom} onChange={(v) => setF({ ...f, effectiveFrom: v })} />
            </Field>
          )}
        </div>

        {/* חיוב יחסי לחודש הראשון — אותה אפשרות שיש בקליטת דייר, אבל כאן
            לתקופה שכבר נוצרה. בלעדיה אין דרך לתקן דייר שהוזן בלי לסמן
            את זה מלכתחילה (למשל דרך האשף המודרך, ששם "דילוג" משאיר חודש
            מלא בלי שום אינדיקציה). */}
        {(() => {
          const p = firstMonthProration(f.rent, f.leaseStart);
          if (!p) return null;
          return (
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-xs rounded-lg border p-2.5 cursor-pointer" style={{ borderColor: C.line, background: "#FBFCFB" }}>
                <input type="checkbox" className="mt-0.5" checked={!!f.prorateFirstMonth}
                  onChange={(e) => setF({ ...f, prorateFirstMonth: e.target.checked })} />
                <span>
                  חיוב יחסי לחודש הראשון ({monthLabel(f.leaseStart.slice(0, 7))}) — {p.occupiedDays} מתוך {p.daysInMonth} ימים
                  {f.prorateFirstMonth ? <> · <b>{nis(f.firstMonthAmount ?? p.prorated)}</b> במקום חודש מלא</> : " (חודש מלא כברירת מחדל)"}
                </span>
              </label>
              {f.prorateFirstMonth && (
                <Field label="סכום אחר לחודש הראשון (אם סוכם בעל פה)">
                  <input style={inputStyle} type="number" placeholder={String(p.prorated)}
                    value={f.firstMonthAmount ?? ""} onChange={(e) => setF({ ...f, firstMonthAmount: e.target.value === "" ? null : Number(e.target.value) })} />
                </Field>
              )}
            </div>
          );
        })()}

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.vaadActive} onChange={(e) => setF({ ...f, vaadActive: e.target.checked })} />
            ועד בית — האם חל על הדייר?
          </label>
          {f.vaadActive && (
            <div className="mt-2">
              <Field label="סכום ועד בית לחודש">
                <NumInput value={f.vaad} placeholder="0" onCommit={(v) => setF({ ...f, vaad: v })} />
              </Field>
              <FirstMonthProrate f={f} setF={setF} prefix="vaad" amount={f.vaad} />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={!!f.arnonaActive} onChange={(e) => setF({ ...f, arnonaActive: e.target.checked })} />
            ארנונה — נגבית דרכך מהדייר?
          </label>
          {f.arnonaActive && (
            <div className="mt-2">
              <Field label="סכום ארנונה לחודש">
                <NumInput value={f.arnona} placeholder="0" onCommit={(v) => setF({ ...f, arnona: v })} />
              </Field>
              <FirstMonthProrate f={f} setF={setF} prefix="arnona" amount={f.arnona} />
            </div>
          )}

          <label className="flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={!!f.municipalActive} onChange={(e) => setF({ ...f, municipalActive: e.target.checked })} />
            היטל עירוני נוסף (שמירה, ביוב וכדומה)
          </label>
          {f.municipalActive && (
            <div className="mt-2">
              <div className="grid grid-cols-2 gap-2">
                <Field label="שם ההיטל">
                  <input style={inputStyle} value={f.municipalLabel || ""} placeholder="היטל שמירה"
                    onChange={(e) => setF({ ...f, municipalLabel: e.target.value })} />
                </Field>
                <Field label="סכום לחודש">
                  <NumInput value={f.municipal} placeholder="0" onCommit={(v) => setF({ ...f, municipal: v })} />
                </Field>
              </div>
              <FirstMonthProrate f={f} setF={setF} prefix="municipal" amount={f.municipal} />
            </div>
          )}
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>תעריפי הצריכה ומי משלם למי</div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Field label="חשמל ₪ לקוט״ש">
              <NumInput value={f.rateElec} placeholder={String(s.elecRate)} onCommit={(v) => setF({ ...f, rateElec: v })} />
            </Field>
            <Field label="מים וביוב ₪ למ״ק">
              <NumInput value={f.rateWS} placeholder={String(s.waterSewageRate)} onCommit={(v) => setF({ ...f, rateWS: v })} />
            </Field>
            <Field label="גז ₪ למ״ק (לא חובה)">
              <NumInput value={f.rateGas} placeholder={String(s.gasRate || 0)} onCommit={(v) => setF({ ...f, rateGas: v })} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "elec", label: "חשמל" }, { k: "water", label: "מים וביוב" }, { k: "gas", label: "גז" },
              ...(f.arnonaActive ? [{ k: "arnona", label: "ארנונה" }] : []),
              ...(f.vaadActive ? [{ k: "vaad", label: "ועד בית" }] : []),
            ].map(({ k, label }) => (
              <Field key={k} label={label}>
                <select style={inputStyle} value={f.utilities?.[k] || ""}
                  onChange={(e) => setF({ ...f, utilities: { ...f.utilities, [k]: e.target.value || undefined } })}>
                  <option value="">ברירת מחדל</option>
                  <option value="landlord">משולם לי</option>
                  <option value="direct">ישירות ל{k === "vaad" ? "ועד" : "ספק"}</option>
                  {k !== "vaad" && <option value="none">ללא חיוב</option>}
                </select>
              </Field>
            ))}
          </div>
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <SecuritiesPicker value={f.securities} rent={Number(f.rent) || 0} settings={s}
            onChange={(v) => setF({ ...f, securities: v })}
            months={leaseMonthsOf(f)}
            rentPay={{ method: f.payMethodRent || "", checks: Number(f.rentChecks) || 0 }}
            onRentPay={(rp) => setF({ ...f, payMethodRent: rp.method, rentChecks: rp.method === "check" ? (rp.checks || "") : "" })} />
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-1" style={{ fontWeight: 600 }}>עליית שכר דירה מדורגת</div>
          <p className="text-[11px] mb-2" style={{ color: C.muted }}>אופציונלי — מאיזה תאריך ומה יהיה שכר הדירה.</p>
          {(f.rentSteps || []).map((st, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <DateField value={st.from} ariaLabel="מתאריך" onChange={(v) => { const r = [...f.rentSteps]; r[i] = { ...r[i], from: v }; setF({ ...f, rentSteps: r }); }} style={{ padding: "6px 8px", fontSize: 13 }} />
              <input type="number" placeholder="שכר דירה" value={st.rent} onChange={(e) => { const r = [...f.rentSteps]; r[i] = { ...r[i], rent: e.target.value }; setF({ ...f, rentSteps: r }); }} style={{ ...inputStyle, padding: "6px 8px", fontSize: 13 }} />
              <button onClick={() => setF({ ...f, rentSteps: f.rentSteps.filter((_, j) => j !== i) })} className="rounded-lg px-2" style={{ border: `1px solid ${C.line}`, color: C.late }}><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={() => setF({ ...f, rentSteps: [...(f.rentSteps || []), { from: addYears(f.leaseStart || TODAY, 1), rent: "" }] })}
            className="text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1" style={{ border: `1px solid ${C.line}`, color: C.teal }}>
            <Plus size={13} /> הוספת מדרגה
          </button>
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>מטלטלין שנמסרו לדייר</div>
          <textarea value={f.contents} onChange={set("contents")} placeholder={"תנור בנוי"} style={{ ...inputStyle, height: 90 }} />
        </div>

        <div className="rounded-lg border p-3" style={{ borderColor: C.line }}>
          <div className="text-xs mb-2" style={{ fontWeight: 600 }}>מסירת הנכס</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="מספר מפתחות שנמסרו"><input style={inputStyle} type="number" placeholder="0" value={f.keys} onChange={set("keys")} /></Field>
            {gasRelevant && (
              <>
                <Field label="בלון גז גדול — לחיוב">
                  <input style={inputStyle} type="number" placeholder="0" value={f.gasAmount} onChange={set("gasAmount")} disabled={!f.gasBalloon} />
                </Field>
                <Field label="בלון גז קטן — לחיוב">
                  <input style={inputStyle} type="number" placeholder="0" value={f.gasSmallAmount} onChange={set("gasSmallAmount")} disabled={!f.gasBalloon} />
                </Field>
              </>
            )}
          </div>
          {gasRelevant && (
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={f.gasBalloon} onChange={(e) => setF({ ...f, gasBalloon: e.target.checked })} />
              נמסר בלון גז מלא במעמד קבלת המפתח
            </label>
          )}
          <div className="mt-3"><Field label="הערות מסירה"><input style={inputStyle} value={f.handoverNotes} onChange={set("handoverNotes")} placeholder="למשל: שלט לחניה, ריהוט שנשאר בדירה" /></Field></div>
        </div>

        {/* הסיסמה של הדייר, שמורה כאן למקרה ששכח. ברירת המחדל: 6 ספרות
            הת"ז; הדייר יכול לבחור אחרת מהאזור האישי שלו. */}
        <div className="rounded-lg border p-3" style={{ borderColor: C.line, background: "#FBFCFB" }}>
          <div className="flex items-center justify-between">
            <div className="text-xs" style={{ fontWeight: 600 }}>הסיסמה לאזור האישי</div>
            {pw === null && (
              <button onClick={showPw} className="text-xs" style={{ color: C.teal }}>הצגה</button>
            )}
          </div>
          {pw === "loading" && <div className="text-xs mt-1" style={{ color: C.muted }}>טוען…</div>}
          {pw && pw !== "loading" && (
            pw.code ? (
              <div className="mt-1 flex items-center justify-between">
                <span className="disp text-xl font-bold" style={{ letterSpacing: 4 }}>{pw.code}</span>
                <span className="text-[11px]" style={{ color: C.muted }}>
                  {pw.setBy === "tenant" ? "הדייר בחר אותה" : "ברירת המחדל"}
                </span>
              </div>
            ) : (
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="text-xs" style={{ color: C.muted }}>
                  {pw.active ? "הסיסמה הונפקה לפני שהסיסמאות נשמרו לצפייה." : "עדיין לא הונפקה סיסמה."}
                </span>
                <button onClick={resetPw} className="text-xs rounded-lg px-2.5 py-1.5 shrink-0"
                  style={{ border: `1px solid ${C.line}`, color: C.teal }}>
                  הנפקה
                </button>
              </div>
            )
          )}
        </div>
      </div>
      {err && <div className="mt-3 rounded-lg p-2.5 text-sm" style={{ background: "#FBF2E3", color: "#6B4E12" }}>{err}</div>}
      <button onClick={save} className="mt-5 w-full rounded-lg py-2.5 text-sm" style={{ background: C.teal, color: "#fff" }}>
        שמירת השינויים
      </button>
    </Modal>
  );
}

/**
 * @param request בקשת הארכה של הדייר שהנספח עונה עליה (אופציונלי). תאריך
 *        הסיום שביקש הוא ברירת המחדל, והשמירה נושאת requestId כדי שהבקשה
 *        תסומן כמאושרת. onDecline דוחה אותה במקום — עם סיבה לדייר.
 */
function ExtendModal({ apt, settings, onSave, onClose, request = null, onDecline = null, declineFirst = false }) {
  const base = apt.leaseEnd ? new Date(new Date(apt.leaseEnd).getTime() + 86400000).toISOString().slice(0, 10) : TODAY;
  const [f, setF] = useState({ from: base, to: request?.to || addYears(base, 1), rent: apt.rent, notes: "" });
  const [declining, setDeclining] = useState(!!(request && declineFirst));
  const [reason, setReason] = useState("");
  const bump = (pct) => setF({ ...f, rent: Math.round(apt.rent * (1 + pct / 100)) });
  return (
    <Modal title={request ? "בקשת הארכה מהדייר" : "הארכת חוזה"} onClose={onClose}>
      <p className="text-sm mb-4" style={{ color: C.muted }}>{apt.tenant || "ללא דייר"} · {apt.name}. החוזה הנוכחי מסתיים ב-{heDate(apt.leaseEnd)}.</p>
      {request && (
        <div className="rounded-lg border p-3 mb-4 text-sm" style={{ borderColor: "#E8C98A", background: "#FBF2E3" }}>
          <div className="flex items-center gap-1.5" style={{ fontWeight: 600, color: "#6B4E12" }}>
            <CalendarPlus size={14} /> {apt.tenant || "הדייר"} ביקש/ה להאריך עד {heDate(request.to)}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "#6B4E12" }}>
            נשלחה ב-{heDate(request.createdAt)}{request.note ? ` · "${request.note}"` : ""}. אפשר לאשר כפי שביקש/ה, לשנות את התאריך או השכר למטה, או לדחות.
          </div>
          {!declining ? (
            <button onClick={() => setDeclining(true)} className="mt-2 text-xs rounded-lg px-2.5 py-1.5 flex items-center gap-1"
              style={{ border: "1px solid #E8C98A", color: "#6B4E12", background: "#fff" }}>
              <X size={12} /> דחיית הבקשה
            </button>
          ) : (
            <div className="mt-2">
              <Field label="סיבת הדחייה (הדייר יראה אותה — לא חובה)">
                <input style={inputStyle} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="למשל: הדירה מיועדת למכירה" />
              </Field>
              <div className="flex gap-2 mt-2">
                <button onClick={() => onDecline && onDecline(reason.trim())} className="flex-1 rounded-lg py-2 text-sm" style={{ background: C.late, color: "#fff" }}>
                  דחיית הבקשה
                </button>
                <button onClick={() => setDeclining(false)} className="rounded-lg px-3 text-sm" style={{ border: `1px solid ${C.line}`, color: C.muted }}>
                  חזרה
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="תחילת התקופה"><DateField value={f.from} onChange={(v) => setF({ ...f, from: v })} /></Field>
          <Field label="סיום התקופה"><DateField value={f.to} onChange={(v) => setF({ ...f, to: v })} /></Field>
        </div>
        <Field label="שכר דירה בתקופה החדשה">
          <input style={inputStyle} type="number" placeholder="0" value={f.rent || ""} onChange={(e) => setF({ ...f, rent: e.target.value === "" ? 0 : Number(e.target.value) })} />
        </Field>
        <div className="flex gap-2">
          {[0, 2, 3, 5].map((p) => (
            <button key={p} onClick={() => bump(p)} className="text-xs rounded-lg px-2.5 py-1.5" style={{ border: `1px solid ${C.line}` }}>
              {p === 0 ? "ללא שינוי" : `+${p}%`}
            </button>
          ))}
        </div>
        {/* שדה שורה אחת לא הספיק: נספח הארכה הוא בדיוק המקום שבו מוסיפים
            את מה שסוכם מאז — חניה, תיקון שהובטח, שינוי בכללי הבית. כל
            שורה נכנסת כסעיף ממוספר. */}
        <Field label="תנאים נוספים שסוכמו — שורה לכל סעיף">
          <textarea value={f.notes} rows={3} onChange={(e) => setF({ ...f, notes: e.target.value })}
            placeholder={"החניה מספר 4 נכללת מעתה"}
            style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
      </div>
      <div className="text-[11px] mt-3" style={{ color: C.muted }}>
        הצמדה למדד: {settings.indexPct || 0}% · ביטחונות: {securitiesLabel(apt.securities, Number(f.rent) || apt.rent || 0, settings)} · יום תשלום שכ״ד: {rentDayOf(apt, settings)} · חשבונות: עד ה-{billsDayOf(apt, settings)}{billsCycleOf(apt, settings) === 2 ? " (דו-חודשי)" : ""}.
      </div>
      {/* מה יופיע בנספח, לפני שמפיקים אותו. הנספח קצר במכוון: הוא לא
          חוזר על ההסכם המקורי אלא אומר מה השתנה ומה מוסיפים. */}
      <div className="mt-3 rounded-lg border p-3 text-[12px]" style={{ borderColor: C.line, background: "#FBFCFB" }}>
        <div className="mb-1" style={{ fontWeight: 600 }}>מה ייכתב בנספח</div>
        <div style={{ color: C.muted }}>
          • התקופה מוארכת {heDate(f.from)} — {heDate(f.to)}<br />
          • דמי שכירות {nis(Number(f.rent) || 0)} לחודש{Number(f.rent) !== Number(apt.rent) ? ` (במקום ${nis(apt.rent)})` : " (ללא שינוי)"}<br />
          {String(f.notes || "").split("\n").filter((x) => x.trim()).map((x, i) => <span key={i}>• {x.trim()}<br /></span>)}
          • יתר תנאי ההסכם המקורי נותרים בתוקפם
          {(apt.guarantors || []).length > 0 && <><br />• הערבים מאשרים שהערבות חלה גם על התקופה המוארכת</>}
        </div>
      </div>

      <button onClick={() => onSave(request ? { ...f, requestId: request.id } : f)} className="mt-4 w-full rounded-lg py-2.5 text-sm" style={{ background: C.teal, color: "#fff" }}>
        {request ? "אישור הבקשה ויצירת נספח הארכה" : "יצירת נספח הארכה"}
      </button>
    </Modal>
  );
}
function ConfirmModal({ title, body, action, onClose }) {
  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm" style={{ color: C.muted }}>{body}</p>
      <div className="flex gap-2 mt-5">
        <button onClick={() => { action(); onClose(); }} className="flex-1 rounded-lg py-2.5 text-sm" style={{ background: C.late, color: "#fff" }}>כן, להסיר</button>
        <button onClick={onClose} className="flex-1 rounded-lg py-2.5 text-sm" style={{ border: `1px solid ${C.line}` }}>ביטול</button>
      </div>
    </Modal>
  );
}
